import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Button, Space, Tag, message, Modal, Descriptions, Card, Form, Input, InputNumber, Row, Col, Select, Typography, Pagination, Tooltip, Switch, Dropdown, Checkbox } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, EditOutlined, CloseCircleOutlined, PlusOutlined, CopyOutlined, MoreOutlined, ExclamationCircleFilled, DeleteOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import request from '../../utils/request';
import { useTableQuery } from '../../hooks/useTableQuery';
import { getCurrentUserProjects, getCustomerList } from '../../services/project';
import { getAllWarehouses } from '../../services/warehouse';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import { getInventoryUnitDetail } from '../../services/inventory';
import { runAutoReviewByTemplateCode, StrategyRegistry } from '../../services/autoReview/strategies';
import InstructionTargetsOverview from './components/InstructionTargetsOverview';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import { downloadFileAsBlob } from '../../utils/downloadFile';
import { getNonStdWaitingIds } from '../../services/nonstandardOps';

const { Title, Text } = Typography;
const { Option } = Select;

// 兼容 --isolatedModules：确保该文件始终被 TypeScript 识别为模块（即使后续合并/裁剪导致 import/export 被意外移除）
export {};

/** 代发指令类型：单品单件、单品多件、多品多件 */
const DROPSHIP_INSTRUCTION_TYPES = ['单品单件', '单品多件', '多品多件'] as const;

interface InstructionFilters {
  system_code?: string;
  status?: string;
  project?: string; // project id as string
  warehouse?: string; // warehouse id as string
  template?: string; // instruction template id as string
  template_system_code?: string; // dashboard 快捷跳转模板系统编码
  keyword?: string; // name or remarks contains
  logistics_info?: string; // related logistics info
  recipient_info?: string; // recipient info
  customer?: string; // customer id (admin only)
  hide_cancelled?: string; // '1' 开启隐藏，'' 关闭
  dropship_instruction_type?: string[]; // 代发指令类型：仅当模板含「一件代发」时生效
}

// 是否允许管理员/员工在列表里手动填写包装结果（点"待仓库提供包装结果"徽标弹窗）。
// 目前关闭：手动填写会绕过执行流程（不产生 IOC、不需要受理），并让客户端误以为"仓库已提供
// 包装结果"。包装结果应由执行阶段创建 IOC 后自动回写。只是禁用入口，弹窗与保存逻辑
// 都保留，以后需要恢复（例如执行阶段回写失败后的人工补录）把它改回 true 即可。
const ALLOW_MANUAL_PACK_RESULT_EDIT = false;

const InstructionListPage: React.FC = () => {
  const [inspect, setInspect] = useState<any | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [form] = Form.useForm();
  const [userMap, setUserMap] = useState<Record<string, string>>({});
  const [targetsOf, setTargetsOf] = useState<any | null>(null);
  const [historyTargetsOf, setHistoryTargetsOf] = useState<any | null>(null);
  const [historyTargetsRows, setHistoryTargetsRows] = useState<any[]>([]);
  const [historyTargetsLoading, setHistoryTargetsLoading] = useState(false);
  const [historyTargetsPage, setHistoryTargetsPage] = useState(1);
  const [historyTargetsPageSize, setHistoryTargetsPageSize] = useState(10);
  const [historyEstimatedQtyByInstructionId, setHistoryEstimatedQtyByInstructionId] = useState<Record<string, number>>({});
  const [historyInputIocRowsCacheByInstructionId, setHistoryInputIocRowsCacheByInstructionId] = useState<Record<string, any[]>>({});
  const countUniqueIocContainers = useCallback((rows: any[]): number => {
    const arr = Array.isArray(rows) ? rows : [];
    if (arr.length === 0) return 0;
    const keys = new Set<string>();
    arr.forEach((r: any, idx: number) => {
      const pid = Number(r?.project_id);
      const projectKey = Number.isFinite(pid) ? String(pid) : '-';
      const id = String(r?.output_container_id || '').trim();
      const code = String(r?.output_code || r?.display_code || '').trim().toUpperCase();
      if (id) {
        keys.add(`${projectKey}::id::${id}`);
        return;
      }
      if (code) {
        keys.add(`${projectKey}::code::${code}`);
        return;
      }
      keys.add(`fallback::${idx}`);
    });
    return keys.size;
  }, []);
  const historyTargetsVisualRows = useMemo(() => {
    const rows = Array.isArray(historyTargetsRows) ? [...historyTargetsRows] : [];
    const parseCodeSortMeta = (row: any) => {
      const code = String(row?.output_code || row?.display_code || '').trim().toUpperCase();
      if (!code) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
      const parts = code.split('-');
      if (parts.length < 2) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
      const groupRaw = parts[parts.length - 2];
      const tailRaw = parts[parts.length - 1];
      const groupNum = Number(groupRaw);
      const tailNum = Number(tailRaw);
      return {
        groupNum: Number.isFinite(groupNum) ? groupNum : -1,
        tailNum: Number.isFinite(tailNum) ? tailNum : Number.MAX_SAFE_INTEGER,
      };
    };
    const getRole = (r: any): 'parent' | 'child' | 'standalone' => {
      const isChild = !!r?.is_child_container || !!r?.parent_container_id || !!r?.parent_code;
      const hasChildren = !!r?.is_parent_container || (Array.isArray(r?.child_codes) && r.child_codes.length > 0);
      if (isChild) return 'child';
      if (hasChildren) return 'parent';
      return 'standalone';
    };
    rows.sort((a: any, b: any) => {
      const roleA = getRole(a);
      const roleB = getRole(b);
      const codeA = String(a?.output_code || a?.display_code || '').trim();
      const codeB = String(b?.output_code || b?.display_code || '').trim();
      const parentCodeA = String(a?.parent_code || '').trim();
      const parentCodeB = String(b?.parent_code || '').trim();
      const rootA = parentCodeA || codeA;
      const rootB = parentCodeB || codeB;
      const rootMetaA = parseCodeSortMeta({ output_code: rootA });
      const rootMetaB = parseCodeSortMeta({ output_code: rootB });
      if (rootMetaA.groupNum !== rootMetaB.groupNum) return rootMetaB.groupNum - rootMetaA.groupNum;
      if (rootMetaA.tailNum !== rootMetaB.tailNum) return rootMetaA.tailNum - rootMetaB.tailNum;
      const groupKeyA = roleA === 'standalone' ? `self::${codeA}` : `hier::${rootA}`;
      const groupKeyB = roleB === 'standalone' ? `self::${codeB}` : `hier::${rootB}`;
      if (groupKeyA !== groupKeyB) return groupKeyA.localeCompare(groupKeyB);
      const roleOrderA = roleA === 'parent' ? 0 : (roleA === 'child' ? 1 : 0);
      const roleOrderB = roleB === 'parent' ? 0 : (roleB === 'child' ? 1 : 0);
      if (roleOrderA !== roleOrderB) return roleOrderA - roleOrderB;
      const selfMetaA = parseCodeSortMeta({ output_code: codeA });
      const selfMetaB = parseCodeSortMeta({ output_code: codeB });
      if (selfMetaA.tailNum !== selfMetaB.tailNum) return selfMetaA.tailNum - selfMetaB.tailNum;
      return codeA.localeCompare(codeB);
    });
    return rows;
  }, [historyTargetsRows]);
  const historyTargetsVisualMeta = useMemo(() => {
    const rowRoleMap = new Map<string, 'parent' | 'child' | 'standalone'>();
    const rowGroupBorderMap = new Map<string, { grouped: boolean; top: boolean; bottom: boolean }>();
    const rowGroupKeyMap = new Map<string, string>();
    const groupCount = new Map<string, number>();
    const getRowKey = (r: any, i?: number) => String(r?.__key || r?.output_container_id || r?.output_code || i || '');
    (historyTargetsVisualRows || []).forEach((r: any, idx: number) => {
      const rowKey = getRowKey(r, idx);
      const code = String(r?.output_code || r?.display_code || '').trim();
      const parentCode = String(r?.parent_code || '').trim();
      const isChild = !!r?.is_child_container || !!r?.parent_container_id || !!parentCode;
      const hasChildren = !!r?.is_parent_container || (Array.isArray(r?.child_codes) && r.child_codes.length > 0);
      const role: 'parent' | 'child' | 'standalone' = isChild ? 'child' : (hasChildren ? 'parent' : 'standalone');
      const rootCode = parentCode || code;
      const groupKey = role === 'standalone' ? `standalone:${code}` : `hier:${rootCode}`;
      rowRoleMap.set(rowKey, role);
      rowGroupKeyMap.set(rowKey, groupKey);
      groupCount.set(groupKey, (groupCount.get(groupKey) || 0) + 1);
    });
    (historyTargetsVisualRows || []).forEach((r: any, idx: number) => {
      const rowKey = getRowKey(r, idx);
      const groupKey = String(rowGroupKeyMap.get(rowKey) || '');
      const prevKey = idx > 0 ? String(rowGroupKeyMap.get(getRowKey(historyTargetsVisualRows[idx - 1], idx - 1)) || '') : '';
      const nextKey = idx < historyTargetsVisualRows.length - 1 ? String(rowGroupKeyMap.get(getRowKey(historyTargetsVisualRows[idx + 1], idx + 1)) || '') : '';
      const grouped = groupKey.startsWith('hier:') && Number(groupCount.get(groupKey) || 0) > 1;
      rowGroupBorderMap.set(rowKey, {
        grouped,
        top: grouped && prevKey !== groupKey,
        bottom: grouped && nextKey !== groupKey,
      });
    });
    return { rowRoleMap, rowGroupBorderMap };
  }, [historyTargetsVisualRows]);
  const [projectsOf, setProjectsOf] = useState<any | null>(null);
  // 执行信息弹窗（与日志一致的时区显示：使用 TimezoneAwareTime）
  const [execInfoOf, setExecInfoOf] = useState<{ row: any; started: string | Date | null; days?: number; unknown?: boolean } | null>(null);
  // 行级乐观覆盖：用于立即显示取消后的状态
  const [rowOverrides, setRowOverrides] = useState<Record<string, Partial<any>>>({});
  // 修改状态弹窗
  const [statusOf, setStatusOf] = useState<any | null>(null);
  const [statusNext, setStatusNext] = useState<string | undefined>(undefined);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [appendAvailabilityById, setAppendAvailabilityById] = useState<Record<string, number>>({});
  // 跨页累积选择（全选仅对本页生效，可多页叠加；取消勾选=从选中中排除）
  const [manualSelectedRowKeys, setManualSelectedRowKeys] = useState<Set<string>>(new Set());
  const isAdminOrStaff = useMemo(() => {
    try {
      const path = String((window as any)?.location?.pathname || '');
      return path.startsWith('/dashboard/admin') || path.startsWith('/dashboard/staff');
    } catch { return false; }
  }, []);
  const normalizeWaybillSource = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (s === 'warehouse' || s === 'upload') return s;
    if (s === '仓库提供') return 'warehouse';
    if (s === '上传运单') return 'upload';
    return s;
  }, []);
  const normalizeWaybillSubmitMode = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (s === 'immediate' || s === 'after_pack') return s;
    if (s === '立即上传') return 'immediate';
    if (s === '先申请包装数据') return 'after_pack';
    return s;
  }, []);
  const isOnePieceRow = useCallback((row: any) => {
    try {
      const snap = row?.template_snapshot || {};
      const detail = row?.template_detail || {};
      const name = snap.name || detail.name || row?.template_name || '';
      return String(name || '').includes('一件代发');
    } catch { return false; }
  }, []);
  const isRegularOutboundRow = useCallback((row: any) => {
    try {
      const snap = row?.template_snapshot || {};
      const detail = row?.template_detail || {};
      const name = snap.name || detail.name || row?.template_name || '';
      return String(name || '').includes('常规出库');
    } catch { return false; }
  }, []);
  const normalizeShippingLabelTargetMappingAnswer = useCallback((raw: any) => {
    const original = String(raw ?? '').trim();
    if (!original) return '';
    const s = original.toLowerCase();
    if (s === 'has_mapping' || s === 'true' || s === '1' || s === 'yes') return 'has_mapping';
    if (s === 'no_mapping' || s === 'false' || s === '0' || s === 'no') return 'no_mapping';
    if (original.includes('有对应关系')) return 'has_mapping';
    if (original.includes('无对应关系')) return 'no_mapping';
    return s;
  }, []);
  const normalizeSortingStrategyAnswer = useCallback((raw: any) => {
    const original = String(raw ?? '').trim();
    if (!original) return '';
    const s = original.toLowerCase();
    if (s === 'grouped' || s === 'group' || s === 'group_mode' || s === 'grouped_mode') return 'grouped';
    if (s === 'standard' || s === 'standard_mode') return 'standard';
    if (original.includes('分组')) return 'grouped';
    if (original.includes('标准')) return 'standard';
    return s;
  }, []);
  const isSortAndCountRow = useCallback((row: any) => {
    try {
      const snap = row?.template_snapshot || {};
      const detail = row?.template_detail || {};
      const name = snap.name || detail.name || row?.template_name || '';
      return String(name || '').includes('库存分拣与清点');
    } catch { return false; }
  }, []);
  const shouldRequireShippingLabelMappingBaseline = useCallback((row: any) => {
    try {
      if (!isRegularOutboundRow(row)) return false;
      const ans = (row?.customer_answers || {}) as any;
      const normalized = normalizeShippingLabelTargetMappingAnswer(ans?.shipping_label_to_target_mapping);
      return normalized === 'has_mapping';
    } catch { return false; }
  }, [isRegularOutboundRow, normalizeShippingLabelTargetMappingAnswer]);
  const shouldRequireGroupListBaseline = useCallback((row: any) => {
    try {
      if (!isSortAndCountRow(row)) return false;
      const ans = (row?.customer_answers || {}) as any;
      const normalized = normalizeSortingStrategyAnswer(ans?.sorting_strategy);
      return normalized === 'grouped';
    } catch { return false; }
  }, [isSortAndCountRow, normalizeSortingStrategyAnswer]);
  const shouldShowPackBadge = useCallback((row: any) => {
    try {
      if (!isOnePieceRow(row)) return false;
      const ans = row?.customer_answers || {};
      const waybillSource = normalizeWaybillSource(ans?.waybill_source);
      const submitMode = normalizeWaybillSubmitMode(ans?.waybill_submit_mode || row?.waybill_submit_mode);
      return waybillSource === 'upload' && submitMode === 'after_pack';
    } catch { return false; }
  }, [isOnePieceRow, normalizeWaybillSource, normalizeWaybillSubmitMode]);
  // 包装结果弹窗（列表内）
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [packModalMode, setPackModalMode] = useState<'view' | 'edit'>('view');
  const [packModalRow, setPackModalRow] = useState<any | null>(null);
  const [packModalSaving, setPackModalSaving] = useState(false);
  const [packModalForm] = Form.useForm();
  // 审核弹窗
  const [reviewOf, setReviewOf] = useState<any | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'accept' | 'reject' | undefined>(undefined);
  const [reviewReason, setReviewReason] = useState<string>('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewExecutionTips, setReviewExecutionTips] = useState<string>('');
  const [estimatedDays, setEstimatedDays] = useState<number | undefined>(undefined);
  const [estimatedUnknown, setEstimatedUnknown] = useState<boolean>(false);
  const [reviewBillingCollapsed, setReviewBillingCollapsed] = useState<boolean>(true);
  // 基准数据（审核/详情流转）
  const [baselineReqItems, setBaselineReqItems] = useState<any[]>([]);
  const [baselineRows, setBaselineRows] = useState<any[]>([]);
  const [baselineLoading, setBaselineLoading] = useState(false);
  // 基线所属指令ID（用于防止串数据）
  const [baselineOwnerId, setBaselineOwnerId] = useState<number | null>(null);
  // BARCODE_MAPPING：上传文件前确认（需要二次确认后才打开文件选择框）
  const bmBaselineFileInputRef = useRef<HTMLInputElement | null>(null);
  const bmBaselineFilePickBypassRef = useRef<boolean>(false);
  // BARCODE_MAPPING：附件预览页自动执行一次“匹配操作对象”
  const bmAttPreviewAutoMatchSigRef = useRef<string>('');
  // 本次审核会话：初始基线文件ID快照 + 是否对基线上传过文件
  const baselineInitialFileIdsRef = React.useRef<Set<number>>(new Set());
  const baselineSessionTouchedRef = React.useRef<boolean>(false);
  // 本次审核会话：初始基线完整快照（用于回滚 状态/新建记录/新增文件）
  const baselineInitialSnapshotRef = React.useRef<{
    idSet: Set<number>;
    idToState: Record<string, string>;
    idToFileIds: Record<string, Set<number>>;
  } | null>(null);
  // 从客户上传附件中选择 → 相关状态
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [attachPickerBaseline, setAttachPickerBaseline] = useState<{ id: number; code: string; acceptExts: string[] } | null>(null);
  const [attachPickerLoading, setAttachPickerLoading] = useState(false);
  const [customerAttachments, setCustomerAttachments] = useState<any[]>([]);
  const [attachPickerSelectedIds, setAttachPickerSelectedIds] = useState<Set<number>>(new Set());
  // 从客户上传附件中选择：分页（受控，用于在分页器右侧放置“关闭”按钮）
  const [attachPickerPage, setAttachPickerPage] = useState<number>(1);
  const [attachPickerPageSize, setAttachPickerPageSize] = useState<number>(8);
  // 客户附件名可编辑映射：attachment.id -> editedName
  const [attachNameEdits, setAttachNameEdits] = useState<Record<string, string>>({});
  // 预览（附件）
  const [attPreviewOpen, setAttPreviewOpen] = useState(false);
  const [attPvLoading, setAttPvLoading] = useState(false);
  const [attPvKind, setAttPvKind] = useState<'csv' | 'xlsx' | 'pdf' | 'other' | null>(null);
  const [attPvCols, setAttPvCols] = useState<any[]>([]);
  const [attPvData, setAttPvData] = useState<any[]>([]);
  const [attPvSheetName, setAttPvSheetName] = useState<string>('');
  const [attPvUrl, setAttPvUrl] = useState<string>('');
  const [attPvError, setAttPvError] = useState<string>('');
  const [attPvPageSize, setAttPvPageSize] = useState<number>(20);
  const [attPvTitle, setAttPvTitle] = useState<string>('');
  // 指令概览附件预览（独立于“从客户上传附件中选择”链路）
  const [ovAttPreviewOpen, setOvAttPreviewOpen] = useState(false);
  const [ovAttPvLoading, setOvAttPvLoading] = useState(false);
  const [ovAttPvKind, setOvAttPvKind] = useState<'csv' | 'xlsx' | 'pdf' | 'image' | 'video' | 'other' | null>(null);
  // 库存非标准操作指令：哪些正在等客户反馈（系统编码旁显示标记）
  const [nonstdWaitingIds, setNonstdWaitingIds] = useState<number[]>([]);
  // 视频预览：下载按钮用的文件名
  const [ovAttPvVideoName, setOvAttPvVideoName] = useState<string>('');
  const [ovAttPvVideoDownloading, setOvAttPvVideoDownloading] = useState(false);
  const [ovAttPvCols, setOvAttPvCols] = useState<any[]>([]);
  const [ovAttPvData, setOvAttPvData] = useState<any[]>([]);
  const [ovAttPvSheetName, setOvAttPvSheetName] = useState<string>('');
  const [ovAttPvUrl, setOvAttPvUrl] = useState<string>('');
  const [ovAttPvError, setOvAttPvError] = useState<string>('');
  const [ovAttPvTitle, setOvAttPvTitle] = useState<string>('');
  // 压缩包在线解压列表（只读预览）
  const [attPvArchList, setAttPvArchList] = useState<Array<{ path: string; dir: boolean; size: number }>>([]);
  const [attPvEntryName, setAttPvEntryName] = useState<string>('');
  const [attPvArchiveRoot, setAttPvArchiveRoot] = useState<string>('');
  // 压缩包条目列表分页（受控）
  const [attPvArchPage, setAttPvArchPage] = useState<number>(1);
  const [attPvArchPageSize, setAttPvArchPageSize] = useState<number>(20);
  // 通用归档：类型与内存缓冲，用于 zip/rar/7z/tar.* 解码器复用
  const [attPvArchiveKind, setAttPvArchiveKind] = useState<string>('');
  const [attPvArchiveBuf, setAttPvArchiveBuf] = useState<ArrayBuffer | null>(null);
  const [attPvArchiveAttachId, setAttPvArchiveAttachId] = useState<number | null>(null);
  // 覆盖：本地编辑版本（仅前端内存）
  const [editedAttachMap, setEditedAttachMap] = useState<Record<string, { blob: Blob; fileName: string; mime?: string; updatedAt: number }>>({});
  // 当前预览绑定的客户附件ID（用于保存编辑）
  const [attPvAttachId, setAttPvAttachId] = useState<number | null>(null);
  // 预览表格是否有未保存修改
  const [attPvDirty, setAttPvDirty] = useState<boolean>(false);
  // BARCODE_MAPPING（新旧条码对应表）内容审核结果：仅在“从客户上传附件中选择”里预览该类型时展示
  const [attPvBmAudit, setAttPvBmAudit] = useState<{
    noTargetRows: Array<{ rowNo: number; old: string }>;
    newEmptyRows: Array<{ rowNo: number; old: string }>;
    sameRows: Array<{ rowNo: number; old: string; newer: string }>;
    dupPairs: Array<{ key: string; count: number; rowNos: number[] }>;
    badRows: Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }>;
    groupOrder: string[];
    groupMap: Record<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }>>;
  } | null>(null);
  // BARCODE_MAPPING：标记错误行（用于在预览表格中高亮）
  const [attPvBmMarkEq, setAttPvBmMarkEq] = useState<Set<number>>(new Set());     // 旧=新（__idx）
  const [attPvBmMarkDup, setAttPvBmMarkDup] = useState<Set<number>>(new Set());   // 重复组合（__idx）
  const [attPvBmMarkBad, setAttPvBmMarkBad] = useState<Set<number>>(new Set());   // 多映射且数量无效（__idx）
  const [attPvBmMarkNewEmpty, setAttPvBmMarkNewEmpty] = useState<Set<number>>(new Set()); // 新条码为空（__idx）
  // BARCODE_MAPPING：匹配操作对象（预览页自动执行）
  const [attPvBmTargetMatchByIdx, setAttPvBmTargetMatchByIdx] = useState<Record<number, string>>({});
  const [attPvBmMarkNoTarget, setAttPvBmMarkNoTarget] = useState<Set<number>>(new Set()); // 无法匹配操作对象（__idx）
  // BARCODE_MAPPING：手动指定过的行（避免“再次审核/自动匹配”覆盖用户手工结果）
  const [attPvBmManualMatchIdx, setAttPvBmManualMatchIdx] = useState<Set<number>>(new Set());
  const [attPvSltmAudit, setAttPvSltmAudit] = useState<{
    incompleteRows: Array<{ rowNo: number; a: string; b: string; c: string; d: string }>;
    invalidBRows: Array<{ rowNo: number; b: string }>;
    dupC: Array<{ value: string; count: number; rowNos: number[] }>;
    dupD: Array<{ value: string; count: number; rowNos: number[] }>;
  } | null>(null);
  const [attPvSltmMarkIncomplete, setAttPvSltmMarkIncomplete] = useState<Set<number>>(new Set()); // 记录不完整
  const [attPvSltmMarkBNotOne, setAttPvSltmMarkBNotOne] = useState<Set<number>>(new Set()); // B列不为1
  const [attPvSltmMarkDupC, setAttPvSltmMarkDupC] = useState<Set<number>>(new Set()); // C列重复
  const [attPvSltmMarkDupD, setAttPvSltmMarkDupD] = useState<Set<number>>(new Set()); // D列重复
  const [bmBatchPickOpen, setBmBatchPickOpen] = useState<boolean>(false);
  const [bmBatchPickRows, setBmBatchPickRows] = useState<any[]>([]);
  const [bmBatchPickSel, setBmBatchPickSel] = useState<Record<number, number[]>>({}); // __idx -> iuIds[]
  const [bmTargetIuOptions, setBmTargetIuOptions] = useState<Array<{ id: number; system_barcode?: string; use_all_available?: boolean; expected_qty?: number | null }>>([]);
  const [bmTargetIuLoading, setBmTargetIuLoading] = useState<boolean>(false);
  const bmEqBg = '#ffa39e';   // 更深浅红
  const bmDupBg = '#91caff';  // 更深浅蓝
  const bmBadBg = '#ffd666';  // 更深浅橙
  const bmNewEmptyBg = '#d9d9d9'; // 灰：缺新条码
  const bmNoTargetBg = '#fff0f6'; // 浅粉：无法匹配操作对象
  const sltmIncompleteBg = '#fff1f0';
  const sltmBNotOneBg = '#fff7e6';
  const sltmDupCBg = '#e6f4ff';
  const sltmDupDBg = '#f6ffed';
  const runBmAuditFromCurrentTable = useCallback(() => {
    try {
      const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
      if (!attachPickerOpen || !isBm) return;
      const normCode = (v:any) => String(v ?? '').trim().toUpperCase();
      const rows = Array.isArray(attPvData) ? attPvData : [];
      const newEmptyRows: Array<{ rowNo: number; old: string }> = [];
      const sameRows: Array<{ rowNo: number; old: string; newer: string }> = [];
      const pairMap: Map<string, Array<{ rowNo: number; old: string; newer: string }>> = new Map();
      const oldToLines = new Map<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any }>>();
      rows.forEach((row:any) => {
        const __idx = Number(row?.__idx);
        if (!Number.isFinite(__idx) || __idx <= 0) return;
        const fileRowNo = __idx + 1; // 含表头（第1行）
        const a = normCode(row?.c0);
        const b = normCode(row?.c1);
        // 新增审核#1：新条码为空（忽略大小写/前后空格后仍为空）
        if (!b) {
          newEmptyRows.push({ rowNo: fileRowNo, old: a || '-' });
          return;
        }
        // 新增审核#2：重复的新旧组合需要包含“旧条码缺失但新条码一样”的记录
        // 旧条码缺失时按 '-' 参与组合统计（key='-→NEW'）
        const aForPair = a || '-';
        const key = `${aForPair}→${b}`;
        const arr = pairMap.get(key) || [];
        arr.push({ rowNo: fileRowNo, old: aForPair, newer: b });
        pairMap.set(key, arr);
        // 其余审核保持不变：仅对 a/b 都存在的行执行
        if (!a) return;
        if (a === b) sameRows.push({ rowNo: fileRowNo, old: a, newer: b });
        const lines = oldToLines.get(a) || [];
        lines.push({ rowNo: fileRowNo, a, b, c: row?.c2, d: row?.c3, e: row?.c4 });
        oldToLines.set(a, lines);
      });
      const dupPairs = Array.from(pairMap.entries())
        .filter(([, arr]) => (arr?.length || 0) >= 2)
        .map(([k, arr]) => ({ key: String(k), count: arr.length, rowNos: arr.map(x => x.rowNo) }));
      const badRows: Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }> = [];
      oldToLines.forEach((arr, a) => {
        if (!arr || arr.length <= 1) return;
        const distinctNew = Array.from(new Set(arr.map(x => x.b).filter(Boolean)));
        if (distinctNew.length <= 1) return;
        const hasInvalidQty = arr.some(x => {
          const v = x.d;
          const num = Number(v);
          return v === undefined || v === null || String(v).trim() === '' || Number.isNaN(num);
        });
        if (!hasInvalidQty) return;
        arr.forEach(x => {
          badRows.push({ rowNo: x.rowNo, a: x.a, b: x.b, c: x.c, d: x.d, e: x.e, groupKey: a });
        });
      });
      const groupOrder: string[] = [];
      const groupMap: Record<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }>> = {};
      badRows.forEach((r) => {
        const k = r.groupKey || '';
        if (!k) return;
        if (!groupMap[k]) { groupMap[k] = []; groupOrder.push(k); }
        groupMap[k].push(r);
      });
      const computed = { newEmptyRows, sameRows, dupPairs, badRows, groupOrder, groupMap };
      setAttPvBmAudit((prev) => {
        const prevNoTarget = Array.isArray(prev?.noTargetRows) ? prev!.noTargetRows : [];
        const has = prevNoTarget.length || newEmptyRows.length || sameRows.length || dupPairs.length || badRows.length;
        if (!has) return null;
        return { noTargetRows: prevNoTarget, newEmptyRows, sameRows, dupPairs, badRows, groupOrder, groupMap };
      });
      // 重新审核后，默认清空高亮标记，避免旧标记与新结果错位
      setAttPvBmMarkEq(new Set());
      setAttPvBmMarkDup(new Set());
      setAttPvBmMarkBad(new Set());
      setAttPvBmMarkNewEmpty(new Set());
      return computed;
    } catch {}
  }, [attachPickerBaseline?.code, attachPickerOpen, attPvData]);
  const runSltmAuditFromCurrentTable = useCallback(() => {
    try {
      const isSltm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
      if (!attachPickerOpen || !isSltm) return;
      const rows = Array.isArray(attPvData) ? attPvData : [];
      const toNorm = (v:any) => String(v ?? '').trim();
      const toDupKey = (v:any) => String(v ?? '').trim().toUpperCase();
      const incompleteRows: Array<{ rowNo: number; a: string; b: string; c: string; d: string }> = [];
      const invalidBRows: Array<{ rowNo: number; b: string }> = [];
      const cMap = new Map<string, number[]>();
      const dMap = new Map<string, number[]>();
      rows.forEach((row:any) => {
        const __idx = Number(row?.__idx);
        if (!Number.isFinite(__idx) || __idx <= 0) return;
        const rowNo = __idx + 1; // 含表头
        const a = toNorm(row?.c0);
        const b = toNorm(row?.c1);
        const c = toNorm(row?.c2);
        const d = toNorm(row?.c3);
        if (!a && !b && !c && !d) return;
        if (!a || !b || (!c && !d)) {
          incompleteRows.push({ rowNo, a, b, c, d });
        }
        if (b && b !== '1') {
          invalidBRows.push({ rowNo, b });
        }
        if (c) {
          const key = toDupKey(c);
          const arr = cMap.get(key) || [];
          arr.push(rowNo);
          cMap.set(key, arr);
        }
        if (d) {
          const key = toDupKey(d);
          const arr = dMap.get(key) || [];
          arr.push(rowNo);
          dMap.set(key, arr);
        }
      });
      const dupC = Array.from(cMap.entries())
        .filter(([, rowNos]) => (rowNos?.length || 0) >= 2)
        .map(([value, rowNos]) => ({ value, count: rowNos.length, rowNos }));
      const dupD = Array.from(dMap.entries())
        .filter(([, rowNos]) => (rowNos?.length || 0) >= 2)
        .map(([value, rowNos]) => ({ value, count: rowNos.length, rowNos }));
      const computed = { incompleteRows, invalidBRows, dupC, dupD };
      const has = incompleteRows.length || invalidBRows.length || dupC.length || dupD.length;
      setAttPvSltmAudit(has ? computed : null);
      setAttPvSltmMarkIncomplete(new Set());
      setAttPvSltmMarkBNotOne(new Set());
      setAttPvSltmMarkDupC(new Set());
      setAttPvSltmMarkDupD(new Set());
      return computed;
    } catch {}
  }, [attachPickerBaseline?.code, attachPickerOpen, attPvData]);
  useEffect(() => {
    try {
      const isSltm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
      const isTable = attPvKind === 'csv' || attPvKind === 'xlsx';
      if (!attPreviewOpen || !isSltm || !isTable || attPvLoading) return;
      if (!Array.isArray(attPvData) || attPvData.length === 0) return;
      runSltmAuditFromCurrentTable();
    } catch {}
  }, [attPreviewOpen, attachPickerBaseline?.code, attPvKind, attPvLoading, attPvData, runSltmAuditFromCurrentTable]);
  // 预览表格选中行（用于插入/删除行）
  const [attPvSelectedRowKeys, setAttPvSelectedRowKeys] = useState<React.Key[]>([]);
  // 预览表格目标行号（用于剪切/复制到指定位置）
  const [attPvTargetIdx, setAttPvTargetIdx] = useState<number>(1);
  // 动态 import 助手（避免 ESLint 对未安装包的静态解析）
  const dynamicImport = async (moduleName: string): Promise<any> => {
    // eslint-disable-next-line no-new-func
    const importer = new Function('m', 'return import(m);') as any;
    return importer(moduleName);
  };
  const normalizeArcPath = (p?: string) => {
    try {
      let s = String(p || '');
      try { s = decodeURIComponent(s); } catch {}
      s = s.replace(/\\/g, '/');
      s = s.replace(/^\.\/+/, '');
      s = s.replace(/\/{2,}/g, '/');
      return s;
    } catch { return String(p || ''); }
  };
  // 简易 TAR 解析（仅用于只读列目录与按条目读取）
  const parseTarEntriesFromBuffer = (buf: ArrayBuffer): Array<{ path: string; dir: boolean; size: number; offset: number }> => {
    const entries: Array<{ path: string; dir: boolean; size: number; offset: number }> = [];
    const u8 = new Uint8Array(buf);
    const textDecoder = new TextDecoder('utf-8');
    const getString = (start: number, length: number) => {
      const slice = u8.subarray(start, start + length);
      // 截断到第一个 0 字节
      let end = 0;
      while (end < slice.length && slice[end] !== 0) end++;
      return textDecoder.decode(slice.subarray(0, end));
    };
    const getOctal = (start: number, length: number) => {
      const str = getString(start, length).trim();
      if (!str) return 0;
      const cleaned = str.replace(/[^\d]/g, '');
      return parseInt(cleaned || '0', 8) || 0;
    };
    let i = 0;
    while (i + 512 <= u8.length) {
      // 结束：两个连续的 512 字节全 0 block
      let allZero = true;
      for (let k = 0; k < 512; k++) { if (u8[i + k] !== 0) { allZero = false; break; } }
      if (allZero) break;
      const name = getString(i + 0, 100);
      const size = getOctal(i + 124, 12);
      const typeFlag = u8[i + 156]; // '0' or 0 for file, '5' for dir
      const isDir = typeFlag === 53 /* '5' */ || name.endsWith('/');
      const dataOffset = i + 512;
      entries.push({ path: name, dir: isDir, size, offset: dataOffset });
      const dataSize = Math.ceil(size / 512) * 512;
      i = dataOffset + dataSize;
    }
    return entries;
  };
  const gunzipToArrayBuffer = async (gzBuf: ArrayBuffer): Promise<ArrayBuffer> => {
    try {
      const mod: any = await dynamicImport('pako');
      const pako = mod?.default || mod;
      const outU8: Uint8Array = pako.ungzip(new Uint8Array(gzBuf));
      // 拷贝到独立的 ArrayBuffer（避免 SharedArrayBuffer 类型告警）
      const out = new ArrayBuffer(outU8.byteLength);
      new Uint8Array(out).set(outU8);
      return out;
    } catch (e) {
      throw new Error('缺少或加载 gzip 解压库（pako）失败');
    }
  };
  // 审核-快速概览弹窗
  const [reviewOverviewOpen, setReviewOverviewOpen] = useState(false);
  const [reviewOverviewLoading, setReviewOverviewLoading] = useState(false);
  const [reviewOverview, setReviewOverview] = useState<any | null>(null);
  const [reviewOverviewShowAllQuestions, setReviewOverviewShowAllQuestions] = useState(false);
  // 审核概览-操作对象分页
  const [overviewTargetsPage, setOverviewTargetsPage] = useState(1);
  const [overviewTargetsPageSize, setOverviewTargetsPageSize] = useState(10);
  const [overviewHistoryIocRows, setOverviewHistoryIocRows] = useState<any[]>([]);
  const [overviewHistoryIocLoading, setOverviewHistoryIocLoading] = useState(false);
  // 自动预审（贴换标+装箱）
  const [autoReviewRunning, setAutoReviewRunning] = useState(false);
  const [autoReviewRes, setAutoReviewRes] = useState<any | null>(null);
  const [autoReviewOpen, setAutoReviewOpen] = useState(false);
  const [autoReviewBmpLoadingKey, setAutoReviewBmpLoadingKey] = useState<string>('');

  const downloadAutoReviewPdfSimBitmap = useCallback(async (pdfBaseName: string) => {
    const baseName = String(pdfBaseName || '').trim();
    if (!baseName) { message.warning('文件名为空'); return; }
    try {
      setAutoReviewBmpLoadingKey(baseName);
      const atts = await (async () => {
        try {
          if (reviewOverview && Array.isArray((reviewOverview as any)?.attachments)) return (reviewOverview as any).attachments;
          const instrId = Number(reviewOf?.id);
          if (!Number.isFinite(instrId) || instrId <= 0) return [];
          const attResp = await request.get('/api/v1/instruction/attachments/', { params: { instruction: instrId, page_size: 200 } });
          return Array.isArray(attResp?.data?.results) ? attResp.data.results : (Array.isArray(attResp?.data) ? attResp.data : []);
        } catch { return []; }
      })();
      // 定位附件：显示名去掉 .pdf 后与 baseName 匹配（忽略大小写）
      const pick = (atts || []).find((a:any) => {
        try {
          const disp = String(getDisplayFileName(a) || a?.file_name || a?.file || a?.file_path || '').trim();
          const noExt = disp.replace(/\.pdf$/i, '');
          return noExt.trim().toUpperCase() === baseName.toUpperCase();
        } catch { return false; }
      });
      if (!pick) { message.warning(`未找到对应PDF附件：${baseName}.pdf`); return; }
      const attId = Number(pick?.id);
      if (!Number.isFinite(attId) || attId <= 0) { message.warning('附件ID无效，无法生成位图'); return; }
      const resp = await request.get(`/api/v1/instruction/attachments/${attId}/pdf-sim-label/`, {
        params: { page: 1, dpi: 203, w_in: 2.25, h_in: 1.25, fit: 'contain', t: Date.now() },
        responseType: 'blob' as any,
      });
      const blob = resp?.data as any;
      if (!blob) { message.error('下载位图失败'); return; }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${baseName}-page1-2.25x1.25-203dpi.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(objUrl); } catch {} }, 2000);
      message.success('已下载预审位图');
    } catch (e:any) {
      message.error(e?.message || '生成预审位图失败');
    } finally {
      setAutoReviewBmpLoadingKey('');
    }
  }, [reviewOverview, reviewOf?.id]);
  const downloadAutoReviewPdf = useCallback(() => {
    try {
      const res = autoReviewRes || {};
      const issues: any[] = Array.isArray(res?.issues) ? res.issues : [];
      const verdict = String(res?.verdict || '');
      const title = '指令预审结果';
      const instrCode = String(reviewOf?.system_code || reviewOf?.id || '').trim();
      if (!verdict && issues.length === 0) {
        message.warning('暂无预审结果可下载');
        return;
      }
      const esc = (s:any) => String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      const now = new Date();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
      const verdictText = verdict === 'error' ? '错误' : verdict === 'warn' ? '警告' : verdict === 'pass' ? '通过' : (verdict || '-');
      const verdictColor = verdict === 'error' ? '#cf1322' : verdict === 'warn' ? '#d48806' : verdict === 'pass' ? '#389e0d' : '#595959';

      const renderLocate = (it:any) => {
        const code = String(it?.code || '');
        const locate = it?.locate || {};
        // coverage.old_not_contains_targets
        if (code === 'coverage.old_not_contains_targets') {
          const arr = Array.isArray(locate?.missing) ? locate.missing : [];
          if (arr.length === 0) return '';
          const texts = arr.map((m:any)=> String(m?.barcode || '')).filter(Boolean);
          const uniq = Array.from(new Set(texts));
          return `<div class="detail">涉及条码：${esc(uniq.join('、'))}</div>`;
        }
        // mapping.old_equals_new
        if (code === 'mapping.old_equals_new') {
          const rows = Array.isArray(locate?.rows) ? locate.rows : [];
          if (rows.length === 0) return '';
          const lines = rows.map((r:any) => `行 ${esc(r?.rowNo)}：${esc(r?.old)} → ${esc(r?.newer)}`).join('<br/>');
          return `<div class="detail">${lines}</div>`;
        }
        // mapping.duplicate_pair
        if (code === 'mapping.duplicate_pair') {
          const groups = Array.isArray(locate?.groups) ? locate.groups : [];
          if (groups.length === 0) return '';
          const lines = groups.map((g:any) => {
            const rowNos = Array.isArray(g?.rowNos) ? g.rowNos.join('、') : '';
            return `组合：${esc(g?.key)}${g?.count ? `（${esc(g.count)}次）` : ''} · 行号：${esc(rowNos)}`;
          }).join('<br/>');
          return `<div class="detail">${lines}</div>`;
        }
        // pdf.new_not_in_mapping
        if (code === 'pdf.new_not_in_mapping') {
          const arr = Array.isArray(locate?.samples) ? locate.samples : [];
          if (arr.length === 0) return '';
          return `<div class="detail">涉及文件名：${esc(arr.join('、'))}</div>`;
        }
        // pdf.page1_no_barcode
        if (code === 'pdf.page1_no_barcode') {
          const arr = Array.isArray(locate?.samples) ? locate.samples : [];
          if (arr.length === 0) return '';
          const lines = arr.map((x:any) => `文件名：${esc(x?.file)} · 解码：<未识别>`).join('<br/>');
          return `<div class="detail">${lines}</div>`;
        }
        // pdf.page1_multi_barcode
        if (code === 'pdf.page1_multi_barcode') {
          const arr = Array.isArray(locate?.samples) ? locate.samples : [];
          if (arr.length === 0) return '';
          const lines = arr.map((x:any) => {
            const decoded = Array.isArray(x?.decoded) ? x.decoded.join('、') : '';
            return `文件名：${esc(x?.file)} · 解码：${esc(decoded || '<未识别>')}`;
          }).join('<br/>');
          return `<div class="detail">${lines}</div>`;
        }
        // pdf.decoded_filename_mismatch
        if (code === 'pdf.decoded_filename_mismatch') {
          const arr = Array.isArray(locate?.samples) ? locate.samples : [];
          if (arr.length === 0) return '';
          const lines = arr.map((x:any) => {
            const decoded = Array.isArray(x?.decoded) ? x.decoded.join('、') : '';
            return `文件名：${esc(x?.file)} · 解码：${esc(decoded || '<未识别或无条码>')}`;
          }).join('<br/>');
          return `<div class="detail">${lines}</div>`;
        }
        return '';
      };

      const issuesHtml = issues.length === 0
        ? `<div style="color:#595959">无问题</div>`
        : `<table class="tbl">
            <thead>
              <tr>
                <th style="width:80px">级别</th>
                <th style="width:220px">代码</th>
                <th>描述</th>
              </tr>
            </thead>
            <tbody>
              ${issues.map((it:any) => {
                const lv = String(it?.level || '');
                const lvText = lv === 'error' ? '错误' : lv === 'warn' ? '警告' : (lv || '-');
                const lvColor = lv === 'error' ? '#cf1322' : lv === 'warn' ? '#d48806' : '#595959';
                return `<tr>
                  <td><span style="color:${lvColor};font-weight:700">${esc(lvText)}</span></td>
                  <td>${esc(it?.code || '')}</td>
                  <td>
                    <div>${esc(it?.message || '')}</div>
                    ${renderLocate(it)}
                  </td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>`;

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans','PingFang SC','Microsoft YaHei',sans-serif;padding:16px;color:#000;}
      h2{margin:0 0 8px 0;}
      .meta{color:#595959;font-size:12px;margin-bottom:12px;}
      .badge{display:inline-block;padding:2px 8px;border-radius:4px;border:1px solid ${verdictColor};background:#fff; color:${verdictColor};font-weight:700;}
      .tbl{width:100%;border-collapse:collapse;}
      th,td{border:1px solid #d9d9d9;padding:6px 8px;vertical-align:top;}
      th{background:#fafafa;text-align:left;}
      .detail{margin-top:6px;color:#595959;white-space:pre-wrap;word-break:break-word;}
      @media print { a{display:none;} }
    </style>
  </head>
  <body>
    <h2>${esc(title)}</h2>
    <div class="meta">
      生成时间：${esc(ts)}
      ${instrCode ? ` · 指令：${esc(instrCode)}` : ''}
      · 结论：<span class="badge">${esc(verdictText)}</span>
      ${res?.meta?.mapping_rows != null ? ` · 映射表行数：${esc(res.meta.mapping_rows)}` : ''}
    </div>
    ${issuesHtml}
  </body>
</html>`;

      const w = window.open('about:blank', '_blank');
      if (!w) { message.error('浏览器阻止了弹窗，请允许弹窗后重试'); return; }
      try {
        try { (w as any).opener = null; } catch {}
        w.document.open();
        w.document.write(html);
        w.document.close();
        const doPrint = () => {
          try { w.focus(); } catch {}
          try { w.onafterprint = () => { try { w.close(); } catch {} }; } catch {}
          try { w.print(); } catch {}
        };
        try { (w as any).onload = () => setTimeout(doPrint, 50); } catch {}
        setTimeout(doPrint, 300);
      } catch {
        try { w.close(); } catch {}
        message.error('生成PDF失败');
      }
    } catch {
      message.error('生成PDF失败');
    }
  }, [autoReviewRes, reviewOf]);
  const copyAutoReviewInfo = useCallback(async () => {
    try {
      const res = autoReviewRes || {};
      const issues: any[] = Array.isArray(res?.issues) ? res.issues : [];
      const verdict = String(res?.verdict || '');
      if (!verdict && issues.length === 0) {
        message.warning('暂无可复制的预审信息');
        return;
      }
      const verdictText = verdict === 'error' ? '错误' : verdict === 'warn' ? '警告' : verdict === 'pass' ? '通过' : (verdict || '-');
      const instrCode = String(reviewOf?.system_code || reviewOf?.id || '-').trim();
      const ts = new Date().toLocaleString();
      const header = [
        '自动预审结果',
        `生成时间：${ts}`,
        `指令：${instrCode || '-'}`,
        `结论：${verdictText}`,
        (res?.meta?.mapping_rows != null ? `映射表行数：${res.meta.mapping_rows}` : null),
      ].filter(Boolean).join('\n');
      const detail = issues.length === 0
        ? '问题列表：无'
        : [
            '问题列表：',
            ...issues.map((it:any, idx:number) => {
              const lv = String(it?.level || '');
              const lvText = lv === 'error' ? '错误' : lv === 'warn' ? '警告' : (lv || '-');
              const code = String(it?.code || '-');
              const msg = String(it?.message || '-');
              return [
                `#${idx + 1}`,
                `级别：${lvText}`,
                `代码：${code}`,
                `描述：${msg}`,
              ].filter(Boolean).join('\n');
            }),
          ].join('\n\n');
      const text = `${header}\n\n${detail}`;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      message.success('已复制完整预审信息');
    } catch {
      message.error('复制失败');
    }
  }, [autoReviewRes, reviewOf]);
  // 概览弹窗：库存单元内容项/类型缓存（用于补拉详情）
  const [overviewUnitContentCache, setOverviewUnitContentCache] = useState<Record<string, Array<{ barcode: string; qty: number }>>>({});
  const [overviewUnitTypeCache, setOverviewUnitTypeCache] = useState<Record<string, string>>({});
  const [overviewUnitSortingGroupCache, setOverviewUnitSortingGroupCache] = useState<Record<string, { code: string; name: string }>>({});
  const loadReviewOverview = async (instrId?: number) => {
    if (!instrId) return null as any;
    setReviewOverviewLoading(true);
    try {
      const [insResp, attResp] = await Promise.allSettled([
        request.get(`/api/v1/instruction/instructions/${instrId}/`),
        request.get('/api/v1/instruction/attachments/', { params: { instruction: instrId, page_size: 200 } }),
      ]);
      const ins = (insResp as any)?.status === 'fulfilled' ? (insResp as any).value?.data : null;
      const atts = (attResp as any)?.status === 'fulfilled'
        ? (Array.isArray((attResp as any).value?.data?.results) ? (attResp as any).value.data.results : ((attResp as any).value?.data || []))
        : [];
      const ov = { detail: ins, attachments: atts };
      setReviewOverview(ov);
      return ov;
    } catch {
      const ov = { detail: null, attachments: [] };
      setReviewOverview(ov);
      return ov;
    } finally {
      setReviewOverviewLoading(false);
    }
  };
  const loadBaselines = async (instrId?: number) => {
    if (!instrId) return;
    try {
      setBaselineLoading(true);
      const resp = await request.get('/api/v1/instruction/baselines/', { params: { instruction: instrId, page_size: 1000 } });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      setBaselineRows(rows);
      setBaselineOwnerId(Number(instrId));
      return rows;
    } catch { setBaselineRows([]); return []; } finally { setBaselineLoading(false); }
  };
  const collectBaselineFileIds = (rows: any[]): number[] => {
    try {
      const ids: number[] = [];
      (rows || []).forEach((b:any) => {
        const list = Array.isArray(b?.files) ? b.files : [];
        list.forEach((f:any) => {
          const id = Number(f?.id);
          if (Number.isFinite(id)) ids.push(id);
        });
      });
      return ids;
    } catch { return []; }
  };
  const makeBaselineSnapshot = (rows: any[] = []) => {
    try {
      const idSet = new Set<number>();
      const idToState: Record<string, string> = {};
      const idToFileIds: Record<string, Set<number>> = {};
      (rows || []).forEach((b:any) => {
        const bid = Number(b?.id);
        if (!Number.isFinite(bid)) return;
        idSet.add(bid);
        idToState[String(bid)] = String(b?.state || 'draft');
        const files = Array.isArray(b?.files) ? b.files : [];
        const s = new Set<number>();
        files.forEach((f:any) => { const fid = Number(f?.id); if (Number.isFinite(fid)) s.add(fid); });
        idToFileIds[String(bid)] = s;
      });
      return { idSet, idToState, idToFileIds };
    } catch {
      return { idSet: new Set<number>(), idToState: {}, idToFileIds: {} as Record<string, Set<number>> };
    }
  };
  const createBaseline = async (code: string, name?: string, bind?: any, schema_hint?: any) => {
    if (!reviewOf) return;
    try {
      await request.post('/api/v1/instruction/baselines/', {
        instruction: reviewOf.id, code, name, bind, schema_hint, state: 'draft'
      });
      message.success('已创建基准数据（草稿）');
      baselineSessionTouchedRef.current = true;
      await loadBaselines(reviewOf.id);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '创建失败');
    }
  };
  const setBaselineReady = async (id: number) => {
    try {
      await request.patch(`/api/v1/instruction/baselines/${id}/`, { state: 'ready' });
      message.success('已设为就绪');
      baselineSessionTouchedRef.current = true;
      await loadBaselines(reviewOf?.id);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };
  const uploadBaselineFile = async (baselineId: number, file: File) => {
    try {
      const fd = new FormData();
      fd.append('baseline', String(baselineId));
      fd.append('file', file);
      await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      message.success('已上传');
      baselineSessionTouchedRef.current = true;
      await loadBaselines(reviewOf?.id);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '上传失败');
    }
  };
  const deleteBaselineFile = async (fileId: number) => {
    try {
      await request.delete(`/api/v1/instruction/baseline-files/${fileId}/`);
      message.success('已删除');
      baselineSessionTouchedRef.current = true;
      await loadBaselines(reviewOf?.id);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };
  const deleteAllBaselineFiles = async (baselineId: number) => {
    try {
      const target = (baselineRows || []).find((b:any)=> Number(b?.id) === Number(baselineId));
      const list: any[] = Array.isArray(target?.files) ? target.files : [];
      if (!list.length) { message.info('无可删除的文件'); return; }
      let ok = 0, fail = 0;
      for (const f of list) {
        try { await request.delete(`/api/v1/instruction/baseline-files/${f.id}/`); ok += 1; } catch { fail += 1; }
      }
      message.success(`删除完成：成功 ${ok}，失败 ${fail}`);
      await loadBaselines(reviewOf?.id);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };
  const uploadBaselineFiles = async (baselineId: number, files: FileList | File[]) => {
    try {
      const list: File[] = Array.isArray(files) ? (files as File[]) : Array.from(files as FileList);
      const arr = list.filter(Boolean);
      if (arr.length === 0) return;
      for (const f of arr) {
        const fd = new FormData();
        fd.append('baseline', String(baselineId));
        fd.append('file', f as Blob, (f as File).name);
        await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      message.success(`已上传 ${arr.length} 个文件`);
      baselineSessionTouchedRef.current = true;
      await loadBaselines(reviewOf?.id);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '上传失败');
    }
  };
  // 加载客户上传附件
  const loadCustomerAttachments = async (instrId?: number) => {
    if (!instrId) return;
    setAttachPickerLoading(true);
    try {
      const resp = await request.get('/api/v1/instruction/attachments/', { params: { instruction: instrId, page_size: 500 } });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      setCustomerAttachments(rows);
    } catch {
      setCustomerAttachments([]);
    } finally {
      setAttachPickerLoading(false);
    }
  };
  // 解析扩展名
  const getExtLower = (name?: string) => {
    try {
      const raw = String(name || '');
      const base = raw.split('?')[0].split('#')[0];
      const n = base.toLowerCase();
      const m = n.match(/\.([a-z0-9]+)$/);
      return m ? m[1] : '';
    } catch { return ''; }
  };
  const getBaseName = (pathOrUrl?: string) => {
    try {
      const s = String(pathOrUrl || '');
      const q = s.split('?')[0];
      const hash = q.split('#')[0];
      const parts = hash.split('/');
      const last = parts[parts.length - 1] || s;
      try { return decodeURIComponent(last); } catch { return last; }
    } catch { return String(pathOrUrl || ''); }
  };
  // 是否为压缩/归档文件（名称匹配，支持多后缀）
  const isArchiveByName = (name?: string) => {
    try {
      const s = String(name || '').toLowerCase();
      if (!s) return false;
      if (/\.(zip|rar|7z|tar)$/.test(s)) return true;
      if (/\.(tar\.gz|tgz|tar\.bz2|tbz2|tar\.xz|txz)$/.test(s)) return true;
      return false;
    } catch { return false; }
  };
  // 从客户上传附件中选择：当前弹窗可见附件列表（按扩展名 + 基线类型特定规则过滤）
  const getAttachPickerVisibleRows = useCallback(() => {
    const exts = attachPickerBaseline?.acceptExts || [];
    const baselineCodeUpper = String(attachPickerBaseline?.code || '').trim().toUpperCase();
    const isSltm = baselineCodeUpper === 'SHIPPING_LABEL_TARGET_MAPPING';
    const isGroupList = baselineCodeUpper === 'GROUP_LIST';
    const rows = Array.isArray(customerAttachments) ? customerAttachments : [];
    return rows.filter((a: any) => {
      if (exts.length > 0) {
        const ext = getExtLower(a?.file_name || a?.file || a?.file_path);
        if (!exts.includes(ext)) return false;
      }
      if (isGroupList) {
        const roleCode = String(a?.role_code || a?.attachment_role_code || '').trim().toUpperCase();
        const code = String(a?.code || a?.attachment_code || '').trim().toUpperCase();
        const label = String(a?.label || a?.attachment_label || '').trim();
        return (
          roleCode === 'GROUP_LIST'
          || code === 'GROUP_LIST'
          || label.includes('分组列表')
          || label.toUpperCase().includes('GROUP_LIST')
        );
      }
      if (!isSltm) return true;
      const lbl = String(a?.label || a?.attachment_label || '').trim();
      const roleCode = String(a?.role_code || a?.attachment_role_code || '').trim().toUpperCase();
      return lbl === '运输标签与出库对象对应表格' || roleCode === 'SHIPPING_LABEL_TARGET_MAPPING';
    });
  }, [attachPickerBaseline?.acceptExts, attachPickerBaseline?.code, customerAttachments]);
  // 将压缩附件解压后逐个采纳为基线文件（保留条目原始文件名）
  const adoptCompressedAttachmentEntriesAsBaseline = async (baselineId: number, att: any) => {
    if (!baselineId) { message.warning('请先创建草稿'); return; }
    const nameStr = String(att?.file_name || att?.file || att?.file_path || '');
    const lower = nameStr.toLowerCase();
    const isZip = /\.zip$/.test(lower);
    const isRar = /\.rar$/.test(lower);
    const isTgz = /\.tar\.gz$/.test(lower) || /\.tgz$/.test(lower);
    const isTar = /\.tar$/.test(lower);
    const isTbz = /\.tar\.bz2$/.test(lower) || /\.tbz2$/.test(lower);
    const isTxz = /\.tar\.xz$/.test(lower) || /\.txz$/.test(lower);
    const closeLoading = message.loading('正在解压并采纳为基线文件...', 0);
    try {
      const uploads: Array<{ name: string; blob: Blob }> = [];
      // 拉取源档案字节
      const fetchArrayBuffer = async (): Promise<ArrayBuffer> => {
        const url = resolveAttachmentRuntimeUrl(att);
        if (!url) throw new Error('无法获取附件URL');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('下载压缩文件失败');
        return await resp.arrayBuffer();
      };
      if (isZip) {
        let JSZip: any = null;
        try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
        if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
        const buf = await fetchArrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        const files = Object.values(zip.files || {}) as any[];
        for (const f of files) {
          const fp = normalizeArcPath(f?.name || f?.path || '');
          if (!fp || f?.dir) continue;
          const base = fp.split('/').pop() || fp;
          const ab = await f.async('arraybuffer');
          const blob = new Blob([ab]);
          uploads.push({ name: base, blob });
        }
      } else if (isRar) {
        // 后端列目录与提取条目
        const listApi = `/api/v1/instruction/attachments/${att?.id}/archive/list/`;
        const listRes = await (request as any).get(listApi, { withCredentials: true });
        const list = Array.isArray(listRes?.data) ? listRes.data : (Array.isArray(listRes?.data?.results) ? listRes.data.results : []);
        for (const it of (list || [])) {
          const p = normalizeArcPath(it?.path || '');
          if (!p || it?.dir) continue;
          const entryApi = `/api/v1/instruction/attachments/${att?.id}/archive/entry/`;
          const res = await (request as any).get(entryApi, { params: { path: p }, responseType: 'blob', withCredentials: true });
          const blobObj: Blob = res?.data as Blob;
          const base = p.split('/').pop() || p;
          uploads.push({ name: base, blob: blobObj });
        }
      } else if (isTgz || isTar) {
        const raw = await fetchArrayBuffer();
        const tarBuf = isTgz ? await gunzipToArrayBuffer(raw) : raw;
        const entries = parseTarEntriesFromBuffer(tarBuf as ArrayBuffer);
        for (const e of entries) {
          const p = normalizeArcPath(e?.path || '');
          if (!p || e?.dir) continue;
          const base = p.split('/').pop() || p;
          const slice = (tarBuf as ArrayBuffer).slice(e.offset, e.offset + e.size);
          uploads.push({ name: base, blob: new Blob([slice]) });
        }
      } else if (isTbz || isTxz) {
        message.warning('暂不支持该压缩格式的自动解压采纳（.tar.bz2 / .tar.xz）');
        return;
      } else if (/\.7z$/.test(lower)) {
        message.warning('暂不支持 7z 自动解压采纳');
        return;
      } else {
        message.warning('不支持的压缩格式');
        return;
      }
      if (uploads.length === 0) { message.info('压缩包内没有可采纳的文件'); return; }
      // 覆盖策略：同名文件若已存在于当前基线，先删除后上传，避免重复
      const targetBaseline = (baselineRows || []).find((b:any)=> Number(b?.id) === Number(baselineId));
      const existing: any[] = Array.isArray(targetBaseline?.files) ? targetBaseline.files : [];
      const nameToIds = new Map<string, number[]>();
      for (const f of existing) {
        try {
          const disp = (getDisplayFileName(f) || f?.file_name || f?.file || '') as string;
          const plain = (disp.split('?')[0] || disp).split('#')[0];
          const base = (plain.split('/').pop() || plain);
          const k = String(base);
          const fid = Number(f?.id);
          if (!nameToIds.has(k)) nameToIds.set(k, []);
          if (Number.isFinite(fid)) nameToIds.get(k)!.push(fid);
        } catch {}
      }
      let ok = 0, fail = 0;
      for (const f of uploads) {
        // 先删同名
        try {
          const sameIds = nameToIds.get(String(f.name)) || [];
          for (const fid of sameIds) {
            try { await request.delete(`/api/v1/instruction/baseline-files/${fid}/`); } catch {}
          }
        } catch {}
        // 再传
        try {
          const fd = new FormData();
          fd.append('baseline', String(baselineId));
          fd.append('file', f.blob, f.name);
          await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          ok += 1;
        } catch { fail += 1; }
      }
      if (ok > 0) {
        baselineSessionTouchedRef.current = true;
        await loadBaselines(reviewOf?.id);
      }
      message.success(`已解压并采纳 ${ok} 个文件${fail ? `，失败 ${fail}` : ''}`);
    } catch (e:any) {
      message.error(e?.message || '解压采纳失败');
    } finally {
      closeLoading();
    }
  };
  const extToDisplay = (ext: string, mime?: string) => {
    if (ext) return ext.toUpperCase();
    const m = String(mime || '').toLowerCase();
    if (m.includes('pdf')) return 'PDF';
    if (m.includes('csv')) return 'CSV';
    if (m.includes('excel') || m.includes('spreadsheet') || m.includes('xlsx') || m.includes('xls')) return 'XLSX';
    return '-';
  };
  const prettifyStorageName = (name?: string) => {
    const s = String(name || '');
    // 识别形如: 名称_随机后缀.xlsx → 去掉 _随机后缀
    const m = s.match(/^(.+)_([A-Za-z0-9]{5,12})\.([A-Za-z0-9]+)$/);
    if (m && m[1] && m[3]) return `${m[1]}.${m[3]}`;
    return s;
  };
  const getDisplayFileName = (row: any) => {
    const v = row?.file_name;
    if (v) return v;
    const metaName = row?.meta?.original_name || row?.meta?.original_file_name || row?.meta?.originalFilename;
    if (metaName) return String(metaName);
    const base = getBaseName(row?.file) || getBaseName(row?.file_path);
    return prettifyStorageName(base);
  };
  const resolveAttachmentRuntimeUrl = (att: any): string => {
    try {
      const rawCandidates = [
        att?.file_url,
        att?.url,
        att?.file,
        att?.file_path,
      ]
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      const localBlobUrl = rawCandidates.find((v) => /^blob:/i.test(v));
      if (localBlobUrl) return localBlobUrl;
      return getAttachmentUrl(att);
    } catch {
      return getAttachmentUrl(att);
    }
  };

  // 从客户上传附件中选择：判定“是否已采纳为基线”
  // 说明：当附件在预览中被保存为“已编辑”版本后，实际上传的基线文件名会变为 “xxx-已编辑.xxx”
  // 旧逻辑只用“附件原始显示名”与 baseline.files[*].file_name 做全等比较，会导致按钮始终显示“采纳为基线”。
  const getAttachmentAdoptNameCandidates = (att: any) => {
    const idStr = String(att?.id ?? '');
    const base = String(getDisplayFileName(att) || att?.file_name || '').trim();
    const set = new Set<string>();
    if (base) set.add(base);
    try {
      const ov = editedAttachMap[idStr];
      const editedName = String(ov?.fileName || '').trim();
      if (editedName) set.add(editedName);
    } catch {}
    try {
      const raw = String((attachNameEdits as any)?.[idStr] ?? '').trim();
      if (raw) {
        let name = raw;
        // 若用户未填写扩展名，自动补齐（与 adoptAttachmentAsBaseline 的上传逻辑保持一致）
        if (name.lastIndexOf('.') < 0 && base && base.lastIndexOf('.') >= 0) {
          const ext = base.slice(base.lastIndexOf('.') + 1);
          if (ext) name = `${name}.${ext}`;
        }
        set.add(name);
      }
    } catch {}
    return Array.from(set).filter(Boolean);
  };
  const isAttachmentAdoptedByFiles = (files: any[], att: any) => {
    try {
      const fileNameSet = new Set<string>(
        (Array.isArray(files) ? files : [])
          .map((f:any) => String(getDisplayFileName(f) || f?.file_name || '').trim())
          .filter(Boolean)
      );
      const candidates = getAttachmentAdoptNameCandidates(att);
      return candidates.some((nm) => fileNameSet.has(String(nm || '').trim()));
    } catch {
      return false;
    }
  };
  // —— 概览弹窗：库存单元内容项提取与渲染（与"预估操作数量"弹窗保持一致） ——
  const extractOverviewUnitContentItems = (row: any): Array<{ barcode: string; qty: number }> => {
    try {
      const snap = row?.snapshot || row?.target?.snapshot || {};
      const candidates: any[] = [
        snap?.content?.items,
        row?.content?.items,
        row?.target?.content?.items,
        row?.inventory_unit?.content?.items,
        (snap?.content && Array.isArray(snap.content) ? snap.content : null),
        row?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          return arr.map((it: any) => {
            const p = it?.product || {};
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(
                p?.sorting_barcode
                || p?.barcode_text
                || p?.system_barcode
                || p?.barcode
                || p?.code
                || p?.sku
                || it?.product_code
                || it?.system_code
                || it?.product_id
                || ''
              );
            }
            return { barcode, qty };
          }).filter(x => x.barcode || x.qty);
        }
      }
    } catch {}
    return [];
  };
  const renderOverviewUnitContentItems = (row: any) => {
    const items = extractOverviewUnitContentItems(row);
    const getAnyUnitId = () => {
      try { return row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? row?.snapshot?.inventory_unit_id; } catch { return undefined; }
    };
    if ((!items || items.length === 0) && getAnyUnitId() != null) {
      const key = String(getAnyUnitId());
      const cached = overviewUnitContentCache[key];
      if (cached && cached.length > 0) {
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {cached.map((it, idx) => (
              <span key={idx} style={{ marginRight: 6 }}>
                {it.barcode || '-'} × {it.qty || 0}{idx < cached.length - 1 ? '、' : ''}
              </span>
            ))}
          </div>
        );
      }
    }
    if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {items.map((it, idx) => (
          <span key={idx} style={{ marginRight: 6 }}>
            {it.barcode || '-'} × {it.qty || 0}{idx < items.length - 1 ? '、' : ''}
          </span>
        ))}
      </div>
    );
  };
  const renderOverviewSortingGroup = (row: any) => {
    try {
      const targetType = String(row?.target_type || row?.target?.target_type || '').trim();
      if (targetType && targetType !== 'inventory_unit') return '-';
      const snap = row?.snapshot || row?.target?.snapshot || {};
      const info = row?.sorting_group_info || row?.target?.sorting_group_info || snap?.sorting_group_info || {};
      const uid = (() => {
        try { return row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? snap?.inventory_unit_id; } catch { return undefined; }
      })();
      const cache = uid != null ? overviewUnitSortingGroupCache[String(uid)] : undefined;
      const code = String(
        info?.system_code
        || info?.code
        || row?.sorting_group_code
        || row?.target?.sorting_group_code
        || cache?.code
        || '',
      ).trim();
      const name = String(
        info?.name
        || row?.sorting_group_name
        || row?.target?.sorting_group_name
        || cache?.name
        || '',
      ).trim();
      if (code && name) return `${code} (${name})`;
      if (code) return code;
      if (name) return name;
      return '-';
    } catch {
      return '-';
    }
  };
  const parseOverviewIocCodesFromSelector = (selector: any): string[] => {
    try {
      if (selector && typeof selector === 'object') {
        const arr = Array.isArray(selector?.ioc_codes) ? selector.ioc_codes : [];
        return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
      }
      const s = String(selector || '').trim();
      if (!s) return [];
      try {
        const j = JSON.parse(s);
        const arr = Array.isArray(j?.ioc_codes) ? j.ioc_codes : [];
        return arr.map((x:any) => String(x || '').trim()).filter(Boolean);
      } catch {}
      return [];
    } catch {
      return [];
    }
  };
  const normalizeOverviewIocCode = (v: any) => String(v || '').trim().toUpperCase();
  const overviewContainerTypeTextMap: Record<string, string> = {
    box: '箱子',
    pallet: '托盘',
    crate: '周转箱',
    bag: '袋',
    envelope: '信封',
    other: '其他',
  };
  const buildHistoryInputIocRowsForRecord = async (record: any): Promise<any[]> => {
    try {
      const d = record || {};
      const list = Array.isArray(d?.targets_detail) ? d.targets_detail : [];
      const historyTargets = (list || []).filter((r:any) => String(r?.target_type || '').trim() === 'instruction_output');
      if (historyTargets.length === 0) return [];
      const projectById = new Map<number, { code?: string; name?: string }>();
      try {
        const prows = Array.isArray(d?.projects_detail) ? d.projects_detail : [];
        prows.forEach((p:any) => {
          const id = Number(p?.id);
          if (!Number.isFinite(id)) return;
          projectById.set(id, { code: p?.project_code, name: p?.name });
        });
      } catch {}
      const baseRows = historyTargets.map((r:any, idx:number) => {
        const snap = r?.snapshot || {};
        const selector = r?.output_selector_obj ?? r?.output_selector;
        const codes = parseOverviewIocCodesFromSelector(selector);
        const pid = Number(r?.project_id);
        const p = Number.isFinite(pid) ? projectById.get(pid) : undefined;
        return {
          __key: `op-hoc-${String(r?.id || idx)}`,
          project_id: Number.isFinite(pid) ? pid : undefined,
          project_code: r?.project_code || snap?.project_code || p?.code,
          project_name: r?.project_name || snap?.project_name || p?.name,
          instruction_id: Number(r?.prev_instruction_id || snap?.prev_instruction_id || snap?.instruction_id || 0) || undefined,
          instruction_code: String(snap?.prev_instruction_code || snap?.instruction_code || '').trim() || undefined,
          output_container_id: snap?.output_container_id ?? snap?.container_id ?? undefined,
          output_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
          display_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
          container_type_display: snap?.container_type_display || snap?.container_type || '-',
          output_content_kind: snap?.output_content_kind || '',
          output_content_kind_display: snap?.output_content_kind_display || '-',
          output_contents_summary: snap?.output_contents_summary || '-',
          output_contents_detail: snap?.output_contents_detail || snap?.contents_detail || '',
          output_contents_direct_total_qty: Number(
            snap?.output_contents_direct_total_qty
            ?? snap?.direct_total_qty
            ?? snap?.output_contents_total_qty
            ?? snap?.total_qty
            ?? 0,
          ) || 0,
          output_contents_total_qty: Number(snap?.output_contents_total_qty || snap?.total_qty || 0) || 0,
          parent_container_id: snap?.parent_container_id,
          parent_code: String(snap?.parent_code || '').trim() || undefined,
          child_codes: Array.isArray(snap?.child_codes) ? snap.child_codes : [],
          is_parent_container: !!snap?.is_parent_container,
          is_child_container: !!snap?.is_child_container,
          length_cm: snap?.length_cm,
          width_cm: snap?.width_cm,
          height_cm: snap?.height_cm,
          weight_g: snap?.weight_g,
          created_at: snap?.created_at,
        } as any;
      });
      let mergedRows = baseRows;
      const prevIds: number[] = Array.from(new Set(
        (baseRows || [])
          .map((x:any) => Number(x?.instruction_id))
          .filter((v:number) => Number.isFinite(v) && v > 0)
      ));
      if (prevIds.length > 0) {
        const detailByCode = new Map<string, any>();
        const detailById = new Map<string, any>();
        await Promise.all(prevIds.map(async (pid:number) => {
          try {
            const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
            const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
            (arr || []).forEach((oc:any) => {
              const idKey = `${pid}::id::${String(oc?.id || '')}`;
              const codeKey = `${pid}::code::${normalizeOverviewIocCode(oc?.code)}`;
              if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
              if (normalizeOverviewIocCode(oc?.code)) detailByCode.set(codeKey, oc);
            });
          } catch {}
        }));
        mergedRows = (baseRows || []).map((row:any) => {
          const pid = Number(row?.instruction_id);
          if (!Number.isFinite(pid) || pid <= 0) return row;
          const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
          const byCode = detailByCode.get(`${pid}::code::${normalizeOverviewIocCode(row?.output_code || row?.display_code)}`);
          const hit = byId || byCode;
          if (!hit) return row;
          const contents = Array.isArray(hit?.contents) ? hit.contents : [];
          const totalQty = contents.reduce((acc:number, c:any) => acc + (Number(c?.quantity || 0) || 0), 0);
          const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
          const contentKindDisplay = kind === 'exception' ? '异常内容容器' : '正常内容容器';
          const detailText = (contents || []).map((c:any) => {
            const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
            const qty = Number(c?.quantity || 0) || 0;
            return code ? `${code}×${qty}` : '';
          }).filter(Boolean).join('；');
          return {
            ...row,
            output_container_id: hit?.id ?? row?.output_container_id,
            output_code: String(hit?.code || row?.output_code || row?.display_code || '').trim(),
            display_code: String(hit?.code || row?.display_code || row?.output_code || '').trim(),
            container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-'
              ? row.container_type_display
              : (String(hit?.container_type || '').trim() || '-'),
            output_content_kind: kind || row?.output_content_kind || '',
            output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-'
              ? row.output_content_kind_display
              : contentKindDisplay,
            output_contents_summary: row?.output_contents_summary && String(row.output_contents_summary).trim() !== '-'
              ? row.output_contents_summary
              : (contents.length > 0 ? `${contents.length}项` : '-'),
            output_contents_detail: String(row?.output_contents_detail || '').trim() || detailText,
            output_contents_direct_total_qty: Number(
              row?.output_contents_direct_total_qty
              ?? hit?.output_contents_direct_total_qty
              ?? row?.output_contents_total_qty
              ?? totalQty
              ?? 0,
            ) || 0,
            output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0 ? Number(row.output_contents_total_qty || 0) : totalQty,
            parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
            parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
            child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
            is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
            is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
            length_cm: hit?.length_cm ?? row?.length_cm,
            width_cm: hit?.width_cm ?? row?.width_cm,
            height_cm: hit?.height_cm ?? row?.height_cm,
            weight_g: hit?.weight_g ?? row?.weight_g,
            created_at: hit?.created_at ?? row?.created_at,
          };
        });
      }
      try {
        const projectIds: number[] = Array.from(new Set(
          (mergedRows || [])
            .map((r:any) => Number(r?.project_id))
            .filter((v:number) => Number.isFinite(v) && v > 0)
        ));
        if (projectIds.length > 0) {
          const warehouseCode = String(d?.warehouse_detail?.code || d?.warehouse?.code || '').trim();
          const unifiedByCode = new Map<string, any>();
          await Promise.all(projectIds.map(async (pid:number) => {
            try {
              const params: any = {
                page: 1,
                page_size: 500,
                container_kind: 'output',
                project: String(pid),
                instruction_id: Number(d?.id || 0) || undefined,
                ordering: '-created_at',
              };
              if (warehouseCode) params.warehouse_code = warehouseCode;
              const resp = await request.get('/api/v1/storage/containers/unified/', { params });
              const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
              (rows || []).forEach((r:any) => {
                const code = normalizeOverviewIocCode(r?.system_code || r?.display_code || '');
                if (code) unifiedByCode.set(`${pid}::${code}`, r);
              });
            } catch {}
          }));
          mergedRows = (mergedRows || []).map((row:any) => {
            const pid = Number(row?.project_id);
            const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
            if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
            const hit = unifiedByCode.get(`${pid}::${code}`);
            if (!hit) return row;
            return {
              ...row,
              output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
              display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
              container_type_display: hit?.container_type_display || row?.container_type_display || '-',
              output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
              output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
              output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
              output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
              output_contents_direct_total_qty: Number(
                hit?.output_contents_direct_total_qty
                ?? row?.output_contents_direct_total_qty
                ?? hit?.output_contents_total_qty
                ?? row?.output_contents_total_qty
                ?? 0,
              ) || 0,
              output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
              parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
              parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
              child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
              is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
              is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
            };
          });
        }
      } catch {}

      // 再补一层层级关系：确保 parent/child 信息稳定可用（用于“层级标识 + 同组边框”）
      try {
        const instrIds: number[] = Array.from(new Set(
          (mergedRows || [])
            .map((r:any) => Number(r?.instruction_id))
            .filter((v:number) => Number.isFinite(v) && v > 0),
        ));
        if (instrIds.length > 0) {
          const hierByInstrCode = new Map<string, any>();
          await Promise.all(instrIds.map(async (pid:number) => {
            try {
              const resp = await request.get('/api/v1/storage/output-containers/', {
                params: { instruction_id: pid, page: 1, page_size: 500, ordering: '-created_at' },
              });
              const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
              (rows || []).forEach((x:any) => {
                const code = normalizeOverviewIocCode(x?.code || x?.system_code || x?.display_code || '');
                if (!code) return;
                hierByInstrCode.set(`${pid}::${code}`, {
                  parent_code: String(x?.parent_code || '').trim() || undefined,
                  child_codes: Array.isArray(x?.child_codes) ? x.child_codes.map((c:any) => String(c || '').trim()).filter(Boolean) : [],
                  is_parent_container: !!x?.is_parent_container || (Array.isArray(x?.child_codes) && x.child_codes.length > 0),
                  is_child_container: !!x?.is_child_container || !!x?.parent_container_id || !!x?.parent_code,
                  parent_container_id: x?.parent_container_id,
                });
              });
            } catch {}
          }));
          if (hierByInstrCode.size > 0) {
            mergedRows = (mergedRows || []).map((row:any) => {
              const pid = Number(row?.instruction_id);
              const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
              if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
              const hit = hierByInstrCode.get(`${pid}::${code}`);
              if (!hit) return row;
              return {
                ...row,
                parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
                parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
                child_codes: Array.isArray(hit?.child_codes) && hit.child_codes.length > 0
                  ? hit.child_codes
                  : (Array.isArray(row?.child_codes) ? row.child_codes : []),
                is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
                is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
              };
            });
          }
        }
      } catch {}
      return mergedRows;
    } catch {
      return [];
    }
  };
  // 缺失内容项时按需补拉库存单元详情（概览弹窗）
  useEffect(() => {
    (async () => {
      try {
        if (!reviewOverviewOpen) return;
        const rows = Array.isArray(reviewOverview?.detail?.targets_detail) ? reviewOverview.detail.targets_detail : [];
        const pendingIds = new Set<string>();
        rows.forEach((r:any) => {
          const has = extractOverviewUnitContentItems(r);
          const uid = ((): any => {
            try { return r?.inventory_unit_id ?? r?.target?.inventory_unit_id ?? r?.snapshot?.inventory_unit_id; } catch { return undefined; }
          })();
          const hasSortingGroup = (() => {
            try {
              const snap = r?.snapshot || r?.target?.snapshot || {};
              const info = r?.sorting_group_info || r?.target?.sorting_group_info || snap?.sorting_group_info || {};
              return !!String(
                info?.system_code
                || info?.code
                || info?.name
                || r?.sorting_group_code
                || r?.target?.sorting_group_code
                || r?.sorting_group_name
                || r?.target?.sorting_group_name
                || '',
              ).trim();
            } catch {
              return false;
            }
          })();
          if (uid != null && ((!has || has.length === 0) || !hasSortingGroup)) {
            const key = String(uid);
            if (!overviewUnitContentCache[key] || !overviewUnitSortingGroupCache[key]) pendingIds.add(key);
          }
        });
        if (pendingIds.size === 0) return;
        const updates: Record<string, Array<{ barcode: string; qty: number }>> = {};
        const typeUpdates: Record<string, string> = {};
        const sortingGroupUpdates: Record<string, { code: string; name: string }> = {};
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            if (d?.unit_type) typeUpdates[id] = String(d.unit_type);
            try {
              const sg = d?.sorting_group_info || {};
              const code = String(sg?.system_code || sg?.code || '').trim();
              const name = String(sg?.name || '').trim();
              if (code || name) sortingGroupUpdates[id] = { code, name };
            } catch {}
            const mapped = (arr || []).map((it: any) => {
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || ''
                );
              }
              return { barcode, qty };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped && mapped.length > 0) updates[id] = mapped;
          } catch {}
        }));
        if (Object.keys(updates).length > 0) setOverviewUnitContentCache(prev => ({ ...prev, ...updates }));
        if (Object.keys(typeUpdates).length > 0) setOverviewUnitTypeCache(prev => ({ ...prev, ...typeUpdates }));
        if (Object.keys(sortingGroupUpdates).length > 0) setOverviewUnitSortingGroupCache(prev => ({ ...prev, ...sortingGroupUpdates }));
      } catch {}
    })();
  }, [reviewOverviewOpen, reviewOverview?.detail?.targets_detail, overviewUnitContentCache, overviewUnitSortingGroupCache]);

  // 概览弹窗：当操作对象为历史指令产出IOC时，复用详情页逻辑生成IOC概览列表
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!reviewOverviewOpen) return;
        const d = reviewOverview?.detail || {};
        const list = Array.isArray(d?.targets_detail) ? d.targets_detail : [];
        const hasHistoryIoc = (list || []).some((r:any) => String(r?.target_type || '').trim() === 'instruction_output');
        if (!hasHistoryIoc) {
          setOverviewHistoryIocRows([]);
          setOverviewHistoryIocLoading(false);
          return;
        }
        setOverviewHistoryIocLoading(true);
        const projectById = new Map<number, { code?: string; name?: string }>();
        try {
          const prows = Array.isArray(d?.projects_detail) ? d.projects_detail : [];
          prows.forEach((p:any) => {
            const id = Number(p?.id);
            if (!Number.isFinite(id)) return;
            projectById.set(id, { code: p?.project_code, name: p?.name });
          });
        } catch {}
        const baseRows = (list || [])
          .filter((r:any) => String(r?.target_type || '').trim() === 'instruction_output')
          .map((r:any, idx:number) => {
            const snap = r?.snapshot || {};
            const selector = r?.output_selector_obj ?? r?.output_selector;
            const codes = parseOverviewIocCodesFromSelector(selector);
            const pid = Number(r?.project_id);
            const p = Number.isFinite(pid) ? projectById.get(pid) : undefined;
            return {
              __key: `ov-hoc-${String(r?.id || idx)}`,
              project_id: Number.isFinite(pid) ? pid : undefined,
              project_code: r?.project_code || snap?.project_code || p?.code,
              project_name: r?.project_name || snap?.project_name || p?.name,
              instruction_id: Number(r?.prev_instruction_id || snap?.prev_instruction_id || snap?.instruction_id || 0) || undefined,
              instruction_code: String(snap?.prev_instruction_code || snap?.instruction_code || '').trim() || undefined,
              output_container_id: snap?.output_container_id ?? snap?.container_id ?? undefined,
              output_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
              display_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
              container_type_display: snap?.container_type_display || snap?.container_type || '-',
              output_content_kind: snap?.output_content_kind || '',
              output_content_kind_display: snap?.output_content_kind_display || '-',
              output_contents_summary: snap?.output_contents_summary || '-',
              output_contents_detail: snap?.output_contents_detail || snap?.contents_detail || '',
              output_contents_direct_total_qty: Number(
                snap?.output_contents_direct_total_qty
                ?? snap?.direct_total_qty
                ?? snap?.output_contents_total_qty
                ?? snap?.total_qty
                ?? 0,
              ) || 0,
              output_contents_total_qty: Number(snap?.output_contents_total_qty || snap?.total_qty || 0) || 0,
              parent_container_id: snap?.parent_container_id,
              parent_code: String(snap?.parent_code || '').trim() || undefined,
              child_codes: Array.isArray(snap?.child_codes) ? snap.child_codes : [],
              is_parent_container: !!snap?.is_parent_container,
              is_child_container: !!snap?.is_child_container,
              length_cm: snap?.length_cm,
              width_cm: snap?.width_cm,
              height_cm: snap?.height_cm,
              weight_g: snap?.weight_g,
              created_at: snap?.created_at,
            } as any;
          });
        let mergedRows = baseRows;
        const prevIds: number[] = Array.from(new Set(
          (baseRows || []).map((x:any) => Number(x?.instruction_id)).filter((v:number) => Number.isFinite(v) && v > 0)
        ));
        if (prevIds.length > 0) {
          const detailByCode = new Map<string, any>();
          const detailById = new Map<string, any>();
          await Promise.all(prevIds.map(async (pid:number) => {
            try {
              const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
              const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
              (arr || []).forEach((oc:any) => {
                const idKey = `${pid}::id::${String(oc?.id || '')}`;
                const codeKey = `${pid}::code::${normalizeOverviewIocCode(oc?.code)}`;
                if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
                if (normalizeOverviewIocCode(oc?.code)) detailByCode.set(codeKey, oc);
              });
            } catch {}
          }));
          mergedRows = (baseRows || []).map((row:any) => {
            const pid = Number(row?.instruction_id);
            if (!Number.isFinite(pid) || pid <= 0) return row;
            const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
            const byCode = detailByCode.get(`${pid}::code::${normalizeOverviewIocCode(row?.output_code || row?.display_code)}`);
            const hit = byId || byCode;
            if (!hit) return row;
            const contents = Array.isArray(hit?.contents) ? hit.contents : [];
            const totalQty = contents.reduce((acc:number, c:any) => acc + (Number(c?.quantity || 0) || 0), 0);
            const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
            const contentKindDisplay = kind === 'exception' ? '异常内容容器' : '正常内容容器';
            const detailText = (contents || []).map((c:any) => {
              const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
              const qty = Number(c?.quantity || 0) || 0;
              return code ? `${code}×${qty}` : '';
            }).filter(Boolean).join('；');
            return {
              ...row,
              output_container_id: hit?.id ?? row?.output_container_id,
              output_code: String(hit?.code || row?.output_code || row?.display_code || '').trim(),
              display_code: String(hit?.code || row?.display_code || row?.output_code || '').trim(),
              container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-' ? row.container_type_display : (String(hit?.container_type || '').trim() || '-'),
              output_content_kind: kind || row?.output_content_kind || '',
              output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-' ? row.output_content_kind_display : contentKindDisplay,
              output_contents_summary: row?.output_contents_summary && String(row.output_contents_summary).trim() !== '-'
                ? row.output_contents_summary
                : (contents.length > 0 ? `${contents.length}项` : '-'),
              output_contents_detail: String(row?.output_contents_detail || '').trim() || detailText,
              output_contents_direct_total_qty: Number(
                row?.output_contents_direct_total_qty
                ?? hit?.output_contents_direct_total_qty
                ?? row?.output_contents_total_qty
                ?? totalQty
                ?? 0,
              ) || 0,
              output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0 ? Number(row.output_contents_total_qty || 0) : totalQty,
              parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
              parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
              child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
              is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
              is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
              length_cm: hit?.length_cm ?? row?.length_cm,
              width_cm: hit?.width_cm ?? row?.width_cm,
              height_cm: hit?.height_cm ?? row?.height_cm,
              weight_g: hit?.weight_g ?? row?.weight_g,
              created_at: hit?.created_at ?? row?.created_at,
            };
          });
        }
        // 再以 unified 接口口径补齐显示字段（换标IOC优先显示新标签及数量）
        try {
          const projectIds: number[] = Array.from(new Set(
            (mergedRows || [])
              .map((r:any) => Number(r?.project_id))
              .filter((v:number) => Number.isFinite(v) && v > 0)
          ));
          if (projectIds.length > 0) {
            const warehouseCode = String(d?.warehouse_detail?.code || d?.warehouse?.code || '').trim();
            const unifiedByCode = new Map<string, any>();
            await Promise.all(projectIds.map(async (pid:number) => {
              try {
                const params: any = {
                  page: 1,
                  page_size: 500,
                  container_kind: 'output',
                  project: String(pid),
                  instruction_id: Number(d?.id || 0) || undefined,
                  ordering: '-created_at',
                };
                if (warehouseCode) params.warehouse_code = warehouseCode;
                const resp = await request.get('/api/v1/storage/containers/unified/', { params });
                const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                (rows || []).forEach((r:any) => {
                  const code = normalizeOverviewIocCode(r?.system_code || r?.display_code || '');
                  if (code) unifiedByCode.set(`${pid}::${code}`, r);
                });
              } catch {}
            }));
            mergedRows = (mergedRows || []).map((row:any) => {
              const pid = Number(row?.project_id);
              const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
              if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
              const hit = unifiedByCode.get(`${pid}::${code}`);
              if (!hit) return row;
              return {
                ...row,
                output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
                display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
                container_type_display: hit?.container_type_display || row?.container_type_display || '-',
                output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
                output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
                output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
                output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
                output_contents_direct_total_qty: Number(
                  hit?.output_contents_direct_total_qty
                  ?? row?.output_contents_direct_total_qty
                  ?? hit?.output_contents_total_qty
                  ?? row?.output_contents_total_qty
                  ?? 0,
                ) || 0,
                output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
                parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
                parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
                child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
                is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
                is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
              };
            });
          }
        } catch {}
        // 再补一层层级关系：确保 parent/child 信息稳定可用（用于“层级标识 + 同组边框”）
        try {
          const instrIds: number[] = Array.from(new Set(
            (mergedRows || [])
              .map((r:any) => Number(r?.instruction_id))
              .filter((v:number) => Number.isFinite(v) && v > 0),
          ));
          if (instrIds.length > 0) {
            const hierByInstrCode = new Map<string, any>();
            await Promise.all(instrIds.map(async (pid:number) => {
              try {
                const resp = await request.get('/api/v1/storage/output-containers/', {
                  params: { instruction_id: pid, page: 1, page_size: 500, ordering: '-created_at' },
                });
                const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                (rows || []).forEach((x:any) => {
                  const code = normalizeOverviewIocCode(x?.code || x?.system_code || x?.display_code || '');
                  if (!code) return;
                  hierByInstrCode.set(`${pid}::${code}`, {
                    parent_code: String(x?.parent_code || '').trim() || undefined,
                    child_codes: Array.isArray(x?.child_codes) ? x.child_codes.map((c:any) => String(c || '').trim()).filter(Boolean) : [],
                    is_parent_container: !!x?.is_parent_container || (Array.isArray(x?.child_codes) && x.child_codes.length > 0),
                    is_child_container: !!x?.is_child_container || !!x?.parent_container_id || !!x?.parent_code,
                    parent_container_id: x?.parent_container_id,
                  });
                });
              } catch {}
            }));
            if (hierByInstrCode.size > 0) {
              mergedRows = (mergedRows || []).map((row:any) => {
                const pid = Number(row?.instruction_id);
                const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
                if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
                const hit = hierByInstrCode.get(`${pid}::${code}`);
                if (!hit) return row;
                return {
                  ...row,
                  parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
                  parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
                  child_codes: Array.isArray(hit?.child_codes) && hit.child_codes.length > 0
                    ? hit.child_codes
                    : (Array.isArray(row?.child_codes) ? row.child_codes : []),
                  is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
                  is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
                };
              });
            }
          }
        } catch {}
        if (!cancelled) setOverviewHistoryIocRows(mergedRows);
      } catch {
        if (!cancelled) setOverviewHistoryIocRows([]);
      } finally {
        if (!cancelled) setOverviewHistoryIocLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [reviewOverviewOpen, reviewOverview?.detail]);

  // 历史输入IOC弹窗：按需加载 IOC 列表（复用与详情页一致的口径构建）
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!historyTargetsOf) {
          setHistoryTargetsRows([]);
          setHistoryTargetsLoading(false);
          return;
        }
        const id = Number(historyTargetsOf?.id || 0);
        const key = String(id || '');
        if (key && Array.isArray(historyInputIocRowsCacheByInstructionId[key])) {
          setHistoryTargetsRows(historyInputIocRowsCacheByInstructionId[key] || []);
          setHistoryTargetsLoading(false);
          return;
        }
        setHistoryTargetsLoading(true);
        const rows = await buildHistoryInputIocRowsForRecord(historyTargetsOf);
        if (cancelled) return;
        setHistoryTargetsRows(rows || []);
        if (key) {
          setHistoryInputIocRowsCacheByInstructionId((prev) => ({ ...prev, [key]: rows || [] }));
          const containerCount = countUniqueIocContainers(rows || []);
          setHistoryEstimatedQtyByInstructionId((prev) => ({ ...prev, [key]: Number(containerCount || 0) || 0 }));
        }
      } catch {
        if (!cancelled) setHistoryTargetsRows([]);
      } finally {
        if (!cancelled) setHistoryTargetsLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [historyTargetsOf, countUniqueIocContainers]);

  // BARCODE_MAPPING：将指令操作对象中指定的操作数量（expected_qty，且“是否操作全部库存”=否）同步到“数量分配”(D列/c3)
  // 复用“查看指令概览”弹窗的库存单元内容项补拉逻辑：当 target.snapshot 不含 items 时，用 inventory_unit_id 补拉分拣编码
  const syncBmQtyFromTargets = useCallback(async () => {
    try {
      const dbg = (...args: any[]) => { try { console.info('[BM][SYNC]', ...args); } catch {} };
      const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
      if (!attachPickerOpen || !isBm) return;

      const instrId = Number(reviewOf?.id);
      if (!Number.isFinite(instrId)) { message.warning('未找到指令ID，无法同步'); return; }
      dbg('start', { instrId, baselineCode: attachPickerBaseline?.code, attachPickerOpen, pvRows: Array.isArray(attPvData) ? attPvData.length : -1 });

      const norm = (v:any) => String(v ?? '').trim().toUpperCase();
      const splitCodes = (v:any) => {
        const s = norm(v);
        if (!s) return [];
        return s.split('/').map(x => norm(x)).filter(Boolean);
      };

      // 取操作对象列表（优先已加载）
      let targets: any[] = [];
      try {
        const fromOv = Array.isArray((reviewOverview as any)?.detail?.targets_detail) ? (reviewOverview as any).detail.targets_detail : [];
        const fromRow = Array.isArray((reviewOf as any)?.targets_detail) ? (reviewOf as any).targets_detail : [];
        targets = (fromOv.length ? fromOv : fromRow) as any[];
        dbg('targets from cache', { fromOv: fromOv.length, fromRow: fromRow.length, chosen: targets.length });
      } catch { targets = []; }
      if (!targets.length) {
        try {
          const resp = await request.get(`/api/v1/instruction/instructions/${instrId}/`);
          const ins = resp?.data || null;
          targets = Array.isArray(ins?.targets_detail) ? ins.targets_detail : [];
          dbg('targets fetched', { fetched: targets.length });
        } catch {}
      }
      if (!targets.length) { message.warning('未找到指令操作对象数据，无法同步'); return; }

      // 为缺失 items 的操作对象补拉库存单元内容项（同概览弹窗 useEffect）
      const getAnyUnitId = (r:any) => { try { return r?.inventory_unit_id ?? r?.target?.inventory_unit_id ?? r?.snapshot?.inventory_unit_id; } catch { return undefined; } };
      const unitItemsById: Record<string, Array<{ barcode: string; qty: number }>> = {};
      const pendingIds = new Set<string>();
      try {
        (targets || []).forEach((t:any) => {
          if (!!t?.use_all_available) return;
          const qty = Number(t?.expected_qty);
          if (!Number.isFinite(qty)) return;
          const has = extractOverviewUnitContentItems(t);
          const uid = getAnyUnitId(t);
          if ((!has || has.length === 0) && uid != null) {
            const key = String(uid);
            const cached = overviewUnitContentCache[key];
            if (cached && cached.length > 0) unitItemsById[key] = cached;
            else pendingIds.add(key);
          }
        });
      } catch {}
      if (pendingIds.size > 0) {
        dbg('unit fetch pending', { pending: pendingIds.size, ids: Array.from(pendingIds).slice(0, 10) });
        const updates: Record<string, Array<{ barcode: string; qty: number }>> = {};
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            const mapped = (arr || []).map((it: any) => {
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(it?.product_system_code || it?.system_code || it?.product_code || '');
              }
              return { barcode, qty };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped && mapped.length > 0) {
              updates[id] = mapped;
              unitItemsById[id] = mapped;
            }
          } catch {}
        }));
        if (Object.keys(updates).length > 0) {
          try { setOverviewUnitContentCache(prev => ({ ...prev, ...updates })); } catch {}
        }
        dbg('unit fetch done', { ok: Object.keys(updates).length, sample: Object.keys(updates).slice(0, 10) });
      }

      // 建立：旧条码(分拣编码) -> expected_qty（仅 use_all_available=否 时）
      const qtyByBarcode = new Map<string, number>();
      let cntUseAll = 0;
      let cntQtyInvalid = 0;
      let cntItemsEmpty = 0;
      let cntItemsNonEmpty = 0;
      let cntBarcodePieces = 0;
      let cntFallbackUsed = 0;
      const sampleBad: any[] = [];
      const safeKeys = (o:any, limit=30) => { try { return Object.keys(o || {}).slice(0, limit); } catch { return []; } };
      const safeJson = (o:any, limit=800) => {
        try {
          const s = JSON.stringify(o);
          if (!s) return '';
          return s.length > limit ? `${s.slice(0, limit)}...` : s;
        } catch { return '[unserializable]'; }
      };
      try {
        (targets || []).forEach((t:any) => {
          const useAll = !!t?.use_all_available;
          if (useAll) { cntUseAll += 1; return; }
          const qty = Number(t?.expected_qty);
          if (!Number.isFinite(qty)) { cntQtyInvalid += 1; if (sampleBad.length < 3) sampleBad.push({ reason:'qty_invalid', expected_qty: t?.expected_qty, use_all_available: t?.use_all_available, keys: Object.keys(t || {}).slice(0, 20) }); return; }
          let items = extractOverviewUnitContentItems(t) || [];
          if (!items.length) {
            const uid = getAnyUnitId(t);
            const key = uid != null ? String(uid) : '';
            const fallback = key ? (unitItemsById[key] || overviewUnitContentCache[key]) : null;
            if (fallback && fallback.length > 0) { items = fallback; cntFallbackUsed += 1; }
          }
          if (!items.length) {
            cntItemsEmpty += 1;
            if (sampleBad.length < 3) {
              const snap = t?.snapshot;
              const snapContent = snap?.content;
              const snapItems = snapContent?.items;
              const snapItemsLen = Array.isArray(snapItems) ? snapItems.length : -1;
              const sample = {
                reason: 'items_empty',
                expected_qty: t?.expected_qty,
                use_all_available: t?.use_all_available,
                inventory_unit_id: t?.inventory_unit_id,
                inventory_unit_system_barcode: t?.inventory_unit_system_barcode,
                keys: Object.keys(t || {}).slice(0, 20),
                snapshotKeys: safeKeys(snap, 30),
                snapshotContentType: Array.isArray(snapContent) ? 'array' : (snapContent && typeof snapContent === 'object' ? 'object' : String(snapContent)),
                snapshotContentKeys: safeKeys(snapContent, 30),
                snapshotItemsLen: snapItemsLen,
                snapshotJson: safeJson(snap, 800),
              };
              sampleBad.push(sample);
              dbg('items_empty sample', sample);
            }
            return;
          }
          cntItemsNonEmpty += 1;
          items.forEach((it:any) => {
            const codes = splitCodes(it?.barcode);
            cntBarcodePieces += codes.length;
            codes.forEach((c) => { if (c && !qtyByBarcode.has(c)) qtyByBarcode.set(c, qty); });
          });
        });
      } catch {}
      dbg('targets stats', {
        total: (targets || []).length,
        useAllSkipped: cntUseAll,
        qtyInvalidSkipped: cntQtyInvalid,
        itemsEmptySkipped: cntItemsEmpty,
        itemsNonEmpty: cntItemsNonEmpty,
        fallbackUsed: cntFallbackUsed,
        barcodePieces: cntBarcodePieces,
        qtyByBarcodeSize: qtyByBarcode.size,
        qtyByBarcodeSample: Array.from(qtyByBarcode.keys()).slice(0, 10),
        sampleBad,
      });
      if (!qtyByBarcode.size) { message.warning('未找到可同步的操作数量（未指定操作数量的不会同步）'); return; }

      // 关键：不要依赖闭包中的 attPvData，避免列头按钮捕获到“旧函数”导致总是读到空数据
      let hadRows = false;
      let hit = 0;
      let miss = 0;
      const missSamples: any[] = [];
      setAttPvData((prev) => {
        const rows = Array.isArray(prev) ? prev : [];
        hadRows = rows.length > 0;
        if (!hadRows) return prev;
        const next = rows.map((r:any) => {
          const oldCodes = splitCodes(r?.c0);
          const key = oldCodes?.[0] || '';
          if (!key) return r;
          const qty = qtyByBarcode.get(key);
          if (qty == null) {
            miss += 1;
            if (missSamples.length < 10) missSamples.push({ __idx: r?.__idx, old: r?.c0, norm: key });
            return r;
          }
          hit += 1;
          return { ...r, c3: String(qty) };
        });
        return next;
      });

      if (!hadRows) { message.warning('当前预览表格无数据，无法同步'); return; }
      if (hit <= 0) { message.info('未匹配到任何可同步的旧条码'); return; }
      setAttPvDirty(true);
      message.success(`同步完成：已更新 ${hit} 行`);
      dbg('row match stats', { hit, miss, missSamples });
      try { setTimeout(() => runBmAuditFromCurrentTable(), 0); } catch {}
    } catch (e:any) {
      try { console.error('[BM][SYNC][error]', e); } catch {}
      message.error(e?.message || '同步失败');
    }
  }, [attachPickerBaseline?.code, attachPickerOpen, reviewOf, reviewOverview, runBmAuditFromCurrentTable, overviewUnitContentCache]);

  // BARCODE_MAPPING：匹配操作对象（旧条码 -> 指令操作对象库存单元内容项分拣条码 -> 库存单元）
  const matchBmTargetsFromCurrentTable = useCallback(async () => {
    try {
      const dbg = (...args: any[]) => { try { console.info('[BM][MATCH_TARGET]', ...args); } catch {} };
      const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
      if (!attachPickerOpen || !isBm) return;
      if (!(attPvKind === 'csv' || attPvKind === 'xlsx')) return;
      const rows = Array.isArray(attPvData) ? attPvData : [];
      if (!rows.length) return;

      const instrId = Number(reviewOf?.id);
      if (!Number.isFinite(instrId)) { message.warning('未找到指令ID，无法匹配操作对象'); return; }
      const norm = (v:any) => String(v ?? '').trim().toUpperCase();
      const splitCodes = (v:any) => {
        const s = norm(v);
        if (!s) return [];
        return s.split('/').map((x:any) => norm(x)).filter(Boolean);
      };

      // 取操作对象列表（优先已加载）
      let targets: any[] = [];
      try {
        const fromOv = Array.isArray((reviewOverview as any)?.detail?.targets_detail) ? (reviewOverview as any).detail.targets_detail : [];
        const fromRow = Array.isArray((reviewOf as any)?.targets_detail) ? (reviewOf as any).targets_detail : [];
        targets = (fromOv.length ? fromOv : fromRow) as any[];
      } catch { targets = []; }
      if (!targets.length) {
        try {
          const resp = await request.get(`/api/v1/instruction/instructions/${instrId}/`);
          const ins = resp?.data || null;
          targets = Array.isArray(ins?.targets_detail) ? ins.targets_detail : [];
        } catch {}
      }
      if (!targets.length) { message.warning('未找到指令操作对象数据，无法匹配操作对象'); return; }

      // 为缺失 items 的操作对象补拉库存单元内容项（复用 sync 的策略）
      const getAnyUnitId = (r:any) => { try { return r?.inventory_unit_id ?? r?.target?.inventory_unit_id ?? r?.snapshot?.inventory_unit_id; } catch { return undefined; } };
      const getAnyUnitSys = (r:any) => { try { return r?.inventory_unit_system_barcode ?? r?.target?.inventory_unit_system_barcode ?? r?.snapshot?.inventory_unit_system_barcode; } catch { return undefined; } };
      const unitItemsById: Record<string, Array<{ barcode: string; qty: number }>> = {};
      const pendingIds = new Set<string>();
      try {
        (targets || []).forEach((t:any) => {
          const has = extractOverviewUnitContentItems(t);
          const uid = getAnyUnitId(t);
          if ((!has || has.length === 0) && uid != null) {
            const key = String(uid);
            const cached = overviewUnitContentCache[key];
            if (cached && cached.length > 0) unitItemsById[key] = cached;
            else pendingIds.add(key);
          }
        });
      } catch {}
      if (pendingIds.size > 0) {
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            const mapped = (arr || []).map((it: any) => {
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(it?.product_system_code || it?.system_code || it?.product_code || '');
              }
              return { barcode, qty };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped && mapped.length > 0) {
              unitItemsById[id] = mapped;
            }
          } catch {}
        }));
      }

      // 建立：旧条码(分拣编码) -> IU 标签集合（可能多个）
      type IUMatch = { iu_id: number; system_barcode?: string; use_all_available: boolean; expected_qty: number | null };
      const iuByBarcode = new Map<string, Map<number, IUMatch>>();
      let targetCount = 0;
      try {
        (targets || []).forEach((t:any) => {
          const uid = getAnyUnitId(t);
          if (uid == null) return;
          const uidNum = Math.floor(Number(uid) || 0);
          if (!Number.isFinite(uidNum) || uidNum <= 0) return;
          const sys = String(getAnyUnitSys(t) || '').trim();
          const useAll = !!t?.use_all_available;
          const eq = (t?.expected_qty == null || t?.expected_qty === '') ? null : Number(t.expected_qty);
          const meta: IUMatch = { iu_id: uidNum, system_barcode: (sys || undefined), use_all_available: useAll, expected_qty: Number.isFinite(eq as any) ? Number(eq) : null };
          let items = extractOverviewUnitContentItems(t) || [];
          if (!items.length) {
            const key = String(uidNum);
            const fallback = unitItemsById[key] || overviewUnitContentCache[key];
            if (fallback && fallback.length > 0) items = fallback;
          }
          if (!items.length) return;
          targetCount += 1;
          items.forEach((it:any) => {
            const codes = splitCodes(it?.barcode);
            codes.forEach((c) => {
              if (!c) return;
              const m = iuByBarcode.get(c) || new Map<number, IUMatch>();
              m.set(uidNum, meta);
              iuByBarcode.set(c, m);
            });
          });
        });
      } catch {}
      dbg('map built', { iuByBarcode: iuByBarcode.size, targetCount });

      const sortMatches = (arr: IUMatch[]) => {
        return (arr || []).slice().sort((a, b) => {
          const ua = a.use_all_available ? 1 : 0;
          const ub = b.use_all_available ? 1 : 0;
          if (ua !== ub) return ua - ub; // 否(0)在前，是(1)在后
          const ea = (a.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
          const eb = (b.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
          if (ea !== eb) return ea - eb;
          const sa = String(a.system_barcode || `IU#${a.iu_id}`);
          const sb = String(b.system_barcode || `IU#${b.iu_id}`);
          return sa.localeCompare(sb);
        });
      };
      const toJson = (arr: IUMatch[]) => {
        try {
          return JSON.stringify(arr.map(x => ({
            iu_id: x.iu_id,
            system_barcode: x.system_barcode || '',
            use_all_available: !!x.use_all_available,
            expected_qty: x.expected_qty == null ? null : Number(x.expected_qty),
          })));
        } catch {
          return '[]';
        }
      };

      // 对预览表格逐行匹配
      const matchByIdx: Record<number, string> = {};
      const noTargetRows: Array<{ rowNo: number; old: string }> = [];
      rows.forEach((row:any) => {
        const __idx = Number(row?.__idx);
        if (!Number.isFinite(__idx) || __idx <= 0) return;
        const fileRowNo = __idx + 1;

        // 若该行曾被用户手动指定，则“再次审核/自动匹配”不覆盖此行
        try {
          if (attPvBmManualMatchIdx?.has(__idx)) {
            const keep = String(attPvBmTargetMatchByIdx?.[__idx] ?? '-').trim();
            matchByIdx[__idx] = keep || '-';
            if (keep === '-' || keep === '' || keep === '[]') {
              const oldRaw = String(row?.c0 ?? '').trim();
              noTargetRows.push({ rowNo: fileRowNo, old: norm(oldRaw) || '-' });
            }
            return;
          }
        } catch {}

        const oldRaw = String(row?.c0 ?? '').trim();
        const oldCodes = splitCodes(oldRaw);
        const hitMap = new Map<number, IUMatch>();
        oldCodes.forEach((c) => {
          const m = iuByBarcode.get(c);
          if (m) {
            m.forEach((v, k) => { hitMap.set(k, v); });
          }
        });
        if (hitMap.size === 0) {
          matchByIdx[__idx] = '-';
          noTargetRows.push({ rowNo: fileRowNo, old: norm(oldRaw) || '-' });
        } else {
          const arr = sortMatches(Array.from(hitMap.values()));
          matchByIdx[__idx] = toJson(arr);
        }
      });
      setAttPvBmTargetMatchByIdx(matchByIdx);
      setAttPvBmAudit((prev) => {
        const base = prev || {
          noTargetRows: [] as any[],
          newEmptyRows: [] as any[],
          sameRows: [] as any[],
          dupPairs: [] as any[],
          badRows: [] as any[],
          groupOrder: [] as any[],
          groupMap: {} as any,
        };
        const next = { ...base, noTargetRows };
        const has = (next.noTargetRows?.length || 0)
          || (next.newEmptyRows?.length || 0)
          || (next.sameRows?.length || 0)
          || (next.dupPairs?.length || 0)
          || (next.badRows?.length || 0);
        return has ? next : null;
      });
      // 重新匹配后默认清空高亮标记
      setAttPvBmMarkNoTarget(new Set());
      // 匹配结果属于“编辑内容”，提示用户需要保存
      setAttPvDirty(true);
      return { matchByIdx, noTargetRows };
    } catch (e:any) {
      message.error(e?.message || '匹配操作对象失败');
    }
  }, [attachPickerBaseline?.code, attachPickerOpen, attPvKind, attPvData, reviewOf, reviewOverview, overviewUnitContentCache, attPvBmManualMatchIdx, attPvBmTargetMatchByIdx]);

  const loadBmTargetIuOptions = useCallback(async () => {
    try {
      const instrId = Number(reviewOf?.id);
      if (!Number.isFinite(instrId)) return;
      setBmTargetIuLoading(true);
      const resp = await request.get(`/api/v1/instruction/instructions/${instrId}/relabel-pack/target-inventory-units/`);
      const items: any[] = Array.isArray(resp?.data?.items) ? resp.data.items : [];
      // 尝试从指令 targets_detail 里补齐 use_all_available / expected_qty
      let targets: any[] = [];
      try {
        const fromOv = Array.isArray((reviewOverview as any)?.detail?.targets_detail) ? (reviewOverview as any).detail.targets_detail : [];
        const fromRow = Array.isArray((reviewOf as any)?.targets_detail) ? (reviewOf as any).targets_detail : [];
        targets = (fromOv.length ? fromOv : fromRow) as any[];
      } catch { targets = []; }
      const getAnyUnitId = (r:any) => { try { return r?.inventory_unit_id ?? r?.target?.inventory_unit_id ?? r?.snapshot?.inventory_unit_id; } catch { return undefined; } };
      const buildItemsBrief = (arr: Array<{ barcode: string; qty: number }>) => {
        try {
          const list = Array.isArray(arr) ? arr : [];
          if (!list.length) return '';
          const parts = list.slice(0, 3).map((it:any) => {
            const bc = String(it?.barcode || '').trim();
            const qty = Number(it?.qty ?? 0) || 0;
            return bc ? `${bc}×${qty}` : '';
          }).filter(Boolean);
          const head = parts.join(' / ');
          if (list.length > 3) return `${head} 等${list.length}项`;
          return head;
        } catch { return ''; }
      };
      const metaById = new Map<number, { use_all_available?: boolean; expected_qty?: number | null; system_barcode?: string; content_brief?: string }>();
      try {
        (targets || []).forEach((t:any) => {
          const uid = Math.floor(Number(getAnyUnitId(t) || 0));
          if (!uid) return;
          const useAll = !!t?.use_all_available;
          const eq = (t?.expected_qty == null || t?.expected_qty === '') ? null : Number(t.expected_qty);
          const sys = String(t?.inventory_unit_system_barcode ?? t?.target?.inventory_unit_system_barcode ?? t?.snapshot?.inventory_unit_system_barcode ?? '').trim();
          let brief = '';
          try {
            const fromTarget = extractOverviewUnitContentItems(t) || [];
            if (fromTarget.length > 0) brief = buildItemsBrief(fromTarget as any);
          } catch {}
          if (!brief) {
            try {
              const cached = overviewUnitContentCache?.[String(uid)] || [];
              if (Array.isArray(cached) && cached.length > 0) brief = buildItemsBrief(cached as any);
            } catch {}
          }
          metaById.set(uid, { use_all_available: useAll, expected_qty: Number.isFinite(eq as any) ? Number(eq) : null, system_barcode: sys || undefined, content_brief: brief || undefined });
        });
      } catch {}
      const opts = items
        .map((x:any) => {
          const id = Math.floor(Number(x?.id || 0));
          const sys = String(x?.system_barcode || '').trim();
          const meta = metaById.get(id) || {};
          return { id, system_barcode: sys || meta.system_barcode, use_all_available: meta.use_all_available, expected_qty: meta.expected_qty ?? null, content_brief: meta.content_brief };
        })
        .filter((x:any) => Number.isFinite(x.id) && x.id > 0);

      // 批量指定弹窗：为缺失内容项概览的库存单元补拉详情（避免下拉框里总是“无内容项”）
      // 说明：targets_detail 通常不含 content.items；该路径不会自动填充 overviewUnitContentCache，因此在此增量补齐。
      try {
        const pending = opts
          .filter((o:any) => {
            const id = Math.floor(Number(o?.id || 0));
            if (!id) return false;
            const cached = overviewUnitContentCache?.[String(id)];
            const hasCached = Array.isArray(cached) && cached.length > 0;
            const hasBrief = String(o?.content_brief || '').trim().length > 0;
            return !hasBrief && !hasCached;
          })
          .map((o:any) => String(Math.floor(Number(o?.id || 0))))
          .filter(Boolean);
        if (pending.length > 0) {
          const updates: Record<string, Array<{ barcode: string; qty: number }>> = {};
          // 控制并发，避免一次性请求过多
          const batchSize = 6;
          for (let i = 0; i < pending.length; i += batchSize) {
            const chunk = pending.slice(i, i + batchSize);
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(chunk.map(async (id) => {
              try {
                const r = await getInventoryUnitDetail(id);
                const d: any = (r as any)?.data || r;
                const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
                const mapped = (arr || []).map((it: any) => {
                  const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
                  let barcode = '';
                  try {
                    const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                    if (sc.length > 0) {
                      const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                      if (codes.length > 0) barcode = codes.join('/');
                    }
                  } catch {}
                  if (!barcode) barcode = String(it?.product_system_code || it?.system_code || it?.product_code || '');
                  return { barcode, qty };
                }).filter((x: any) => x.barcode || x.qty);
                if (mapped && mapped.length > 0) updates[id] = mapped;
              } catch {}
            }));
          }
          if (Object.keys(updates).length > 0) {
            setOverviewUnitContentCache(prev => ({ ...(prev || {}), ...updates }));
          }
        }
      } catch {}

      // 用最新缓存重算一次 content_brief
      const buildItemsBrief2 = (arr: Array<{ barcode: string; qty: number }>) => {
        try {
          const list = Array.isArray(arr) ? arr : [];
          if (!list.length) return '';
          const parts = list.slice(0, 3).map((it:any) => {
            const bc = String(it?.barcode || '').trim();
            const qty = Number(it?.qty ?? 0) || 0;
            return bc ? `${bc}×${qty}` : '';
          }).filter(Boolean);
          const head = parts.join(' / ');
          if (list.length > 3) return `${head} 等${list.length}项`;
          return head;
        } catch { return ''; }
      };
      const nextOpts = opts.map((o:any) => {
        const id = Math.floor(Number(o?.id || 0));
        const cached = id ? (overviewUnitContentCache?.[String(id)] || []) : [];
        const brief = String(o?.content_brief || '').trim() || (Array.isArray(cached) && cached.length > 0 ? buildItemsBrief2(cached as any) : '');
        return { ...o, content_brief: brief || undefined };
      });
      setBmTargetIuOptions(nextOpts);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || e?.message || '加载操作目标库存单元失败');
      setBmTargetIuOptions([]);
    } finally {
      setBmTargetIuLoading(false);
    }
  }, [reviewOf, reviewOverview, overviewUnitContentCache]);

  // BARCODE_MAPPING：预览弹窗打开后自动执行一次“匹配操作对象”
  useEffect(() => {
    try {
      const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
      if (!attPreviewOpen || !isBm) return;
      if (!(attPvKind === 'csv' || attPvKind === 'xlsx')) return;
      if (attPvLoading) return;
      const rows = Array.isArray(attPvData) ? attPvData : [];
      if (!rows.length) return;
      const sig = `${String(attPvAttachId || '')}|${String(attachPickerBaseline?.id || '')}`;
      if (!sig || sig === bmAttPreviewAutoMatchSigRef.current) return;
      bmAttPreviewAutoMatchSigRef.current = sig;
      setTimeout(() => {
        try { matchBmTargetsFromCurrentTable(); } catch {}
      }, 0);
    } catch {}
  }, [attPreviewOpen, attPvKind, attPvLoading, attPvData, attPvAttachId, attachPickerBaseline?.code, attachPickerBaseline?.id, matchBmTargetsFromCurrentTable]);

  // 关闭预览时清理标记，避免下次不同附件被误判为已同步
  useEffect(() => {
    if (attPreviewOpen) return;
    try { bmAttPreviewAutoMatchSigRef.current = ''; } catch {}
  }, [attPreviewOpen]);

  const closeOverviewAttachmentPreview = () => {
    try {
      if (ovAttPvUrl && String(ovAttPvUrl).startsWith('blob:')) {
        URL.revokeObjectURL(ovAttPvUrl);
      }
    } catch {}
    setOvAttPreviewOpen(false);
    setOvAttPvKind(null);
    setOvAttPvCols([]);
    setOvAttPvData([]);
    setOvAttPvSheetName('');
    setOvAttPvUrl('');
    setOvAttPvError('');
    setOvAttPvTitle('');
  };
  // 指令概览附件预览（增强显示：完整列/行 + 横向滚动）
  const previewOverviewAttachment = async (att: any) => {
    setOvAttPreviewOpen(true);
    setOvAttPvLoading(true);
    setOvAttPvError('');
    setOvAttPvCols([]);
    setOvAttPvData([]);
    setOvAttPvSheetName('');
    setOvAttPvUrl('');
    setOvAttPvKind(null);
    try {
      const url = resolveAttachmentRuntimeUrl(att);
      const disp = getDisplayFileName(att) || (att?.file_name ? prettifyStorageName(att.file_name) : '') || getBaseName(url);
      setOvAttPvTitle(String(disp || '附件预览'));
      const ext = getExtLower(att?.file_name || url);
      if (!url) throw new Error('无法获取文件URL');
      if (ext === 'csv') {
        let Papa: any = null;
        try { Papa = (await import('papaparse')).default || (await import('papaparse')); } catch {}
        if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
        const resp = await fetch(url, { credentials: 'include' as any });
        const csvText = await resp.text();
        const parsed = Papa.parse(csvText, { skipEmptyLines: false });
        const rows: any[] = Array.isArray(parsed?.data) ? parsed.data : [];
        const maxCols = Math.max(0, ...rows.map((r: any) => (Array.isArray(r) ? r.length : 0)));
        const rawHeader = Array.isArray(rows?.[0]) ? rows[0] : [];
        const headerRow = Array.from({ length: maxCols }, (_: any, i: number) => {
          const hv = rawHeader?.[i];
          return (hv === undefined || hv === null || String(hv).trim() === '') ? `列${i + 1}` : hv;
        });
        const bodyRows = rows.slice(1).map((r: any) => (
          Array.from({ length: maxCols }, (_: any, i: number) => {
            if (!Array.isArray(r)) return '';
            const v = r[i];
            return v === undefined || v === null ? '' : v;
          })
        ));
        const cols = headerRow.map((h: any, i: number) => ({
          title: String(h),
          dataIndex: `c${i}`,
          width: 180,
          render: (v: any) => {
            const txt = String(v ?? '');
            return (
              <Tooltip title={txt || '-'}>
                <span style={{ display: 'inline-block', minWidth: 40 }}>{txt}</span>
              </Tooltip>
            );
          },
        }));
        const data = bodyRows.map((r: any[], idx: number) => {
          const obj: any = { __idx: idx + 1 };
          r.forEach((v: any, i: number) => { obj[`c${i}`] = v; });
          return obj;
        });
        setOvAttPvKind('csv');
        setOvAttPvCols([{ title: '序号', dataIndex: '__idx', width: 70, render: (v: any) => <span style={{ color: '#999' }}>{v}</span> }, ...cols]);
        setOvAttPvData(data);
      } else if (ext === 'xlsx' || ext === 'xls') {
        let XLSX: any = null;
        try { XLSX = await import('xlsx'); } catch {}
        if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
        const resp = await fetch(url, { credentials: 'include' as any });
        const buf = await resp.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sn = wb.SheetNames?.[0];
        const ws = sn ? wb.Sheets[sn] : null;
        if (!ws) throw new Error('未找到工作表');
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[];
        const rows: any[] = Array.isArray(json) ? json : [];
        const maxCols = Math.max(0, ...rows.map((r: any) => (Array.isArray(r) ? r.length : 0)));
        const rawHeader = Array.isArray(rows?.[0]) ? rows[0] : [];
        const headerRow = Array.from({ length: maxCols }, (_: any, i: number) => {
          const hv = rawHeader?.[i];
          return (hv === undefined || hv === null || String(hv).trim() === '') ? `列${i + 1}` : hv;
        });
        const bodyRows = rows.slice(1).map((r: any) => (
          Array.from({ length: maxCols }, (_: any, i: number) => {
            if (!Array.isArray(r)) return '';
            const v = r[i];
            return v === undefined || v === null ? '' : v;
          })
        ));
        const cols = headerRow.map((h: any, i: number) => ({
          title: String(h),
          dataIndex: `c${i}`,
          width: 180,
          render: (v: any) => {
            const txt = String(v ?? '');
            return (
              <Tooltip title={txt || '-'}>
                <span style={{ display: 'inline-block', minWidth: 40 }}>{txt}</span>
              </Tooltip>
            );
          },
        }));
        const data = bodyRows.map((r: any[], idx: number) => {
          const obj: any = { __idx: idx + 1 };
          r.forEach((v: any, i: number) => { obj[`c${i}`] = v; });
          return obj;
        });
        setOvAttPvKind('xlsx');
        setOvAttPvCols([{ title: '序号', dataIndex: '__idx', width: 70, render: (v: any) => <span style={{ color: '#999' }}>{v}</span> }, ...cols]);
        setOvAttPvData(data);
        setOvAttPvSheetName(sn || '');
      } else if (ext === 'pdf') {
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('PDF 加载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        setOvAttPvKind('pdf');
        setOvAttPvUrl(objectUrl);
      } else if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'apng', 'svg', 'bmp', 'ico'].includes(ext)) {
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('图片加载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        setOvAttPvKind('image');
        setOvAttPvUrl(objectUrl);
      } else if (['mp4', 'mov', 'webm'].includes(ext)) {
        // 视频：不下载成 blob（最大 100MB，要边下边播），直接把地址交给 <video>，在弹窗里播放
        setOvAttPvKind('video');
        setOvAttPvVideoName(String(disp || `video.${ext}`));
        setOvAttPvUrl(url);
      } else {
        setOvAttPvKind('other');
        setOvAttPvUrl(url);
      }
    } catch (e: any) {
      setOvAttPvError(e?.message || '预览失败');
    } finally {
      setOvAttPvLoading(false);
    }
  };
  // 预览附件（csv/xlsx/pdf）
  const previewAttachment = async (att: any) => {
    setAttPreviewOpen(true);
    setAttPvLoading(true);
    setAttPvError('');
    setAttPvCols([]);
    setAttPvData([]);
    setAttPvSheetName('');
    setAttPvUrl('');
    setAttPvArchList([]);
    setAttPvArchiveRoot('');
    setAttPvArchiveKind('');
    setAttPvArchiveBuf(null);
    setAttPvAttachId(Number(att?.id ?? 0) || null);
    setAttPvDirty(false);
    setAttPvBmAudit(null);
    setAttPvBmMarkEq(new Set());
    setAttPvBmMarkDup(new Set());
    setAttPvBmMarkBad(new Set());
    setAttPvBmMarkNewEmpty(new Set());
    setAttPvBmManualMatchIdx(new Set());
    setAttPvBmTargetMatchByIdx({});
    setAttPvBmMarkNoTarget(new Set());
    setAttPvSltmAudit(null);
    setAttPvSltmMarkIncomplete(new Set());
    setAttPvSltmMarkBNotOne(new Set());
    setAttPvSltmMarkDupC(new Set());
    setAttPvSltmMarkDupD(new Set());
    // 打开预览时重置归档列表分页
    setAttPvArchPage(1);
    setAttPvArchPageSize(20);
    try {
      const url = resolveAttachmentRuntimeUrl(att);
      const baselineCodeUpper = String(attachPickerBaseline?.code || '').trim().toUpperCase();
      const isShippingLabelTargetMapping = baselineCodeUpper === 'SHIPPING_LABEL_TARGET_MAPPING';
      try {
        const disp = getDisplayFileName(att) || (att?.file_name ? prettifyStorageName(att.file_name) : '') || getBaseName(url);
        setAttPvTitle(String(disp || '附件预览'));
      } catch { setAttPvTitle('附件预览'); }
      const ext = getExtLower(att?.file_name || url);
      if (!url) throw new Error('无法获取文件URL');
      const isArchive = ((): boolean => {
        const arcSet = new Set(['zip','rar','7z','tar','tgz','tar.gz','tbz2','tar.bz2','txz','tar.xz']);
        return arcSet.has(ext);
      })();
      if (isArchive) {
        setAttPvKind('other');
        // RAR 走后端；其余保持前端解析
        if (ext === 'rar') {
          if (!att?.id) throw new Error('缺少附件ID');
          try {
            const resp = await request.get(`/api/v1/instruction/attachments/${att.id}/archive/list/`);
            const list = Array.isArray(resp?.data) ? resp.data : [];
            setAttPvArchList(list);
            setAttPvArchiveAttachId(Number(att.id));
            setAttPvArchiveKind('rar');
            setAttPvArchiveBuf(null);
            const root = 'arc:' + String(att.id);
            setAttPvArchiveRoot(root);
            setAttPvEntryName('');
            setAttPvUrl(root);
            setAttPvArchPage(1);
            return;
          } catch (e:any) {
            setAttPvError(e?.response?.data?.detail || e?.message || 'RAR 在线解压失败'); setAttPvKind('other'); setAttPvUrl(url); return;
          }
        }
        const resp = await fetch(url, { credentials: 'include' as any, cache: 'no-store' as any });
        if (!resp.ok) throw new Error('压缩包加载失败');
        const buf = await resp.arrayBuffer();
        // 根据扩展名选择解码器；优先 zip 走 JSZip，其他尝试 wasm 解码器，失败则报不支持
        const list: Array<{ path: string; dir: boolean; size: number }> = [];
        const setListing = (entries: Array<{ path: string; dir: boolean; size: number }>, kind: string) => {
          const sorted = [...entries].sort((a, b) => (a.dir === b.dir ? a.path.localeCompare(b.path) : (a.dir ? -1 : 1)));
          setAttPvArchList(sorted);
          const root = 'arc:' + url;
          setAttPvArchiveRoot(root);
          setAttPvArchiveKind(kind);
          setAttPvArchiveBuf(buf);
          setAttPvEntryName('');
          setAttPvUrl(root);
          setAttPvArchPage(1);
        };
        if (ext === 'zip') {
          let JSZip: any = null;
          try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
          if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
          const zip = await JSZip.loadAsync(buf);
          zip.forEach((relPath: string, entry: any) => {
            list.push({ path: relPath, dir: !!entry.dir, size: Number(entry?._dataUncompressedSize || entry?.uncompressedSize || 0) || 0 });
          });
          setListing(list, 'zip');
          return;
        }
        // RAR 分支已通过后端处理，前端不再处理
        // 7Z（占位：若库可用可在此处实现）
        if (ext === '7z') {
          try {
            // 占位：尝试 7z-wasm/7zip-wasm（若安装）
            // const mod: any = await import('7z-wasm');
            throw new Error('7z 解码器未集成');
          } catch {
            setAttPvError('7Z 在线解压暂不可用，请下载查看'); setAttPvKind('other'); setAttPvUrl(url); return;
          }
        }
        // tar/tgz/tbz2/txz（占位：优选 libarchive-wasm）
        if (ext === 'tar') {
          try {
            const entries = parseTarEntriesFromBuffer(buf);
            setListing(entries.map(e => ({ path: e.path, dir: e.dir, size: e.size })), 'tar');
            // 缓存 tar 原始 buffer（后续按条目提取）
            return;
          } catch {
            setAttPvError('TAR 解析失败'); setAttPvKind('other'); setAttPvUrl(url); return;
          }
        }
        if (ext === 'tgz' || ext === 'tar.gz') {
          try {
            const tarBuf = await gunzipToArrayBuffer(buf) as ArrayBuffer;
            const entries = parseTarEntriesFromBuffer(tarBuf as ArrayBuffer);
            setAttPvArchiveBuf(tarBuf as ArrayBuffer);
            setListing(entries.map(e => ({ path: e.path, dir: e.dir, size: e.size })), 'tar');
            return;
          } catch (e:any) {
            setAttPvError(e?.message || 'TGZ 解压失败'); setAttPvKind('other'); setAttPvUrl(url); return;
          }
        }
        if (['tbz2','tar.bz2','txz','tar.xz'].includes(ext)) {
          setAttPvError('该压缩格式在线解压暂不可用，请下载查看'); setAttPvKind('other'); setAttPvUrl(url); return;
        }
        // 兜底
        setAttPvError('该压缩格式暂不支持在线预览'); setAttPvKind('other'); setAttPvUrl(url);
        return;
      } else if (ext === 'csv') {
        setAttPvKind('csv');
        let Papa: any = null;
        try { Papa = (await import(/* webpackChunkName: "papaparse" */ 'papaparse')).default || (await import('papaparse')); } catch {}
        if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
        // 覆盖优先：若存在已编辑版本，直接从 Blob 解析
        let csvText: string;
        const ov = attPvAttachId ? editedAttachMap[String(attPvAttachId)] : null;
        if (ov?.blob) {
          csvText = await (ov.blob as Blob).text();
        } else {
          const resp = await fetch(url, { credentials: 'include' as any });
          csvText = await resp.text();
        }
        const parsed = Papa.parse(csvText, { skipEmptyLines: false });
        const rows: any[] = Array.isArray(parsed?.data) ? parsed.data : [];
        let headerRow: any[] = Array.isArray(rows?.[0]) ? rows[0] : [];
        let bodyRows: any[] = rows.slice(headerRow.length > 0 ? 1 : 0);
        if (isShippingLabelTargetMapping) {
          const matrixRows = Array.isArray(rows) ? rows : [];
          const maxCols = Math.max(0, ...matrixRows.map((r: any) => (Array.isArray(r) ? r.length : 0)));
          const rawHeader = Array.isArray(matrixRows?.[0]) ? matrixRows[0] : [];
          headerRow = Array.from({ length: maxCols }, (_: any, i: number) => {
            const hv = rawHeader?.[i];
            return (hv === undefined || hv === null || String(hv).trim() === '') ? `列${i + 1}` : hv;
          });
          bodyRows = matrixRows.slice(1).map((r: any) => (
            Array.from({ length: maxCols }, (_: any, i: number) => {
              if (!Array.isArray(r)) return '';
              const v = r[i];
              return v === undefined || v === null ? '' : v;
            })
          ));
        }
        const cols = (headerRow.length > 0 ? headerRow : (rows?.[0] || [])).map((h: any, i: number) => {
          const headerText = (() => {
            try {
              if (h === null || h === undefined) return `列${i + 1}`;
              if (typeof h === 'string') return h || `列${i + 1}`;
              if (typeof h === 'number' || typeof h === 'boolean' || typeof h === 'bigint') return String(h);
              if (h instanceof Date) return String(h);
              if (typeof h === 'object') {
                // 兼容某些解析结果返回对象的情况（避免 String(h) => [object Object]）
                const v = (h as any)?.w ?? (h as any)?.v ?? (h as any)?.text ?? (h as any)?.label ?? (h as any)?.name;
                if (v !== null && v !== undefined) return String(v);
              }
            } catch {}
            return `列${i + 1}`;
          })();
          return {
            title: headerText,
            titleText: headerText,
          dataIndex: `c${i}`,
          render: (v:any, rec:any) => (
            <input
              style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent' }}
              value={v ?? ''}
              onChange={(e)=> {
                setAttPvData(prev => {
                  const next = prev.slice();
                  const idx = next.findIndex((r:any)=> r.__idx === rec.__idx);
                  if (idx >= 0) next[idx] = { ...next[idx], [`c${i}`]: e.target.value };
                  return next;
                });
                setAttPvDirty(true);
              }}
            />
          )
          };
        });
        // BARCODE_MAPPING：在“数量分配”(第4列/c3)列头添加提示（移除“同步”按钮）
        try {
          const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
          if (attachPickerOpen && isBm) {
            const tipQty = '默认为空，仅用于一个旧条码对应多个新条码时按顺序指定数量限制，请参考指令详情手动修改。';
            for (let i = 0; i < cols.length; i++) {
              if (String(cols[i]?.dataIndex) === 'c3') {
                const baseTitle = ((): string => {
                  try {
                    if (typeof (cols[i] as any)?.titleText === 'string') return String((cols[i] as any).titleText);
                    if (typeof cols[i]?.title === 'string') return String(cols[i].title);
                  } catch {}
                  return '数量分配';
                })();
                cols[i] = {
                  ...cols[i],
                  title: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title={tipQty}>
                        <span style={{ cursor: 'help' }}>{baseTitle}</span>
                      </Tooltip>
                    </span>
                  )
                };
                break;
              }
            }
          }
        } catch {}
        const data = bodyRows.map((r: any[], idx: number) => {
          const obj: any = { __idx: idx + 1 };
          r.forEach((v: any, i: number) => { obj[`c${i}`] = v; });
          return obj;
        });
        setAttPvCols([{ title: '行号', dataIndex: '__idx', width: 70, render: (v: any) => <span style={{ color: '#999' }}>{Number.isFinite(Number(v)) ? Number(v) + 1 : '-'}</span> }, ...cols]);
        setAttPvData(data);
        try { setAttPvTargetIdx((data?.length || 0) + 1); } catch {}
        // BARCODE_MAPPING：若文件中已包含“操作对象匹配信息/匹配操作对象”列，则初始化到 state，并将其视为“手动结果”（避免再次审核覆盖）
        try {
          const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
          if (attachPickerOpen && isBm) {
            const isMatchTitle = (t:any) => {
              try { const s = String(t || ''); return s.includes('操作对象匹配信息') || s.includes('匹配操作对象'); } catch { return false; }
            };
            const matchColIdx = (headerRow || []).findIndex((h:any) => isMatchTitle(h));
            if (matchColIdx >= 0) {
              const map: Record<number, string> = {};
              const manual = new Set<number>();
              (data || []).forEach((row:any) => {
                const idx = Number(row?.__idx);
                if (!Number.isFinite(idx) || idx <= 0) return;
                const raw = String(row?.[`c${matchColIdx}`] ?? '').trim();
                const v = raw || '-';
                map[idx] = v;
                if (v !== '-' && v !== '' && v !== '[]') manual.add(idx);
              });
              setAttPvBmTargetMatchByIdx(map);
              setAttPvBmManualMatchIdx(manual);
            }
          }
        } catch {}
        // BARCODE_MAPPING 内容审核（复用创建指令时的3项审核）：仅在审核页“从客户上传附件中选择”且目标基线为 BARCODE_MAPPING 时显示
        try {
          const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
          if (attachPickerOpen && isBm) {
            const normCode = (v:any) => String(v ?? '').trim().toUpperCase();
            const newEmptyRows: Array<{ rowNo: number; old: string }> = [];
            const sameRows: Array<{ rowNo: number; old: string; newer: string }> = [];
            const pairMap: Map<string, Array<{ rowNo: number; old: string; newer: string }>> = new Map();
            // 第3类：同一旧条码对应多个新条码且 D 列无效（保持与创建指令一致的提示含义）
            const oldToLines = new Map<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any }>>();
            bodyRows.forEach((r:any[], i:number) => {
              const fileRowNo = i + 2; // 含表头
              const a = normCode(r?.[0]);
              const b = normCode(r?.[1]);
              // 新条码为空
              if (!b) { newEmptyRows.push({ rowNo: fileRowNo, old: a || '-' }); return; }
              // 重复组合：包含旧条码缺失的情况（旧为空按 '-' 参与）
              const aForPair = a || '-';
              const key = `${aForPair}→${b}`;
              const arr = pairMap.get(key) || [];
              arr.push({ rowNo: fileRowNo, old: aForPair, newer: b });
              pairMap.set(key, arr);
              // 其余审核保持不变：仅对 a/b 都存在的行执行
              if (!a) return;
              if (a === b) sameRows.push({ rowNo: fileRowNo, old: a, newer: b });
              const lines = oldToLines.get(a) || [];
              lines.push({ rowNo: fileRowNo, a, b, c: r?.[2], d: r?.[3], e: r?.[4] });
              oldToLines.set(a, lines);
            });
            const dupPairs = Array.from(pairMap.entries())
              .filter(([, arr]) => (arr?.length || 0) >= 2)
              .map(([k, arr]) => ({ key: String(k), count: arr.length, rowNos: arr.map(x => x.rowNo) }));
            const badRows: Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }> = [];
            oldToLines.forEach((arr, a) => {
              if (!arr || arr.length <= 1) return;
              const distinctNew = Array.from(new Set(arr.map(x => x.b).filter(Boolean)));
              if (distinctNew.length <= 1) return;
              const hasInvalidQty = arr.some(x => {
                const v = x.d;
                const num = Number(v);
                return v === undefined || v === null || String(v).trim() === '' || Number.isNaN(num);
              });
              if (!hasInvalidQty) return;
              arr.forEach(x => {
                badRows.push({ rowNo: x.rowNo, a: x.a, b: x.b, c: x.c, d: x.d, e: x.e, groupKey: a });
              });
            });
            const groupOrder: string[] = [];
            const groupMap: Record<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }>> = {};
            badRows.forEach((r) => {
              const k = r.groupKey || '';
              if (!k) return;
              if (!groupMap[k]) { groupMap[k] = []; groupOrder.push(k); }
              groupMap[k].push(r);
            });
            setAttPvBmAudit((prev) => {
              const prevNoTarget = Array.isArray(prev?.noTargetRows) ? prev!.noTargetRows : [];
              const has = prevNoTarget.length || newEmptyRows.length || sameRows.length || dupPairs.length || badRows.length;
              if (!has) return null;
              return { noTargetRows: prevNoTarget, newEmptyRows, sameRows, dupPairs, badRows, groupOrder, groupMap };
            });
          }
        } catch {}
        try {
          const isSltm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
          if (attachPickerOpen && isSltm) {
            runSltmAuditFromCurrentTable();
          }
        } catch {}
      } else if (ext === 'xlsx' || ext === 'xls') {
        setAttPvKind('xlsx');
        let XLSX: any = null;
        try { XLSX = await import(/* webpackChunkName: "xlsx" */ 'xlsx'); } catch {}
        if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
        // 覆盖优先
        const ov = attPvAttachId ? editedAttachMap[String(attPvAttachId)] : null;
        let buf: ArrayBuffer;
        if (ov?.blob) {
          buf = await ov.blob.arrayBuffer();
        } else {
          const resp = await fetch(url, { credentials: 'include' as any });
          buf = await resp.arrayBuffer();
        }
        const wb = XLSX.read(buf, { type: 'array' });
        const sn = wb.SheetNames?.[0];
        const ws = sn ? wb.Sheets[sn] : null;
        if (!ws) throw new Error('未找到工作表');
        const json = XLSX.utils.sheet_to_json(
          ws,
          isShippingLabelTargetMapping ? { header: 1, defval: '' } : { header: 1 },
        ) as any[];
        const rows: any[] = Array.isArray(json) ? json : [];
        let headerRow: any[] = Array.isArray(rows?.[0]) ? rows[0] : [];
        let bodyRows: any[] = rows.slice(headerRow.length > 0 ? 1 : 0);
        if (isShippingLabelTargetMapping) {
          const matrixRows = Array.isArray(rows) ? rows : [];
          const maxCols = Math.max(0, ...matrixRows.map((r: any) => (Array.isArray(r) ? r.length : 0)));
          const rawHeader = Array.isArray(matrixRows?.[0]) ? matrixRows[0] : [];
          headerRow = Array.from({ length: maxCols }, (_: any, i: number) => {
            const hv = rawHeader?.[i];
            return (hv === undefined || hv === null || String(hv).trim() === '') ? `列${i + 1}` : hv;
          });
          bodyRows = matrixRows.slice(1).map((r: any) => (
            Array.from({ length: maxCols }, (_: any, i: number) => {
              if (!Array.isArray(r)) return '';
              const v = r[i];
              return v === undefined || v === null ? '' : v;
            })
          ));
        }
        const cols = (headerRow.length > 0 ? headerRow : (rows?.[0] || [])).map((h: any, i: number) => {
          const headerText = (() => {
            try {
              if (h === null || h === undefined) return `列${i + 1}`;
              if (typeof h === 'string') return h || `列${i + 1}`;
              if (typeof h === 'number' || typeof h === 'boolean' || typeof h === 'bigint') return String(h);
              if (h instanceof Date) return String(h);
              if (typeof h === 'object') {
                const v = (h as any)?.w ?? (h as any)?.v ?? (h as any)?.text ?? (h as any)?.label ?? (h as any)?.name;
                if (v !== null && v !== undefined) return String(v);
              }
            } catch {}
            return `列${i + 1}`;
          })();
          return {
            title: headerText,
            titleText: headerText,
          dataIndex: `c${i}`,
          render: (v:any, rec:any) => (
            <input
              style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent' }}
              value={v ?? ''}
              onChange={(e)=> {
                setAttPvData(prev => {
                  const next = prev.slice();
                  const idx = next.findIndex((r:any)=> r.__idx === rec.__idx);
                  if (idx >= 0) next[idx] = { ...next[idx], [`c${i}`]: e.target.value };
                  return next;
                });
                setAttPvDirty(true);
              }}
            />
          )
          };
        });
        // BARCODE_MAPPING：在“数量分配”(第4列/c3)列头添加提示（移除“同步”按钮）
        try {
          const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
          if (attachPickerOpen && isBm) {
            const tipQty = '默认为空，仅用于一个旧条码对应多个新条码时按顺序指定数量限制，请参考指令详情手动修改。';
            for (let i = 0; i < cols.length; i++) {
              if (String(cols[i]?.dataIndex) === 'c3') {
                const baseTitle = ((): string => {
                  try {
                    if (typeof (cols[i] as any)?.titleText === 'string') return String((cols[i] as any).titleText);
                    if (typeof cols[i]?.title === 'string') return String(cols[i].title);
                  } catch {}
                  return '数量分配';
                })();
                cols[i] = {
                  ...cols[i],
                  title: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title={tipQty}>
                        <span style={{ cursor: 'help' }}>{baseTitle}</span>
                      </Tooltip>
                    </span>
                  )
                };
                break;
              }
            }
          }
        } catch {}
        const data = bodyRows.map((r: any[], idx: number) => {
          const obj: any = { __idx: idx + 1 };
          r.forEach((v: any, i: number) => { obj[`c${i}`] = v; });
          return obj;
        });
        setAttPvCols([{ title: '行号', dataIndex: '__idx', width: 70, render: (v: any) => <span style={{ color: '#999' }}>{Number.isFinite(Number(v)) ? Number(v) + 1 : '-'}</span> }, ...cols]);
        setAttPvData(data);
        setAttPvSheetName(sn || '');
        try { setAttPvTargetIdx((data?.length || 0) + 1); } catch {}
        // BARCODE_MAPPING：若文件中已包含“操作对象匹配信息/匹配操作对象”列，则初始化到 state，并将其视为“手动结果”（避免再次审核覆盖）
        try {
          const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
          if (attachPickerOpen && isBm) {
            const isMatchTitle = (t:any) => {
              try { const s = String(t || ''); return s.includes('操作对象匹配信息') || s.includes('匹配操作对象'); } catch { return false; }
            };
            const matchColIdx = (headerRow || []).findIndex((h:any) => isMatchTitle(h));
            if (matchColIdx >= 0) {
              const map: Record<number, string> = {};
              const manual = new Set<number>();
              (data || []).forEach((row:any) => {
                const idx = Number(row?.__idx);
                if (!Number.isFinite(idx) || idx <= 0) return;
                const raw = String(row?.[`c${matchColIdx}`] ?? '').trim();
                const v = raw || '-';
                map[idx] = v;
                if (v !== '-' && v !== '' && v !== '[]') manual.add(idx);
              });
              setAttPvBmTargetMatchByIdx(map);
              setAttPvBmManualMatchIdx(manual);
            }
          }
        } catch {}
        // BARCODE_MAPPING 内容审核（同 CSV 分支）
        try {
          const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
          if (attachPickerOpen && isBm) {
            const normCode = (v:any) => String(v ?? '').trim().toUpperCase();
            const newEmptyRows: Array<{ rowNo: number; old: string }> = [];
            const sameRows: Array<{ rowNo: number; old: string; newer: string }> = [];
            const pairMap: Map<string, Array<{ rowNo: number; old: string; newer: string }>> = new Map();
            const oldToLines = new Map<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any }>>();
            bodyRows.forEach((r:any[], i:number) => {
              const fileRowNo = i + 2; // 含表头
              const a = normCode(r?.[0]);
              const b = normCode(r?.[1]);
              // 新条码为空
              if (!b) { newEmptyRows.push({ rowNo: fileRowNo, old: a || '-' }); return; }
              // 重复组合：包含旧条码缺失的情况（旧为空按 '-' 参与）
              const aForPair = a || '-';
              const key = `${aForPair}→${b}`;
              const arr = pairMap.get(key) || [];
              arr.push({ rowNo: fileRowNo, old: aForPair, newer: b });
              pairMap.set(key, arr);
              // 其余审核保持不变：仅对 a/b 都存在的行执行
              if (!a) return;
              if (a === b) sameRows.push({ rowNo: fileRowNo, old: a, newer: b });
              const lines = oldToLines.get(a) || [];
              lines.push({ rowNo: fileRowNo, a, b, c: r?.[2], d: r?.[3], e: r?.[4] });
              oldToLines.set(a, lines);
            });
            const dupPairs = Array.from(pairMap.entries())
              .filter(([, arr]) => (arr?.length || 0) >= 2)
              .map(([k, arr]) => ({ key: String(k), count: arr.length, rowNos: arr.map(x => x.rowNo) }));
            const badRows: Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }> = [];
            oldToLines.forEach((arr, a) => {
              if (!arr || arr.length <= 1) return;
              const distinctNew = Array.from(new Set(arr.map(x => x.b).filter(Boolean)));
              if (distinctNew.length <= 1) return;
              const hasInvalidQty = arr.some(x => {
                const v = x.d;
                const num = Number(v);
                return v === undefined || v === null || String(v).trim() === '' || Number.isNaN(num);
              });
              if (!hasInvalidQty) return;
              arr.forEach(x => {
                badRows.push({ rowNo: x.rowNo, a: x.a, b: x.b, c: x.c, d: x.d, e: x.e, groupKey: a });
              });
            });
            const groupOrder: string[] = [];
            const groupMap: Record<string, Array<{ rowNo: number; a: string; b: string; c: any; d: any; e: any; groupKey: string }>> = {};
            badRows.forEach((r) => {
              const k = r.groupKey || '';
              if (!k) return;
              if (!groupMap[k]) { groupMap[k] = []; groupOrder.push(k); }
              groupMap[k].push(r);
            });
            setAttPvBmAudit((prev) => {
              const prevNoTarget = Array.isArray(prev?.noTargetRows) ? prev!.noTargetRows : [];
              const has = prevNoTarget.length || newEmptyRows.length || sameRows.length || dupPairs.length || badRows.length;
              if (!has) return null;
              return { noTargetRows: prevNoTarget, newEmptyRows, sameRows, dupPairs, badRows, groupOrder, groupMap };
            });
          }
        } catch {}
        try {
          const isSltm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
          if (attachPickerOpen && isSltm) {
            runSltmAuditFromCurrentTable();
          }
        } catch {}
      } else if (ext === 'pdf') {
        setAttPvKind('pdf');
        // 以 Blob 方式加载，生成 blob: URL，绕过 X-Frame 限制
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('PDF 加载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        setAttPvUrl(objectUrl);
      } else {
        setAttPvKind('other');
        setAttPvUrl(url);
      }
    } catch (e: any) {
      setAttPvError(e?.message || '预览失败');
    } finally {
      setAttPvLoading(false);
    }
  };
  // 保存 CSV/XLSX 编辑结果为覆盖 Blob，供选择弹窗使用
  const saveEditedPreviewAsOverlay = async () => {
    try {
      if (!attPvAttachId) { message.warning('未关联附件，无法保存'); return; }
      if (!(attPvKind === 'csv' || attPvKind === 'xlsx')) { message.warning('该类型不支持保存编辑'); return; }
      const rows = Array.isArray(attPvData) ? attPvData : [];
      if (rows.length === 0) { message.warning('无可保存内容'); return; }
      const getTitleText = (c:any) => {
        try {
          if (typeof c?.titleText === 'string') return c.titleText;
          if (typeof c?.title === 'string') return c.title;
          if (c?.dataIndex) return String(c.dataIndex);
        } catch {}
        return '';
      };
      const colPairs = (attPvCols || [])
        .filter((c:any)=> c?.dataIndex && c.dataIndex !== '__idx')
        .map((c:any)=> {
          const key = String(c.dataIndex);
          const title0 = String(getTitleText(c) || '');
          const title = (title0.includes('匹配操作对象') || title0.includes('操作对象匹配信息')) ? '操作对象匹配信息' : title0;
          return { key, title };
        });

      const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
      const matchTitle = '操作对象匹配信息';
      const hasMatchColAlready = colPairs.some(p => String(p.title || '').includes('匹配操作对象') || String(p.title || '').includes(matchTitle));

      const buildMatchText = (r:any): string => {
        try {
          const __idx = Number(r?.__idx);
          if (Number.isFinite(__idx) && __idx > 0) {
            const v = String((attPvBmTargetMatchByIdx as any)?.[__idx] ?? '').trim();
            return v || '-';
          }
        } catch {}
        return '-';
      };

      const headerTitles = (() => {
        const base = colPairs.map(p => p.title);
        if (!isBm) return base;
        if (hasMatchColAlready) return base;
        // 插入在“数量分配(c3)”与“备注(c4)”之间：按列 key 定位 c3
        const idx = colPairs.findIndex(p => p.key === 'c3');
        if (idx < 0) return base.concat([matchTitle]);
        const next = [...base.slice(0, idx + 1), matchTitle, ...base.slice(idx + 1)];
        return next;
      })();

      const body = rows.map((r:any) => {
        // 先按现有列输出；如果已有匹配列，则覆盖其值；如果没有，则在 c3 后插入
        const baseVals = colPairs.map((p) => {
          if (isBm && (String(p.title || '').includes('匹配操作对象') || String(p.title || '').includes(matchTitle))) return buildMatchText(r);
          return r?.[p.key] ?? '';
        });
        if (!isBm) return baseVals;
        if (hasMatchColAlready) return baseVals;
        const idx = colPairs.findIndex(p => p.key === 'c3');
        if (idx < 0) return [...baseVals, buildMatchText(r)];
        return [...baseVals.slice(0, idx + 1), buildMatchText(r), ...baseVals.slice(idx + 1)];
      });

      const aoa = [headerTitles, ...body];
      const idStr = String(attPvAttachId);
      // 生成文件名
      const src = (customerAttachments || []).find((x:any)=> Number(x?.id) === Number(attPvAttachId));
      const orig = getDisplayFileName(src) || src?.file_name || '附件已编辑';
      const plain = (orig.split('?')[0] || orig).split('#')[0];
      const dot = plain.lastIndexOf('.');
      let outName = '';
      let outBlob: Blob | undefined;
      if (attPvKind === 'csv') {
        let Papa: any = null;
        try { Papa = (await import('papaparse')).default || (await import('papaparse')); } catch {}
        if (!Papa) { message.error('缺少 CSV 库'); return; }
        const text = Papa.unparse(aoa);
        outBlob = new Blob([text], { type: 'text/csv;charset=utf-8' });
        outName = (dot >= 0 ? plain.slice(0, dot) : plain) + '-已编辑.csv';
      } else {
        let XLSX: any = null;
        try { XLSX = await import('xlsx'); } catch {}
        if (!XLSX) { message.error('缺少 Excel 库'); return; }
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, attPvSheetName || 'Sheet1');
        const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
        outBlob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        outName = (dot >= 0 ? plain.slice(0, dot) : plain) + '-已编辑.xlsx';
      }
      if (!outBlob) { message.error('生成文件失败'); return; }
      const finalBlob: Blob = outBlob as Blob;
      setEditedAttachMap(prev => ({ ...prev, [idStr]: { blob: finalBlob, fileName: outName, mime: finalBlob.type, updatedAt: Date.now() } }));
      setAttPvDirty(false);
      message.success('已保存编辑内容，将在选择弹窗优先使用该版本');
    } catch {
      message.error('保存编辑失败');
    }
  };
  // 采纳为基线：优先调用后端 adopt 接口；失败则回退为前端下载后上传
  const adoptAttachmentAsBaseline = async (baselineId: number, att: any, overrideName?: string) => {
    if (!baselineId) { message.warning('请先创建草稿'); return; }
    try {
      // 若存在前端编辑覆盖版本，优先直接上传覆盖 Blob
      try {
        const idStr = String(att?.id ?? '');
        const ov = editedAttachMap[idStr];
        if (ov?.blob) {
          const fd = new FormData();
          fd.append('baseline', String(baselineId));
          // 若指定了 overrideName，按 overrideName 命名；否则用覆盖文件名
          let uploadName = String(overrideName || ov.fileName || getDisplayFileName(att) || att?.file_name || 'attachment');
          // 若 overrideName 无扩展名，自动补齐
          if (overrideName) {
            const orig = getDisplayFileName(att) || att?.file_name || '';
            const ext = (() => {
              const i = orig.lastIndexOf('.');
              return i >= 0 ? orig.slice(i + 1) : '';
            })();
            if (ext && uploadName.lastIndexOf('.') < 0) uploadName = `${uploadName}.${ext}`;
          }
          fd.append('file', ov.blob, uploadName);
          await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          message.success('已采纳为基线文件');
          baselineSessionTouchedRef.current = true;
          await loadBaselines(reviewOf?.id);
          return;
        }
      } catch {}
      // 若指定了新文件名，则直接走"下载后上传"分支，以确保文件名可控
      if (!overrideName) {
        // 优先尝试 adopt（不改名时更高效）
        try {
          await request.post('/api/v1/instruction/baseline-files/adopt/', {
            baseline: baselineId,
            source_attachment_id: att?.id,
          });
        } catch {
          // 回退：fetch blob 再上传
          const url = resolveAttachmentRuntimeUrl(att);
          const baseName = getDisplayFileName(att) || att?.file_name || 'attachment';
          if (!url) throw new Error('无法获取文件URL');
          const resp = await fetch(url, { credentials: 'include' as any });
          const blob = await resp.blob();
          const fd = new FormData();
          fd.append('baseline', String(baselineId));
          fd.append('file', blob, baseName);
          await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      } else {
        // 指定新名称：下载后按指定名称上传
        const url = resolveAttachmentRuntimeUrl(att);
        if (!url) throw new Error('无法获取文件URL');
        // 保底追加扩展名（若用户未填写扩展名）
        const original = getDisplayFileName(att) || att?.file_name || '';
        const origExt = (() => {
          const s = String(original || '');
          const i = s.lastIndexOf('.');
          return i >= 0 ? s.slice(i + 1) : '';
        })();
        let finalName = String(overrideName || '').trim();
        if (origExt && finalName && finalName.lastIndexOf('.') < 0) {
          finalName = `${finalName}.${origExt}`;
        }
        const resp = await fetch(url, { credentials: 'include' as any });
        const blob = await resp.blob();
        const fd = new FormData();
        fd.append('baseline', String(baselineId));
        fd.append('file', blob, finalName || (original || 'attachment'));
        await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      message.success('已采纳为基线文件');
      baselineSessionTouchedRef.current = true;
      await loadBaselines(reviewOf?.id);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '采纳失败');
    }
  };
  // 文件项 hover 显示删除图标
  const [hoveredFileId, setHoveredFileId] = useState<number | null>(null);
  // 审核-计划编辑器状态
  const [planSteps, setPlanSteps] = useState<Array<{ template_code: string; after?: string[] }>>([]);
  const [wotplOptions, setWotplOptions] = useState<Array<{ value: string; label: React.ReactNode; searchLabel: string; code?: string; name?: string }>>([]);
  const [wotplLoading, setWotplLoading] = useState(false);
  const [planPreview, setPlanPreview] = useState<any | null>(null);
  const [planPreviewLoading, setPlanPreviewLoading] = useState(false);
  // 工单模板预览展开/收起（默认收起）
  const [woPreviewCollapsed, setWoPreviewCollapsed] = useState(true);
  const [showAdvancedDeps, setShowAdvancedDeps] = useState(false);
  // 收费规则预览
  const [rulesPreview, setRulesPreview] = useState<Array<{ key: string; template_code: string; operation_type: string; rule_system_code?: string; unit?: string; currency?: string; rule_type?: string; tier?: string; fallback_price?: string }>>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  // 临时关闭收费规则摘要（隐藏并停止请求）
  const RULES_SUMMARY_ENABLED = false;
  // 指令模板下拉数据
  const [tplOptions, setTplOptions] = useState<Array<{ value: string; label: string; name?: string; code?: string; is_active?: boolean; level?: string }>>([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplOnlyUsed, setTplOnlyUsed] = useState(false);
  const [usedTplCodes, setUsedTplCodes] = useState<Set<string>>(new Set());
  // 基础集合：系统级 + 当前客户级 + 当前客户项目级（含已禁用）
  const loadTemplateUniverse = async (customerIdOverride?: number) => {
    try {
      setTplLoading(true);
      const requests: Array<Promise<any>> = [];
      // 推导当前客户ID（基于页面上下文与项目列表）
      let derivedCustomerId: number | undefined = undefined;
      try {
        // 优先：根据路径判断客户后台，再从项目里推导唯一客户ID
        const path = String((window as any)?.location?.pathname || '');
        const isCustomerPortal = path.startsWith('/dashboard/customer');
        const isAdmin = path.startsWith('/dashboard/admin');
        const isStaff = path.startsWith('/dashboard/staff');
        const isAdminStaff = isAdmin || isStaff;
        const setIds = new Set<number>();
        (projects || []).forEach((p:any) => {
          const cid = Number(p?.customer_id ?? p?.customer?.id);
          if (Number.isFinite(cid)) setIds.add(cid);
        });
        if (isCustomerPortal && setIds.size >= 1) {
          // 客户后台：若能推导出客户ID集合，则任选第一个（通常唯一）
          derivedCustomerId = Array.from(setIds)[0];
        } else if (setIds.size === 1) {
          // 兜底：非客户后台但项目唯一客户
          derivedCustomerId = Array.from(setIds)[0];
        }
        // 管理员/员工后台：模板全集（不依赖客户选择）
        if (isAdminStaff) {
          requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'system', page_size: 1000 } }));
          requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'customer', page_size: 1000 } }));
          requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'project', page_size: 1000 } }));
          // 后续不再走客户/项目派生逻辑
          derivedCustomerId = undefined;
        }
      } catch {}
      // 显式覆盖（管理员在筛选框选择了客户时）
      if (Number.isFinite(Number(customerIdOverride))) {
        derivedCustomerId = Number(customerIdOverride);
      }

      // 始终优先拉取系统级模板
      // 注：管理员/员工后台上方已加入一次系统级；这里统一去重合并，重复请求不会影响最终结果
      requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'system', page_size: 1000 } }));

      // 客户后台优先补充该客户专属（客户级 + 项目级）
      const pathNow = String((window as any)?.location?.pathname || '');
      const isCustomerPortalNow = pathNow.startsWith('/dashboard/customer');
      if (isCustomerPortalNow && derivedCustomerId) {
        // 客户后台：直接按当前登录客户ID过滤客户级与项目级（不依赖项目加载）
        requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'customer', customer_id: derivedCustomerId, page_size: 1000 } }));
        requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'project', customer_id: derivedCustomerId, page_size: 1000 } }));
      } else {
        // 管理端/无法推导客户时：补充客户级（如能推导）与当前可见项目的项目级
        if (derivedCustomerId) {
          requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'customer', customer_id: derivedCustomerId, page_size: 1000 } }));
          // 管理端同样支持通过 customer_id 一次性获取该客户的所有项目级模板
          requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'project', customer_id: derivedCustomerId, page_size: 1000 } }));
        }
        const projectIds = Array.isArray(projects) ? projects.map((p:any)=> Number(p?.id)).filter((v:number)=>Number.isFinite(v)) : [];
        projectIds.slice(0, 50).forEach((pid:number) => {
          requests.push(request.get('/api/v1/instruction/templates/', { params: { level: 'project', project_id: pid, page_size: 1000 } }));
        });
      }
      const respList = await Promise.allSettled(requests);
      const acc: Array<any> = [];
      respList.forEach((r:any) => {
        if (r.status === 'fulfilled') {
          const data = r.value?.data;
          const rows = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          acc.push(...rows);
        }
      });
      // 去重：按 system_code 上屏
      // 已下线的系统预设模板（"不使用任何指令模板…"，已被"库存非标准操作"取代）不再出现在筛选里，不考虑历史数据
      const RETIRED_TEMPLATE_CODES = new Set(['INSTRUCTIONTEMPLATE-SYS-14']);
      const map = new Map<string, any>();
      (acc || []).forEach((t:any) => {
        const code = String(t?.system_code || '').toUpperCase();
        if (!code || RETIRED_TEMPLATE_CODES.has(code)) return;
        if (!map.has(code)) map.set(code, t);
      });
      const opts = Array.from(map.values()).map((t:any) => ({
        value: String(t.id ?? t.system_code ?? ''),
        label: `${t.name || '-'} · ${t.system_code || ''}`.trim(),
        name: t.name,
        code: t.system_code,
        is_active: !!t.is_active,
        level: t.level,
      }));
      setTplOptions(opts);
    } catch {
      setTplOptions([]);
    } finally {
      setTplLoading(false);
    }
  };
  // 加载"使用过"的模板编码集合（与当前筛选一致）
  const loadUsedTemplateCodes = async () => {
    try {
      const params: any = {};
      const f = state?.filters || {};
      if (f.system_code) params.system_code = f.system_code;
      if (f.status) params.status = f.status;
      if (f.customer) params.customer_id = f.customer;
      if (f.project) params.project_id = f.project;
      if (f.warehouse) params.warehouse_id = f.warehouse;
      if (f.keyword) params.keyword = f.keyword;
      const resp = await request.get('/api/v1/instruction/instructions/used-templates/', { params });
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const set = new Set<string>();
      rows.forEach((r:any) => {
        const code = String(r?.system_code || '').toUpperCase();
        if (code) set.add(code);
      });
      setUsedTplCodes(set);
    } catch {
      setUsedTplCodes(new Set());
    }
  };
  const loadWorkOrderTemplates = async () => {
    if (wotplOptions.length > 0) return;
    setWotplLoading(true);
    const endpoints = ['/api/v1/workorders/workorder-templates/', '/api/v1/workorders/templates/'];
    try {
      let list:any[] = [];
      for (const ep of endpoints) {
        try {
          const resp = await request.get(ep, { params: { is_active: true, all: 1 } });
          const data = resp?.data;
          list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          if (list && list.length) break;
        } catch {}
      }
      if (!list || list.length === 0) {
        try {
          const resp = await request.get(endpoints[0], { params: { page: 1, page_size: 200 } });
          list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        } catch {}
      }
      const opts = (list || []).map((t:any) => {
        const name = String(t.name || '-');
        const code = String(t.code || '');
        const full = `${name} · ${code}`.trim();
        return {
          value: code,
          label: (<span title={full}>{name}</span>),
          searchLabel: `${name} ${code}`.trim(),
          code,
          name
        };
      }).filter((o:any) => !!o.value);
      setWotplOptions(opts);
    } finally { setWotplLoading(false); }
  };
  const openReviewWithPlan = async (row:any) => {
    // 打开前先清理与上一次审核相关的状态，避免"串数据/闪现"
    setBaselineRows([]);
    setBaselineOwnerId(null);
    baselineInitialFileIdsRef.current = new Set();
    baselineSessionTouchedRef.current = false;
    baselineInitialSnapshotRef.current = null;
    setAttachPickerBaseline(null);
    setAttachPickerOpen(false);
    setAttachNameEdits({});
    setCustomerAttachments([]);
    setAttPreviewOpen(false);
    setAttPvUrl('');
    setAttPvData([]);
    setAttPvCols([]);
    setAttPvError('');
    setAutoReviewOpen(false);
    setAutoReviewRes(null);
    setAutoReviewRunning(false);
    setReviewOf(row);
    try {
      console.debug('[PreReview][BaselineGate][openReviewWithPlan]', {
        instruction_id: row?.id,
        template_name: row?.template_snapshot?.name || row?.template_detail?.name || row?.template_name || '',
        customer_answers_keys: Object.keys((row?.customer_answers || {}) as any),
        shipping_label_to_target_mapping: (row?.customer_answers || {})?.shipping_label_to_target_mapping,
      });
    } catch {}
    setReviewDecision(undefined);
    setReviewReason('');
    setReviewBillingCollapsed(true);
    setPlanSteps([]);
    setPlanPreview(null);
    // 预先拉取概览数据（含附件），避免首次点击"自动预审"时 attachments 为空
    try {
      const ov = await loadReviewOverview(row?.id);
      try {
        const detail = ov?.detail || {};
        console.debug('[PreReview][BaselineGate][reviewOverview]', {
          instruction_id: row?.id,
          detail_template_name: detail?.template_snapshot?.name || detail?.template_detail?.name || detail?.template_name || '',
          detail_customer_answers_keys: Object.keys((detail?.customer_answers || {}) as any),
          detail_shipping_label_to_target_mapping: (detail?.customer_answers || {})?.shipping_label_to_target_mapping,
        });
      } catch {}
    } catch {}
    // 预拉基线并记录初始快照（供取消时回滚）
    try {
      const rows = await loadBaselines(row?.id);
      const initIds = new Set<number>(collectBaselineFileIds(rows || []));
      baselineInitialFileIdsRef.current = initIds;
      baselineInitialSnapshotRef.current = makeBaselineSnapshot(rows || []);
    } catch {}
    try { await loadWorkOrderTemplates(); } catch {}
    // 预加载默认/路由计划，便于在编辑器中起步
    try {
      setPlanPreviewLoading(true);
      const resp = await request.post(`/api/v1/instruction/instructions/${row.id}/plan/preview/`, {});
      const steps = Array.isArray(resp?.data?.steps) ? resp.data.steps : [];
      const mapped = steps.map((s:any) => ({ template_code: String(s.template_code || ''), after: Array.isArray(s.after) ? s.after : [] }));
      if (mapped.length > 0) setPlanSteps(mapped);
      setPlanPreview(resp?.data || null);
    } catch (e:any) {
      // 不阻塞：保持空计划，交由用户选择默认工单模板或手动添加
      setPlanPreview(null);
    } finally { setPlanPreviewLoading(false); }
  };
  const handleReviewDecisionChange = useCallback((nextDecision: 'accept' | 'reject') => {
    setReviewDecision(nextDecision);
    if (nextDecision !== 'accept') {
      return;
    }
    // 受理时默认收起“计费计划（虚拟工单，可编辑）”
    setReviewBillingCollapsed(true);
    if (isOnePieceRow(reviewOf)) {
      // 一件代发：预估耗时默认 0（可改）
      setEstimatedUnknown(false);
      setEstimatedDays(0);
    } else {
      // 其他模板：不预填
      setEstimatedDays(undefined);
    }
  }, [isOnePieceRow, reviewOf]);
  const previewCurrentPlan = async () => {
    if (!reviewOf) return;
    try {
      setPlanPreviewLoading(true);
      const resp = await request.post(`/api/v1/instruction/instructions/${reviewOf.id}/plan/preview/`, { plan_override: planSteps });
      setPlanPreview(resp?.data || null);
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '预览失败');
    } finally {
      setPlanPreviewLoading(false);
    }
  };

  // —— 结构化操作：基于当前 after 自动分层（拓扑）并调整 ——
  const getStagesInfo = () => {
    const n = planSteps.length;
    const codeToIdxs = new Map<string, number[]>();
    planSteps.forEach((s, i) => {
      const code = String(s.template_code || '').trim();
      if (!code) return;
      const arr = codeToIdxs.get(code) || [];
      arr.push(i);
      codeToIdxs.set(code, arr);
    });
    const adj: Record<number, number[]> = {}; const indeg: number[] = new Array(n).fill(0);
    for (let i=0;i<n;i++) adj[i]=[];
    for (let j=0;j<n;j++) {
      const a = planSteps[j]?.after;
      if (Array.isArray(a)) {
        a.forEach((ac) => {
          const targets = codeToIdxs.get(String(ac)) || [];
          targets.forEach((pi) => { adj[pi].push(j); indeg[j]+=1; });
        });
      }
    }
    const used = new Array(n).fill(false);
    const stages:number[][] = [];
    while (stages.reduce((a,g)=>a+g.length,0) < n) {
      const layer:number[] = [];
      for (let i=0;i<n;i++) if (!used[i] && indeg[i]===0) layer.push(i);
      if (layer.length===0) { for (let i=0;i<n;i++) if(!used[i]) layer.push(i); }
      layer.forEach(i=> used[i]=true);
      layer.forEach(i=> (adj[i]||[]).forEach(v=> indeg[v]=Math.max(0,indeg[v]-1)));
      stages.push(layer);
    }
    const codeToStage = new Map<string, number>();
    stages.forEach((layer, si) => layer.forEach((i)=> {
      const code = String(planSteps[i]?.template_code || '');
      if (code) codeToStage.set(code, si);
    }));
    return { stages, codeToIdxs, codeToStage };
  };
  const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));
  const refreshPreviewSoon = () => { try { setTimeout(() => { previewCurrentPlan(); }, 0); } catch {} };

  // 提升为新阶段：让该步依赖本阶段的所有其他步骤
  const promoteToNextStage = (idx: number) => {
    const { stages } = getStagesInfo();
    const layerIdx = stages.findIndex(layer => layer.includes(idx));
    if (layerIdx < 0) return;
    const currentStageCodes = stages[layerIdx].map(i => String(planSteps[i]?.template_code || '')).filter(Boolean);
    const selfCode = String(planSteps[idx]?.template_code || '');
    const addDeps = currentStageCodes.filter(c => c && c !== selfCode);
    const next = [...planSteps];
    const curAfter = Array.isArray(next[idx].after) ? next[idx].after as string[] : [];
    next[idx] = { ...next[idx], after: uniq([...(curAfter||[]), ...addDeps]) };
    setPlanSteps(next);
    refreshPreviewSoon();
  };
  // 合并到上一阶段：移除对"上一阶段"的依赖（尽量前移）
  const mergeToPrevStage = (idx: number) => {
    const { stages } = getStagesInfo();
    const layerIdx = stages.findIndex(layer => layer.includes(idx));
    if (layerIdx <= 0) return;
    const prevStageCodes = stages[layerIdx-1].map(i => String(planSteps[i]?.template_code || '')).filter(Boolean);
    const prevSet = new Set(prevStageCodes);
    const next = [...planSteps];
    const curAfter = Array.isArray(next[idx].after) ? (next[idx].after as string[]) : [];
    const filtered = (curAfter||[]).filter(c => !prevSet.has(String(c)));
    next[idx] = { ...next[idx], after: filtered };
    setPlanSteps(next);
    refreshPreviewSoon();
  };
  // 清理本阶段内依赖：切换为独立分组
  const clearIntraStageDeps = (idx: number) => {
    const { stages, codeToStage } = getStagesInfo();
    const layerIdx = stages.findIndex(layer => layer.includes(idx));
    if (layerIdx < 0) return;
    const next = [...planSteps];
    const curAfter = Array.isArray(next[idx].after) ? (next[idx].after as string[]) : [];
    const filtered = (curAfter||[]).filter(c => codeToStage.get(String(c)) !== layerIdx);
    next[idx] = { ...next[idx], after: filtered };
    setPlanSteps(next);
    refreshPreviewSoon();
  };
  // 组内上移：去掉一个同阶段依赖（若存在）
  const moveUpInGroup = (idx: number) => {
    const { stages, codeToStage } = getStagesInfo();
    const layerIdx = stages.findIndex(layer => layer.includes(idx));
    if (layerIdx < 0) return;
    const next = [...planSteps];
    const curAfter = Array.isArray(next[idx].after) ? (next[idx].after as string[]) : [];
    const sameStageDeps = (curAfter||[]).filter(c => codeToStage.get(String(c)) === layerIdx);
    if (sameStageDeps.length === 0) return;
    const removeOne = sameStageDeps[sameStageDeps.length - 1]; // 近似"上移"
    next[idx] = { ...next[idx], after: (curAfter||[]).filter(c => String(c) !== String(removeOne)) };
    setPlanSteps(next);
    refreshPreviewSoon();
  };
  // 组内下移：添加一个同阶段依赖（选择本阶段中第一个未依赖的兄弟）
  const moveDownInGroup = (idx: number) => {
    const { stages } = getStagesInfo();
    const layerIdx = stages.findIndex(layer => layer.includes(idx));
    if (layerIdx < 0) return;
    const selfCode = String(planSteps[idx]?.template_code || '');
    const siblings = stages[layerIdx].map(i => String(planSteps[i]?.template_code || '')).filter(c => c && c !== selfCode);
    const next = [...planSteps];
    const curAfter = Array.isArray(next[idx].after) ? (next[idx].after as string[]) : [];
    const set = new Set(curAfter||[]);
    const candidate = siblings.find(c => !set.has(String(c)));
    if (!candidate) return;
    next[idx] = { ...next[idx], after: uniq([...(curAfter||[]), candidate]) };
    setPlanSteps(next);
    refreshPreviewSoon();
  };

  // 根据计划预览加载收费规则摘要
  useEffect(() => {
    (async () => {
      if (!RULES_SUMMARY_ENABLED) { setRulesPreview([]); return; }
      try {
        if (!reviewOf) { setRulesPreview([]); return; }
        const steps = Array.isArray(planPreview?.steps) ? planPreview.steps : [];
        if (steps.length === 0) { setRulesPreview([]); return; }
        setRulesLoading(true);
        const cid = reviewOf.customer_id || reviewOf.customer || reviewOf.customerId;
        const pid = reviewOf.project_id || reviewOf.project || reviewOf.projectId;
        const wid = reviewOf.warehouse_id || reviewOf.warehouse || reviewOf.warehouseId;
        const templateCode = reviewOf?.template_snapshot?.system_code || reviewOf?.template_system_code;
        const results: Array<{ key:string; template_code:string; operation_type:string; rule_system_code?:string; unit?:string; currency?:string; rule_type?:string; tier?:string; fallback_price?:string }> = [];
        for (const s of steps) {
          const op = s.operation_type || '';
          let rule: any = undefined;
          try {
            const params: any = { operation_type: op };
            if (cid) params.customer_id = cid;
            if (pid) params.project_id = pid;
            if (wid) params.warehouse_id = wid;
            if (templateCode) params.instruction_template_code = templateCode;
            const resp = await request.get('/api/v1/billing/billing-rules/effective', { params });
            const arr = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp?.data?.results) ? resp.data.results : []);
            rule = arr && arr.length > 0 ? arr[0] : undefined;
          } catch {}
          const fb = (() => {
            try {
              const unitPrice = reviewOf?.template_snapshot?.meta?.billing?.unit_price;
              const cur = reviewOf?.template_snapshot?.meta?.billing?.currency;
              if (unitPrice != null) return `${unitPrice}${cur ? ' ' + cur : ''}`;
              return undefined;
            } catch { return undefined; }
          })();
          results.push({
            key: `${s.template_code || op}`,
            template_code: s.template_code,
            operation_type: op,
            rule_system_code: rule?.system_code,
            unit: rule?.billing_unit || rule?.unit_label || rule?.unit,
            currency: rule?.currency,
            rule_type: rule?.rule_type,
            tier: (rule?.tiers && Array.isArray(rule.tiers) && rule.tiers.length > 0) ? `${rule.tiers.length}档` : undefined,
            fallback_price: rule ? undefined : fb,
          });
        }
        setRulesPreview(results);
      } catch {
        setRulesPreview([]);
      } finally {
        setRulesLoading(false);
      }
    })();
  }, [planPreview, reviewOf]);

  // 全局状态渲染辅助：供弹窗使用（列定义内部也有同名逻辑，但作用域不同）
  const statusTextGlobal = (s: string) => {
    const map: Record<string, string> = {
      draft: '草稿',
      pending: '待受理',
      accepted: '已受理',
      in_progress: '执行中',
      operation_done: '执行阶段已完成（待终审）',
      completed: '已完成',
      rejected: '已驳回',
      cancelled: '已取消',
    };
    return map[s] || s || '-';
  };
  const statusColorGlobal = (s: string) => {
    return s === 'completed' ? 'green'
      : s === 'in_progress' ? 'blue'
      : s === 'operation_done' ? 'orange'
      : s === 'pending' ? 'gold'
      : s === 'accepted' ? 'cyan'
      : s === 'rejected' ? 'red'
      : s === 'cancelled' ? 'red'
      : 'default';
  };
  const getEffectiveStatusGlobal = (row: any) => {
    const ov = rowOverrides[String(row?.id)] || {};
    return (ov.status as any) || row?.status;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dashboardQuickJumpAppliedSigRef = useRef<string>('');
  const dashboardQuickJumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dashboardQuickJumpReplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectQuickJumpAppliedSigRef = useRef<string>('');
  const projectQuickJumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectQuickJumpReplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { state, data, loading, actions, paginationConfig } = useTableQuery<InstructionFilters>({
    initialState: {
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: { system_code: '', status: '', project: '', warehouse: '', template: '', template_system_code: '', keyword: '', logistics_info: '', recipient_info: '', customer: '', hide_cancelled: '1', dropship_instruction_type: [] as string[] },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,
      urlKeys: ['pagination', 'filters', 'sorting']
    },
    fetchData: async (s) => {
      const params: any = {
        page: s.pagination.current,
        page_size: s.pagination.pageSize,
        // 防缓存：每次请求带时间戳，避免某些代理/浏览器复用结果
        t: Date.now(),
      };

      if (s.filters?.system_code) params.system_code = s.filters.system_code;
      if (s.filters?.status) params.status = s.filters.status;
      if (s.filters?.template_system_code) params.template_system_code = s.filters.template_system_code;
      if (s.filters?.customer) params.customer_id = s.filters.customer;
      if (s.filters?.project) params.project_id = s.filters.project;
      if (s.filters?.warehouse) params.warehouse_id = s.filters.warehouse;
      if (s.filters?.template && s.filters.template !== '__NONE__') params.template = s.filters.template;
      const dtArr = s.filters?.dropship_instruction_type;
      if (Array.isArray(dtArr) && dtArr.length > 0) {
        params.instruction_type = dtArr.filter(Boolean).join(',');
      }
      if (s.filters?.logistics_info) params.logistics_info = s.filters.logistics_info;
      if (s.filters?.recipient_info) params.recipient_info = s.filters.recipient_info;
      if (s.filters?.keyword) params.keyword = (s.filters.keyword || '').toString().trim();
      // 后端过滤"隐藏已取消"
      const hideCancelledOn = (s.filters?.hide_cancelled === undefined) ? true : String(s.filters?.hide_cancelled) === '1';
      if (hideCancelledOn && s.filters?.status !== 'cancelled') {
        (params as any).hide_cancelled = 1;
        // 同时显式排除 cancelled，增强兼容性（部分环境 BooleanFilter 解析差异）
        (params as any).exclude_status = 'cancelled';
      }

      if (s.sorting?.field && s.sorting?.order) {
        const prefix = s.sorting.order === 'desc' ? '-' : '';
        params.ordering = `${prefix}${s.sorting.field}`;
      }

      try {
        const resp = await request.get('/api/v1/instruction/instructions/', { params });
        const payload = resp?.data || {};
        let results = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
        // 系统编码已由后端按包含匹配过滤，此处不再前端过滤
        // 模板"未使用任何模板"过滤（前端过滤）
        if (s.filters?.template === '__NONE__') {
          try {
            results = results.filter((r:any) => r?.template == null);
          } catch {}
        }
        // 关键字已由后端按名称/备注过滤，此处不再前端过滤
        const count = Number(payload?.count ?? results.length ?? 0);
        return { results, count };
      } catch (error: any) {
      message.error('加载失败');
        throw error;
      }
    }
  });

  // 库存非标准操作指令：本页里哪些正在等客户反馈（失败不影响列表）
  useEffect(() => {
    const rows: any[] = Array.isArray(data) ? (data as any[]) : [];
    const ids = rows
      .filter((r: any) => String(r?.template_snapshot?.meta?.engine || '') === 'nonstandard_ops')
      .map((r: any) => Number(r?.id))
      .filter((id: number) => Number.isFinite(id));
    if (ids.length === 0) { setNonstdWaitingIds([]); return; }
    let cancelled = false;
    getNonStdWaitingIds(ids)
      .then(found => { if (!cancelled) setNonstdWaitingIds(found); })
      .catch(() => { if (!cancelled) setNonstdWaitingIds([]); });
    return () => { cancelled = true; };
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    const loadAppendAvailability = async () => {
      try {
        const rows = Array.isArray(data) ? data : [];
        const completedIds = rows
          .filter((r: any) => String(getEffectiveStatusGlobal(r) || '').trim().toLowerCase() === 'completed')
          .map((r: any) => Number(r?.id))
          .filter((id: number) => Number.isFinite(id) && id > 0);
        if (completedIds.length === 0) {
          if (!cancelled) setAppendAvailabilityById({});
          return;
        }
        const resp = await request.post('/api/v1/instruction/instructions/append-availability/', {
          instruction_ids: completedIds,
        });
        if (cancelled) return;
        const arr = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        const next: Record<string, number> = {};
        (arr || []).forEach((x: any) => {
          const id = Number(x?.instruction_id);
          if (!Number.isFinite(id) || id <= 0) return;
          next[String(id)] = Number(x?.available_ioc_count || 0) || 0;
        });
        setAppendAvailabilityById(next);
      } catch {
        if (!cancelled) setAppendAvailabilityById({});
      }
    };
    void loadAppendAvailability();
    return () => { cancelled = true; };
  }, [data, rowOverrides]);

  // 列表页：为“历史指令操作结果”类型补齐预估操作数量（输入IOC容器数量）
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const rows = Array.isArray(data) ? data : [];
        const candidates = rows.filter((r:any) => {
          const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
          return (targets || []).some((t:any) => String(t?.target_type || '').trim() === 'instruction_output');
        });
        if (candidates.length === 0) return;
        const qtyUpdates: Record<string, number> = {};
        const cacheUpdates: Record<string, any[]> = {};
        await Promise.all(candidates.map(async (r:any) => {
          const id = Number(r?.id || 0);
          if (!Number.isFinite(id) || id <= 0) return;
          const key = String(id);
          const cachedRows = historyInputIocRowsCacheByInstructionId[key];
          const useRows = Array.isArray(cachedRows) ? cachedRows : await buildHistoryInputIocRowsForRecord(r);
          if (!Array.isArray(cachedRows)) cacheUpdates[key] = useRows;
          const containerCount = countUniqueIocContainers(useRows || []);
          qtyUpdates[key] = Number(containerCount || 0) || 0;
        }));
        if (cancelled) return;
        if (Object.keys(cacheUpdates).length > 0) {
          setHistoryInputIocRowsCacheByInstructionId((prev) => ({ ...prev, ...cacheUpdates }));
        }
        if (Object.keys(qtyUpdates).length > 0) {
          setHistoryEstimatedQtyByInstructionId((prev) => ({ ...prev, ...qtyUpdates }));
        }
      } catch {}
    };
    void run();
    return () => { cancelled = true; };
  }, [data, countUniqueIocContainers]);

  const pageRowKeys = useMemo(() => {
    try {
      return (Array.isArray(data) ? data : []).map((r: any) => String(r?.id ?? ''));
    } catch {
      return [];
    }
  }, [data]);

  const effectiveSelectedRowKeys = useMemo(() => {
    if (!isAdminOrStaff) return [];
    return Array.from(manualSelectedRowKeys);
  }, [isAdminOrStaff, manualSelectedRowKeys]);

  const selectedTotal = useMemo(() => {
    if (!isAdminOrStaff) return 0;
    return manualSelectedRowKeys.size;
  }, [isAdminOrStaff, manualSelectedRowKeys.size]);

  useEffect(() => {
    if (!isAdminOrStaff) return;
    setManualSelectedRowKeys(new Set());
  }, [isAdminOrStaff, state.filters]);

  const openPackModal = useCallback((row: any, mode: 'view' | 'edit') => {
    // 手动填写入口已禁用（见 ALLOW_MANUAL_PACK_RESULT_EDIT），兜底防止被其他调用点以编辑模式打开
    if (mode === 'edit' && !ALLOW_MANUAL_PACK_RESULT_EDIT) return;
    setPackModalRow(row);
    setPackModalMode(mode);
    try {
      packModalForm.setFieldsValue({
        actual_pack_length_cm: row?.actual_pack_length_cm ?? undefined,
        actual_pack_width_cm: row?.actual_pack_width_cm ?? undefined,
        actual_pack_height_cm: row?.actual_pack_height_cm ?? undefined,
        actual_gross_weight_g: row?.actual_gross_weight_g ?? undefined,
      });
    } catch {}
    setPackModalOpen(true);
  }, [packModalForm]);

  const savePackModal = useCallback(async () => {
    if (!packModalRow) return;
    try {
      const v = await packModalForm.validateFields();
      const l = Number(v?.actual_pack_length_cm);
      const w = Number(v?.actual_pack_width_cm);
      const h = Number(v?.actual_pack_height_cm);
      const g = Number(v?.actual_gross_weight_g);
      const hasComplete =
        Number.isFinite(l) && l > 0
        && Number.isFinite(w) && w > 0
        && Number.isFinite(h) && h > 0
        && Number.isFinite(g) && g > 0;
      if (!hasComplete) {
        message.warning('请填写完整的包装信息');
        return;
      }
      const payload: any = {
        actual_pack_length_cm: l,
        actual_pack_width_cm: w,
        actual_pack_height_cm: h,
        actual_gross_weight_g: g,
        pack_result_status: 'provided',
      };
      setPackModalSaving(true);
      await request.patch(`/api/v1/instruction/instructions/${packModalRow.id}/`, payload);
      message.success('包装结果已保存');
      setPackModalOpen(false);
      setPackModalRow(null);
      try { await (actions as any).refresh?.(); } catch { try { actions.refresh(); } catch {} }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setPackModalSaving(false);
    }
  }, [actions, packModalForm, packModalRow]);

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const list = await getCurrentUserProjects();
      setProjects(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error('加载项目失败');
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const list = await getAllWarehouses();
      setWarehouses(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error('加载仓库失败');
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const resp: any = await getCustomerList({ onlyMain: true } as any);
      const rows = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp?.results)
            ? resp.results
            : Array.isArray(resp?.data?.results)
              ? resp.data.results
              : [];
      const isMain = (c:any) => {
        try {
          if (Object.prototype.hasOwnProperty.call(c, 'is_main_account')) return !!c.is_main_account;
          if (Object.prototype.hasOwnProperty.call(c, 'parent_id')) return !c.parent_id;
          if (typeof c.role === 'string') return (c.role === 'customer' || c.role === 'main_account');
        } catch {}
        // 无法判定时，保留（尽量展示主账号，后端如含子账号应补字段以便前端精准过滤）
        return true;
      };
      setCustomers(rows.filter(isMain));
    } catch (e) {
      message.error('加载客户失败');
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    loadProjects();
    loadWarehouses();
    // 预加载模板全集（系统级 + 客户/项目级），会话内缓存
    (async () => { try { await loadTemplateUniverse(); } catch {} })();
    try {
      const p = String((window as any)?.location?.pathname || '');
      if (p.startsWith('/dashboard/admin')) {
        loadCustomers();
      }
    } catch {}
  }, []);

  // 客户后台：当项目列表加载完成或变化后，重新补全客户专属模板（避免初始时 derivedCustomerId 或项目ID未知导致的缺失）
  useEffect(() => {
    try {
      const p = String((window as any)?.location?.pathname || '');
      if (!p.startsWith('/dashboard/customer')) return;
      // projects 变化后重新尝试（内部会根据可推导的 customerId 与项目IDs 决定拉取哪些）
      (async () => { try { await loadTemplateUniverse(); } catch {} })();
    } catch {}
  }, [projects]);

  // 监听其它页面对指令的变更（如详情页取消），自动刷新列表
  useEffect(() => {
    const handler = (e: any) => {
      try { actions.refresh(); } catch {}
    };
    try { window.addEventListener('instruction:changed' as any, handler); } catch {}
    return () => { try { window.removeEventListener('instruction:changed' as any, handler); } catch {} };
  }, [actions]);

  // URL 带 template 时若 tplOptions 为空，主动加载模板（确保能正确判断是否为一件代发）
  useEffect(() => {
    const tplId = state.filters?.template;
    if (tplId && tplId !== '__NONE__' && tplOptions.length === 0 && !tplLoading) {
      (async () => { try { await loadTemplateUniverse(); } catch {} })();
    }
  }, [state.filters?.template, tplOptions.length, tplLoading]);

  // Dashboard 快捷筛选：当仅有 template_system_code 时，自动映射并回填模板下拉（显示“一件代发/常规出库”）
  useEffect(() => {
    const code = String(state.filters?.template_system_code || '').trim().toUpperCase();
    if (!code) return;
    if (tplOptions.length === 0) {
      if (!tplLoading) {
        (async () => { try { await loadTemplateUniverse(); } catch {} })();
      }
      return;
    }
    const hit = (tplOptions || []).find((o: any) => String(o?.code || '').trim().toUpperCase() === code);
    if (!hit) return;
    const nextTemplateId = String((hit as any).value || '');
    if (!nextTemplateId) return;
    if (String(state.filters?.template || '') === nextTemplateId) return;
    actions.updateFilters({ template: nextTemplateId } as Partial<InstructionFilters>);
    try { form.setFieldsValue({ template: nextTemplateId }); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters?.template_system_code, state.filters?.template, tplOptions, tplLoading]);

  // 已知一件代发模板的 system_code（与后端 final_pricing 等逻辑保持一致）
  const DROPSHIP_TEMPLATE_CODES = ['INSTRUCTIONTEMPLATE_SYS_19'];
  // 当前选中的模板是否为「一件代发」：tplOptions 可查到时按名称或 system_code 判断，否则只要选中了模板则乐观展示
  const isDropshipTemplateSelected = useMemo(() => {
    const tplId = state.filters?.template;
    if (!tplId || tplId === '__NONE__') return false;
    const opt = (tplOptions || []).find((o: any) => String(o?.value) === String(tplId));
    if (opt) {
      const o = opt as { name?: string; label?: string; code?: string };
      const name = String(o?.name || o?.label || '');
      const code = String(o?.code || '').toUpperCase();
      return name.includes('一件代发') || DROPSHIP_TEMPLATE_CODES.some(c => code.includes(c));
    }
    // 模板已选中但 tplOptions 中暂无（可能尚未加载）：乐观展示，确保用户选中一件代发时能用到
    return true;
  }, [state.filters?.template, tplOptions]);

  // 恢复页面状态时，同步回填筛选框的表单值
  useEffect(() => {
    try {
      const hv = state.filters?.hide_cancelled;
      form.setFieldsValue({
        system_code: state.filters?.system_code || undefined,
        status: state.filters?.status || undefined,
        project: state.filters?.project || undefined,
        warehouse: state.filters?.warehouse || undefined,
        template: state.filters?.template || undefined,
        keyword: state.filters?.keyword || undefined,
        hide_cancelled: hv === undefined ? true : String(hv) === '1',
        dropship_instruction_type: state.filters?.dropship_instruction_type || [],
      });
    } catch {}
  }, [state.filters, form]);

  // 筛选变化时：若开启"只显示使用过"，重新拉取 used-templates
  useEffect(() => {
    if (!tplOnlyUsed) return;
    (async () => { try { await loadUsedTemplateCodes(); } catch {} })();
  }, [tplOnlyUsed, state?.filters?.system_code, state?.filters?.status, state?.filters?.project, state?.filters?.warehouse, state?.filters?.keyword, state?.filters?.customer]);

  // 补全创建者用户名（后端如仅返回ID，则按需拉取 /api/v1/users/{id}/ 获取用户名）
  useEffect(() => {
    (async () => {
      try {
        const ids: string[] = [];
        (data || []).forEach((row: any) => {
          const val = (row && (row.created_by_id ?? row.created_by)) as any;
          if (val && typeof val === 'number') {
            const idStr = String(val);
            if (!userMap[idStr]) ids.push(idStr);
          }
        });
        const uniq = Array.from(new Set(ids));
        if (uniq.length === 0) return;
        const results = await Promise.all(uniq.map(async (id) => {
          try {
            const resp = await request.get(`/api/v1/users/${id}/`);
            const u = resp?.data || {};
            const name = u.username || u.name || `用户${id}`;
            return [id, name] as const;
          } catch {
            return [id, `用户${id}`] as const;
          }
        }));
        const next: Record<string, string> = { ...userMap };
        results.forEach(([id, name]) => { next[id] = name; });
        setUserMap(next);
      } catch {}
    })();
  }, [data]);

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    if (sorter && !Array.isArray(sorter)) {
      const field = sorter.field as string;
      const order = sorter.order === 'descend' ? 'desc' : sorter.order === 'ascend' ? 'asc' : undefined;
      if (field && order && (field !== state.sorting.field || order !== state.sorting.order)) {
        // 先锁定分页大小并回到第一页，再更新排序，避免排序更新触发的请求使用了默认的pageSize
        actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
        actions.updateSorting({ field, order });
      }
    }
    if (
      pagination &&
      typeof pagination.current === 'number' &&
      typeof pagination.pageSize === 'number' &&
      (pagination.current !== state.pagination.current || pagination.pageSize !== state.pagination.pageSize)
    ) {
      actions.updatePagination({ current: pagination.current, pageSize: pagination.pageSize });
    }
  };

  const handleFilterChange = (key: keyof InstructionFilters, value: any) => {
    if (key === 'status') {
      const next: any = { status: value };
      if (value === 'cancelled') {
        next.hide_cancelled = '';
        try { form.setFieldsValue({ hide_cancelled: false }); } catch {}
      } else {
        // 非"已取消"状态时，如当前为关闭则恢复开启
        const currentlyOn = String(state.filters?.hide_cancelled || '1') === '1';
        if (!currentlyOn) {
          next.hide_cancelled = '1';
          try { form.setFieldsValue({ hide_cancelled: true }); } catch {}
        }
      }
      actions.updateFilters(next);
      form.setFieldsValue({ status: value });
    } else if (key === 'system_code') {
      // 系统编码实时筛选：更新过滤并回到第1页
      actions.updateFilters({ system_code: value } as Partial<InstructionFilters>);
      try { actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize }); } catch {}
      try { form.setFieldsValue({ system_code: value }); } catch {}
    } else if (key === 'hide_cancelled') {
      actions.updateFilters({ hide_cancelled: value } as Partial<InstructionFilters>);
      form.setFieldsValue({ hide_cancelled: value === '1' });
    } else {
      const patch: Partial<InstructionFilters> = { [key]: value };
      if (key === 'template') {
        patch.dropship_instruction_type = [];
        actions.updateFilters(patch);
        form.setFieldsValue({ [key]: value, dropship_instruction_type: [] });
      } else {
        actions.updateFilters(patch);
        form.setFieldsValue({ [key]: value });
      }
    }
    // 管理端选择"客户"后，动态补充该客户的专属模板到"指令模板"筛选下拉
    if (key === 'customer') {
      const cidNum = Number(value);
      if (Number.isFinite(cidNum)) {
        (async () => { try { await loadTemplateUniverse(cidNum); } catch {} })();
      } else {
        (async () => { try { await loadTemplateUniverse(undefined); } catch {} })();
      }
    }
    // 关键字筛选为前端过滤，切换关键字时固定回到第1页，避免分页信息与结果不一致
    if (key === 'keyword') {
      actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
    }
  };

  // 工作台快捷跳转：支持通过 URL 强制注入状态筛选（例如 filter_status=pending）
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const quickStatus = String(params.get('filter_status') || '').trim();
      const fromDashboard = String(params.get('from_dashboard') || '').trim() === '1';
      const jumpTs = String(params.get('jump_ts') || '').trim();
      const quickTemplateSystemCode = String(params.get('quick_template_system_code') || '').trim();
      if (!quickStatus) return;
      if (fromDashboard) {
        const sig = `${quickStatus}|${quickTemplateSystemCode}|${jumpTs || ''}`;
        if (dashboardQuickJumpAppliedSigRef.current === sig) return;
        if (dashboardQuickJumpTimerRef.current) {
          clearTimeout(dashboardQuickJumpTimerRef.current);
          dashboardQuickJumpTimerRef.current = null;
        }
        if (dashboardQuickJumpReplayTimerRef.current) {
          clearTimeout(dashboardQuickJumpReplayTimerRef.current);
          dashboardQuickJumpReplayTimerRef.current = null;
        }
        const applyDashboardQuickJumpFilters = () => {
          try {
            form.resetFields();
            form.setFieldsValue({
              system_code: '',
              status: quickStatus,
              project: '',
              warehouse: '',
              template: '',
              template_system_code: quickTemplateSystemCode,
              keyword: '',
              logistics_info: '',
              recipient_info: '',
              customer: '',
              hide_cancelled: true,
              dropship_instruction_type: [],
            });
            actions.updateFilters({
              system_code: '',
              status: quickStatus,
              project: '',
              warehouse: '',
              template: '',
              template_system_code: quickTemplateSystemCode,
              keyword: '',
              logistics_info: '',
              recipient_info: '',
              customer: '',
              hide_cancelled: '1',
              dropship_instruction_type: [] as string[],
              __nonce: String(Date.now()),
            } as any);
            actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
          } catch {}
        };
        // 等待 useTableQuery 的 tab 状态恢复完成后再覆盖一次，避免被旧筛选立即回写。
        dashboardQuickJumpTimerRef.current = setTimeout(() => {
          applyDashboardQuickJumpFilters();
          dashboardQuickJumpTimerRef.current = null;
        }, 180);
        // 再做一次延迟重放，覆盖首开标签页时 TabsManager/useTableQuery 的二次状态恢复。
        dashboardQuickJumpReplayTimerRef.current = setTimeout(() => {
          applyDashboardQuickJumpFilters();
          dashboardQuickJumpAppliedSigRef.current = sig;
          dashboardQuickJumpReplayTimerRef.current = null;
        }, 520);
      } else {
        const patch: any = { status: quickStatus };
        if (quickTemplateSystemCode) patch.template_system_code = quickTemplateSystemCode;
        actions.updateFilters(patch);
        actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
      }
    } catch {}
    return () => {
      if (dashboardQuickJumpTimerRef.current) {
        clearTimeout(dashboardQuickJumpTimerRef.current);
        dashboardQuickJumpTimerRef.current = null;
      }
      if (dashboardQuickJumpReplayTimerRef.current) {
        clearTimeout(dashboardQuickJumpReplayTimerRef.current);
        dashboardQuickJumpReplayTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 项目列表快捷跳转：仅覆盖“项目”筛选，不改变通用 tab 状态恢复机制
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const fromProjectList = String(params.get('from_project_list') || '').trim() === '1';
      const quickProjectId = String(params.get('filter_project') || '').trim();
      const jumpTs = String(params.get('jump_ts') || '').trim();
      if (!fromProjectList || !quickProjectId) return;
      const sig = `${quickProjectId}|${jumpTs || ''}`;
      if (projectQuickJumpAppliedSigRef.current === sig) return;

      if (projectQuickJumpTimerRef.current) {
        clearTimeout(projectQuickJumpTimerRef.current);
        projectQuickJumpTimerRef.current = null;
      }
      if (projectQuickJumpReplayTimerRef.current) {
        clearTimeout(projectQuickJumpReplayTimerRef.current);
        projectQuickJumpReplayTimerRef.current = null;
      }

      const applyProjectQuickJump = () => {
        try {
          actions.updateFilters({ project: quickProjectId } as Partial<InstructionFilters>);
          form.setFieldsValue({ project: quickProjectId });
          actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
        } catch {}
      };

      // 先应用一次，再做一次延迟重放，覆盖可能的 tab 状态回填
      projectQuickJumpTimerRef.current = setTimeout(() => {
        applyProjectQuickJump();
        projectQuickJumpTimerRef.current = null;
      }, 160);
      projectQuickJumpReplayTimerRef.current = setTimeout(() => {
        applyProjectQuickJump();
        projectQuickJumpAppliedSigRef.current = sig;
        projectQuickJumpReplayTimerRef.current = null;
      }, 520);

      // 清理一次性跳转参数，保留 tabId 与其他状态参数
      params.delete('from_project_list');
      params.delete('filter_project');
      params.delete('jump_ts');
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    } catch {}
    return () => {
      if (projectQuickJumpTimerRef.current) {
        clearTimeout(projectQuickJumpTimerRef.current);
        projectQuickJumpTimerRef.current = null;
      }
      if (projectQuickJumpReplayTimerRef.current) {
        clearTimeout(projectQuickJumpReplayTimerRef.current);
        projectQuickJumpReplayTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 项目列表跳转兜底桥接：当 URL 参数被 tab 状态恢复覆盖时，仍可应用项目筛选
  useEffect(() => {
    const applyProjectQuickJump = (projectId: string, jumpTs?: string) => {
      const pid = String(projectId || '').trim();
      if (!pid) return;
      const sig = `${pid}|${String(jumpTs || '').trim()}`;
      if (projectQuickJumpAppliedSigRef.current === sig) return;
      try {
        actions.updateFilters({ project: pid } as Partial<InstructionFilters>);
        form.setFieldsValue({ project: pid });
        actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
        projectQuickJumpAppliedSigRef.current = sig;
      } catch {}
    };

    const consumeBridge = () => {
      try {
        const raw = sessionStorage.getItem('instruction_project_quick_jump');
        if (!raw) return;
        const data = JSON.parse(raw || '{}') as { projectId?: string; jumpTs?: string };
        if (!data?.projectId) return;
        applyProjectQuickJump(String(data.projectId), String(data.jumpTs || ''));
        sessionStorage.removeItem('instruction_project_quick_jump');
      } catch {}
    };

    consumeBridge();

    const handler = (ev: any) => {
      try {
        const d = (ev as CustomEvent)?.detail || {};
        if (!d?.projectId) return;
        applyProjectQuickJump(String(d.projectId), String(d.jumpTs || ''));
      } catch {}
    };
    try { window.addEventListener('instruction:project-quick-jump' as any, handler as any); } catch {}
    return () => {
      try { window.removeEventListener('instruction:project-quick-jump' as any, handler as any); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSearch = () => {
    const values = form.getFieldsValue();
    const next: any = { ...values };
    if (Object.prototype.hasOwnProperty.call(values, 'hide_cancelled')) {
      next.hide_cancelled = values.hide_cancelled ? '1' : '';
    }
    actions.updateFilters(next);
  };

  const handleReset = () => {
    form.resetFields();
    actions.reset();
  };

  const handleDownloadAttachment = async (att: any) => {
    try {
      const url = resolveAttachmentRuntimeUrl(att);
      const name = getDisplayFileName(att) || att?.file_name || '附件';
      if (!url) { message.warning('无可用文件'); return; }
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch { message.error('下载失败'); }
  };

  return (
    <div className="list-page-container">
      <Card>
        {/* 1. 页面标题和操作区域 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>指令列表</Title>
              <div className="flex items-center space-x-2">
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  共 {state.pagination.total} 条记录
                </Tag>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button icon={<ReloadOutlined />} onClick={() => {
                try {
                  // 注入一个一次性 nonce 打破 useTableQuery 的重复请求去重
                  actions.updateFilters({ __nonce: String(Date.now()) } as any);
                } catch {}
                try {
                  // 回到首页以看到最新记录
                  actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
                } catch {}
                try { actions.refresh(); } catch {}
              }}>
                刷新
              </Button>
              <Button type="primary" onClick={() => {
                try {
                  const path = String((window as any)?.location?.pathname || '');
                  let url = '/dashboard/customer/instructions/create';
                  if (path.startsWith('/dashboard/staff')) url = '/dashboard/staff/instructions/create';
                  else if (path.startsWith('/dashboard/admin')) url = '/dashboard/admin/instructions/create';
                  navigate(url);
                } catch {
                  try { navigate('/dashboard/customer/instructions/create'); } catch {}
                }
              }}>
                创建指令
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  try {
                    const path = String((window as any)?.location?.pathname || '');
                    let url = '/dashboard/customer/instructions/bulk-create';
                    if (path.startsWith('/dashboard/staff')) url = '/dashboard/staff/instructions/bulk-create';
                    else if (path.startsWith('/dashboard/admin')) url = '/dashboard/admin/instructions/bulk-create';
                    navigate(url);
                  } catch {
                    try { navigate('/dashboard/customer/instructions/bulk-create'); } catch {}
                  }
                }}
              >
                批量创建指令
              </Button>
              {String((window as any)?.location?.pathname || '').startsWith('/dashboard/admin') && (
                <>
                  <span style={{ display: 'inline-block', width: 3, height: 22, background: '#d9d9d9', margin: '0 20px' }} />
                  <Tooltip title="只有同仓库/同类型/未加入其他批次/处于待受理或者已驳回状态的指令可以创建批次">
                    <Button
                      type="primary"
                      onClick={async () => {
                      try {
                        const totalSelected = selectedTotal;
                        if (totalSelected <= 0) {
                          message.warning('请先选择指令');
                          return;
                        }
                        const ids = Array.from(manualSelectedRowKeys || []).map((k: string) => Number(k)).filter(n => Number.isFinite(n));
                        const payload = { instruction_ids: ids };
                        const resp = await request.post('/api/v1/instruction/batches/', payload);
                        if (!resp) {
                          message.error('生成批次失败');
                          return;
                        }
                        message.success('批次已生成');
                        const tplCode = String((resp as any)?.data?.template_system_code || '').trim();
                        const pathWithTab = tplCode ? `/dashboard/admin/instructions/batch?template_system_code=${encodeURIComponent(tplCode)}` : '/dashboard/admin/instructions/batch';
                        navigate(pathWithTab);
                      } catch (e: any) {
                        const respDetail = e?.response?.data?.detail;
                        if (respDetail === 'multiple_warehouses') {
                          Modal.warning({
                            title: '仓库不一致，已阻断生成批次',
                            content: (
                              <div>
                                <div style={{ marginBottom: 8 }}>
                                  仅允许同仓库指令生成批次，请重新选择指令。
                                </div>
                              </div>
                            ),
                            okText: '知道了',
                          });
                          return;
                        }
                        if (respDetail === 'conflict_instruction_ids') {
                          const ids = Array.isArray(e?.response?.data?.instruction_ids)
                            ? e.response.data.instruction_ids
                            : [];
                          const codes = Array.isArray(e?.response?.data?.instruction_system_codes)
                            ? e.response.data.instruction_system_codes.filter((c: any) => c)
                            : [];
                          const total = Number(e?.response?.data?.conflict_count || codes.length || ids.length || 0);
                          const copyText = (codes.length > 0 ? codes : ids).join('\n');
                          const titleNode = (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>存在冲突指令，已阻断生成批次</span>
                              <Button
                                size="small"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(copyText);
                                    message.success('已复制');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              >
                                复制
                              </Button>
                            </div>
                          );
                          Modal.warning({
                            title: titleNode,
                            content: (
                              <div>
                                <div style={{ marginBottom: 8 }}>
                                  已有批次的指令不可重复加入新批次，请先处理冲突指令。
                                </div>
                                <div>冲突数量：{total}</div>
                                {codes.length > 0 ? (
                                  <div style={{ marginTop: 8 }}>
                                    冲突指令: {codes.join(', ')}
                                  </div>
                                ) : (ids.length > 0 ? (
                                  <div style={{ marginTop: 8 }}>
                                    冲突指令: {ids.join(', ')}
                                  </div>
                                ) : null)}
                              </div>
                            ),
                            okText: '知道了',
                          });
                          return;
                        }
                        if (respDetail === 'multiple_templates') {
                          Modal.warning({
                            title: '模板不一致，已阻断生成批次',
                            content: (
                              <div>
                                <div style={{ marginBottom: 8 }}>
                                  仅允许同模板指令生成批次，请重新选择指令。
                                </div>
                              </div>
                            ),
                            okText: '知道了',
                          });
                          return;
                        }
                        if (respDetail === 'invalid_status') {
                          Modal.warning({
                            title: '指令状态不符合要求，已阻断生成批次',
                            content: (
                              <div>
                                <div style={{ marginBottom: 8 }}>
                                  仅允许“待受理/已驳回”的指令生成批次，请重新选择指令。
                                </div>
                              </div>
                            ),
                            okText: '知道了',
                          });
                          return;
                        }
                        message.error(respDetail || e?.message || '生成批次失败');
                      }
                    }}
                  >
                      {`生成批次(${selectedTotal}条)`}
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 2. 筛选组件 */}
        <div style={{ marginBottom: 16 }}>
          <Form form={form} layout="horizontal" onFinish={handleSearch}>
            {(() => {
              const p = String((window as any)?.location?.pathname || '');
              const isAdmin = p.startsWith('/dashboard/admin');
              const isStaff = p.startsWith('/dashboard/staff');
              const isAdminStaff = isAdmin || isStaff;
              if (!isAdminStaff) {
                // 客户后台：保持原布局与样式
                return (
                  <>
                    <Row gutter={[12, 12]}>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item name="keyword" label="名称/备注">
                          <Input
                            placeholder="按名称或备注模糊搜索"
                            allowClear
                            value={state.filters?.keyword || undefined}
                            onChange={(e) => handleFilterChange('keyword', e.target.value)}
                            onPressEnter={handleSearch}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item name="system_code" label="系统编码">
                          <Input
                            placeholder="请输入系统编码"
                            allowClear
                            value={state.filters?.system_code || undefined}
                            onChange={(e) => handleFilterChange('system_code', e.target.value)}
                            onPressEnter={handleSearch}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item name="status" label="状态">
                          <Select
                            placeholder="选择状态"
                            allowClear
                            value={state.filters?.status || undefined}
                            onChange={(v) => handleFilterChange('status', v)}
                            style={{ width: '100%' }}
                          >
                            <Option value="pending">待受理</Option>
                            <Option value="accepted">已受理</Option>
                            <Option value="in_progress">执行中</Option>
                            <Option value="operation_done">执行阶段完成(待终审)</Option>
                            <Option value="completed">已完成</Option>
                            <Option value="rejected">已驳回</Option>
                            <Option value="cancelled">已取消</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item name="project" label="项目">
                          <Select
                            placeholder="选择项目"
                            allowClear
                            loading={loadingProjects}
                            value={state.filters?.project || undefined}
                            onChange={(v) => handleFilterChange('project', v)}
                            showSearch
                            optionFilterProp="children"
                            dropdownStyle={{ maxHeight: 300, minWidth: '320px' }}
                            style={{ width: '100%' }}
                          >
                            {projects.map((p: any) => (
                              <Option key={p.id} value={p.id?.toString?.() || String(p.id)}>
                                {p.project_code ? `${p.project_code} - ${p.name}` : (p.name || p.id)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item name="warehouse" label="仓库">
                          <Select
                            placeholder="选择仓库"
                            allowClear
                            loading={loadingWarehouses}
                            value={state.filters?.warehouse || undefined}
                            onChange={(v) => handleFilterChange('warehouse', v)}
                            style={{ width: 220 }}
                            dropdownStyle={{ width: 220 }}
                          >
                            {warehouses.map((w: any) => (
                              <Option key={w.id} value={w.id?.toString?.() || String(w.id)}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span style={{ marginRight: 12 }}>{w.name || '-'}</span>
                                  <span style={{ color: '#8c8c8c' }}>{w.code || w.warehouse_code || w.id}</span>
                                </div>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 12]}>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        {/* 指令模板（客户端保留原样式） */}
                        <Form.Item name="template" label="指令模板" extra={(
                          <div style={{ marginTop: 4 }}>
                            <Space size={12}>
                              <span style={{ color: '#8c8c8c' }}>只筛选使用过的模板</span>
                              <Switch checked={tplOnlyUsed} onChange={async (ck)=> {
                                setTplOnlyUsed(ck);
                                if (ck) { await loadUsedTemplateCodes(); }
                              }} />
      </Space>
                          </div>
                        )}>
                          {/* 下拉渲染块复用原实现 */}
                          {(() => {
                            // 直接复用原 Select 块（上文）
                            return (
                              <Select
                                placeholder="选择指令模板"
                                allowClear
                                loading={tplLoading}
                                value={state.filters?.template || undefined}
                                onChange={(v) => handleFilterChange('template', v)}
                                showSearch
                                filterOption={(input, option:any) => {
                                  const text = String(option?.label || '').toLowerCase();
                                  return text.includes(String(input || '').toLowerCase());
                                }}
                                onFocus={() => { if (tplOptions.length === 0) loadTemplateUniverse(); }}
                                dropdownStyle={{ maxHeight: 320, minWidth: 420 }}
                                style={{ width: 420 }}
                              >
                                {(() => {
                                  const list = (tplOptions || []).filter((o:any) => {
                                    if (!tplOnlyUsed) return true;
                                    const code = String(o?.code || '').toUpperCase();
                                    return usedTplCodes.has(code);
                                  });
                                  const sys = list.filter((o:any)=> String(o.level) === 'system');
                                  const scoped = list.filter((o:any)=> String(o.level) === 'customer' || String(o.level) === 'project');
                                  const nodes: any[] = [];
                                  sys.forEach((o:any) => {
                                    const fullText = `${o.name || o.label || ''}${o.is_active === false ? ' 未启用' : ''} · ${o.code || ''}`.trim();
                                    nodes.push(
                                      <Option key={o.value} value={o.value} label={fullText}>
                                        <Tooltip title={fullText} placement="left">
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}>
                                            <span style={{ marginRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {o.name || o.label}
                                              {o.is_active === false ? <Tag color="default" style={{ marginLeft: 8 }}>未启用</Tag> : null}
                                            </span>
                                            <span style={{ color: '#8c8c8c', flexShrink: 0 }}>{o.code}</span>
                                          </div>
                                        </Tooltip>
                                      </Option>
                                    );
                                  });
                                  if (sys.length > 0 && scoped.length > 0) {
                                    nodes.push(
                                      <Option key="__DIVIDER__" value="__DIVIDER__" disabled>
                                        —— 客户/项目专属 ——
                                      </Option>
                                    );
                                  }
                                  scoped.forEach((o:any) => {
                                    const fullText = `${o.name || o.label || ''}${o.is_active === false ? ' 未启用' : ''} · ${o.code || ''}`.trim();
                                    nodes.push(
                                      <Option key={o.value} value={o.value} label={fullText}>
                                        <Tooltip title={fullText} placement="left">
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}>
                                            <span style={{ marginRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {o.name || o.label}
                                              {o.is_active === false ? <Tag color="default" style={{ marginLeft: 8 }}>未启用</Tag> : null}
                                            </span>
                                            <span style={{ color: '#8c8c8c', flexShrink: 0 }}>{o.code}</span>
                                          </div>
                                        </Tooltip>
                                      </Option>
                                    );
                                  });
                                  return nodes;
                                })()}
                              </Select>
                            );
                          })()}
                        </Form.Item>
                      </Col>
                      {isDropshipTemplateSelected && (
                        <Col xs={24} sm={12} md={6} lg={4}>
                          <Form.Item name="dropship_instruction_type" label="代发指令类型">
                            <Select
                              mode="multiple"
                              placeholder="不限"
                              allowClear
                              value={state.filters?.dropship_instruction_type || []}
                              onChange={(v: string[]) => handleFilterChange('dropship_instruction_type', v || [])}
                              options={DROPSHIP_INSTRUCTION_TYPES.map(t => ({ label: t, value: t }))}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <Form.Item name="logistics_info" label="相关物流信息">
                          <Input
                            placeholder="物流单号等"
                            allowClear
                            value={state.filters?.logistics_info || undefined}
                            onChange={(e) => handleFilterChange('logistics_info', e.target.value)}
                            onPressEnter={handleSearch}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <Form.Item name="recipient_info" label="收件人信息">
                          <Input
                            placeholder="姓名/电话/地址"
                            allowClear
                            value={state.filters?.recipient_info || undefined}
                            onChange={(e) => handleFilterChange('recipient_info', e.target.value)}
                            onPressEnter={handleSearch}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <Form.Item name="hide_cancelled" label="隐藏已取消指令" valuePropName="checked">
                          <Switch
                            checkedChildren="隐藏"
                            unCheckedChildren="显示"
                            onChange={(checked) => handleFilterChange('hide_cancelled', checked ? '1' : '')}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <Form.Item>
          <Space>
                            <Button type="primary" icon={<SearchOutlined />} htmlType="submit" loading={loading}>
                              搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                              重置
                            </Button>
          </Space>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                );
              }
              // 管理员/员工后台：两行布局，避免宽度溢出
              return (
                <>
                  <Row gutter={[12, 12]}>
                    {/* 客户（缩小宽度） */}
                    <Col xs={24} sm={12} md={5} lg={4}>
                      <Form.Item name="customer" label="客户">
                        <Select
                          placeholder="选择客户"
                          allowClear
                          loading={loadingCustomers}
                          value={state.filters?.customer || undefined}
                          onChange={(v) => handleFilterChange('customer', v)}
                          showSearch
                          optionFilterProp="children"
                          dropdownStyle={{ maxHeight: 300, minWidth: '320px' }}
                          style={{ width: '100%' }}
                        >
                          {customers.map((c: any) => (
                            <Option key={c.id} value={c.id?.toString?.() || String(c.id)}>
                              {c.username || c.name || c.code || c.id}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* 系统编码（缩小宽度） */}
                    <Col xs={24} sm={12} md={5} lg={6}>
                      <Form.Item name="system_code" label="系统编码">
                        <Input
                          placeholder="请输入系统编码"
                          allowClear
                          value={state.filters?.system_code || undefined}
                          onChange={(e) => handleFilterChange('system_code', e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    {/* 状态（缩小宽度） */}
                    <Col xs={24} sm={12} md={4} lg={4}>
                      <Form.Item name="status" label="状态">
                        <Select
                          placeholder="选择状态"
                          allowClear
                          value={state.filters?.status || undefined}
                          onChange={(v) => handleFilterChange('status', v)}
                          style={{ width: '100%' }}
                        >
                          <Option value="pending">待受理</Option>
                          <Option value="accepted">已受理</Option>
                          <Option value="in_progress">执行中</Option>
                          <Option value="operation_done">执行阶段完成(待终审)</Option>
                          <Option value="completed">已完成</Option>
                          <Option value="rejected">已驳回</Option>
                          <Option value="cancelled">已取消</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* 名称/备注（缩小宽度） */}
                    <Col xs={24} sm={12} md={4} lg={4}>
                      <Form.Item name="keyword" label="名称/备注">
                        <Input
                          placeholder="按名称或备注模糊搜索"
                          allowClear
                          value={state.filters?.keyword || undefined}
                          onChange={(e) => handleFilterChange('keyword', e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    {/* 项目：移至第一行末尾 */}
                    <Col xs={24} sm={12} md={6} lg={6}>
                      <Form.Item name="project" label="项目">
                        <Select
                          placeholder="选择项目"
                          allowClear
                          loading={loadingProjects}
                          value={state.filters?.project || undefined}
                          onChange={(v) => handleFilterChange('project', v)}
                          showSearch
                          optionFilterProp="children"
                          dropdownStyle={{ maxHeight: 300, minWidth: '320px' }}
                          style={{ width: '100%' }}
                        >
                          {projects.map((p: any) => (
                            <Option key={p.id} value={p.id?.toString?.() || String(p.id)}>
                              {p.project_code ? `${p.project_code} - ${p.name}` : (p.name || p.id)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    {/* 仓库 */}
                    <Col xs={24} sm={12} md={6} lg={5}>
                      <Form.Item name="warehouse" label="仓库">
                        <Select
                          placeholder="选择仓库"
                          allowClear
                          loading={loadingWarehouses}
                          value={state.filters?.warehouse || undefined}
                          onChange={(v) => handleFilterChange('warehouse', v)}
                          style={{ width: 260 }}
                          dropdownStyle={{ width: 260 }}
                        >
                          {warehouses.map((w: any) => (
                            <Option key={w.id} value={w.id?.toString?.() || String(w.id)}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ marginRight: 12 }}>{w.name || '-'}</span>
                                <span style={{ color: '#8c8c8c' }}>{w.code || w.warehouse_code || w.id}</span>
                              </div>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* 指令模板 */}
                    <Col xs={24} sm={24} md={10} lg={8}>
                      {/* 指令模板（管理员/员工：占更大宽度） */}
                      <Form.Item name="template" label="指令模板" extra={(
                        <div style={{ marginTop: 4 }}>
                          <Space size={12}>
                            <span style={{ color: '#8c8c8c' }}>只筛选使用过的模板</span>
                            <Switch checked={tplOnlyUsed} onChange={async (ck)=> {
                              setTplOnlyUsed(ck);
                              if (ck) { await loadUsedTemplateCodes(); }
                            }} />
                          </Space>
                        </div>
                      )}>
                        {(() => {
                          return (
                            <Select
                              placeholder="选择指令模板"
                              allowClear
                              loading={tplLoading}
                              value={state.filters?.template || undefined}
                              onChange={(v) => handleFilterChange('template', v)}
                              showSearch
                              filterOption={(input, option:any) => {
                                const text = String(option?.label || '').toLowerCase();
                                return text.includes(String(input || '').toLowerCase());
                              }}
                              onFocus={() => { if (tplOptions.length === 0) loadTemplateUniverse(); }}
                              dropdownStyle={{ maxHeight: 320, minWidth: 420 }}
                              style={{ width: '100%' }}
                            >
                              {(() => {
                                const list = (tplOptions || []).filter((o:any) => {
                                  if (!tplOnlyUsed) return true;
                                  const code = String(o?.code || '').toUpperCase();
                                  return usedTplCodes.has(code);
                                });
                                const sys = list.filter((o:any)=> String(o.level) === 'system');
                                const scoped = list.filter((o:any)=> String(o.level) === 'customer' || String(o.level) === 'project');
                                const nodes: any[] = [];
                                sys.forEach((o:any) => {
                                  const fullText = `${o.name || o.label || ''}${o.is_active === false ? ' 未启用' : ''} · ${o.code || ''}`.trim();
                                  nodes.push(
                                    <Option key={o.value} value={o.value} label={fullText}>
                                      <Tooltip title={fullText} placement="left">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}>
                                          <span style={{ marginRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {o.name || o.label}
                                            {o.is_active === false ? <Tag color="default" style={{ marginLeft: 8 }}>未启用</Tag> : null}
                                          </span>
                                          <span style={{ color: '#8c8c8c', flexShrink: 0 }}>{o.code}</span>
                                        </div>
                                      </Tooltip>
                                    </Option>
                                  );
                                });
                                if (sys.length > 0 && scoped.length > 0) {
                                  nodes.push(
                                    <Option key="__DIVIDER__" value="__DIVIDER__" disabled>
                                      —— 客户/项目专属 ——
                                    </Option>
                                  );
                                }
                                scoped.forEach((o:any) => {
                                  const fullText = `${o.name || o.label || ''}${o.is_active === false ? ' 未启用' : ''} · ${o.code || ''}`.trim();
                                  nodes.push(
                                    <Option key={o.value} value={o.value} label={fullText}>
                                      <Tooltip title={fullText} placement="left">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}>
                                          <span style={{ marginRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {o.name || o.label}
                                            {o.is_active === false ? <Tag color="default" style={{ marginLeft: 8 }}>未启用</Tag> : null}
                                          </span>
                                          <span style={{ color: '#8c8c8c', flexShrink: 0 }}>{o.code}</span>
                                        </div>
                                      </Tooltip>
                                    </Option>
                                  );
                                });
                                return nodes;
                              })()}
                            </Select>
                          );
                        })()}
                      </Form.Item>
                    </Col>
                    {isDropshipTemplateSelected && (
                      <Col xs={24} sm={12} md={6} lg={6}>
                        <Form.Item name="dropship_instruction_type" label="代发指令类型">
                          <Select
                            mode="multiple"
                            placeholder="不限"
                            allowClear
                            value={state.filters?.dropship_instruction_type || []}
                            onChange={(v: string[]) => handleFilterChange('dropship_instruction_type', v || [])}
                            options={DROPSHIP_INSTRUCTION_TYPES.map(t => ({ label: t, value: t }))}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={24} sm={12} md={6} lg={6}>
                      <Form.Item name="logistics_info" label="相关物流信息">
                        <Input
                          placeholder="物流服务商或跟踪号查询"
                          allowClear
                          value={state.filters?.logistics_info || undefined}
                          onChange={(e) => handleFilterChange('logistics_info', e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={6}>
                      <Form.Item name="recipient_info" label="收件人信息">
                        <Input
                          placeholder="姓名/电话/地址"
                          allowClear
                          value={state.filters?.recipient_info || undefined}
                          onChange={(e) => handleFilterChange('recipient_info', e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    {/* 隐藏已取消 + 按钮（合并一列，避免换行） */}
                    <Col xs={24} sm={24} md={8} lg={11}>
                      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
                        <Form.Item name="hide_cancelled" label="隐藏已取消指令" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch
                            checkedChildren="隐藏"
                            unCheckedChildren="显示"
                            onChange={(checked) => handleFilterChange('hide_cancelled', checked ? '1' : '')}
                          />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                          <Space>
                            <Button type="primary" icon={<SearchOutlined />} htmlType="submit" loading={loading}>
                              搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                              重置
                            </Button>
                          </Space>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </>
              );
            })()}
          </Form>
        </div>

        {/* 3. 数据表格 */}
        <div style={{ marginBottom: 16 }}>
          <Table
            columns={(() => {
              const projectMap = new Map<string, any>(projects.map((p:any)=> [String(p.id), p]));
              const warehouseMap = new Map<string, any>(warehouses.map((w:any)=> [String(w.id), w]));
              const renderProject = (record:any) => {
                const pdArr = Array.isArray(record?.projects_detail) ? record.projects_detail : [];
                if (pdArr.length > 0) {
                  const first = pdArr[0];
                  const rest = pdArr.length - 1;
                  return (
                    <Space size={4}>
                      <Space direction="vertical" size={0}>
                        <span>{first?.name || '-'}</span>
                        <Tag color="blue">{first?.project_code || first?.code || first?.id}</Tag>
                      </Space>
                      {rest > 0 && (
                        <Button type="link" size="small" onClick={() => setProjectsOf(record)}>
                          +{rest}个
                        </Button>
                      )}
                    </Space>
                  );
                }
                // 单项目回退：用当前项目ID匹配已加载项目或detail
                const pid = record.project_id != null ? String(record.project_id) : '';
                const p = projectMap.get(pid);
                if (p) return (
                  <Space direction="vertical" size={0}>
                    <span>{p.name || '-'}</span>
                    <Tag color="blue">{p.project_code || p.code || p.id}</Tag>
                  </Space>
                );
                const pd = record.project_detail || record.project;
                if (pd && (pd.name || pd.project_code)) return (
                  <Space direction="vertical" size={0}>
                    <span>{pd.name || '-'}</span>
                    <Tag color="blue">{pd.project_code || pd.code || pd.id}</Tag>
      </Space>
                );
                return <span style={{ color: '#999' }}>-</span>;
              };
              const renderWarehouse = (record:any) => {
                const wid = record.warehouse_id != null ? String(record.warehouse_id) : '';
                const w = warehouseMap.get(wid) || record.warehouse_detail || record.warehouse;
                if (w) return (
                  <Space direction="vertical" size={0}>
                    <span>{w.name || '-'}</span>
                    <Tag>{w.code || w.warehouse_code || w.id}</Tag>
          </Space>
                );
                return <span style={{ color: '#999' }}>-</span>;
              };
              const renderTemplateReadable = (record:any) => {
                try {
                  const snap = record?.template_snapshot || {};
                  const detail = record?.template_detail || {};
                  const name = snap.name || detail.name || record?.template_name || '-';
                  const code = snap.system_code || detail.system_code || record?.template_system_code || record?.template || '';
                  return (
                    <Space direction="vertical" size={0}>
                      <span>{name || '-'}</span>
                      {code ? <Tag color="blue">{code}</Tag> : <span style={{ color: '#999' }}>-</span>}
                    </Space>
                  );
                } catch {
                  return <span style={{ color: '#999' }}>-</span>;
                }
              };
              const priorityLabel = (val:string) => {
                if (val === 'high') return '一般优先';
                if (val === 'super') return '超级优先';
                return '普通';
              };
              const renderCreator = (record:any) => {
                if (record.created_by_username) return record.created_by_username;
                const d = record.created_by_detail || record.created_by;
                if (d && typeof d === 'object') return d.username || d.name || `用户${d.id}`;
                const rawId = (record.created_by_id != null ? record.created_by_id : record.created_by) as any;
                if (typeof rawId === 'number') {
                  const key = String(rawId);
                  return userMap[key] || `用户${rawId}`;
                }
                return typeof d === 'string' && d.trim() !== '' ? d : '-';
              };
              const statusText = (s: string) => {
                const map: Record<string, string> = {
                  draft: '草稿',
                  pending: '待受理',
                  accepted: '已受理',
                  in_progress: '执行中',
                  operation_done: '执行阶段已完成（待终审）',
                  completed: '已完成',
                  rejected: '已驳回',
                  cancelled: '已取消',
                };
                return map[s] || s || '-';
              };
              const statusColor = (s: string) => {
                return s === 'completed' ? 'green'
                  : s === 'in_progress' ? 'blue'
                  : s === 'operation_done' ? 'orange'
                  : s === 'pending' ? 'gold'
                  : s === 'accepted' ? 'cyan'
                  : s === 'rejected' ? 'red'
                  : s === 'cancelled' ? 'red'
                  : 'default';
              };
              const renderOpsCountButton = (
                count: number,
                kind: 'IU' | 'IOC',
                onClick: () => void,
              ) => {
                const badgeStyle = kind === 'IU'
                  ? { background: '#f6ffed', border: '1px solid #b7eb8f', color: '#389e0d' }
                  : { background: '#fff7e6', border: '1px solid #ffd591', color: '#d46b08' };
                return (
                <Tooltip title={kind === 'IU' ? 'IU：当前可用库存的库存单元数量' : 'IOC：指令产出容器数量'}>
                  <Button type="link" size="small" onClick={onClick}>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                      <span>{Number(count || 0)}</span>
                      <span
                        style={{
                          ...badgeStyle,
                          fontSize: 12,
                          lineHeight: 1.2,
                          borderRadius: 10,
                          padding: '1px 6px',
                          fontWeight: 600,
                        }}
                      >
                        {kind}
                      </span>
                    </span>
                  </Button>
                </Tooltip>
                );
              };
              const renderEstimatedOps = (record:any) => {
                try {
                  const arr = Array.isArray(record?.targets_detail) ? record.targets_detail : [];
                  const hasHistoryIoc = (arr || []).some((t:any) => String(t?.target_type || '').trim() === 'instruction_output');
                  if (hasHistoryIoc) {
                    const key = String(record?.id || '');
                    const historyQty = historyEstimatedQtyByInstructionId[key];
                    if (historyQty != null && Number.isFinite(Number(historyQty))) {
                      return renderOpsCountButton(
                        Number(historyQty || 0),
                        'IOC',
                        () => {
                          setHistoryTargetsPage(1);
                          setHistoryTargetsPageSize(10);
                          setHistoryTargetsOf(record);
                        },
                      );
                    }
                    return '-';
                  }
                  let total = 0;
                  for (const t of arr) {
                    const v = Number((t && t.expected_qty) ?? 0);
                    if (!isNaN(v)) total += v;
                  }
                  if (total > 0) {
                    return renderOpsCountButton(
                      Number(total || 0),
                      'IU',
                      () => setTargetsOf(record),
                    );
                  }
                } catch {}
                if (record.estimated_operation_count != null) return record.estimated_operation_count;
                if (record.expected_operation_count != null) return record.expected_operation_count;
                if (record.actions_count != null) return record.actions_count;
                const snap = record.template_snapshot;
                const cnt = Array.isArray(snap?.actions) ? snap.actions.length : null;
                return cnt != null ? cnt : '-';
              };
              const getEffectiveStatus = (row:any) => {
                const ov = rowOverrides[String(row?.id)] || {};
                return (ov.status as any) || row?.status;
              };
              return [
                { title: '系统编码', dataIndex: 'system_code', key: 'system_code', width: 180, render: (v:any, r:any) => (
                  v ? (
                    <span>
                      <Text code copyable={{ text: String(v) }}>{v}</Text>
                      {nonstdWaitingIds.includes(Number(r?.id)) ? (
                        <Tooltip title="请至“指令详情页”的“非标准操作指令执行记录”子页面进行回复">
                          <Tag color="orange" style={{ marginLeft: 4, cursor: 'help' }}>等待客户反馈</Tag>
                        </Tooltip>
                      ) : null}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )
                ) },
                { title: '名称', dataIndex: 'name', key: 'name', width: 200, render: (v:any)=> v || '-' },
                { title: '指令模板', key: 'template', width: 240, render: (_:any, r:any) => renderTemplateReadable(r) },
                { title: '项目', key: 'project', width: 180, render: (_:any, r:any) => renderProject(r) },
                { title: '预估操作数量', key: 'estimated_ops', width: 100, render: (_:any, r:any) => renderEstimatedOps(r) },
                { title: '优先级', dataIndex: 'priority', key: 'priority', width: 100, render: (v:string)=> {
                  const label = priorityLabel(v);
                  const color = v === 'super' ? 'red' : v === 'high' ? 'orange' : 'default';
                  return <Tag color={color}>{label}</Tag>;
                } },
                { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (_: any, r:any) => {
                  const s = getEffectiveStatus(r);
                  const days = r?.estimated_working_days;
                  const hasDays = days !== undefined && days !== null && days !== '';
                  const isUnknown = String(r?.meta?.estimated_working_days_label || '') === '未知';
                  const labelNode = (() => {
                    if (s === 'in_progress' && (hasDays || isUnknown)) {
                      return (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'left', textAlign: 'left', lineHeight: 1.2 }}>
                          <span style={{ fontSize: 12 }}>{statusText(s)}</span>
                          <span style={{ fontSize: 10, color: '#595959' }}>
                            {isUnknown ? '预计未知' : `预计${days}天完成`}
                          </span>
                        </span>
                      );
                    }
                    return statusText(s);
                  })();
                  const tagEl = (
                    <Tag
                      color={statusColor(s)}
                      onClick={async () => {
                        if (s !== 'in_progress') return;
                        // 直接使用数据库字段（与日志一致的来源），不再请求审计日志
                        const started: string | Date | null =
                          r?.execution_started_at
                          || r?.meta?.execution_started_at
                          || null;
                        setExecInfoOf({ row: r, started, days, unknown: isUnknown });
                      }}
                      style={{ cursor: s === 'in_progress' ? 'pointer' : 'default' }}
                    >
                      {labelNode}
                    </Tag>
                  );
                  const packBadge = (() => {
                    if (!shouldShowPackBadge(r)) return null;
                    const ps = String(r?.pack_result_status || '');
                    const isPending = ps === '' || ps === 'pending';
                    const isProvided = ps === 'provided';
                    const label = isProvided ? '仓库已提供包装结果' : '待仓库提供包装结果';
                    const color = isProvided ? 'green' : 'orange';
                    const canEdit = ALLOW_MANUAL_PACK_RESULT_EDIT && isAdminOrStaff && isPending;
                    const canView = isProvided;
                    const clickable = canEdit || canView;
                    const tip = canEdit
                      ? '点击填写包装结果'
                      : (canView ? '点击查看包装结果' : '包装结果待提供');
                    return (
                      <Tooltip title={tip}>
                        <Tag
                          color={color}
                          style={{ cursor: clickable ? 'pointer' : 'default', marginRight: 0, ...(isProvided ? {} : { background: '#e6fffb', borderColor: '#87e8de', color: '#08979c' }) }}
                          onClick={() => {
                            if (!clickable) return;
                            openPackModal(r, canEdit ? 'edit' : 'view');
                          }}
                        >
                          {label}
                        </Tag>
                      </Tooltip>
                    );
                  })();
                  const wrapWithPack = (node: React.ReactNode) => (
                    packBadge ? <Space size={4}>{node}{packBadge}</Space> : node
                  );
                  // 被驳回且需要再次审核时，显示提示小圆点
                  if (s === 'rejected' && r?.re_review_required) {
                    return (
                      wrapWithPack(
                        <Space size={4}>
                          {tagEl}
                          <Tooltip title="指令已修改，待再次审核">
                            <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 14 }} />
                          </Tooltip>
                        </Space>
                      )
                    );
                  }
                  if (s === 'rejected' && r?.reject_reason) {
                    return (
                      wrapWithPack(
                        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{r.reject_reason}</span>}>
                          {tagEl}
                        </Tooltip>
                      )
                    );
                  }
                  if (s === 'in_progress') {
                    return (
                      wrapWithPack(
                        <Tooltip title="点击查看预计完成时间">
                          {tagEl}
                        </Tooltip>
                      )
                    );
                  }
                  return wrapWithPack(tagEl);
                } },
                { title: '创建者', key: 'creator', width: 120, render: (_:any, r:any) => renderCreator(r) },
                { title: '创建/完成时间', key: 'created_completed', width: 220, sorter: true, sortDirections: ['descend','ascend'], render: (_:any, r:any) => {
                  const created = r?.created_at;
                  const completed =
                    r?.completed_at
                    || r?.meta?.execution_completed_at
                    || r?.execution_completed_at
                    || null;
                  return (
                    <Space direction="vertical" size={0}>
                      <span>
                        创建：{created ? <TimezoneAwareTime time={created} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : <span style={{ color: '#999' }}>-</span>}
                      </span>
                      <span>
                        完成：{completed ? <TimezoneAwareTime time={completed} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : <span style={{ color: '#999' }}>-</span>}
                      </span>
                    </Space>
                  );
                } },
                 { title: '操作', key: 'ops', width: 280, fixed: 'right' as const, render: (_:any, r:any) => {
                  const path = String((window as any)?.location?.pathname || '');
                  const base = path.startsWith('/dashboard/staff')
                    ? '/dashboard/staff'
                    : path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer';
                  const isAdminOrStaff = base === '/dashboard/admin' || base === '/dashboard/staff';
                  const goto = (suffix: string) => {
                    try { navigate(`${base}${suffix}`); } catch {}
                  };
                  const effStatus = getEffectiveStatus(r);
                  const canEdit = effStatus === 'pending' || effStatus === 'rejected';
                  const canAppend = effStatus === 'completed';
                  const canAppendEnabled = canAppend && (Number(appendAvailabilityById[String(r?.id)] || 0) > 0);
                  const appendTip = canAppendEnabled
                    ? '创建新指令,并将当前指令的执行结果作为新指令的操作对象'
                    : '当前指令无可用的指令产出结果,无法直接追加指令';
                  const handleCancel = async () => {
                    Modal.confirm({
                      title: '确定取消该指令？',
                      content: `系统编码：${r?.system_code || r?.id}`,
                      okText: '确认取消',
                      okButtonProps: { danger: true },
                      cancelText: '再想想',
                      onOk: async () => {
                        try {
                          await request.post(`/api/v1/instruction/instructions/${r.id}/cancel/`);
                          message.success('已取消');
                          // 乐观更新：立即显示取消状态
                          setRowOverrides(prev => ({ ...prev, [String(r.id)]: { status: 'cancelled' } }));
                          try { await (actions as any).refresh?.(); } catch { try { actions.refresh(); } catch {} }
                        } catch (e:any) {
                          message.error(e?.response?.data?.detail || '取消失败');
                        }
                      }
                    });
                  };
                  const handleChangeStatus = (row:any) => {
                    try {
                      const current = getEffectiveStatus(row);
                      setStatusOf(row);
                      setStatusNext(current);
                    } catch {
                      setStatusOf(row);
                    }
                  };
                  const handleAudit = (row:any) => { openReviewWithPlan(row); };
                  const handleSubmitResult = (row:any) => {
                    const st = String(getEffectiveStatus(row) || '').trim().toLowerCase();
                    if (st === 'rejected') {
                      message.warning('该指令已驳回，禁止进入终审');
                      return;
                    }
                    // 终审工作台：包含“生成最终结果包 + 上传终审附件 + 备注 + 提交”
                    // 注意：goto() 会自动拼接 base=/dashboard/admin，所以 suffix 这里不能再带 /admin 前缀
                    goto(`/instructions/${row.id}/final-review`);
                  };
                  const needsReReview = r?.re_review_required === true;
                  const moreItems: any[] = [
                    { key: 'view', label: '查看', onClick: () => goto(`/instructions/i-${r.id}`) },
                    ...(canEdit ? [{ key: 'edit', label: '修改', onClick: () => {
                      try {
                        const w:any = window as any;
                        const path = String(w?.location?.pathname || '');
                        const base = path.startsWith('/dashboard/staff')
                          ? '/dashboard/staff'
                          : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
                        w?.tabsManager?.closeTabByPath?.(`${base}/instructions/${r.id}`);
                      } catch {}
                      goto(`/instructions/${r.id}/edit`);
                    }}] : []),
                    ...(canEdit ? [{ key: 'cancel', label: '取消', onClick: () => handleCancel() }] : []),
                    ...(canAppend ? [{
                      key: 'append',
                      label: (
                        <Tooltip title={appendTip}>
                          <span>{'追加指令'}</span>
                        </Tooltip>
                      ),
                      disabled: !canAppendEnabled,
                      onClick: () => {
                        try {
                          sessionStorage.setItem('ci_append_from_hint', JSON.stringify({ id: Number(r?.id), ts: Date.now() }));
                        } catch {}
                        goto(`/instructions/create?append_from=${r.id}`);
                      }
                    }] : []),
                    ...(isAdminOrStaff && effStatus === 'rejected' && needsReReview ? [{ key: 're-review', label: '预审', onClick: () => handleAudit(r) }] : []),
                  ];
                  return (
                    <Space size={4} wrap>
                      {isAdminOrStaff && (
                        <>
                          <Button size="small" onClick={() => handleChangeStatus(r)}>修改状态</Button>
                        {(effStatus === 'pending' || (effStatus === 'rejected' && needsReReview)) && (
                            <Button
                              size="small"
                              style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff', fontWeight: 700 }}
                              onClick={() => handleAudit(r)}
                            >
                              预审
                            </Button>
                          )}
                          {effStatus === 'operation_done' && (
                            <Button size="small" type="primary" onClick={() => handleSubmitResult(r)}>终审</Button>
                          )}
                          {effStatus === 'completed' && (
                            <Button
                              size="small"
                              style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 700 }}
                              onClick={() => handleSubmitResult(r)}
                            >
                              编辑终审结果
                            </Button>
                          )}
                          <Dropdown menu={{ items: moreItems }} trigger={["click"]} placement="bottomRight">
                            <Button size="small" icon={<MoreOutlined />}>更多操作</Button>
                          </Dropdown>
                        </>
                      )}
                      {!isAdminOrStaff && (
                        <>
                          <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => goto(`/instructions/i-${r.id}`)}>查看</Button>
                          {canEdit && (
                            <Button size="small" type="link" icon={<EditOutlined />} onClick={() => {
                              try {
                                const w:any = window as any;
                                const path = String(w?.location?.pathname || '');
                                const base = path.startsWith('/dashboard/staff')
                                  ? '/dashboard/staff'
                                  : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
                                w?.tabsManager?.closeTabByPath?.(`${base}/instructions/${r.id}`);
                              } catch {}
                              goto(`/instructions/${r.id}/edit`);
                            }}>修改</Button>
                          )}
                          {canEdit && (
                            <Button size="small" type="link" danger icon={<CloseCircleOutlined />} onClick={handleCancel}>取消</Button>
                          )}
                          {canAppend && (
                            <Tooltip title={appendTip}>
                              <span>
                                <Button
                                  size="small"
                                  type="link"
                                  icon={<PlusOutlined />}
                                  disabled={!canAppendEnabled}
                                  onClick={() => {
                                    try {
                                      sessionStorage.setItem('ci_append_from_hint', JSON.stringify({ id: Number(r?.id), ts: Date.now() }));
                                    } catch {}
                                    goto(`/instructions/create?append_from=${r.id}`);
                                  }}
                                >
                                  追加指令
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Space>
                  );
                } }
              ];
            })()}
            dataSource={data}
            rowKey={(r: any) => String(r?.id ?? '')}
            loading={loading}
            rowSelection={isAdminOrStaff ? ({
              type: 'checkbox',
              selectedRowKeys: effectiveSelectedRowKeys as any,
              onChange: (keys: any) => {
                const keySet = new Set((keys || []).map((k: any) => String(k)));
                setManualSelectedRowKeys((prev) => {
                  const next = new Set(prev);
                  pageRowKeys.forEach((pk) => {
                    const spk = String(pk);
                    if (keySet.has(spk)) next.add(spk);
                    else next.delete(spk);
                  });
                  return next;
                });
              },
              onSelectAll: (selected: boolean) => {
                setManualSelectedRowKeys((prev) => {
                  const next = new Set(prev);
                  pageRowKeys.forEach((pk) => {
                    const spk = String(pk);
                    if (selected) next.add(spk);
                    else next.delete(spk);
                  });
                  return next;
                });
              },
            } as any) : undefined}
            pagination={false}
            onChange={handleTableChange}
          />
        </div>

        {/* 4. 分页组件 */}
        <div className="flex justify-end items-center">
          <div className="flex items-center" style={{ gap: 12 }}>
            <span className="text-gray-600">
              {(() => {
                const current = Number(state?.pagination?.current ?? 1) || 1;
                const pageSize = Number(state?.pagination?.pageSize ?? 10) || 10;
                const total = Number(state?.pagination?.total ?? 0) || 0;
                const rowsOnPage = Array.isArray(data) ? data.length : 0;
                const start = total > 0 ? ((current - 1) * pageSize) + 1 : 0;
                const end = total > 0 ? start + Math.max(rowsOnPage - 1, 0) : 0;
                return `第 ${start} - ${end} 条， 共 ${total} 条记录`;
              })()}
            </span>
            <Pagination
              current={state.pagination.current}
              pageSize={state.pagination.pageSize}
              total={state.pagination.total}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={[10, 20, 50, 100] as any}
              onChange={(page, pageSize) => {
                if (page !== state.pagination.current || pageSize !== state.pagination.pageSize) {
                  actions.updatePagination({ current: page, pageSize });
                }
              }}
            />
          </div>
        </div>

        {/* 执行信息弹窗：与日志一致的时区显示 */}
        <Modal
          title="预计完成时间"
          open={!!execInfoOf}
          onCancel={() => setExecInfoOf(null)}
          footer={null}
          width={520}
        >
          {execInfoOf ? (
            <div style={{ lineHeight: 1.6 }}>
              <div style={{ marginBottom: 8 }}>
                开始执行时间：
                {execInfoOf.started ? (
                  <TimezoneAwareTime time={execInfoOf.started} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} />
                ) : (
                  <span>-</span>
                )}
              </div>
              <div>
                预计耗时：
                {execInfoOf.unknown ? (
                  <span>未知</span>
                ) : execInfoOf.days !== undefined && execInfoOf.days !== null && execInfoOf.days !== ('' as any) ? (
                  <span>预计{execInfoOf.days}个工作日完成 <span style={{ color: '#8c8c8c' }}>不含周末及节假日</span></span>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          ) : null}
        </Modal>
        {/* 自动预审结果弹窗 */}
        <Modal
          title="自动预审结果（贴换标+装箱）"
          open={autoReviewOpen}
          onCancel={() => setAutoReviewOpen(false)}
          footer={(
            <div style={{ display:'flex', justifyContent:'flex-end', gap: 8, width:'100%' }}>
              <Button onClick={copyAutoReviewInfo} style={{ background:'#fa541c', borderColor:'#fa541c', color:'#fff' }}>
                复制信息
              </Button>
              <Button type="primary" onClick={() => setAutoReviewOpen(false)}>关闭</Button>
            </div>
          )}
          width={900}
        >
          {(() => {
            const issues = Array.isArray(autoReviewRes?.issues) ? autoReviewRes.issues : [];
            const verdict = String(autoReviewRes?.verdict || '');
            return (
              <div className="space-y-3">
                <div>
                  结果：{verdict === 'error' ? <Tag color="red">错误</Tag> : verdict === 'warn' ? <Tag color="gold">警告</Tag> : verdict ? <Tag color="green">通过</Tag> : <Tag>未运行</Tag>}
                  {autoReviewRes?.meta?.mapping_rows != null ? <span style={{ marginLeft: 8, color: '#999' }}>映射表行数：{autoReviewRes.meta.mapping_rows}</span> : null}
                </div>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>问题列表</div>
                  {issues.length === 0 ? <div style={{ color: '#999' }}>无</div> : (
                    <div className="space-y-1">
                      {issues.map((it:any, idx:number) => {
                        const code = String(it?.code || '');
                        const locate = it?.locate || {};
                        const renderDetail = () => {
                          // 覆盖检查：展示缺失的条码清单
                          if (code === 'coverage.old_not_contains_targets') {
                            const arr = Array.isArray(locate?.missing) ? locate.missing : [];
                            const texts = arr.map((m:any) => String(m?.barcode || '')).filter(Boolean);
                            const uniq = Array.from(new Set(texts));
                            if (uniq.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                涉及条码：{uniq.slice(0, 50).join('、')}{uniq.length > 50 ? ` 等共 ${uniq.length} 个` : ''}
                              </div>
                            );
                          }
                            // 新增内容审核：旧条码=新条码
                            if (code === 'mapping.old_equals_new') {
                              const rows = Array.isArray(locate?.rows) ? locate.rows : [];
                              if (rows.length === 0) return null;
                              return (
                                <div style={{ color:'#8c8c8c' }}>
                                  {rows.slice(0, 20).map((r:any, i:number) => (
                                    <div key={i}>
                                      行 {r?.rowNo ?? '-'}：{String(r?.old ?? '-')} → {String(r?.newer ?? '-')}
                                    </div>
                                  ))}
                                  {rows.length > 20 ? <div>……等共 {rows.length} 条</div> : null}
                                </div>
                              );
                            }
                            // 新增内容审核：重复的新旧条码组合
                            if (code === 'mapping.duplicate_pair') {
                              const groups = Array.isArray(locate?.groups) ? locate.groups : [];
                              if (groups.length === 0) return null;
                              return (
                                <div style={{ color:'#8c8c8c' }}>
                                  {groups.slice(0, 12).map((g:any, i:number) => (
                                    <div key={i}>
                                      组合：{String(g?.key || '-')}{g?.count ? `（${g.count}次）` : ''} · 行号：{Array.isArray(g?.rowNos) ? g.rowNos.join('、') : '-'}
                                    </div>
                                  ))}
                                  {groups.length > 12 ? <div>……等共 {groups.length} 组</div> : null}
                                </div>
                              );
                            }
                          // 常规出库：运输标签与出库对象对应表格校验
                          if (code === 'mapping.sltm.incomplete_row') {
                            const rows = Array.isArray(locate?.rows) ? locate.rows : [];
                            if (rows.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                {rows.slice(0, 20).map((r:any, i:number) => (
                                  <div key={i}>
                                    行 {r?.rowNo ?? '-'}：A={String(r?.a ?? '-')}，B={String(r?.b ?? '-')}，C={String(r?.c ?? '-')}，D={String(r?.d ?? '-')}
                                  </div>
                                ))}
                                {rows.length > 20 ? <div>……等共 {rows.length} 条</div> : null}
                              </div>
                            );
                          }
                          if (code === 'mapping.sltm.b_not_one') {
                            const rows = Array.isArray(locate?.rows) ? locate.rows : [];
                            if (rows.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                {rows.slice(0, 20).map((r:any, i:number) => (
                                  <div key={i}>行 {r?.rowNo ?? '-'}：B={String(r?.b ?? '-')}</div>
                                ))}
                                {rows.length > 20 ? <div>……等共 {rows.length} 条</div> : null}
                              </div>
                            );
                          }
                          if (code === 'mapping.sltm.duplicate_c' || code === 'mapping.sltm.duplicate_d') {
                            const groups = Array.isArray(locate?.groups) ? locate.groups : [];
                            if (groups.length === 0) return null;
                            const colName = code === 'mapping.sltm.duplicate_c' ? 'C' : 'D';
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                {groups.slice(0, 12).map((g:any, i:number) => (
                                  <div key={i}>
                                    {colName}列值：{String(g?.value || '-')}（{Number(g?.count || 0)}次） · 行号：{Array.isArray(g?.rowNos) ? g.rowNos.join('、') : '-'}
                                  </div>
                                ))}
                                {groups.length > 12 ? <div>……等共 {groups.length} 组</div> : null}
                              </div>
                            );
                          }
                          // PDF 不在新条码列：展示文件名（或基名）
                          if (code === 'pdf.new_not_in_mapping') {
                            const arr = Array.isArray(locate?.samples) ? locate.samples : [];
                            if (arr.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                涉及文件名：{arr.slice(0, 50).join('、')}{arr.length > 50 ? ` 等共 ${arr.length} 个` : ''}
                              </div>
                            );
                          }
                          // PDF：第一页按2.25×1.25打印模拟后无法解析条码
                          if (code === 'pdf.page1_no_barcode') {
                            const arr = Array.isArray(locate?.samples) ? locate.samples : [];
                            if (arr.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                {arr.slice(0, 12).map((x:any, i:number) => (
                                  <div key={i} style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                                    <span>文件名：{String(x?.file || '-')} · 解码：&lt;未识别&gt;</span>
                                    <Button
                                      size="small"
                                      loading={autoReviewBmpLoadingKey === String(x?.file || '')}
                                      onClick={() => downloadAutoReviewPdfSimBitmap(String(x?.file || ''))}
                                    >
                                      下载预审位图
                                    </Button>
                                  </div>
                                ))}
                                {arr.length > 12 ? <div>……等共 {arr.length} 个</div> : null}
                              </div>
                            );
                          }
                          // PDF：第一页存在多个可解析条码
                          if (code === 'pdf.page1_multi_barcode') {
                            const arr = Array.isArray(locate?.samples) ? locate.samples : [];
                            if (arr.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                {arr.slice(0, 10).map((x:any, i:number) => (
                                  <div key={i} style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                                    <span>
                                      文件名：{String(x?.file || '-')}
                                      {Array.isArray(x?.decoded) && x.decoded.length > 0 ? ` · 解码：${x.decoded.join('、')}` : ' · 解码：<未识别>'}
                                    </span>
                                    <Button
                                      size="small"
                                      loading={autoReviewBmpLoadingKey === String(x?.file || '')}
                                      onClick={() => downloadAutoReviewPdfSimBitmap(String(x?.file || ''))}
                                    >
                                      下载预审位图
                                    </Button>
                                  </div>
                                ))}
                                {arr.length > 10 ? <div>……等共 {arr.length} 个</div> : null}
                              </div>
                            );
                          }
                          // PDF 文件名 vs 真实解码不一致：展示文件名与解码结果
                          if (code === 'pdf.decoded_filename_mismatch') {
                            const arr = Array.isArray(locate?.samples) ? locate.samples : [];
                            if (arr.length === 0) return null;
                            return (
                              <div style={{ color:'#8c8c8c' }}>
                                {arr.slice(0, 10).map((x:any, i:number) => (
                                  <div key={i} style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
                                    <span>
                                      文件名：{String(x?.file || '-')}
                                      {Array.isArray(x?.decoded) && x.decoded.length > 0 ? ` · 解码：${x.decoded.join('、')}` : ' · 解码：<未识别或无条码>'}
                                    </span>
                                    <Button
                                      size="small"
                                      loading={autoReviewBmpLoadingKey === String(x?.file || '')}
                                      onClick={() => downloadAutoReviewPdfSimBitmap(String(x?.file || ''))}
                                    >
                                      下载预审位图
                                    </Button>
                                  </div>
                                ))}
                                {arr.length > 10 ? <div>……等共 {arr.length} 个</div> : null}
                              </div>
                            );
                          }
                          return null;
                        };
                        return (
                          <div key={idx} style={{ display:'flex', flexDirection:'column', gap: 2 }}>
                            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                              <Tag color={it.level==='error'?'red':'gold'}>{it.level==='error'?'错误':'警告'}</Tag>
                              <span>{it.message}</span>
                            </div>
                            {renderDetail()}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </Modal>

        {/* 校验弹窗 */}
      <Modal title="校验缺失项与附件清单" open={!!inspect} onCancel={()=>setInspect(null)} footer={null} width={920}>
        {inspect && (
          <VerifyPanel instructionId={inspect.id} />
        )}
      </Modal>
        {/* 审核弹窗 */}
        <Modal
          title={(() => {
            try {
              const sc = reviewOf?.system_code || reviewOf?.id;
              const name = (reviewOf?.name || '').toString().trim();
              const ts = (reviewOf?.template_snapshot || {}) as any;
              const td = (reviewOf?.template_detail || {}) as any;
              const tplCode = ts.system_code || ts.code || td.system_code || td.code;
              const tplName = ts.name || td.name;
              const tplKey = String(tplCode || '').toUpperCase();
              const regKeys = Object.keys(StrategyRegistry || {});
              try {
                // 调试日志：帮助定位严格匹配时按钮不显示的原因
                // eslint-disable-next-line no-console
                console.debug('[AutoReview][TitleDetect]', { tplCode, tplName, tplKey, registryKeys: regKeys, hasStrategy: !!StrategyRegistry[tplKey] });
              } catch {}
              const isRegularOutbound = isRegularOutboundRow(reviewOf);
              const hasStrategy = !!StrategyRegistry[tplKey] || isRegularOutbound;
              const onRun = async (e:any) => {
                try {
                  e?.stopPropagation?.();
                } catch {}
                try {
                  let ov = reviewOverview;
                  if (!ov) {
                    ov = await loadReviewOverview(reviewOf?.id);
                  }
                  setAutoReviewRunning(true);
                  const d = ov?.detail || {};
                  const atts = Array.isArray(ov?.attachments) ? ov.attachments : [];
                  try {
                    // eslint-disable-next-line no-console
                    console.debug('[AutoReview][Run]', { tplKey, attachmentsCount: atts?.length || 0, hasTargets: Array.isArray(d?.targets_detail) && d.targets_detail.length>0 });
                  } catch {}
                  const targets = Array.isArray(d?.targets_detail) ? d.targets_detail : [];
                  const runKey = StrategyRegistry[tplKey] ? tplKey : (isRegularOutbound ? 'REGULAR_OUTBOUND' : tplKey);
                  const res = await runAutoReviewByTemplateCode(runKey, { targets, attachments: atts });
                  setAutoReviewRes(res);
                  setAutoReviewOpen(true);
                } catch (e:any) {
                  setAutoReviewRes({ verdict: 'error', issues: [{ level:'error', code:'engine.failed', message: e?.message || '运行失败' }] });
                  setAutoReviewOpen(true);
                } finally {
                  setAutoReviewRunning(false);
                }
              };
              return (
                <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
                  {/* 左侧：系统编码与名称 */}
                  <span>{`审核-${sc ?? ''}`}</span>
                  {name ? <span style={{ color: '#1677ff' }}>{name}</span> : null}
                  {/* 区隔线（加粗并增加左右间距） */}
                  <span style={{ width: 2, height: 18, background: '#d9d9d9', display:'inline-block', margin: '0 16px' }} />
                  {/* 模板信息：对齐"指令详情"页样式（名称 + 蓝色Tag显示系统码） */}
                  {tplCode ? (
                    <span style={{ display:'inline-flex', alignItems:'center', gap: 8 }}>
                      <span>{tplName || '-'}</span>
                      <Tag color="blue">{tplCode}</Tag>
                    </span>
                  ) : null}
                  {/* 自动预审按钮紧邻模板信息右侧（仅当存在策略时显示） */}
                  {hasStrategy ? (
                    <Button size="small" type="primary" loading={autoReviewRunning} onClick={onRun} style={{ background:'#fa8c16', borderColor:'#fa8c16' }}>
                      自动预审
                    </Button>
                  ) : null}
                </div>
              )
            } catch { return '审核'; }
          })()}
          open={!!reviewOf}
          okText="提交"
          cancelText="返回"
          onCancel={async () => {
            try {
              // 若本次会话有基线变更，则执行"完整回滚"：删除本次新建的基线、删除本次新增的文件、回滚状态
              if (reviewOf && baselineSessionTouchedRef.current) {
                try {
                  const resp = await request.get('/api/v1/instruction/baselines/', { params: { instruction: reviewOf.id, page_size: 1000 } });
                  const rowsNow = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
                  const initSnap = baselineInitialSnapshotRef.current || { idSet: new Set<number>(), idToState: {}, idToFileIds: {} as Record<string, Set<number>> };
                  const nowSnap = makeBaselineSnapshot(rowsNow);
                  // 1) 删除本次新建的基线记录（打开前不存在的）
                  const newlyCreated: number[] = [];
                  nowSnap.idSet.forEach((bid:number) => { if (!initSnap.idSet.has(bid)) newlyCreated.push(bid); });
                  for (const bid of newlyCreated) {
                    try {
                      await request.delete(`/api/v1/instruction/baselines/${bid}/`);
                    } catch {
                      // 若后端不允许删除记录：清空文件并置为 draft
                      const b = rowsNow.find((x:any)=> Number(x?.id)===Number(bid));
                      const files = Array.isArray(b?.files) ? b.files : [];
                      for (const f of files) {
                        try { await request.delete(`/api/v1/instruction/baseline-files/${f.id}/`); } catch {}
                      }
                      try { await request.patch(`/api/v1/instruction/baselines/${bid}/`, { state: 'draft' }); } catch {}
                    }
                  }
                  // 2) 对会话前已存在的基线：删除新增文件（now-init），回滚 state 到初始
                  initSnap.idSet.forEach(async (bidNum:number) => {
                    const bid = Number(bidNum);
                    const initFiles = initSnap.idToFileIds[String(bid)] || new Set<number>();
                    const nowFiles = nowSnap.idToFileIds[String(bid)] || new Set<number>();
                    const added: number[] = [];
                    nowFiles.forEach((fid:number) => { if (!initFiles.has(fid)) added.push(fid); });
                    for (const fid of added) {
                      try { await request.delete(`/api/v1/instruction/baseline-files/${fid}/`); } catch {}
                    }
                    const initState = String(initSnap.idToState[String(bid)] || 'draft');
                    const nowState = String(nowSnap.idToState[String(bid)] || 'draft');
                    if (initState !== nowState) {
                      try { await request.patch(`/api/v1/instruction/baselines/${bid}/`, { state: initState }); } catch {}
                    }
                  });
                  // 强制刷新一次，缓解 files/files_count 可能的短时不一致
                  try { await loadBaselines(reviewOf.id); } catch {}
                } catch {}
              }
            } finally {
              setReviewOf(null);
              setReviewDecision(undefined);
              setReviewReason('');
              setEstimatedDays(undefined);
              setEstimatedUnknown(false);
              setReviewBillingCollapsed(true);
              // 关闭时同步清理与审核有关的易串状态
              setBaselineRows([]);
              setBaselineOwnerId(null);
              baselineInitialFileIdsRef.current = new Set();
              baselineSessionTouchedRef.current = false;
              baselineInitialSnapshotRef.current = null;
              setAttachPickerBaseline(null);
              setAttachPickerOpen(false);
              setAttachNameEdits({});
              setCustomerAttachments([]);
              setAttPreviewOpen(false);
              setAttPvUrl('');
              setAttPvData([]);
              setAttPvCols([]);
              setAttPvError('');
              setAutoReviewOpen(false);
              setAutoReviewRes(null);
              setAutoReviewRunning(false);
            }
          }}
          confirmLoading={reviewSubmitting}
          width={1600}
          onOk={async () => {
            if (!reviewOf) return;
            if (!reviewDecision) { message.warning('请选择审核结果'); return; }
            if (reviewDecision === 'reject' && !reviewReason.trim()) { message.warning('请填写驳回原因'); return; }
            if (reviewDecision === 'accept') {
              const isNum = typeof estimatedDays === 'number' && !isNaN(estimatedDays as any);
              if (!estimatedUnknown && !isNum) { message.warning('请填写预估耗时（工作日），或选择"未知"'); return; }
            }
            // 统一的提交动作
            const doSubmit = async () => {
              try {
                setReviewSubmitting(true);
                await request.post(`/api/v1/instruction/instructions/${reviewOf.id}/review/`, {
                  decision: reviewDecision,
                  reason: reviewDecision === 'reject' ? reviewReason : undefined,
                  plan_override: reviewDecision === 'accept' ? planSteps : undefined,
                  estimated_working_days: reviewDecision === 'accept' ? (estimatedUnknown ? '' : estimatedDays) : undefined,
                  estimated_working_days_unknown: reviewDecision === 'accept' ? (estimatedUnknown ? true : undefined) : undefined,
                  execution_tips: reviewDecision === 'accept' ? (reviewExecutionTips || undefined) : undefined,
                });
                message.success('审核已提交');
                const nextStatus = reviewDecision === 'accept' ? 'accepted' : 'rejected';
                setRowOverrides(prev => ({ ...prev, [String(reviewOf.id)]: { status: nextStatus } }));
                setReviewOf(null);
                setReviewDecision(undefined);
                setReviewReason('');
                setPlanSteps([]);
                setEstimatedDays(undefined);
                setEstimatedUnknown(false);
                setReviewBillingCollapsed(true);
                setReviewExecutionTips('');
                setPlanPreview(null);
                // 成功后清理基线与附件选择相关状态，避免影响后续指令
                setBaselineRows([]);
                setBaselineOwnerId(null);
                setAttachPickerBaseline(null);
                setAttachPickerOpen(false);
                setAttachNameEdits({});
                setCustomerAttachments([]);
                setAttPreviewOpen(false);
                setAttPvUrl('');
                setAttPvData([]);
                setAttPvCols([]);
                setAttPvError('');
                setAutoReviewOpen(false);
                setAutoReviewRes(null);
                setAutoReviewRunning(false);
                try { await (actions as any).refresh?.(); } catch { try { actions.refresh(); } catch {} }
              } catch (e:any) {
                message.error(e?.response?.data?.detail || '提交失败');
              } finally {
                setReviewSubmitting(false);
              }
            };
            // 审核通过时，校验"已上传但未设为就绪"的基线，提醒操作者
            if (reviewDecision === 'accept') {
              try {
                const resp = await request.get('/api/v1/instruction/baselines/', { params: { instruction: reviewOf.id, page_size: 1000 } });
                const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);

                // ===== 新增：模板规定“必须提交”的基准数据文件完整性硬校验 =====
                // 仅增加完整性校验；不改变任何既有的提交/提示/设为就绪逻辑。
                // 校验顺序：先验证“必填项是否齐全且数量相符”，通过后再执行现有“是否设为就绪”的校验。
                try {
                  const brItems = Array.isArray(reviewOf?.template_detail?.baseline_requirements?.items)
                    ? reviewOf.template_detail.baseline_requirements.items
                    : [];
                  const shouldRequireMappingBaseline = shouldRequireShippingLabelMappingBaseline(reviewOf);
                  const shouldRequireGroupListBaselineByAnswer = shouldRequireGroupListBaseline(reviewOf);
                  const mappingBaselineCodeCandidates = new Set([
                    'SHIPPING_LABEL_TARGET_MAPPING',
                    'SHIPPING_LABEL_TO_TARGET_MAPPING',
                    'WAYBILL_TARGET_MAPPING',
                    'WAYBILL_TO_TARGET_MAPPING',
                  ]);
                  const groupListBaselineCodeCandidates = new Set(['GROUP_LIST']);

                  const toNum = (v:any): number | undefined => {
                    const n = Number(v);
                    return Number.isFinite(n) ? n : undefined;
                  };

                  const pickRange = (it:any): { min: number; max: number } => {
                    // 兼容多种字段命名（模板字段变化时尽量不中断）
                    const hint = it?.schema_hint || {};
                    const exact =
                      toNum(it?.exact_files) ?? toNum(it?.exact_file_count) ?? toNum(it?.exact_count) ??
                      toNum(hint?.exact_files) ?? toNum(hint?.exact_file_count) ?? toNum(hint?.exact_count);
                    if (typeof exact === 'number' && exact >= 0) return { min: Math.floor(exact), max: Math.floor(exact) };

                    const minC =
                      toNum(it?.min_files) ?? toNum(it?.min_file_count) ?? toNum(it?.min_count) ?? toNum(it?.min) ??
                      toNum(hint?.min_files) ?? toNum(hint?.min_file_count) ?? toNum(hint?.min_count) ?? toNum(hint?.min);
                    const maxC =
                      toNum(it?.max_files) ?? toNum(it?.max_file_count) ?? toNum(it?.max_count) ?? toNum(it?.max) ??
                      toNum(hint?.max_files) ?? toNum(hint?.max_file_count) ?? toNum(hint?.max_count) ?? toNum(hint?.max);

                    const min = typeof minC === 'number' ? Math.max(0, Math.floor(minC)) : 0;
                    const max = typeof maxC === 'number' ? Math.max(0, Math.floor(maxC)) : Number.POSITIVE_INFINITY;
                    return { min, max };
                  };

                  const isRequired = (it:any): boolean => {
                    if (it?.required === true) return true;
                    const { min } = pickRange(it);
                    return min >= 1;
                  };

                  const reqItems = (brItems || []).filter((it:any) => !!it && isRequired(it));
                  if (shouldRequireMappingBaseline) {
                    const mappingItem = (brItems || []).find((it:any) => {
                      const code = String(it?.code || '').trim().toUpperCase();
                      return !!code && mappingBaselineCodeCandidates.has(code);
                    });
                    if (!mappingItem) {
                      Modal.error({
                        title: '模板配置缺失',
                        content: '当前指令选择了“上传的运输标签与出库对象有对应关系”，但模板未配置对应关系基线项（建议 code: SHIPPING_LABEL_TARGET_MAPPING）。请先完善模板后再受理。',
                        okText: '返回',
                      });
                      return;
                    }
                    if (!reqItems.includes(mappingItem)) {
                      reqItems.push({
                        ...mappingItem,
                        required: true,
                        min_files: Math.max(1, Number(mappingItem?.min_files || 0) || 0),
                      });
                    }
                  }
                  if (shouldRequireGroupListBaselineByAnswer) {
                    const groupListItem = (brItems || []).find((it:any) => {
                      const code = String(it?.code || '').trim().toUpperCase();
                      return !!code && groupListBaselineCodeCandidates.has(code);
                    });
                    if (!groupListItem) {
                      Modal.error({
                        title: '模板配置缺失',
                        content: '当前指令分拣策略为“分组模式”，但模板未配置分组列表基线项（code: GROUP_LIST）。请先完善模板后再受理。',
                        okText: '返回',
                      });
                      return;
                    }
                    if (!reqItems.includes(groupListItem)) {
                      reqItems.push({
                        ...groupListItem,
                        required: true,
                        min_files: Math.max(1, Number(groupListItem?.min_files || 0) || 0),
                      });
                    }
                  }
                  if (reqItems.length > 0) {
                    const norm = (v:any) => String(v ?? '').trim();
                    const issues: Array<{ code: string; name: string; expect: string; got?: number; missing?: boolean }> = [];

                    for (const it of reqItems) {
                      const code = norm(it?.code);
                      const name = norm(it?.name) || code || '-';
                      if (!code) continue;

                      const found = (rows || []).find((b:any) => norm(b?.code) === code);
                      const rangeRaw = pickRange(it);
                      // required 但模板未提供 min 时，默认至少 1 个文件
                      const minNeed = Math.max(rangeRaw.min, 1);
                      const maxNeed = rangeRaw.max;
                      const expect = maxNeed === Number.POSITIVE_INFINITY
                        ? `至少 ${minNeed} 个文件`
                        : (minNeed === maxNeed ? `${minNeed} 个文件` : `${minNeed}~${maxNeed} 个文件`);

                      if (!found) {
                        issues.push({ code, name, expect, missing: true });
                        continue;
                      }

                      const fileList = Array.isArray(found?.files) ? found.files : [];
                      const filesCount = (fileList.length || 0) || Number(found?.files_count || 0) || 0;
                      const ok = filesCount >= minNeed && (maxNeed === Number.POSITIVE_INFINITY ? true : filesCount <= maxNeed);
                      if (!ok) {
                        issues.push({ code, name, expect, got: filesCount });
                      }
                    }

                    if (issues.length > 0) {
                      Modal.error({
                        title: '基准数据未齐全（模板要求）',
                        content: (
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              以下“必须提交”的基线未满足要求（必须先补齐，之后才会继续校验是否设为就绪）：
                            </div>
                            <ul style={{ paddingLeft: 18, margin: 0 }}>
                              {issues.slice(0, 10).map((x, i) => (
                                <li key={i}>
                                  <span style={{ fontFamily: 'monospace' }}>{x.code}</span>
                                  {' '}（{x.name}）：
                                  {x.missing ? '未创建/不存在' : `文件数不符（当前 ${x.got}）`}
                                  {' '}，要求：{x.expect}
                                </li>
                              ))}
                              {issues.length > 10 ? <li>……等共 {issues.length} 项</li> : null}
                            </ul>
                          </div>
                        ),
                        okText: '返回',
                      });
                      return;
                    }
                  }
                } catch {}

                const unreadyWithFiles = (rows || []).filter((b:any) => {
                  const files = Array.isArray(b?.files) ? b.files : [];
                  const filesCount = files.length || Number(b?.files_count || 0);
                  return filesCount > 0 && String(b?.state || '').toLowerCase() !== 'ready';
                });
                if (unreadyWithFiles.length > 0) {
                  Modal.confirm({
                    title: '基准数据未设为就绪',
                    content: (
                      <div>
                        <div style={{ marginBottom: 8 }}>检测到以下基线已上传文件但未设为"就绪"，提交后执行端可能无法读取：</div>
                        <ul style={{ paddingLeft: 18 }}>
                          {unreadyWithFiles.slice(0, 6).map((b:any, i:number) => (
                            <li key={i}>{b?.name || b?.code || `基线${i+1}`}（当前状态：{b?.state || '-'}，文件数：{Array.isArray(b?.files) ? b.files.length : (b?.files_count || 0)}）</li>
                          ))}
                          {unreadyWithFiles.length > 6 ? <li>…… 共 {unreadyWithFiles.length} 项</li> : null}
                        </ul>
                        <div style={{ color:'#cf1322' }}>请先设为"就绪"再提交，或点击下方"设为就绪并提交"。</div>
                      </div>
                    ),
                    okText: '设为就绪并提交',
                    cancelText: '返回设置',
                    onOk: async () => {
                      try {
                        // 批量设为 ready
                        for (const b of unreadyWithFiles) {
                          try {
                            await request.patch(`/api/v1/instruction/baselines/${b.id}/`, { state: 'ready' });
                          } catch {}
                        }
                        await doSubmit();
                      } catch {
                        // 忽略，交由 doSubmit 内部提示
                      }
                    },
                  });
                  return;
                }
              } catch {}
            }
            await doSubmit();
          }}
        >
          {reviewOf ? (
            <div>
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>当前状态：{(() => {
                const s = getEffectiveStatusGlobal(reviewOf);
                return <Tag color={statusColorGlobal(s)}>{statusTextGlobal(s)}</Tag>;
              })()}
                <Button size="small" onClick={async () => { setReviewOverviewShowAllQuestions(false); setReviewOverviewOpen(true); await loadReviewOverview(reviewOf?.id); }}>查看指令概览</Button>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ marginRight: 8 }}>审核结果：</span>
                <Select
                  style={{ minWidth: 220 }}
                  placeholder="请选择"
                  value={reviewDecision}
                  onChange={(v) => handleReviewDecisionChange(v as 'accept' | 'reject')}
                >
                  <Option value="accept">受理</Option>
                  <Option value="reject">驳回</Option>
                </Select>
              </div>
              {reviewDecision === 'reject' && (
                <div>
                  <span style={{ marginRight: 8 }}>驳回原因：</span>
                  <Input.TextArea
                    value={reviewReason}
                    onChange={(e) => setReviewReason(e.target.value)}
                    placeholder="请输入驳回原因"
                    rows={3}
                  />
                </div>
              )}
              {reviewDecision === 'accept' && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ marginRight: 8 }}>
                      预估耗时（工作日）{reviewDecision === 'accept' ? '（必填）' : ''}：
                    </span>
                    <InputNumber min={0} precision={0} value={estimatedDays} onChange={(v:any)=> setEstimatedDays(typeof v === 'number' ? v : undefined)} placeholder="例如：3" disabled={estimatedUnknown} />
                    <span style={{ marginLeft: 12 }}>
                      <span style={{ marginRight: 6, color: '#666' }}>未知：</span>
                      <Switch checked={estimatedUnknown} onChange={(ck)=> { setEstimatedUnknown(ck); if (ck) setEstimatedDays(undefined); }} />
                    </span>
                    <span style={{ marginLeft: 8, color: '#999' }}>领取后日志将显示"预计X个工作日内完成"；选择"未知"时不展示天数</span>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 6 }}>执行提醒/备注（给仓库端）</div>
                    <Input.TextArea
                      value={reviewExecutionTips}
                      onChange={(e)=> setReviewExecutionTips(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      placeholder="用于提醒仓库员工的执行注意事项（例如：拣货时注意批次区分；装箱按项目分箱等）"
                    />
                  </div>
                  {(() => {
                    const brItems = Array.isArray(reviewOf?.template_detail?.baseline_requirements?.items)
                      ? reviewOf.template_detail.baseline_requirements.items : [];
                    const templateNeedBaseline = (brItems || []).some((it:any) => !!(it && it.required));
                    const shouldRequireByAnswer = shouldRequireShippingLabelMappingBaseline(reviewOf);
                    const shouldRequireGroupListByAnswer = shouldRequireGroupListBaseline(reviewOf);
                    const needBaseline = templateNeedBaseline || shouldRequireByAnswer || shouldRequireGroupListByAnswer;
                    try {
                      const rawAns = (reviewOf?.customer_answers || {})?.shipping_label_to_target_mapping;
                      const normalizedAns = normalizeShippingLabelTargetMappingAnswer(rawAns);
                      console.debug('[PreReview][BaselineGate][render]', {
                        instruction_id: reviewOf?.id,
                        template_name: reviewOf?.template_snapshot?.name || reviewOf?.template_detail?.name || reviewOf?.template_name || '',
                        template_need_baseline: templateNeedBaseline,
                        answer_raw: rawAns,
                        answer_normalized: normalizedAns,
                        should_require_by_answer: shouldRequireByAnswer,
                        should_require_group_list_by_answer: shouldRequireGroupListByAnswer,
                        need_baseline: needBaseline,
                        baseline_items_count: Array.isArray(brItems) ? brItems.length : 0,
                      });
                    } catch {}
                    if (!needBaseline) return null;
                    return (
                      <Card size="small" title="基准数据规范(仓库操作基线文件)" style={{ marginTop: 12 }} extra={<Space>
                        <Button size="small" onClick={() => loadBaselines(reviewOf?.id)}>刷新</Button>
                        <Button
                          size="small"
                          type="primary"
                          onClick={async () => {
                            try {
                              if (!reviewOf?.id) return;
                              // 确保使用最新数据
                              const rows = await loadBaselines(reviewOf.id) as any[] || (baselineRows || []);
                              const candidates = (rows || []).filter((b:any) => {
                                const files = Array.isArray(b?.files) ? b.files : [];
                                const filesCount = files.length || Number(b?.files_count || 0);
                                return filesCount > 0 && String(b?.state || '').toLowerCase() !== 'ready';
                              });
                              if (candidates.length === 0) { message.info('没有需要设为就绪的基线'); return; }
                              let ok = 0, fail = 0;
                              for (const b of candidates) {
                                try {
                                  await request.patch(`/api/v1/instruction/baselines/${b.id}/`, { state: 'ready' });
                                  ok += 1;
                                } catch { fail += 1; }
                              }
                              baselineSessionTouchedRef.current = true;
                              await loadBaselines(reviewOf.id);
                              message.success(`已设为就绪：成功 ${ok}${fail ? `，失败 ${fail}` : ''}`);
                            } catch {}
                          }}
                        >
                          全部设为就绪
                        </Button>
                      </Space>}>
                        <div>
                  <div style={{ marginBottom: 8, color: '#666' }}>
                    说明：此处为"操作基线"数据（如换标映射表）。执行页会按阶段/分组/步骤读取 state=ready 的基线。
                    <div style={{ marginTop: 4 }}>
                      使用顺序：1) 创建草稿 → 2) 上传文件 → 3) 点击刷新查看数量/文件名 → 4) 设为就绪。
                    </div>
                          </div>
                          <Table
                            size="small"
                            rowKey={(r:any, i?:number)=> String(r?.code || i || 0)}
                            dataSource={brItems}
                            pagination={false}
                            columns={[
                              { title: 'Code', dataIndex: 'code', width: 160 },
                              { title: '名称', dataIndex: 'name', width: 200 },
                              { title: '绑定', dataIndex: 'bind', width: 260, render: (v:any)=> {
                                const st = v?.stage != null ? `stage=${v.stage}` : '';
                                const gk = v?.group_key ? `group=${v.group_key}` : '';
                                const ss = Array.isArray(v?.steps) && v.steps.length > 0 ? `steps=${v.steps.join('/')}` : '';
                                const segs = [st,gk,ss].filter(Boolean);
                                return segs.length ? segs.join(' · ') : <span style={{ color:'#999' }}>-</span>;
                              }},
                      { title: '上传与就绪', key: 'ops', render: (_:any, row:any) => {
                                const code = String(row?.code || '').trim();
                                const rowsForCurrent = (baselineOwnerId != null && reviewOf?.id != null && Number(baselineOwnerId) === Number(reviewOf.id)) ? (baselineRows || []) : [];
                                const existing = rowsForCurrent.filter((b:any)=> String(b?.code || '').trim() === code);
                        const current = existing?.[0];
                        const fileList = Array.isArray(current?.files) ? current.files : [];
                        const fileCount = fileList.length;
                                return (
                                  <div style={{ width: '100%' }}>
                                    {/* 操作行：创建草稿、上传、状态/文件数/删除全部/设为就绪 */}
                                    <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap', width:'100%' }}>
                                      <Button size="small" onClick={() => createBaseline(code, row?.name, row?.bind, row?.schema_hint)}>创建草稿</Button>
                                      <input
                                        type="file"
                                        multiple
                                        style={{ width: 260 }}
                                        ref={(el) => {
                                          try {
                                            const isBm = String(code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
                                            if (isBm) bmBaselineFileInputRef.current = el;
                                          } catch {}
                                        }}
                                        onClick={(e:any) => {
                                          try {
                                            const isBm = String(code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
                                            if (!isBm) return;
                                            const b = current;
                                            if (!b?.id) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              message.warning('请先创建草稿');
                                              return;
                                            }
                                            if (bmBaselineFilePickBypassRef.current) {
                                              bmBaselineFilePickBypassRef.current = false;
                                              return;
                                            }
                                            e.preventDefault();
                                            e.stopPropagation();
                                            Modal.confirm({
                                              title: '上传 BARCODE_MAPPING 前提醒',
                                              content: (
                                                <div style={{ lineHeight: 1.7 }}>
                                                  请确保基线文件中包含数量限制信息和完整操作对象信息(由旧条码映射操作对象)，或者使用“从客户上传附件中选择”功能实现数量同步及数据完整性审核。
                                                </div>
                                              ),
                                              okText: '确认',
                                              cancelText: '取消',
                                              onOk: async () => {
                                                bmBaselineFilePickBypassRef.current = true;
                                                // 确保在确认按钮的用户手势链路中触发文件选择框
                                                setTimeout(() => {
                                                  try { bmBaselineFileInputRef.current?.click(); } catch {}
                                                }, 0);
                                              }
                                            });
                                          } catch {}
                                        }}
                                        onChange={async (e:any)=> {
                                        const files = e?.target?.files;
                                        const b = current;
                                        if (!files || files.length === 0) return;
                                        if (!b?.id) { message.warning('请先创建草稿'); e.target.value=''; return; }
                                        // BARCODE_MAPPING：阶段A（简单版）
                                        // 直接上传的本地文件先进入“从客户上传附件中选择”弹窗，复用预览→审核→匹配→编辑→采纳的既有链路。
                                        // 注意：不覆盖原文件；最终以“采纳为基线”的结果落库作为跨设备依据。
                                        try {
                                          const baselineCodeUpper = String(code || '').trim().toUpperCase();
                                          if (baselineCodeUpper === 'BARCODE_MAPPING') {
                                            const arr: File[] = Array.from(files as any);
                                            if (arr.length === 0) { e.target.value=''; return; }
                                            if (arr.length > 1) {
                                              message.warning('BARCODE_MAPPING 建议一次只处理 1 个文件：已自动取第一个文件进入预览流程');
                                            }
                                            const f = arr[0];
                                            const url = (() => {
                                              try { return URL.createObjectURL(f); } catch { return ''; }
                                            })();
                                            const tempId = -1 * (Date.now() + Math.floor(Math.random() * 1000));
                                            const tempRow: any = {
                                              id: tempId,
                                              file: url,
                                              file_path: url,
                                              file_name: String((f as any)?.name || 'BARCODE_MAPPING'),
                                              created_at: new Date().toISOString(),
                                              mime_type: String((f as any)?.type || ''),
                                              file_size: Number((f as any)?.size || 0) || 0,
                                              _local: true,
                                            };
                                            // 识别可接受扩展名（从 schema_hint/accept_formats 推断）
                                            const acceptExts: string[] = (() => {
                                              const hint = row?.schema_hint || {};
                                              const fmts = hint?.accept_formats || hint?.formats || row?.accept_formats || row?.formats || [];
                                              if (Array.isArray(fmts) && fmts.length > 0) return fmts.map((x:any)=> String(x).toLowerCase());
                                              return [];
                                            })();
                                            // 打开“从客户上传附件中选择”弹窗（不改其内部逻辑），并把本地文件作为一条候选记录插入列表顶部
                                            setAttachPickerBaseline({ id: Number(b.id), code, acceptExts });
                                            setAttachNameEdits({});
                                            setAttachPickerSelectedIds(new Set([tempId]));
                                            setAttachPickerOpen(true);
                                            // 先加载真实客户附件，再把本地文件插入顶部
                                            try { await loadCustomerAttachments(reviewOf?.id); } catch {}
                                            setCustomerAttachments((prev:any[]) => {
                                              const base = Array.isArray(prev) ? prev : [];
                                              // 避免重复插入
                                              if (base.some((x:any) => Number(x?.id) === tempId)) return base;
                                              return [tempRow, ...base];
                                            });
                                            message.info('已将本地文件加入候选列表：请在弹窗中点击“预览”，完成审核与匹配后再采纳为基线文件');
                                            e.target.value = '';
                                            return;
                                          }
                                        } catch {}
                                        await uploadBaselineFiles(Number(b.id), files);
                                        e.target.value = '';
                                      }}
                                      />
                                      <Button
                                        size="small"
                                        onClick={async () => {
                                          const b = current;
                                          if (!b?.id) { message.warning('请先创建草稿'); return; }
                                          // 识别可接受扩展名（从 schema_hint/accept_formats 推断）
                                          const acceptExts: string[] = (() => {
                                            const hint = row?.schema_hint || {};
                                            const fmts = hint?.accept_formats || hint?.formats || row?.accept_formats || row?.formats || [];
                                            if (Array.isArray(fmts) && fmts.length > 0) return fmts.map((x:any)=> String(x).toLowerCase());
                                            return [];
                                          })();
                                          const baselineCodeUpper = String(code || '').trim().toUpperCase();
                                          const openPicker = async () => {
                                            setAttachPickerBaseline({ id: Number(b.id), code, acceptExts });
                                            // 打开选择弹窗前清空上一次编辑的自定义文件名，避免串入
                                            setAttachNameEdits({});
                                            setAttachPickerSelectedIds(new Set());
                                            setAttachPickerOpen(true);
                                            await loadCustomerAttachments(reviewOf?.id);
                                          };
                                          if (baselineCodeUpper === 'BARCODE_MAPPING') {
                                            Modal.warning({
                                              title: '重要提示',
                                              content: '请在接下来打开的弹窗中,点击操作列"预览"按钮实现数据审核及操作对象匹配，禁止直接采纳为基线文件',
                                              okText: '我已知晓，继续',
                                              onOk: async () => { await openPicker(); },
                                            });
                                            return;
                                          }
                                          await openPicker();
                                        }}
                                      >
                                        从客户上传附件中选择
                                      </Button>
                                      <span style={{ flex: 1 }} />
                                      {existing?.length > 0 ? (
                                        <span style={{ color:'#666', display:'inline-flex', alignItems:'center', gap:8 }}>
                                          <span>{`状态：${current?.state || '-'} ｜ 文件数：${fileCount}`}</span>
                                          {fileList.length > 0 ? (
                                            <Tooltip title="全部删除">
                                              <Button
                                                size="small"
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => deleteAllBaselineFiles(Number(current?.id))}
                                                style={{ color: '#cf1322' }}
                                              />
                                            </Tooltip>
                                          ) : null}
                                          {current?.state !== 'ready' ? (
                                            <Button size="small" type="primary" onClick={()=> setBaselineReady(Number(current?.id))}>设为就绪</Button>
                                          ) : <Tag color="green">就绪</Tag>}
                                        </span>
                                      ) : <span style={{ color:'#999' }}>未创建</span>}
                                    </div>
                                    {/* 文件行：始终独立于操作行，置于下一行 */}
                                    {existing?.length > 0 && fileList.length > 0 ? (
                                      <div style={{ width:'100%', display:'flex', flexWrap:'wrap', gap:6, marginTop: 6 }}>
                                        {fileList.map((f:any)=> (
                                          <span
                                            key={f.id}
                                            onMouseEnter={()=> setHoveredFileId(Number(f.id))}
                                            onMouseLeave={()=> setHoveredFileId((v)=> v === Number(f.id) ? null : v)}
                                            style={{
                                              background:'#f5f5f5',
                                              border:'1px solid #d9d9d9',
                                              borderRadius: 4,
                                              padding:'2px 6px',
                                              display:'inline-flex',
                                              alignItems:'center',
                                              gap:6,
                                              maxWidth: 260
                                            }}
                                          >
                                            <span
                                              title={f.file_name || f.file}
                                              style={{ maxWidth: 220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                                            >
                                              {f.file_name || f.file}
                                            </span>
                                            <Tooltip title="删除">
                                              <Button
                                                size="small"
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => deleteBaselineFile(Number(f.id))}
                                                style={{ visibility: hoveredFileId === Number(f.id) ? 'visible' : 'hidden', color: '#cf1322' }}
                                              />
                                            </Tooltip>
                                          </span>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              }},
                            ] as any}
                          />
                        </div>
                      </Card>
                    );
                  })()}
                  {/* 收费规则摘要（暂时隐藏） */}
                  {RULES_SUMMARY_ENABLED && (
                    <Card size="small" title="收费规则摘要" style={{ marginTop: 12 }}>
                      {rulesLoading ? <div style={{ color: '#999' }}>加载中...</div> : (
                        rulesPreview.length === 0 ? <div style={{ color: '#999' }}>无可预览的工单步骤</div> : (
                          <Table
                            size="small"
                            rowKey={(r:any, i?:number) => String(i ?? 0)}
                            dataSource={rulesPreview}
                            pagination={false}
                            columns={[
                              { title: '工单模板', dataIndex: 'template_code', width: 180 },
                              { title: '操作类型', dataIndex: 'operation_type', width: 140 },
                              { title: '规则系统编码', dataIndex: 'rule_system_code', width: 180, render: (v:any)=> v || <span style={{ color:'#999' }}>未命中</span> },
                              { title: '单位', dataIndex: 'unit', width: 120 },
                              { title: '币种', dataIndex: 'currency', width: 100 },
                              { title: '规则类型', dataIndex: 'rule_type', width: 120 },
                              { title: '梯度', dataIndex: 'tier', width: 100, render: (v:any)=> v || '-' },
                              { title: '回退单价', dataIndex: 'fallback_price', width: 140, render: (v:any)=> v ? <Tag color="gold">{v}</Tag> : '-' },
                            ] as any}
                          />
                        )
                      )}
                    </Card>
                  )}
                  <div style={{ marginTop: 12, marginBottom: 8 }}>
                    <Button size="small" onClick={() => setReviewBillingCollapsed(v => !v)}>
                      {reviewBillingCollapsed ? '展开计费计划' : '收起计费计划'}
                    </Button>
                  </div>
                  {!reviewBillingCollapsed && (
                    <Card size="small" title="计费计划（虚拟工单，可编辑）" extra={(
                      <Space>
                        <Tooltip title={'新增一条"计费项（操作类型/变体）"，用于生成虚拟工单并匹配计费规则'}>
                          <Button size="small" onClick={() => setPlanSteps(prev => ([...prev, { template_code: '', after: [] }]))}>新增计费项（操作类型）</Button>
                        </Tooltip>
                        <Button size="small" loading={planPreviewLoading} onClick={previewCurrentPlan}>预览计划</Button>
                        <Button size="small" type={showAdvancedDeps ? 'primary' as any : 'default' as any} onClick={()=> setShowAdvancedDeps(v=>!v)}>
                          {showAdvancedDeps ? '高级模式：开' : '高级模式：关'}
                        </Button>
                      </Space>
                    )}>
                      <div className="space-y-2">
                        <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fffbe6', border: '1px solid #ffe58f', color: '#ad6800', borderRadius: 4 }}>
                          说明：这是"虚拟工单（计费行）"计划，仅用于匹配操作类型和计费规则，生成自动计费记录。仓库员工实际执行的"动作序列"在"执行动作序列"中维护，与此处的"计费项"无直接对应关系。
                        </div>
                        {(() => {
                          // 计算基于依赖关系的"阶段(stage)"分组：无入度为第一阶段，逐层拓扑
                          const n = planSteps.length;
                          if (n === 0) return <div style={{ color: '#999' }}>未设置计划：将使用路由或默认工单模板</div>;
                          const codeToIdxs = new Map<string, number[]>();
                          planSteps.forEach((s, i) => {
                            const code = String(s.template_code || '').trim();
                            if (!code) return;
                            const arr = codeToIdxs.get(code) || [];
                            arr.push(i);
                            codeToIdxs.set(code, arr);
                          });
                          // 邻接表与入度
                          const adj: Record<number, number[]> = {};
                          const indeg: number[] = new Array(n).fill(0);
                          for (let i = 0; i < n; i++) adj[i] = [];
                          for (let j = 0; j < n; j++) {
                            const a = planSteps[j]?.after;
                            if (Array.isArray(a)) {
                              a.forEach((ac) => {
                                const targets = codeToIdxs.get(String(ac)) || [];
                                targets.forEach((pi) => { adj[pi].push(j); indeg[j] += 1; });
                              });
                            }
                          }
                          // Kahn 分层
                          const used = new Array(n).fill(false);
                          const stages: number[][] = [];
                          while (stages.reduce((a, g) => a + g.length, 0) < n) {
                            const layer: number[] = [];
                            for (let i = 0; i < n; i++) {
                              if (!used[i] && indeg[i] === 0) {
                                layer.push(i);
                              }
                            }
                            // 若存在环，兜底将未放入的节点整体作为一层
                            if (layer.length === 0) {
                              for (let i = 0; i < n; i++) {
                                if (!used[i]) layer.push(i);
                              }
                            }
                            // 标记并"移除"本层边
                            layer.forEach((i) => { used[i] = true; });
                            layer.forEach((i) => { (adj[i] || []).forEach((v) => { indeg[v] = Math.max(0, indeg[v] - 1); }); });
                            stages.push(layer);
                          }
                          // 渲染分组：每个阶段增加视觉分隔
                          return (
                            <div>
                              {stages.map((layer, si) => {
                                return (
                                  <div key={`stage-${si}`} style={{ marginBottom: 12 }}>
                                    <div style={{ margin: '8px 0', padding: '6px 8px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 4, color: '#0958d9', fontWeight: 600 }}>
                                      阶段 {si + 1}
                                    </div>
                                    {layer.map((idx) => {
                                      const step = planSteps[idx];
                                      const allCodes = planSteps.map(s => s.template_code).filter(Boolean);
                                      // 构建 code -> 可读名称 映射（来自预览与模板选项）
                                      const codeNameMap: Record<string, string> = {};
                                      try {
                                        const stepsPrev = Array.isArray(planPreview?.steps) ? planPreview.steps : [];
                                        stepsPrev.forEach((s:any) => { if (s?.template_code) codeNameMap[String(s.template_code)] = String(s.template_name || s.template_code); });
                                      } catch {}
                                      try {
                                        (wotplOptions || []).forEach((o:any) => {
                                          if (o?.value) {
                                            const nm = (o?.name || (typeof o?.label === 'string' ? o.label : undefined));
                                            if (nm) codeNameMap[String(o.value)] = String(nm);
                                          }
                                        });
                                      } catch {}
                                      return (
                                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '360px 360px auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                          <Select
                                            showSearch allowClear
                                            placeholder="选择工单模板"
                                            style={{ width: 360 }}
                                            value={step.template_code || undefined}
                                            options={wotplOptions}
                                            loading={wotplLoading}
                                            onFocus={() => loadWorkOrderTemplates()}
                                            filterOption={(input, opt:any) => String(opt?.searchLabel || '').toLowerCase().includes(String(input).toLowerCase())}
                                            onChange={(v) => {
                                              const next = [...planSteps];
                                              next[idx] = { ...next[idx], template_code: v || '' };
                                              setPlanSteps(next);
                                            }}
                                          />
                                          {showAdvancedDeps ? (
                                            <Select
                                              mode="multiple"
                                              allowClear
                                              placeholder="高级依赖（可选）"
                                              style={{ width: 360 }}
                                              value={step.after || []}
                                              options={(allCodes || []).filter(c => c && c !== step.template_code).map(c => {
                                                const readable = codeNameMap[c] || c;
                                                const full = `${readable} · ${c}`;
                                                return { value: c, label: (<span title={full}>{readable}</span>) };
                                              })}
                                              onChange={(vals) => {
                                                const next = [...planSteps];
                                                next[idx] = { ...next[idx], after: vals as string[] };
                                                setPlanSteps(next);
                                              }}
                                            />
                                          ) : (
                                            <div style={{ width: 360, color: Array.isArray(step.after) && step.after.length > 0 ? '#ad6800' : '#999' }}>
                                              {Array.isArray(step.after) && step.after.length > 0 ? <Tag color="gold">高级依赖</Tag> : <span>（并行，暂无高级依赖）</span>}
                                            </div>
                                          )}
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Dropdown
                                              menu={{
                                                items: [
                                                  { key: 'promote', label: '提升为新阶段', onClick: () => promoteToNextStage(idx) },
                                                  { key: 'mergePrev', label: '合并到上一阶段', onClick: () => mergeToPrevStage(idx) },
                                                ]
                                              }}
                                            >
                                              <Button size="small">结构化调整</Button>
                                            </Dropdown>
                                            <Button danger size="small" onClick={() => {
                                              const next = planSteps.filter((_, i) => i !== idx);
                                              setPlanSteps(next);
                                            }}>删除</Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                      {planPreview && (
                        <div style={{ marginTop: 8 }}>
                          {Array.isArray(planPreview.errors) && planPreview.errors.length > 0 && (
                            <div style={{ color: '#d4380d', marginBottom: 6 }}>错误：{planPreview.errors.join('；')}</div>
                          )}
                          {Array.isArray(planPreview.warnings) && planPreview.warnings.length > 0 && (
                            <div style={{ color: '#fa8c16', marginBottom: 6 }}>警告：{planPreview.warnings.join('；')}</div>
                          )}
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
                            <div style={{ fontWeight: 600 }}>计费模板（预览）</div>
                            <Button type="link" size="small" onClick={()=> setWoPreviewCollapsed(prev => !prev)}>
                              {woPreviewCollapsed ? '展开' : '收起'}
                            </Button>
                          </div>
                          {!woPreviewCollapsed && (
                            <Table
                              size="small"
                              rowKey={(r:any, i?:number)=> String(i ?? 0)}
                              dataSource={Array.isArray(planPreview.steps) ? planPreview.steps : []}
                              pagination={false}
                              columns={[
                                { title: '计费模板编码', dataIndex: 'template_code' },
                                { title: '名称', dataIndex: 'template_name' },
                                { title: '计费操作类型', dataIndex: 'operation_type' },
                                { title: '依赖（先于本计费项完成）', dataIndex: 'after', render: (v:any)=> (Array.isArray(v) && v.length>0) ? v.join('、') : '-' },
                              ] as any}
                            />
                          )}
                        </div>
                      )}
                    </Card>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </Modal>
        {/* 从客户上传附件中选择 */}
        <Modal
          title="指令概览"
          open={reviewOverviewOpen}
          onCancel={() => setReviewOverviewOpen(false)}
          footer={null}
          width={1200}
        >
          {reviewOverviewLoading ? <div style={{ color:'#999' }}>加载中...</div> : (
            (() => {
              const d = reviewOverview?.detail || {};
              const atts = Array.isArray(reviewOverview?.attachments) ? reviewOverview.attachments : [];
              const ts = d?.template_snapshot || {};
              const td = d?.template_detail || {};
              const code = ts.system_code || ts.code || td.system_code;
              const name = ts.name || td.name;
              const warehouse = d?.warehouse_detail || d?.warehouse;
              const projects = Array.isArray(d?.projects_detail) ? d.projects_detail : [];
              const qn = (d?.questionnaire_snapshot || {}) as any;
              const qs = Array.isArray(qn?.questions) ? qn.questions : [];
              const ans = (d?.customer_answers || {}) as any;
              const isEmptyLike = (v: any) => {
                if (v === undefined || v === null) return true;
                if (Array.isArray(v)) return v.length === 0;
                const s = String(v).trim().toLowerCase();
                return s === '' || s === 'none' || s === 'null' || s === 'undefined';
              };
              const renderQVal = (q:any) => {
                const key = String(q.key||'').trim();
                const keyLower = key.toLowerCase();
                const qLabel = String(q.label || q.key || '').trim();
                const val = ans[key];
                const type = String(q.type||'');
                if (isEmptyLike(val)) return <span />;
                const renderStateBadge = (text: string, tone: 'red' | 'green') => {
                  const isRed = tone === 'red';
                  return (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        border: `1px solid ${isRed ? '#ff4d4f' : '#95de64'}`,
                        background: isRed ? '#fff1f0' : '#f6ffed',
                        color: isRed ? '#cf1322' : '#389e0d',
                        borderRadius: 4,
                        padding: '0 8px',
                        height: 22,
                      }}
                    >
                      {text}
                    </span>
                  );
                };
                const parseRepackAnswer = (raw: any): 'yes' | 'no' | '' => {
                  if (raw === true) return 'yes';
                  if (raw === false) return 'no';
                  const s = String(raw ?? '').trim().toLowerCase();
                  if (!s) return '';
                  if (['true', '1', 'yes', 'y', '是', '需要', '需重新包装', '需要重新包装'].includes(s)) return 'yes';
                  if (['false', '0', 'no', 'n', '否', '不需要', '无需重新包装', '不需重新包装', '不需要重新包装'].includes(s)) return 'no';
                  if (s.includes('无需') || s.includes('不需要')) return 'no';
                  if (s.includes('需') || s.includes('需要')) return 'yes';
                  return '';
                };
                const parseMappingAnswer = (raw: any): 'has' | 'none' | '' => {
                  if (raw === true) return 'has';
                  if (raw === false) return 'none';
                  const s = String(raw ?? '').trim().toLowerCase();
                  if (!s) return '';
                  if (['has_mapping', 'true', '1', 'yes', 'y'].includes(s)) return 'has';
                  if (['no_mapping', 'false', '0', 'no', 'n'].includes(s)) return 'none';
                  if (s.includes('有对应关系')) return 'has';
                  if (s.includes('无对应关系')) return 'none';
                  return '';
                };
                const isRepackQuestion = keyLower === 'is_repack_required' || keyLower === 'repack_required' || qLabel === '是否需重新包装';
                const isMappingQuestion = keyLower === 'shipping_label_to_target_mapping' || qLabel === '运输标签与出库对象是否存在对应关系';
                if (isRepackQuestion) {
                  const parsed = parseRepackAnswer(val);
                  if (parsed === 'yes') return renderStateBadge('需重新包装', 'red');
                  if (parsed === 'no') return renderStateBadge('无需重新包装', 'green');
                }
                if (type === 'boolean') {
                  const yes = !!val;
                  const reverseColorKeys = new Set([
                    'signature_required',
                    'insurance_required',
                    'is_liftgate_required',
                    'is_appointment_required',
                    'limited_access_delivery',
                  ]);
                  const useReverseColor = reverseColorKeys.has(key);
                  return (
                    <Button
                      size="small"
                      style={useReverseColor
                        ? (yes
                          ? { background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 600, minWidth: 44 }
                          : { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 600, minWidth: 44 })
                        : (yes
                          ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 600, minWidth: 44 }
                          : { background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 600, minWidth: 44 })
                      }
                    >
                      {yes ? '是' : '否'}
                    </Button>
                  );
                }
                if (type === 'select') {
                  const opt = (q.options||[]).find((o:any)=> String(o.value)===String(val));
                  if (isMappingQuestion) {
                    const parsed = parseMappingAnswer((opt?.label ?? val));
                    if (parsed === 'has') return renderStateBadge('有对应关系', 'red');
                    if (parsed === 'none') return renderStateBadge('无对应关系', 'green');
                  }
                  return opt ? (opt.label || opt.value) : String(val);
                }
                if (isMappingQuestion) {
                  const parsed = parseMappingAnswer(val);
                  if (parsed === 'has') return renderStateBadge('有对应关系', 'red');
                  if (parsed === 'none') return renderStateBadge('无对应关系', 'green');
                }
                if (type === 'multiselect') {
                  const arr = Array.isArray(val) ? val : [];
                  const labels = arr.map((v:any)=> {
                    const opt = (q.options||[]).find((o:any)=> String(o.value)===String(v));
                    return opt ? (opt.label || opt.value) : String(v);
                  });
                  return labels.join('、');
                }
                if (type === 'number') {
                  if (String(q.unit || '').trim() === '$') return `$ ${String(val)}`;
                  return String(val) + (q.unit ? ` ${q.unit}` : '');
                }
                return String(val);
              };
              const downloadAttachment = async (att:any) => {
                try {
                  const url = resolveAttachmentRuntimeUrl(att);
                  const name = getDisplayFileName(att) || att?.file_name || '附件';
                  if (!url) { message.warning('无可用文件'); return; }
                  const resp = await fetch(url, { credentials: 'include' as any });
                  if (!resp.ok) throw new Error('下载失败');
                  const blob = await resp.blob();
                  const href = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = href;
                  a.download = name;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(href);
                } catch { message.error('下载失败'); }
              };
              return (
                <div className="space-y-6">
                  <Card size="small" title="概览">
                    <Descriptions bordered size="small" column={2}>
                      <Descriptions.Item label="系统编码" span={1}>
                        {d?.system_code ? (<Text strong style={{ fontSize: 16 }} code copyable={{ text: String(d.system_code) }}>{d.system_code}</Text>) : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="指令模板" span={1}>
                        <Space align="start" size={4}>
                          <span>{name || '-'}</span>
                          {code ? <Tag color="blue">{code}</Tag> : <span style={{ color: '#999' }}>-</span>}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="指令名称" span={1}>{d?.name || '-'}</Descriptions.Item>
                      <Descriptions.Item label="优先级" span={1}><Tag color={d?.priority==='super'?'red': d?.priority==='high'?'orange':'default'}>{(d?.priority==='super'?'超级优先': d?.priority==='high'?'一般优先':'普通')}</Tag></Descriptions.Item>
                      <Descriptions.Item label="项目" span={1}>
                        {projects.length > 0 ? (
                          <span>{projects.map((p:any)=> `${p.project_code || p.code || p.id} - ${p.name || '-'}`).join('、')}</span>
                        ) : (
                          (() => {
                            const pd = d?.project_detail || d?.project;
                            if (pd && (pd.name || pd.project_code || pd.code || pd.id)) {
                              const code = pd.project_code || pd.code || pd.id;
                              const name = pd.name || '-';
                              return <span>{code} - {name}</span>;
                            }
                            return <span style={{ color: '#999' }}>-</span>;
                          })()
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="仓库" span={1}>
                        {warehouse ? (
                          <Space direction="vertical" size={0}>
                            <span>{warehouse.name || '-'}</span>
                            <Tag>{warehouse.code || warehouse.warehouse_code || ''}</Tag>
                          </Space>
                        ) : <span style={{ color: '#999' }}>-</span>}
                      </Descriptions.Item>
                      <Descriptions.Item label="状态" span={1}>
                        <Tag color={statusColorGlobal(String(d?.status || ''))}>{statusTextGlobal(String(d?.status || ''))}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="创建者" span={1}>
                        {d?.created_by_username
                          || d?.created_by_detail?.username
                          || d?.created_by_detail?.name
                          || (d?.created_by_id != null ? `用户${d.created_by_id}` : '-')}
                      </Descriptions.Item>
                      <Descriptions.Item label="创建时间" span={1}>
                        {d?.created_at ? <TimezoneAwareTime time={d.created_at} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="备注" span={1}>
                        {d?.remarks ? <span style={{ color: '#cf1322', fontWeight: 700 }}>{d.remarks}</span> : '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                  {/* 操作对象（精简版） */}
                  <Card size="small">
                    <InstructionTargetsOverview
                      targets={Array.isArray(d?.targets_detail) ? d.targets_detail : []}
                      loading={reviewOverviewLoading}
                      page={overviewTargetsPage}
                      pageSize={overviewTargetsPageSize}
                      onPageChange={(p, ps) => { setOverviewTargetsPage(p); setOverviewTargetsPageSize(ps); }}
                      historyIocRows={overviewHistoryIocRows}
                      historyIocLoading={overviewHistoryIocLoading}
                      historyTitleText="操作对象(历史指令执行结果)"
                      headerAction={(
                        <Button
                          size="small"
                          style={{ borderColor: '#91caff', background: '#e6f4ff', color: '#0958d9' }}
                          onClick={() => {
                            try {
                              const targetRows: any[] = Array.isArray(d?.targets_detail) ? d.targets_detail : [];
                              const hasHistoryIoc = (targetRows || []).some((r:any) => String(r?.target_type || '').trim() === 'instruction_output');
                              const historyRows: any[] = Array.isArray(overviewHistoryIocRows) ? overviewHistoryIocRows : [];
                              const toCode = (row:any) => {
                                try {
                                  const snap = row?.snapshot || {};
                                  const code = snap.system_barcode || row?.inventory_unit_system_barcode || row?.target?.inventory_unit_system_barcode;
                                  return code || row?.inventory_unit_id || '-';
                                } catch { return '-'; }
                              };
                              const toItems = (row:any) => {
                                try {
                                  const items = extractOverviewUnitContentItems(row);
                                  const getAnyUnitId = () => {
                                    try { return row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? row?.snapshot?.inventory_unit_id; } catch { return undefined; }
                                  };
                                  let list = items;
                                  if ((!list || list.length === 0) && getAnyUnitId() != null) {
                                    const key = String(getAnyUnitId());
                                    const cached = overviewUnitContentCache[key];
                                    if (cached && cached.length > 0) list = cached;
                                  }
                                  if (!list || list.length === 0) return '-';
                                  return list.map((it:any) => `${it?.barcode || '-' } × ${Number(it?.qty || 0)}`).join('、');
                                } catch { return '-'; }
                              };
                              const toQty = (row:any) => Number(row?.expected_qty ?? 0) || 0;
                              const toAll = (row:any) => (!!row?.use_all_available ? '是' : '否');
                              const doc = window.open('', '_blank');
                              if (!doc || !doc.document) { message.error('请允许弹出窗口以预览打印'); return; }
                              const now = new Date();
                              const html = hasHistoryIoc ? `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>操作对象打印预览</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif; padding: 16px; color: #000; }
    h2 { margin: 0 0 12px; }
    .meta { color: #666; font-size: 12px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; vertical-align: top; }
    th { background: #f5f5f5; }
  </style>
</head>
<body>
  <h2>操作对象打印预览</h2>
  <div class="meta">生成时间：${now.toLocaleString()}</div>
  <table>
    <thead>
      <tr>
        <th>容器系统编码</th>
        <th>容器类型</th>
        <th>内容类型</th>
        <th>容器内容统计</th>
      </tr>
    </thead>
    <tbody>
      ${historyRows.map(r => {
        const code = String(r?.output_code || r?.display_code || '-');
        const typeRaw = String(r?.container_type_display || '').trim();
        const typeText = overviewContainerTypeTextMap[typeRaw.toLowerCase()] || typeRaw || '-';
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const kindText = String(r?.output_content_kind_display || (kind === 'exception' ? '异常内容容器' : '正常内容容器') || '-');
        const qty = Number(r?.output_contents_total_qty || 0);
        const rawSummary = String(r?.output_contents_summary || '').trim();
        const m = String(r?.output_contents_detail || '').split(/[；;]/).map((x:any)=> String(x || '').trim()).filter(Boolean).length;
        const summary = kind === 'exception'
          ? `${Number.isFinite(qty) ? qty : 0}件`
          : ((rawSummary && rawSummary.includes('款') && rawSummary.includes('件')) ? rawSummary : `${Math.max(0, m)}款${Math.max(0, qty)}件`);
        return `
        <tr>
          <td>${code}</td>
          <td>${typeText}</td>
          <td>${kindText}</td>
          <td>${summary}</td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>
  <script>
    (function () {
      function tryClose() {
        try { window.close(); } catch (e) {}
      }
      if ('onafterprint' in window) {
        window.onafterprint = function () { tryClose(); };
      }
      try { window.addEventListener('afterprint', tryClose); } catch (e) {}
      setTimeout(function () {
        try { window.print(); } catch (e) {}
        setTimeout(tryClose, 500);
      }, 50);
    })();
  </script>
</body>
</html>` : `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>操作对象打印预览</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif; padding: 16px; color: #000; }
    h2 { margin: 0 0 12px; }
    .meta { color: #666; font-size: 12px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; vertical-align: top; }
    th { background: #f5f5f5; }
  </style>
</head>
<body>
  <h2>操作对象打印预览</h2>
  <div class="meta">生成时间：${now.toLocaleString()}</div>
  <table>
    <thead>
      <tr>
        <th>库存单元系统编码</th>
        <th>库存单元内容项</th>
        <th>预计数量</th>
        <th>是否操作全部库存</th>
      </tr>
    </thead>
    <tbody>
      ${targetRows.map(r => `
        <tr>
          <td>${toCode(r)}</td>
          <td>${toItems(r)}</td>
          <td>${toQty(r)}</td>
          <td>${toAll(r)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  <script>
    (function () {
      function tryClose() {
        try { window.close(); } catch (e) {}
      }
      if ('onafterprint' in window) {
        window.onafterprint = function () { tryClose(); };
      }
      try { window.addEventListener('afterprint', tryClose); } catch (e) {}
      setTimeout(function () {
        try { window.print(); } catch (e) {}
        setTimeout(tryClose, 500);
      }, 50);
    })();
  </script>
</body>
</html>`;
                              doc.document.open();
                              doc.document.write(html);
                              doc.document.close();
                            } catch {
                              message.error('打印失败');
                            }
                          }}
                        >
                          打印
                        </Button>
                      )}
                      historyIocColumns={[
                        { title: '项目', key: 'project', render: (_:any, row:any) => {
                          const code = row?.project_code || row?.project_id;
                          const name = row?.project_name || '';
                          return code ? `${code}${name ? ` - ${name}` : ''}` : '-';
                        }},
                        { title: '容器系统编码', key: 'output_code', render: (_:any, row:any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.output_code || row?.display_code || '-')}</span> },
                        { title: '容器类型', dataIndex: 'container_type_display', width: 120, render: (v:any)=> {
                          const key = String(v || '').trim().toLowerCase();
                          return overviewContainerTypeTextMap[key] || String(v || '-');
                        }},
                        { title: '内容类型', dataIndex: 'output_content_kind_display', width: 140, render: (v:any, row:any)=> {
                          const kind = String(row?.output_content_kind || '').toLowerCase();
                          const isException = kind === 'exception';
                          return (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 4,
                              border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
                              background: isException ? '#fff7e6' : '#f6ffed',
                              color: isException ? '#fa8c16' : '#389e0d',
                              lineHeight: 1.5,
                            }}>
                              {String(v || '-')}
                            </span>
                          );
                        }},
                        { title: '容器内容统计', dataIndex: 'output_contents_summary', width: 160, render: (_:any, row:any) => {
                          const kind = String(row?.output_content_kind || '').toLowerCase();
                          const qty = Number(row?.output_contents_total_qty || 0);
                          if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
                          const raw = String(row?.output_contents_summary || '').trim();
                          if (raw && raw.includes('款') && raw.includes('件')) return raw;
                          const m = String(row?.output_contents_detail || '').split(/[；;]/).map((x:any)=> String(x || '').trim()).filter(Boolean).length;
                          return `${Math.max(0, m)}款${Math.max(0, qty)}件`;
                        }},
                        { title: '容器内容', dataIndex: 'output_contents_detail', width: 260, render: (v:any, row:any) => {
                          const kind = String(row?.output_content_kind || '').toLowerCase();
                          if (kind === 'exception') {
                            return (
                              <Tooltip title="无法操作等异常产品">
                                <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  无法操作等异常产品
                                </div>
                              </Tooltip>
                            );
                          }
                          const full = String(v || '').trim();
                          if (!full) return '-';
                          const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                          if (lines.length === 0) return '-';
                          const preview = lines.slice(0, 3);
                          const hasMore = lines.length > 3;
                          return (
                            <div style={{ maxWidth: 300 }}>
                              {preview.map((line, idx) => (
                                <div
                                  key={`${line}-${idx}`}
                                  style={{
                                    position: 'relative',
                                    minHeight: 20,
                                    marginBottom: idx === preview.length - 1 ? 0 : 2,
                                  }}
                                >
                                  <Tooltip title={line}>
                                    <div
                                      style={{
                                        color: '#595959',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                                      }}
                                    >
                                      {line}
                                    </div>
                                  </Tooltip>
                                  {hasMore && idx === preview.length - 1 ? (
                                    <Button
                                      type="link"
                                      size="small"
                                      style={{
                                        position: 'absolute',
                                        right: 0,
                                        bottom: -2,
                                        padding: 0,
                                        height: 'auto',
                                        lineHeight: 1.2,
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const iocCode = String(row?.output_code || row?.display_code || '-');
                                        Modal.info({
                                          title: (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                                              <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                                              <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                                              <span />
                                            </div>
                                          ),
                                          width: 760,
                                          centered: true,
                                          content: (
                                            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                              {lines.map((x, i) => (
                                                <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                                  {i + 1}. {x}
                                                </div>
                                              ))}
                                            </div>
                                          ),
                                        });
                                      }}
                                    >
                                      [...]
                                    </Button>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          );
                        }},
                        { title: '容器尺寸', key: 'dims', width: 180, render: (_:any, row:any) => (row?.length_cm != null && row?.width_cm != null && row?.height_cm != null) ? `${row.length_cm}×${row.width_cm}×${row.height_cm} cm` : '' },
                        { title: '容器重量', dataIndex: 'weight_g', width: 120, render: (v:any) => (v != null ? `${v}g` : '') },
                      ] as any}
                      inventoryColumns={[
                        { title: '项目', key: 'project', render: (_:any, row:any) => {
                          const snap = row?.snapshot || {};
                          const code = row?.project_code || snap.project_code || snap.code;
                          const name = row?.project_name || snap.project_name || snap.name;
                          return (code || name) ? `${code || ''}${name ? (code ? ' - ' : '') + name : ''}` : '-';
                        }},
                        { title: '类型', key: 'type', render: (_:any, row:any) => {
                          const v = String(row?.target_type || '').trim();
                          if (v === 'inventory_unit') {
                            const ut = (() => {
                              try { const snap = row?.snapshot || {}; return String(snap?.unit_type || row?.unit_type || row?.target?.unit_type || snap?.inventory_unit?.unit_type || ''); } catch { return ''; }
                            })();
                            const map: Record<string,string> = { LIU:'散货库存单元', BIU:'箱库存单元', PIU:'托盘库存单元', AIU:'异常库存单元', NIU:'非产品库存单元' };
                            return map[String(ut).toUpperCase()] || '库存单元';
                          }
                          const map2: Record<string,string> = { inventory_unit:'库存单元', sorting_group:'分拣组', project_all:'项目内全部', instruction_output:'上游指令产出' };
                          return map2[v] || v || '-';
                        }},
                        { title: '库存单元系统编码', key: 'inventory_unit', render: (_:any, row:any) => {
                          const snap = row?.snapshot || {};
                          const code = snap.system_barcode || row?.inventory_unit_system_barcode || row?.target?.inventory_unit_system_barcode;
                          return code || row?.inventory_unit_id || '-';
                        }},
                        { title: '分拣组', key: 'sorting_group', render: (_:any, row:any) => renderOverviewSortingGroup(row) },
                        { title: '库存单元内容项', key: 'inventory_unit_items', render: (_:any, row:any) => renderOverviewUnitContentItems(row) },
                        { title: '预计数量', dataIndex: 'expected_qty', key: 'expected_qty', render: (v:any) => (v ?? 0) },
                        { title: '是否操作全部库存', dataIndex: 'use_all_available', key: 'use_all_available', render: (v:any) => {
                          const isAll = !!v;
                          const text = isAll ? '是' : '否';
                          const style: React.CSSProperties = isAll
                            ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' }
                            : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' };
                          return <span style={style}>{text}</span>;
                        }},
                      ] as any}
                    />
                  </Card>
                  {/* 问题集：默认仅显示有答案的问题，支持开关显示全部 */}
                  <Card
                    size="small"
                    title={(
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12 }}>
                        <span>问题集</span>
                        <Space size={8}>
                          <span style={{ color: '#8c8c8c', fontSize: 12 }}>显示所有问题项</span>
                          <Switch size="small" checked={reviewOverviewShowAllQuestions} onChange={setReviewOverviewShowAllQuestions} />
                        </Space>
                      </div>
                    )}
                  >
                    {qs.length === 0 ? <div style={{ padding: '8px 0' }}>无</div> : (() => {
                      const visibleQs = (reviewOverviewShowAllQuestions
                        ? qs
                        : qs.filter((q:any) => {
                            const key = String(q?.key || '').trim();
                            return !isEmptyLike(ans?.[key]);
                          })
                      ) as any[];
                      const requiredQs = visibleQs.filter((q:any)=> !!q?.required);
                      const optionalQs = visibleQs.filter((q:any)=> !q?.required);
                      const renderBlock = (title:string, arr:any[]) => (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontWeight: 600, margin: '6px 0' }}>{title}</div>
                          <div className="space-y-2">
                            {arr.map((q:any, idx:number) => (
                              <div key={(q.key||'')+idx} style={{ display: 'grid', gridTemplateColumns: '280px auto 120px', alignItems: 'center', gap: 8 }}>
                                <div>
                                  {q.required ? <span style={{ color: '#ff4d4f' }}>*</span> : null}
                                  <span>{q.label || q.key || `问题${idx+1}`}</span>
                                </div>
                                <div>{renderQVal(q)}</div>
                                <div>
                                  {q.tips ? (
                                    <Tooltip title={q.tips}>
                                      <Button type="link" size="small">提示</Button>
                                    </Tooltip>
                                  ) : null}
                                </div>
                              </div>
                            ))}
                            {arr.length === 0 ? <div style={{ color:'#999' }}>无</div> : null}
                          </div>
                        </div>
                      );
                      return (
                        <div>
                          {renderBlock('必答', requiredQs)}
                          {renderBlock('选答', optionalQs)}
                        </div>
                      );
                    })()}
                  </Card>
                  {/* 附件：按必须/可选分组与批量下载 */}
                  <Card size="small" title="附件" extra={<Button size="small" onClick={async ()=>{
                    try {
                      const list = atts || [];
                      if (!list || list.length === 0) { message.info('无可下载附件'); return; }
                      const mod = await import('jszip');
                      const JSZip = (mod as any).default || mod;
                      const zip = new JSZip();
                      const addedNames = new Set<string>();
                      for (let i = 0; i < list.length; i++) {
                        const a = list[i];
                        const url = resolveAttachmentRuntimeUrl(a);
                        if (!url) continue;
                        const resp = await fetch(url, { credentials: 'include' as any });
                        if (!resp.ok) continue;
                        const blob = await resp.blob();
                        let name = String(getDisplayFileName(a) || a?.file_name || a?.file || a?.file_path || `附件_${i+1}`);
                        try {
                          const base = (name.split('?')[0] || name).split('#')[0];
                          const last = base.split('/').pop() || base;
                          name = decodeURIComponent(last);
                        } catch {}
                        if (addedNames.has(name)) {
                          const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
                          const base = ext ? name.slice(0, -ext.length) : name;
                          let idx = 2; let candidate = `${base}(${idx})${ext}`;
                          while (addedNames.has(candidate)) { idx++; candidate = `${base}(${idx})${ext}`; }
                          name = candidate;
                        }
                        addedNames.add(name);
                        zip.file(name, blob);
                      }
                      const zipBlob = await zip.generateAsync({ type: 'blob' });
                      const href = URL.createObjectURL(zipBlob);
                      const a = document.createElement('a');
                      a.href = href;
                      a.download = `${d?.system_code || d?.id || 'instruction'}-attachments.zip`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(href);
                    } catch {
                      message.error('批量下载失败');
                    }
                  }} style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}>批量下载</Button>}>
                    {(() => {
                      const ts2 = d?.template_snapshot || {};
                      const td2 = d?.template_detail || {};
                      const reqRoot = (ts2?.attachment_requirements || td2?.attachment_requirements || {}) as any;
                      const reqLabelSet = new Set<string>(); const reqMimeSet = new Set<string>(); const reqExtSet = new Set<string>();
                      const normalizeLocal = (v:any) => String(v || '').trim().toLowerCase();
                      const collectRequiredLocal = (node:any, scope?:string) => {
                        if (!node) return;
                        const sc = node.scope || scope;
                        const min = Number(node.min ?? node.min_count ?? 0);
                        const reqArr = Array.isArray(node.required) ? node.required : [];
                        reqArr.forEach((it:any) => {
                          if (typeof it === 'string') reqLabelSet.add(normalizeLocal(it));
                          else if (it && typeof it === 'object') {
                            if (it.label) reqLabelSet.add(normalizeLocal(it.label));
                            if (it.mime_type) reqMimeSet.add(normalizeLocal(it.mime_type));
                            if (it.ext) {
                              const ex = normalizeLocal(it.ext);
                              reqExtSet.add(ex);
                              reqExtSet.add(ex.replace(/^\./,''));
                            }
                          }
                        });
                        // 仅当该节点自身为"必交"时，才采集其 label/mime_type/ext 作为必交判定依据
                        if (node.required === true || (!isNaN(min) && min >= 1)) {
                          if (node.label) reqLabelSet.add(normalizeLocal(node.label));
                          if (node.mime_type) reqMimeSet.add(normalizeLocal(node.mime_type));
                          if (node.ext) {
                            const ex2 = normalizeLocal(node.ext);
                            reqExtSet.add(ex2);
                            reqExtSet.add(ex2.replace(/^\./,''));
                          }
                        }
                        if (Array.isArray(node)) node.forEach((child)=> collectRequiredLocal(child, sc));
                        else if (typeof node === 'object') {
                          Object.entries(node).forEach(([key, child]) => {
                            const nextScope = ['order','orders','action','actions','*'].includes(String(key)) ? String(key) : sc;
                            collectRequiredLocal(child, nextScope);
                          });
                        }
                      };
                      try { collectRequiredLocal(reqRoot); } catch {}
                      const isReq = (a:any) => {
                        const lbl = normalizeLocal(a?.label);
                        const mime = normalizeLocal(a?.mime_type);
                        // 使用可读文件名以规避存储随机后缀的干扰
                        const name = String(getDisplayFileName(a) || a?.file_name || a?.file || a?.file_path || '');
                        const ext = normalizeLocal(name.includes('.') ? name.split('.').pop() : '');
                        if (lbl && reqLabelSet.has(lbl)) return true;
                        if (mime && reqMimeSet.has(mime)) return true;
                        if (ext && (reqExtSet.has(ext) || reqExtSet.has(ext.replace(/^\./,'')))) return true;
                        return false;
                      };
                      let reqAtts: any[] = [];
                      let optAtts: any[] = [];
                      if (Array.isArray(atts) && atts.length > 0 && typeof (atts[0] as any)?.required === 'boolean') {
                        reqAtts = atts.filter((a:any)=> a.required === true);
                        optAtts = atts.filter((a:any)=> a.required !== true);
                      } else {
                        reqAtts = atts.filter((a:any)=> isReq(a));
                        optAtts = atts.filter((a:any)=> !isReq(a));
                      }
                      const paperSizeLabel = (v: string) => {
                        if (v === '4x6_thermal') return '4×6英寸热敏纸打印';
                        if (v === 'A4') return 'A4激光打印';
                        return v || '-';
                      };
                      const placementLabel = (v: string) => {
                        if (v === 'inside') return '放入包裹内';
                        if (v === 'outside') return '贴在包裹外';
                        return v || '-';
                      };
                      const buildPrintText = (a: any) => {
                        const pc = a?.meta?.print_config;
                        if (!pc) return '';
                        if (pc.print) return `打印设置：${pc.copies ?? '-'} 份 · ${paperSizeLabel(pc.paper_size)} · ${placementLabel(pc.placement)}`;
                        return '打印设置：不打印';
                      };
                      return (
                        <div className="space-y-4" style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 8 }}>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', marginBottom: 6 }}>
                              <div style={{ fontWeight: 600, display:'inline-flex', alignItems:'center', gap: 8 }}>
                                <span>必须提交</span>
                                <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:600 }}>
                                  已提交 {reqAtts.length}
                                </span>
                              </div>
                            </div>
                            <Descriptions size="small" column={1} bordered>
                              {reqAtts.map((att:any)=>(
                                <Descriptions.Item key={att.id} label={`${att.label || att.mime_type || '附件'} (${att.scope || 'order'})`}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
                                      <span>{getDisplayFileName(att)}</span>
                                      {buildPrintText(att) ? (
                                        <span style={{ fontSize: 12, color: '#ff4d4f' }}>{buildPrintText(att)}</span>
                                      ) : null}
                                    </div>
                                    <Space>
                                      <Button size="small" onClick={() => { void previewOverviewAttachment(att); }}>预览</Button>
                                      <Button size="small" onClick={() => handleDownloadAttachment(att)} style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}>下载</Button>
                                    </Space>
                                  </div>
                                </Descriptions.Item>
                              ))}
                              {reqAtts.length === 0 && <Descriptions.Item label="附件">无</Descriptions.Item>}
                            </Descriptions>
                          </div>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-start', marginBottom: 6 }}>
                              <div style={{ fontWeight: 600, display:'inline-flex', alignItems:'center', gap: 8 }}>
                                <span>可选择提交</span>
                                <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #ffe58f', background:'#fffbe6', color:'#ad8b00', borderRadius:4, fontWeight:600 }}>
                                  已提交 {optAtts.length}
                                </span>
                              </div>
                            </div>
                            <Descriptions size="small" column={1} bordered>
                              {optAtts.map((att:any)=>(
                                <Descriptions.Item key={att.id} label={`${att.label || att.mime_type || '附件'} (${att.scope || 'order'})`}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
                                      <span>{getDisplayFileName(att)}</span>
                                      {buildPrintText(att) ? (
                                        <span style={{ fontSize: 12, color: '#ff4d4f' }}>{buildPrintText(att)}</span>
                                      ) : null}
                                    </div>
                                    <Space>
                                      <Button size="small" onClick={() => { void previewOverviewAttachment(att); }}>预览</Button>
                                      <Button size="small" onClick={async () => {
                                        try {
                                          const url = resolveAttachmentRuntimeUrl(att);
                                          const name = getDisplayFileName(att) || att?.file_name || '附件';
                                          if (!url) { message.warning('无可用文件'); return; }
                                          const resp = await fetch(url, { credentials: 'include' as any });
                                          if (!resp.ok) throw new Error('下载失败');
                                          const blob = await resp.blob();
                                          const href = URL.createObjectURL(blob);
                                          const anchorEl = document.createElement('a');
                                          anchorEl.href = href;
                                          anchorEl.download = name;
                                          document.body.appendChild(anchorEl);
                                          anchorEl.click();
                                          document.body.removeChild(anchorEl);
                                          URL.revokeObjectURL(href);
                                        } catch { message.error('下载失败'); }
                                      }} style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}>下载</Button>
                                    </Space>
                                  </div>
                                </Descriptions.Item>
                              ))}
                              {optAtts.length === 0 && <Descriptions.Item label="附件">无</Descriptions.Item>}
                            </Descriptions>
                          </div>
                        </div>
                      );
                    })()}
                  </Card>
                </div>
              );
            })()
          )}
        </Modal>
        <Modal
          open={ovAttPreviewOpen}
          onCancel={closeOverviewAttachmentPreview}
          footer={(
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={closeOverviewAttachmentPreview}>关闭</Button>
            </div>
          )}
          width={1280}
          zIndex={1090}
          destroyOnClose
          title={<span style={{ color: '#1677ff', fontWeight: 700 }}>{ovAttPvTitle || '附件预览'}</span>}
        >
          {ovAttPvLoading ? <div style={{ color:'#999' }}>加载中...</div> : (
            ovAttPvError ? <div style={{ color:'#cf1322' }}>{ovAttPvError}</div> : (
              ovAttPvKind === 'pdf' ? (
                <iframe title="overview-pdf" src={ovAttPvUrl} style={{ width:'100%', height: 680, border: 0 }} />
              ) : ovAttPvKind === 'image' ? (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
                  <img alt="overview-image" src={ovAttPvUrl} style={{ maxWidth:'100%', maxHeight: 680, objectFit: 'contain' }} />
                </div>
              ) : ovAttPvKind === 'video' && ovAttPvUrl ? (
                <div>
                  <video key={ovAttPvUrl} src={ovAttPvUrl} controls preload="metadata" style={{ width: '100%', maxHeight: '70vh', background: '#000', borderRadius: 4 }} />
                  <Space style={{ marginTop: 12 }} wrap>
                    <Button
                      loading={ovAttPvVideoDownloading}
                      onClick={async () => {
                        setOvAttPvVideoDownloading(true);
                        try { await downloadFileAsBlob(ovAttPvUrl, ovAttPvVideoName); } catch { message.error('下载失败'); } finally { setOvAttPvVideoDownloading(false); }
                      }}
                    >下载</Button>
                    <Button onClick={() => window.open(ovAttPvUrl, '_blank')}>在新窗口打开</Button>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>如果无法播放（例如 iPhone 拍摄的 HEVC 编码 .mov），请下载后用本地播放器打开。</span>
                  </Space>
                </div>
              ) : ovAttPvKind === 'csv' || ovAttPvKind === 'xlsx' ? (
                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
                    <div style={{ color: '#666' }}>
                      {ovAttPvKind === 'xlsx' && ovAttPvSheetName ? <span>工作表：{ovAttPvSheetName}</span> : <span>可预览表格</span>}
                    </div>
                  </div>
                  <Table
                    size="small"
                    rowKey={(r:any, i?:number)=> String(r?.__idx ?? i ?? 0)}
                    dataSource={ovAttPvData}
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: true,
                      pageSizeOptions: [10, 20, 50, 100] as any,
                    }}
                    columns={ovAttPvCols as any}
                    scroll={{ x: 'max-content', y: 600 }}
                    tableLayout="auto"
                  />
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 8, color:'#666' }}>无法在线解析该格式，点击下方打开原文件：</div>
                  <Button type="link" onClick={()=> { if (ovAttPvUrl) window.open(ovAttPvUrl, '_blank'); }}>在新窗口打开</Button>
                </div>
              )
            )
          )}
        </Modal>
        <Modal
          title={(() => {
            // 计算是否选择了压缩文件（压缩文件不能采纳为基线文件）
            const dataset = getAttachPickerVisibleRows();
            const selectedRows = dataset.filter((a:any)=> attachPickerSelectedIds.has(Number(a?.id)));
            const isCompressedName = (name?: string) => {
              try {
                const s = String(name || '').toLowerCase();
                if (!s) return false;
                if (/\.(zip|rar|7z|tar)$/.test(s)) return true;
                if (/\.(tar\.gz|tgz|tar\.bz2|tbz2|tar\.xz|txz)$/.test(s)) return true;
                return false;
              } catch { return false; }
            };
            const hasCompressedSelected = selectedRows.some((r:any) => isCompressedName(r?.file_name || r?.file || r?.file_path));
            const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
            return (
              <div style={{ position:'relative', display:'flex', alignItems:'center', justifyContent:'space-between', paddingRight: 60 }}>
                <span>从客户上传附件中选择</span>
                {hasCompressedSelected ? (
                  <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', color:'#cf1322', fontWeight: 700 }}>
                    压缩文件无法采纳为基线文件
                  </div>
                ) : null}
                <div style={{ display:'inline-flex', gap: 8 }}>
                  {isBm ? (
                    <span style={{ display:'inline-block', padding:'2px 8px', background:'#fffb8f', color:'#cf1322', borderRadius: 4 }}>
                      请点击预览实现数据审核及操作对象匹配
                    </span>
                  ) : null}
                  <Button
                    size="small"
                    disabled={attachPickerSelectedIds.size === 0}
                    style={attachPickerSelectedIds.size === 0 ? undefined : { borderColor: '#faad14', background: '#fff7e6', color: '#d48806' }}
                    onClick={async () => {
                    try {
                      const dataset = getAttachPickerVisibleRows();
                      const selected = dataset.filter((a:any)=> attachPickerSelectedIds.has(Number(a?.id)));
                      if (selected.length === 0) { message.info('请先勾选要下载的附件'); return; }
                      const mod = await import('jszip');
                      const JSZip = (mod as any).default || mod;
                      const zip = new JSZip();
                      const addedNames = new Set<string>();
                      for (let i = 0; i < selected.length; i++) {
                        const a = selected[i];
                        const idStr = String(a?.id ?? '');
                        const ov = editedAttachMap[idStr];
                        let blob: Blob | null = null;
                        let name = '';
                        if (ov?.blob) {
                          blob = ov.blob;
                          name = ov.fileName || String(getDisplayFileName(a) || a?.file_name || `附件_${i+1}`);
                        } else {
                          const url = resolveAttachmentRuntimeUrl(a);
                          if (!url) continue;
                          const resp = await fetch(url, { credentials: 'include' as any });
                          if (!resp.ok) continue;
                          blob = await resp.blob();
                          name = String(getDisplayFileName(a) || a?.file_name || a?.file || a?.file_path || `附件_${i+1}`);
                        }
                        try {
                          const base = (name.split('?')[0] || name).split('#')[0];
                          const last = base.split('/').pop() || base;
                          name = decodeURIComponent(last);
                        } catch {}
                        if (addedNames.has(name)) {
                          const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
                          const base = ext ? name.slice(0, -ext.length) : name;
                          let idx = 2; let candidate = `${base}(${idx})${ext}`;
                          while (addedNames.has(candidate)) { idx++; candidate = `${base}(${idx})${ext}`; }
                          name = candidate;
                        }
                        addedNames.add(name);
                        zip.file(name, blob);
                      }
                      const zipBlob = await zip.generateAsync({ type: 'blob' });
                      const href = URL.createObjectURL(zipBlob);
                      const a = document.createElement('a');
                      a.href = href;
                      const sysCode = String(reviewOf?.system_code || 'instruction');
                      a.download = `${sysCode}-selected-attachments.zip`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(href);
                    } catch {
                      message.error('批量下载失败');
                    }
                    }}
                  >
                    批量下载
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    disabled={attachPickerSelectedIds.size === 0 || hasCompressedSelected}
                    onClick={async () => {
                    try {
                      const baselineId = attachPickerBaseline?.id;
                      if (!baselineId) { message.warning('请先创建草稿'); return; }
                      const dataset = getAttachPickerVisibleRows();
                      const selected = dataset.filter((a:any)=> attachPickerSelectedIds.has(Number(a?.id)));
                      if (selected.length === 0) { message.info('请先勾选要采纳的附件'); return; }
                      // 如仍包含压缩文件，直接提示并返回（双重校验）
                      const hasZip = selected.some((r:any) => {
                        const s = String(r?.file_name || r?.file || r?.file_path || '').toLowerCase();
                        return /\.(zip|rar|7z|tar)$/.test(s) || /\.(tar\.gz|tgz|tar\.bz2|tbz2|tar\.xz|txz)$/.test(s);
                      });
                      if (hasZip) { message.warning('选中内容包含压缩文件，无法采纳为基线文件'); return; }
                      let ok = 0, skip = 0;
                      for (const r of selected) {
                        const idStr = String(r?.id ?? '');
                        const edited = String((attachNameEdits[idStr] ?? '')).trim();
                        const baseName = getDisplayFileName(r) || '';
                        const needName = edited && edited !== baseName ? edited : undefined;
                        // 已采纳的跳过
                        const adopted = (() => {
                          try {
                            const bId = attachPickerBaseline?.id;
                            const b = (baselineRows || []).find((x:any)=> Number(x?.id) === Number(bId));
                            const files = Array.isArray(b?.files) ? b.files : [];
                            return isAttachmentAdoptedByFiles(files, r);
                          } catch { return false; }
                        })();
                        if (adopted) { skip += 1; continue; }
                        try { await adoptAttachmentAsBaseline(Number(baselineId), r, needName); ok += 1; } catch {}
                      }
                      message.success(`批量采纳完成：成功 ${ok}${skip ? `，跳过已采纳 ${skip}` : ''}`);
                      // 采纳后刷新并保留弹窗
                      await loadBaselines(reviewOf?.id);
                    } catch {}
                    }}
                  >
                    批量采纳为基线文件
                  </Button>
                </div>
              </div>
            );
          })()}
          open={attachPickerOpen}
          onCancel={async () => {
            const closeNow = () => { setAttachPickerOpen(false); setAttachPickerBaseline(null); };
            try {
              const baselineId = Number(attachPickerBaseline?.id || 0);
              const visibleRows = getAttachPickerVisibleRows();
              if (!visibleRows.length) { closeNow(); return; }
              // 未采纳判断：不在该基线 files 列表中
              const files = (() => {
                try {
                  const b = (baselineRows || []).find((x:any)=> Number(x?.id) === Number(baselineId));
                  return Array.isArray(b?.files) ? b.files : [];
                } catch { return []; }
              })();
              const isAdopted = (r:any) => {
                try {
                  if (!baselineId) return false;
                  return isAttachmentAdoptedByFiles((files || []) as any, r);
                } catch { return false; }
              };
              const unadopted = visibleRows.filter((r:any) => !isAdopted(r));
              if (!unadopted.length) { closeNow(); return; }

              // 三选项：全部采纳并关闭 / 直接关闭 / 返回修改
              const inst = Modal.confirm({
                title: '存在未采纳为基线文件的记录',
                icon: null,
                width: 860,
                centered: true,
                maskClosable: true,
                content: (
                  <div>
                    <div style={{ color:'#595959', marginBottom: 10, lineHeight: 1.6 }}>
                      当前文件列表中存在<span style={{ fontWeight: 700 }}>“未采纳为基线文件”</span>的记录，是否全部采纳为基线文件并关闭？
                    </div>
                    <div style={{ display:'flex', justifyContent:'flex-end', gap: 8, flexWrap:'wrap', maxWidth:'100%' }}>
                      <Button
                        onClick={() => { try { inst.destroy(); } catch {} }}
                      >
                        返回修改
                      </Button>
                      <Button
                        onClick={() => {
                          try { inst.destroy(); } catch {}
                          closeNow();
                        }}
                      >
                        直接关闭
                      </Button>
                      <Button
                        type="primary"
                        style={{ maxWidth: 360, whiteSpace: 'normal', height: 'auto', lineHeight: '16px', padding: '6px 12px' }}
                        onClick={async () => {
                          try {
                            if (!baselineId) { message.warning('请先创建草稿'); return; }
                            // 若包含压缩包：不支持一键采纳（需要逐个“解压并采纳”）
                            const hasArc = unadopted.some((r:any) => isArchiveByName(r?.file_name || r?.file || r?.file_path));
                            if (hasArc) {
                              message.warning('列表包含压缩包，无法一键采纳，请逐个“解压并采纳为基线文件”后再关闭');
                              return;
                            }
                            let ok = 0, skip = 0;
                            for (const r of unadopted) {
                              const idStr = String(r?.id ?? '');
                              const edited = String((attachNameEdits[idStr] ?? '')).trim();
                              const baseName = getDisplayFileName(r) || '';
                              const needName = edited && edited !== baseName ? edited : undefined;
                              try { await adoptAttachmentAsBaseline(Number(baselineId), r, needName); ok += 1; } catch { skip += 1; }
                            }
                            try { await loadBaselines(reviewOf?.id); } catch {}
                            message.success(`已全部采纳为基线文件：成功 ${ok}${skip ? `，失败 ${skip}` : ''}`);
                            try { inst.destroy(); } catch {}
                            closeNow();
                          } catch {
                            message.error('一键采纳失败');
                          }
                        }}
                      >
                        全部采纳为基线文件并关闭
                      </Button>
                    </div>
                  </div>
                ),
                okButtonProps: { style: { display: 'none' } },
                cancelButtonProps: { style: { display: 'none' } },
              });
            } catch {
              closeNow();
            }
          }}
          footer={null}
          width={900}
          zIndex={1000}
        >
          <div style={{ marginBottom: 8, color: '#666' }}>
            {attachPickerBaseline?.acceptExts?.length
              ? <>仅显示匹配格式：{attachPickerBaseline.acceptExts.join(', ')}</>
              : <>未限制格式，显示该指令的全部客户附件</>
            }
          </div>
          {(() => {
            const visibleRows = getAttachPickerVisibleRows();
            const total = visibleRows.length;
            const pageSize = Math.max(1, Number(attachPickerPageSize || 8) || 8);
            const maxPage = Math.max(1, Math.ceil(total / pageSize));
            const curPage = Math.min(Math.max(1, Number(attachPickerPage || 1) || 1), maxPage);
            const pageRows = visibleRows.slice((curPage - 1) * pageSize, curPage * pageSize);
            return (
              <div>
                <Table
                  size="small"
                  rowKey={(r:any, i?:number)=> String(r?.id ?? i ?? 0)}
                  loading={attachPickerLoading}
                  dataSource={pageRows}
                  pagination={false}
                  columns={[
              { title: (() => {
                  const idsAll = new Set<number>(visibleRows.map((r:any)=> Number(r?.id)).filter((n:number)=>Number.isFinite(n)));
                  const total = idsAll.size;
                  const selectedCnt = Array.from(attachPickerSelectedIds).filter(id => idsAll.has(id)).length;
                  const checked = total > 0 && selectedCnt === total;
                  const indeterminate = selectedCnt > 0 && selectedCnt < total;
                  return (
                    <Checkbox
                      indeterminate={indeterminate}
                      checked={checked}
                      onChange={(e)=> {
                        const next = new Set<number>(attachPickerSelectedIds);
                        if (e.target.checked) {
                          idsAll.forEach(id => next.add(id));
                        } else {
                          idsAll.forEach(id => next.delete(id));
                        }
                        setAttachPickerSelectedIds(next);
                      }}
                    />
                  );
                })(), dataIndex: '__pick__', width: 70, render: (_:any, r:any) => {
                const id = Number(r?.id);
                const checked = attachPickerSelectedIds.has(id);
                return (
                  <Checkbox
                    checked={checked}
                    onChange={(e)=> {
                      setAttachPickerSelectedIds(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(id); else next.delete(id);
                        return next;
                      });
                    }}
                  />
                );
              }},
              { title: '文件名', dataIndex: 'file_name', render: (_:any, r:any) => {
                const id = String(r?.id ?? '');
                const base = getDisplayFileName(r);
                const val = (attachNameEdits[id] ?? base) as string;
                const ov = editedAttachMap[id];
                return (
                  <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                    <Input
                      size="small"
                      value={val}
                      onChange={(e)=> setAttachNameEdits(prev => ({ ...prev, [id]: e.target.value }))}
                      placeholder={base}
                    />
                    {ov ? <Tag color="green">已编辑</Tag> : null}
                  </div>
                );
              }},
              { title: '类型', dataIndex: 'mime_type', width: 120, render: (v:any, r:any) => extToDisplay(getExtLower(r?.file_name || r?.file || r?.file_path), v) },
              { title: '上传时间', dataIndex: 'created_at', width: 180, render: (v:any)=> v ? String(v).replace('T',' ').replace('+08:00','') : '-' },
              { title: '操作', key: 'ops', width: 220, render: (_:any, r:any) => {
                const baselineId = attachPickerBaseline?.id;
                const adopted = (() => {
                  try {
                    const bId = attachPickerBaseline?.id;
                    const b = (baselineRows || []).find((x:any)=> Number(x?.id) === Number(bId));
                    const files = Array.isArray(b?.files) ? b.files : [];
                    return isAttachmentAdoptedByFiles(files, r);
                  } catch { return false; }
                })();
                const isArchive = isArchiveByName(r?.file_name || r?.file || r?.file_path);
                return (
                  <Space>
                    <Button size="small" onClick={() => previewAttachment(r)} style={{ borderColor: '#91caff', background: '#e6f4ff', color: '#0958d9' }}>预览</Button>
                    <Button size="small" onClick={async () => {
                      try {
                        const id = String(r?.id ?? '');
                        const ov = editedAttachMap[id];
                        let dlBlob: Blob;
                        let name = '';
                        if (ov?.blob) {
                          dlBlob = ov.blob;
                          name = ov.fileName || getDisplayFileName(r) || r?.file_name || '附件';
                        } else {
                          const url = resolveAttachmentRuntimeUrl(r);
                          name = getDisplayFileName(r) || r?.file_name || '附件';
                          if (!url) { message.warning('无可用文件'); return; }
                          const resp = await fetch(url, { credentials: 'include' as any });
                          if (!resp.ok) throw new Error('下载失败');
                          dlBlob = await resp.blob();
                        }
                        const href = URL.createObjectURL(dlBlob);
                        const a = document.createElement('a');
                        a.href = href;
                        a.download = name;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(href);
                      } catch { message.error('下载失败'); }
                    }} style={{ borderColor: '#faad14', background: '#fff7e6', color: '#d48806' }}>下载</Button>
                    <Button
                      size="small"
                      type={adopted ? 'default' as any : 'primary' as any}
                      style={adopted ? { background:'#52c41a', borderColor:'#52c41a', color:'#fff' } : undefined}
                      onClick={() => {
                        if (!baselineId) return;
                        if (isArchive) {
                          adoptCompressedAttachmentEntriesAsBaseline(baselineId, r);
                          return;
                        }
                        const id = String(r?.id ?? '');
                        const edited = String((attachNameEdits[id] ?? '')).trim();
                        const base = getDisplayFileName(r) || '';
                        const needName = edited && edited !== base ? edited : undefined;
                        adoptAttachmentAsBaseline(baselineId, r, needName);
                      }}
                    >
                      {isArchive ? '解压并采纳为基线文件' : (adopted ? '已采纳为基线' : '采纳为基线')}
                    </Button>
                  </Space>
                );
              }},
                  ] as any}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                  <Pagination
                    size="small"
                    current={curPage}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    pageSizeOptions={['8', '10', '20', '50', '100'] as any}
                    showTotal={(t:number, range:[number, number]) => `共${t}条记录，显示${range[0]}-${range[1]}条`}
                    onChange={(p:number, ps:number) => {
                      setAttachPickerPage(p);
                      setAttachPickerPageSize(ps);
                    }}
                  />
                  <Button onClick={async () => {
                    // 与 onCancel 保持一致
                    const closeNow = () => { setAttachPickerOpen(false); setAttachPickerBaseline(null); };
                    try {
                      const baselineId = Number(attachPickerBaseline?.id || 0);
                      const visibleRows = getAttachPickerVisibleRows();
                      if (!visibleRows.length) { closeNow(); return; }
                      const files = (() => {
                        try {
                          const b = (baselineRows || []).find((x:any)=> Number(x?.id) === Number(baselineId));
                          return Array.isArray(b?.files) ? b.files : [];
                        } catch { return []; }
                      })();
                      const isAdopted = (r:any) => {
                        try {
                          if (!baselineId) return false;
                          return isAttachmentAdoptedByFiles((files || []) as any, r);
                        } catch { return false; }
                      };
                      const unadopted = visibleRows.filter((r:any) => !isAdopted(r));
                      if (!unadopted.length) { closeNow(); return; }

                      const inst = Modal.confirm({
                        title: '存在未采纳为基线文件的记录',
                        icon: null,
                        width: 860,
                        centered: true,
                        maskClosable: true,
                        content: (
                          <div>
                            <div style={{ color:'#595959', marginBottom: 10, lineHeight: 1.6 }}>
                              当前文件列表中存在<span style={{ fontWeight: 700 }}>“未采纳为基线文件”</span>的记录，是否全部采纳为基线文件并关闭？
                            </div>
                            <div style={{ display:'flex', justifyContent:'flex-end', gap: 8, flexWrap:'wrap', maxWidth:'100%' }}>
                              <Button onClick={() => { try { inst.destroy(); } catch {} }}>返回修改</Button>
                              <Button onClick={() => { try { inst.destroy(); } catch {} ; closeNow(); }}>直接关闭</Button>
                              <Button type="primary" style={{ maxWidth: 360, whiteSpace: 'normal', height: 'auto', lineHeight: '16px', padding: '6px 12px' }} onClick={async () => {
                                try {
                                  if (!baselineId) { message.warning('请先创建草稿'); return; }
                                  const hasArc = unadopted.some((r:any) => isArchiveByName(r?.file_name || r?.file || r?.file_path));
                                  if (hasArc) {
                                    message.warning('列表包含压缩包，无法一键采纳，请逐个“解压并采纳为基线文件”后再关闭');
                                    return;
                                  }
                                  let ok = 0, skip = 0;
                                  for (const r of unadopted) {
                                    const idStr = String(r?.id ?? '');
                                    const edited = String((attachNameEdits[idStr] ?? '')).trim();
                                    const baseName = getDisplayFileName(r) || '';
                                    const needName = edited && edited !== baseName ? edited : undefined;
                                    try { await adoptAttachmentAsBaseline(Number(baselineId), r, needName); ok += 1; } catch { skip += 1; }
                                  }
                                  try { await loadBaselines(reviewOf?.id); } catch {}
                                  message.success(`已全部采纳为基线文件：成功 ${ok}${skip ? `，失败 ${skip}` : ''}`);
                                  try { inst.destroy(); } catch {}
                                  closeNow();
                                } catch { message.error('一键采纳失败'); }
                              }}>全部采纳为基线文件并关闭</Button>
                            </div>
                          </div>
                        ),
                        okButtonProps: { style: { display: 'none' } },
                        cancelButtonProps: { style: { display: 'none' } },
                      });
                    } catch {
                      closeNow();
                    }
                  }}>
                    关闭
                  </Button>
                </div>
              </div>
            );
          })()}
        </Modal>
        {/* 附件预览 */}
        <Modal
          open={attPreviewOpen}
          onCancel={async () => {
            // 条目视图 -> 返回列表视图（不触发关闭确认）
            if (attPvUrl && attPvUrl.startsWith('blob:')) {
              try { URL.revokeObjectURL(attPvUrl); } catch {}
              if (attPvArchList && attPvArchList.length > 0 && attPvArchiveRoot) {
                setAttPvKind('other');
                setAttPvUrl(attPvArchiveRoot);
                setAttPvEntryName('');
                return;
              }
            }

            const closeNow = () => {
              setAttPreviewOpen(false);
              setAttPvArchiveRoot('');
              setAttPvEntryName('');
            };

            // 对 BARCODE_MAPPING / SHIPPING_LABEL_TARGET_MAPPING 的表格预览：关闭前自动执行一次“再次审核”
            try {
              const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
              const isSltm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
              if (!((isBm || isSltm) && (attPvKind === 'csv' || attPvKind === 'xlsx'))) { closeNow(); return; }

              // 未保存编辑：关闭前提示“保存并关闭页面 / 返回”，支持自动保存
              if (attPvDirty) {
                const ok = await new Promise<boolean>((resolve) => {
                  const inst = Modal.confirm({
                    title: '存在未保存的编辑，是否保存并关闭？',
                    content: '检测到当前预览内容有未保存修改。你可以选择“保存并关闭页面”自动保存，也可以选择“返回”继续编辑。',
                    okText: '保存并关闭页面',
                    cancelText: '返回',
                    onOk: async () => {
                      try { await saveEditedPreviewAsOverlay(); resolve(true); } catch { resolve(false); }
                    },
                    onCancel: () => resolve(false),
                    footer: (
                      <div style={{ display:'flex', justifyContent:'flex-end', gap: 8 }}>
                        <Button onClick={() => { try { inst.destroy(); } catch {} ; resolve(false); }}>
                          返回
                        </Button>
                        <Button danger onClick={() => { try { inst.destroy(); } catch {} ; resolve(true); }}>
                          直接关闭
                        </Button>
                        <Button type="primary" onClick={async () => {
                          try { await saveEditedPreviewAsOverlay(); resolve(true); } catch { resolve(false); }
                          try { inst.destroy(); } catch {}
                        }}>
                          保存并关闭页面
                        </Button>
                      </div>
                    ),
                  });
                });
                if (!ok) return;
              }

              let hasErr = false;
              if (isBm) {
                // 执行一次“再次审核”（内容审核 + 操作对象匹配）
                const auditRes: any = runBmAuditFromCurrentTable();
                const matchRes: any = await matchBmTargetsFromCurrentTable();
                const noTargetLen = Array.isArray(matchRes?.noTargetRows)
                  ? matchRes.noTargetRows.length
                  : (attPvBmAudit?.noTargetRows?.length ?? 0);
                hasErr = (
                  noTargetLen > 0
                  || (auditRes?.newEmptyRows?.length || 0) > 0
                  || (auditRes?.sameRows?.length || 0) > 0
                  || (auditRes?.dupPairs?.length || 0) > 0
                  || (auditRes?.badRows?.length || 0) > 0
                );
              } else if (isSltm) {
                const auditRes: any = runSltmAuditFromCurrentTable();
                hasErr = (
                  (auditRes?.incompleteRows?.length || 0) > 0
                  || (auditRes?.invalidBRows?.length || 0) > 0
                  || (auditRes?.dupC?.length || 0) > 0
                  || (auditRes?.dupD?.length || 0) > 0
                );
              }
              if (!hasErr) { closeNow(); return; }

              const ok = await new Promise<boolean>((resolve) => {
                Modal.confirm({
                  title: '仍存在审核错误，确认关闭？',
                  content: '关闭后将退出预览。若要修复，请先在预览中修改并保存编辑后再关闭。',
                  okText: '确认关闭',
                  cancelText: '取消',
                  onOk: () => resolve(true),
                  onCancel: () => resolve(false),
                });
              });
              if (ok) closeNow();
            } catch {
              // 审核异常时不阻断关闭
              closeNow();
            }
          }}
          footer={(
            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <Button onClick={async () => {
                // 与 onCancel 保持一致
                if (attPvUrl && attPvUrl.startsWith('blob:')) {
                  try { URL.revokeObjectURL(attPvUrl); } catch {}
                  if (attPvArchList && attPvArchList.length > 0 && attPvArchiveRoot) {
                    setAttPvKind('other');
                    setAttPvUrl(attPvArchiveRoot);
                    setAttPvEntryName('');
                    return;
                  }
                }

                const closeNow = () => {
                  setAttPreviewOpen(false);
                  setAttPvArchiveRoot('');
                  setAttPvEntryName('');
                };

                try {
                  const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
                  const isSltm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
                  if (!((isBm || isSltm) && (attPvKind === 'csv' || attPvKind === 'xlsx'))) { closeNow(); return; }

                  // 未保存编辑：关闭前提示“保存并关闭页面 / 返回”，支持自动保存
                  if (attPvDirty) {
                    const ok = await new Promise<boolean>((resolve) => {
                      const inst = Modal.confirm({
                        title: '存在未保存的编辑，是否保存并关闭？',
                        content: '检测到当前预览内容有未保存修改。你可以选择“保存并关闭页面”自动保存，也可以选择“返回”继续编辑。',
                        okText: '保存并关闭页面',
                        cancelText: '返回',
                        onOk: async () => {
                          try { await saveEditedPreviewAsOverlay(); resolve(true); } catch { resolve(false); }
                        },
                        onCancel: () => resolve(false),
                        footer: (
                          <div style={{ display:'flex', justifyContent:'flex-end', gap: 8 }}>
                            <Button onClick={() => { try { inst.destroy(); } catch {} ; resolve(false); }}>
                              返回
                            </Button>
                            <Button danger onClick={() => { try { inst.destroy(); } catch {} ; resolve(true); }}>
                              直接关闭
                            </Button>
                            <Button type="primary" onClick={async () => {
                              try { await saveEditedPreviewAsOverlay(); resolve(true); } catch { resolve(false); }
                              try { inst.destroy(); } catch {}
                            }}>
                              保存并关闭页面
                            </Button>
                          </div>
                        ),
                      });
                    });
                    if (!ok) return;
                  }

                  let hasErr = false;
                  if (isBm) {
                    const auditRes: any = runBmAuditFromCurrentTable();
                    const matchRes: any = await matchBmTargetsFromCurrentTable();
                    const noTargetLen = Array.isArray(matchRes?.noTargetRows)
                      ? matchRes.noTargetRows.length
                      : (attPvBmAudit?.noTargetRows?.length ?? 0);
                    hasErr = (
                      noTargetLen > 0
                      || (auditRes?.newEmptyRows?.length || 0) > 0
                      || (auditRes?.sameRows?.length || 0) > 0
                      || (auditRes?.dupPairs?.length || 0) > 0
                      || (auditRes?.badRows?.length || 0) > 0
                    );
                  } else if (isSltm) {
                    const auditRes: any = runSltmAuditFromCurrentTable();
                    hasErr = (
                      (auditRes?.incompleteRows?.length || 0) > 0
                      || (auditRes?.invalidBRows?.length || 0) > 0
                      || (auditRes?.dupC?.length || 0) > 0
                      || (auditRes?.dupD?.length || 0) > 0
                    );
                  }
                  if (!hasErr) { closeNow(); return; }

                  const ok = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                      title: '仍存在审核错误，确认关闭？',
                      content: '关闭后将退出预览。若要修复，请先在预览中修改并保存编辑后再关闭。',
                      okText: '确认关闭',
                      cancelText: '取消',
                      onOk: () => resolve(true),
                      onCancel: () => resolve(false),
                    });
                  });
                  if (ok) closeNow();
                } catch {
                  closeNow();
                }
              }}>
                关闭
              </Button>
            </div>
          )}
          width={1280}
          zIndex={1100}
          title={(() => {
            const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
            const isShippingLabelTargetMapping = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
            return (
              <div style={{ position:'relative', display:'flex', alignItems:'center', justifyContent:'space-between', paddingRight: 6 }}>
                <span style={{ color: '#1677ff', fontWeight: 700 }}>
                  {attPvTitle || '附件预览'}{attPvEntryName ? ` / ${attPvEntryName}` : ''}
                </span>
                {isBm ? (
                  <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', padding:'2px 8px', background:'#fffb8f', color:'#cf1322', borderRadius: 4, width: '780px', maxWidth: 'calc(100% - 260px)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    系统会自动匹配操作对象信息，请参看指令详情手动修改数量分配列(适用于旧条码1对多)
                  </div>
                ) : isShippingLabelTargetMapping ? (
                  <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', padding:'2px 8px', background:'#fffb8f', color:'#cf1322', fontWeight: 700, borderRadius: 4, width: '780px', maxWidth: 'calc(100% - 260px)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    请删除示例行,仅保留真实数据行
                  </div>
                ) : null}
                <span />
              </div>
            );
          })()}
        >
          {attPvLoading ? <div style={{ color:'#999' }}>加载中...</div> : (
            attPvError ? <div style={{ color:'#cf1322' }}>{attPvError}</div> : (
              (attPvUrl && (attPvUrl.startsWith('zip:') || attPvUrl.startsWith('arc:'))) ? (
                <div>
                  <div style={{ marginBottom: 8, color:'#666' }}>在线解压（只读预览，不改变原附件）：{attPvArchList.length} 项</div>
                  <Table
                    size="small"
                    rowKey={(r:any, i?:number)=> String(r?.path || i)}
                    dataSource={attPvArchList}
                    pagination={{
                      current: attPvArchPage,
                      pageSize: attPvArchPageSize,
                      total: attPvArchList.length,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: ['10','20','50','100'] as any,
                      showTotal: (total:number, range:[number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                      onChange: (p:number, ps:number) => { setAttPvArchPage(p); setAttPvArchPageSize(ps); },
                    }}
                    columns={[
                      { title: '序号', key: '__idx', width: 80, align: 'center', render: (_:any, __:any, idx:number) => ((attPvArchPage - 1) * attPvArchPageSize + idx + 1) },
                      { title: '路径', dataIndex: 'path', render: (v:any, r:any) => r.dir ? <span style={{ color:'#8c8c8c' }}>[目录] {v}</span> : v },
                      { title: '操作', key: 'ops', width: 200, render: (_:any, r:any) => {
                        if (r.dir) return <span style={{ color:'#999' }}>-</span>;
                        return (
                          <Space size={8}>
                            <Button size="small" onClick={async () => {
                              try {
                                // 调试：点击条目预览
                                try { console.debug('[ArcPreview][Click]', { entry: r.path, kind: attPvArchiveKind }); } catch {}
                                // 使用已缓存的归档 buffer + kind 解压单条目
                                const ab: ArrayBuffer = await (async () => {
                                  const kind = attPvArchiveKind || 'zip';
                                  const buf = attPvArchiveBuf;
                                  if (!buf && kind !== 'rar') throw new Error('归档数据未缓存');
                                  if (kind === 'zip') {
                                    let JSZip: any = null;
                                    try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
                                    if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
                                    const zip = await JSZip.loadAsync(buf);
                                    const file = zip.file(r.path);
                                    if (!file) throw new Error('条目不存在');
                                    return await file.async('arraybuffer');
                                  } else if (kind === 'rar') {
                                    const attachId = attPvArchiveAttachId;
                                    if (!attachId) throw new Error('缺少附件ID');
                                    // 对于图片与 PDF 都通过后端接口获取字节，避免前端相对路径问题
                                    const api = `/api/v1/instruction/attachments/${attachId}/archive/entry/`;
                                    const res = await (request as any).get(api, { params: { path: r.path }, responseType: 'blob', withCredentials: true });
                                    const blobObj = res?.data as Blob;
                                    const buf2 = await blobObj.arrayBuffer();
                                    return buf2;
                                  } else if (kind === 'tar') {
                                    if (!buf) throw new Error('归档数据未缓存');
                                    const entries = parseTarEntriesFromBuffer(buf as ArrayBuffer);
                                    const hit = entries.find(e => e.path === r.path && !e.dir);
                                    if (!hit) throw new Error('条目不存在');
                                    return (buf as ArrayBuffer).slice(hit.offset, hit.offset + hit.size);
                                  } else {
                                    throw new Error('该归档类型的条目预览暂不支持');
                                  }
                                })();
                                const ext = String(r.path || '').toLowerCase().split('.').pop() || '';
                                const imgSet = new Set(['png','jpg','jpeg','webp','gif','apng','svg','bmp','ico']);
                                const mime = ext === 'pdf'
                                  ? 'application/pdf'
                                  : imgSet.has(ext) ? (
                                      ext === 'svg' ? 'image/svg+xml'
                                      : ext === 'ico' ? 'image/x-icon'
                                      : ext === 'jpg' ? 'image/jpeg'
                                      : `image/${ext}`
                                    )
                                  : '';
                                const blob = new Blob([ab], { type: mime || undefined });
                                const href = URL.createObjectURL(blob);
                                if (ext === 'pdf') {
                                  // 简单PDF签名校验（%PDF）
                                  try {
                                    const u8 = new Uint8Array(ab as ArrayBuffer);
                                    const isPdf = u8 && u8.length >= 4 && u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46;
                                    if (!isPdf && attPvArchiveKind === 'rar' && attPvArchiveAttachId) {
                                      // 通过后端接口以 blob 方式获取并内嵌预览，避免前端 3000 端口 404
                                      try {
                                        const api = `/api/v1/instruction/attachments/${attPvArchiveAttachId}/archive/entry/`;
                                        const res = await (request as any).get(api, { params: { path: r.path }, responseType: 'blob', withCredentials: true });
                                        const objUrl = URL.createObjectURL(res?.data);
                                        setAttPvKind('pdf');
                                        setAttPvUrl(objUrl);
                                        try {
                                          const base = String(r.path || '');
                                          const name = base.split('/').pop() || base;
                                          setAttPvEntryName(name);
                                        } catch { setAttPvEntryName(''); }
                                        return;
                                      } catch (e) {
                                        const rel = `/api/v1/instruction/attachments/${attPvArchiveAttachId}/archive/entry/?path=${encodeURIComponent(r.path)}`;
                                        window.open(rel, '_blank');
                                        return;
                                      }
                                    }
                                  } catch {}
                                  setAttPvKind('pdf');
                                  setAttPvUrl(href);
                                  try {
                                    const base = String(r.path || '');
                                    const name = base.split('/').pop() || base;
                                    setAttPvEntryName(name);
                                    try { console.debug('[ZipPreview][TitleSet]', { name }); } catch {}
                                  } catch { setAttPvEntryName(''); }
                                } else if (imgSet.has(ext)) {
                                  // 在弹窗内用 <img> 预览
                                  setAttPvKind('image' as any);
                                  setAttPvUrl(href);
                                  try {
                                    const base = String(r.path || '');
                                    const name = base.split('/').pop() || base;
                                    setAttPvEntryName(name);
                                    try { console.debug('[ZipPreview][TitleSet]', { name }); } catch {}
                                  } catch { setAttPvEntryName(''); }
                                } else {
                                  const a = document.createElement('a');
                                  a.href = href;
                                  a.target = '_blank';
                                  a.rel = 'noopener';
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  setTimeout(()=> URL.revokeObjectURL(href), 2000);
                                }
                              } catch { message.error('预览失败'); }
                            }}>预览</Button>
                            <Button size="small" type="primary" onClick={async () => {
                              try {
                                const ab: ArrayBuffer = await (async () => {
                                  const kind = attPvArchiveKind || 'zip';
                                  const buf = attPvArchiveBuf;
                                  if (!buf && kind !== 'rar') throw new Error('归档数据未缓存');
                                  if (kind === 'zip') {
                                    let JSZip: any = null;
                                    try { JSZip = (await import('jszip') as any); JSZip = JSZip.default || JSZip; } catch {}
                                    if (!JSZip) throw new Error('缺少 ZIP 解压库（jszip）');
                                    const zip = await JSZip.loadAsync(buf);
                                    const file = zip.file(r.path);
                                    if (!file) throw new Error('条目不存在');
                                    return await file.async('arraybuffer');
                                  } else if (kind === 'rar') {
                                    const attachId = attPvArchiveAttachId;
                                    if (!attachId) throw new Error('缺少附件ID');
                                    // 对于图片与 PDF 都通过后端接口获取字节，避免前端相对路径问题
                                    const api = `/api/v1/instruction/attachments/${attachId}/archive/entry/`;
                                    const res = await (request as any).get(api, { params: { path: r.path }, responseType: 'blob', withCredentials: true });
                                    const blobObj = res?.data as Blob;
                                    const buf2 = await blobObj.arrayBuffer();
                                    return buf2;
                                  } else if (kind === 'tar') {
                                    if (!buf) throw new Error('归档数据未缓存');
                                    const entries = parseTarEntriesFromBuffer(buf as ArrayBuffer);
                                    const hit = entries.find(e => e.path === r.path && !e.dir);
                                    if (!hit) throw new Error('条目不存在');
                                    return (buf as ArrayBuffer).slice(hit.offset, hit.offset + hit.size);
                                  } else {
                                    throw new Error('该归档类型的条目下载暂不支持');
                                  }
                                })();
                                const ext = String(r.path || '').toLowerCase().split('.').pop() || '';
                                const mime = ext === 'pdf' ? 'application/pdf' : '';
                                const blob = new Blob([ab], { type: mime || undefined });
                                const href = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = href;
                                a.download = r.path.split('/').pop() || 'entry';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                setTimeout(()=> URL.revokeObjectURL(href), 2000);
                              } catch { message.error('下载失败'); }
                            }}>下载条目</Button>
                          </Space>
                        );
                      }},
                    ] as any}
                  />
                </div>
              ) : attPvKind === 'pdf' ? (
                <iframe title="pdf" src={attPvUrl} style={{ width:'100%', height: 600, border: 0 }} />
              ) : (attPvKind as any) === 'image' ? (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
                  <img alt={attPvEntryName || 'image'} src={attPvUrl} style={{ maxWidth:'100%', maxHeight: 680, objectFit: 'contain' }} />
                </div>
              ) : attPvKind === 'csv' || attPvKind === 'xlsx' ? (
                <div>
                  {attPvBmAudit ? (
                    <div style={{ marginBottom: 8, padding: '8px 10px', border: '1px solid #ffccc7', background: '#fff2f0', color: '#a8071a', borderRadius: 6 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                        <span>新旧条码对应表附件内容审核（错误）</span>
                        <Button size="small" type="primary" onClick={() => { runBmAuditFromCurrentTable(); matchBmTargetsFromCurrentTable(); }}>
                          再次审核
                        </Button>
                      </div>
                      {attPvBmAudit.noTargetRows?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#cf1322', fontWeight: 800 }}>存在无法匹配操作对象的记录</span>：{attPvBmAudit.noTargetRows.length} 行
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                            <Button
                              size="small"
                              onClick={async () => {
                                try {
                                  // 打开批量指定弹窗：只列出无法匹配的行
                                  const miss = Array.isArray(attPvBmAudit?.noTargetRows) ? attPvBmAudit.noTargetRows : [];
                                  const map = new Map<number, any>();
                                  // 基于当前预览表格行数据补齐新条码等展示字段
                                  (attPvData || []).forEach((row:any) => {
                                    const __idx = Number(row?.__idx);
                                    if (!Number.isFinite(__idx) || __idx <= 0) return;
                                    const rowNo = __idx + 1;
                                    map.set(rowNo, row);
                                  });
                                  const rowsForModal = miss.map((m:any) => {
                                    const rowNo = Number(m?.rowNo);
                                    const row = map.get(rowNo) || {};
                                    return {
                                      rowNo,
                                      __idx: rowNo - 1,
                                      old: String(row?.c0 ?? '').trim() || String(m?.old || '').trim() || '-',
                                      newer: String(row?.c1 ?? '').trim() || '-',
                                      qty: String(row?.c3 ?? '').trim(),
                                      remark: String(row?.c4 ?? '').trim(),
                                    };
                                  });
                                  setBmBatchPickRows(rowsForModal);
                                  setBmBatchPickSel({});
                                  await loadBmTargetIuOptions();
                                  setBmBatchPickOpen(true);
                                } catch {}
                              }}
                            >
                              批量指定
                            </Button>
                            <Button
                              size="small"
                              disabled={attPvBmMarkNoTarget.size > 0}
                              style={attPvBmMarkNoTarget.size > 0 ? undefined : { background: bmNoTargetBg, borderColor: bmNoTargetBg, color: '#cf1322', fontWeight: 700 }}
                              onClick={() => {
                                try {
                                  const s = new Set<number>();
                                  (attPvBmAudit.noTargetRows || []).forEach((r:any) => {
                                    const idx = Number(r?.rowNo) - 1;
                                    if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                  });
                                  setAttPvBmMarkNoTarget(s);
                                } catch {}
                              }}
                            >
                              标记错误行
                            </Button>
                            <Button size="small" disabled={attPvBmMarkNoTarget.size === 0} onClick={() => setAttPvBmMarkNoTarget(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvBmAudit.newEmptyRows?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#595959', fontWeight: 800 }}>新条码为空</span>：{attPvBmAudit.newEmptyRows.length} 行
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                            <Button
                              size="small"
                              disabled={attPvBmMarkNewEmpty.size > 0}
                              style={attPvBmMarkNewEmpty.size > 0 ? undefined : { background: bmNewEmptyBg, borderColor: bmNewEmptyBg, color: '#000' }}
                              onClick={() => {
                                try {
                                  const s = new Set<number>();
                                  (attPvBmAudit.newEmptyRows || []).forEach((r:any) => {
                                    const idx = Number(r?.rowNo) - 1;
                                    if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                  });
                                  setAttPvBmMarkNewEmpty(s);
                                } catch {}
                              }}
                            >
                              标记错误行
                            </Button>
                            <Button size="small" disabled={attPvBmMarkNewEmpty.size === 0} onClick={() => setAttPvBmMarkNewEmpty(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvBmAudit.sameRows?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#cf1322', fontWeight: 800 }}>旧条码=新条码</span>：{attPvBmAudit.sameRows.length} 条
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                          <Button
                            size="small"
                            disabled={attPvBmMarkEq.size > 0}
                            style={attPvBmMarkEq.size > 0 ? undefined : { background: bmEqBg, borderColor: bmEqBg, color: '#fff' }}
                            onClick={() => {
                            try {
                              const s = new Set<number>();
                              (attPvBmAudit.sameRows || []).forEach((r:any) => {
                                const idx = Number(r?.rowNo) - 1; // rowNo 含表头（2开始），__idx = rowNo-1
                                if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                              });
                              setAttPvBmMarkEq(s);
                            } catch {}
                          }}>标记错误行</Button>
                          <Button size="small" disabled={attPvBmMarkEq.size === 0} onClick={() => setAttPvBmMarkEq(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvBmAudit.dupPairs?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#1677ff', fontWeight: 800 }}>重复的新旧组合</span>：{attPvBmAudit.dupPairs.length} 组
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                          <Button
                            size="small"
                            disabled={attPvBmMarkDup.size > 0}
                            style={attPvBmMarkDup.size > 0 ? undefined : { background: bmDupBg, borderColor: bmDupBg, color: '#fff' }}
                            onClick={() => {
                            try {
                              const s = new Set<number>();
                              (attPvBmAudit.dupPairs || []).forEach((g:any) => {
                                const rowNos = Array.isArray(g?.rowNos) ? g.rowNos : [];
                                rowNos.forEach((rn:any) => {
                                  const idx = Number(rn) - 1;
                                  if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                });
                              });
                              setAttPvBmMarkDup(s);
                            } catch {}
                          }}>标记错误行</Button>
                          <Button size="small" disabled={attPvBmMarkDup.size === 0} onClick={() => setAttPvBmMarkDup(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvBmAudit.badRows?.length ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#fa8c16', fontWeight: 800 }}>一个旧条码对应多个新条码且数量列无效</span>：{attPvBmAudit.badRows.length} 行
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                          <Button
                            size="small"
                            disabled={attPvBmMarkBad.size > 0}
                            style={attPvBmMarkBad.size > 0 ? undefined : { background: bmBadBg, borderColor: bmBadBg, color: '#fff' }}
                            onClick={() => {
                            try {
                              const s = new Set<number>();
                              (attPvBmAudit.badRows || []).forEach((r:any) => {
                                const idx = Number(r?.rowNo) - 1;
                                if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                              });
                              setAttPvBmMarkBad(s);
                            } catch {}
                          }}>标记错误行</Button>
                          <Button size="small" disabled={attPvBmMarkBad.size === 0} onClick={() => setAttPvBmMarkBad(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {attPvSltmAudit ? (
                    <div style={{ marginBottom: 8, padding: '8px 10px', border: '1px solid #ffccc7', background: '#fff2f0', color: '#a8071a', borderRadius: 6 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                        <span>运输标签与出库对象对应表格附件内容审核（警告）</span>
                        <Button size="small" type="primary" onClick={() => { runSltmAuditFromCurrentTable(); }}>
                          再次审核
                        </Button>
                      </div>
                      {attPvSltmAudit.incompleteRows?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#cf1322', fontWeight: 800 }}>记录不完整</span>：{attPvSltmAudit.incompleteRows.length} 行（A/B 必填，C/D 至少一列有值）
                            <span style={{ color:'#8c8c8c', marginLeft: 8 }}>
                              行号：{attPvSltmAudit.incompleteRows.slice(0, 20).map((x:any) => x.rowNo).join('、')}
                              {attPvSltmAudit.incompleteRows.length > 20 ? ` …共${attPvSltmAudit.incompleteRows.length}行` : ''}
                            </span>
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                            <Button
                              size="small"
                              disabled={attPvSltmMarkIncomplete.size > 0}
                              style={attPvSltmMarkIncomplete.size > 0 ? undefined : { background: sltmIncompleteBg, borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}
                              onClick={() => {
                                try {
                                  const s = new Set<number>();
                                  (attPvSltmAudit.incompleteRows || []).forEach((r:any) => {
                                    const idx = Number(r?.rowNo) - 1;
                                    if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                  });
                                  setAttPvSltmMarkIncomplete(s);
                                } catch {}
                              }}
                            >
                              标记错误行
                            </Button>
                            <Button size="small" disabled={attPvSltmMarkIncomplete.size === 0} onClick={() => setAttPvSltmMarkIncomplete(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvSltmAudit.invalidBRows?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#cf1322', fontWeight: 800 }}>B列值不为1</span>：{attPvSltmAudit.invalidBRows.length} 行
                            <span style={{ color:'#8c8c8c', marginLeft: 8 }}>
                              行号：{attPvSltmAudit.invalidBRows.slice(0, 20).map((x:any) => x.rowNo).join('、')}
                              {attPvSltmAudit.invalidBRows.length > 20 ? ` …共${attPvSltmAudit.invalidBRows.length}行` : ''}
                            </span>
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                            <Button
                              size="small"
                              disabled={attPvSltmMarkBNotOne.size > 0}
                              style={attPvSltmMarkBNotOne.size > 0 ? undefined : { background: sltmBNotOneBg, borderColor: '#ffd591', color: '#d46b08', fontWeight: 700 }}
                              onClick={() => {
                                try {
                                  const s = new Set<number>();
                                  (attPvSltmAudit.invalidBRows || []).forEach((r:any) => {
                                    const idx = Number(r?.rowNo) - 1;
                                    if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                  });
                                  setAttPvSltmMarkBNotOne(s);
                                } catch {}
                              }}
                            >
                              标记错误行
                            </Button>
                            <Button size="small" disabled={attPvSltmMarkBNotOne.size === 0} onClick={() => setAttPvSltmMarkBNotOne(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvSltmAudit.dupC?.length ? (
                        <div style={{ marginBottom: 4, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#cf1322', fontWeight: 800 }}>C列重复值</span>：{attPvSltmAudit.dupC.length} 组
                            <span style={{ color:'#8c8c8c', marginLeft: 8 }}>
                              行号：{Array.from(new Set((attPvSltmAudit.dupC || []).flatMap((g:any) => Array.isArray(g?.rowNos) ? g.rowNos : []))).slice(0, 20).join('、')}
                            </span>
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                            <Button
                              size="small"
                              disabled={attPvSltmMarkDupC.size > 0}
                              style={attPvSltmMarkDupC.size > 0 ? undefined : { background: sltmDupCBg, borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}
                              onClick={() => {
                                try {
                                  const s = new Set<number>();
                                  (attPvSltmAudit.dupC || []).forEach((g:any) => {
                                    const rowNos = Array.isArray(g?.rowNos) ? g.rowNos : [];
                                    rowNos.forEach((rn:any) => {
                                      const idx = Number(rn) - 1;
                                      if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                    });
                                  });
                                  setAttPvSltmMarkDupC(s);
                                } catch {}
                              }}
                            >
                              标记错误行
                            </Button>
                            <Button size="small" disabled={attPvSltmMarkDupC.size === 0} onClick={() => setAttPvSltmMarkDupC(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                      {attPvSltmAudit.dupD?.length ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12 }}>
                          <span>
                            <span style={{ color: '#cf1322', fontWeight: 800 }}>D列重复值</span>：{attPvSltmAudit.dupD.length} 组
                            <span style={{ color:'#8c8c8c', marginLeft: 8 }}>
                              行号：{Array.from(new Set((attPvSltmAudit.dupD || []).flatMap((g:any) => Array.isArray(g?.rowNos) ? g.rowNos : []))).slice(0, 20).join('、')}
                            </span>
                          </span>
                          <div style={{ display:'inline-flex', gap: 8 }}>
                            <Button
                              size="small"
                              disabled={attPvSltmMarkDupD.size > 0}
                              style={attPvSltmMarkDupD.size > 0 ? undefined : { background: sltmDupDBg, borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}
                              onClick={() => {
                                try {
                                  const s = new Set<number>();
                                  (attPvSltmAudit.dupD || []).forEach((g:any) => {
                                    const rowNos = Array.isArray(g?.rowNos) ? g.rowNos : [];
                                    rowNos.forEach((rn:any) => {
                                      const idx = Number(rn) - 1;
                                      if (Number.isFinite(idx) && idx >= 1) s.add(idx);
                                    });
                                  });
                                  setAttPvSltmMarkDupD(s);
                                } catch {}
                              }}
                            >
                              标记错误行
                            </Button>
                            <Button size="small" disabled={attPvSltmMarkDupD.size === 0} onClick={() => setAttPvSltmMarkDupD(new Set())}>取消标记</Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
                    <div style={{ color: '#666' }}>
                      {attPvSheetName ? <span>工作表：{attPvSheetName}</span> : <span>可编辑表格</span>}
                      {attPvAttachId && editedAttachMap[String(attPvAttachId)] ? (
                        <Tag color="green" style={{ marginLeft: 8 }}>已存在编辑版本</Tag>
                      ) : null}
                      {attPvDirty ? <Tag color="orange" style={{ marginLeft: 8 }}>未保存修改</Tag> : null}
                    </div>
                    <Space size={8} wrap>
                      {/* 组1：目标行 + 剪切/复制（需有选中） */}
                      <span style={{ color:'#666' }}>
                        目标行：
                        <InputNumber
                          size="small"
                          min={1}
                          max={(attPvData?.length || 0) + 1}
                          value={attPvTargetIdx}
                          onChange={(v:any)=> {
                            const num = Number(v);
                            if (!Number.isFinite(num)) return;
                            const maxv = (attPvData?.length || 0) + 1;
                            const clamped = Math.max(1, Math.min(maxv, num));
                            setAttPvTargetIdx(clamped);
                          }}
                          style={{ width: 90, marginLeft: 6 }}
                        />
                      </span>
                      <Button
                        size="small"
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length === 0}
                        onClick={() => {
                          try {
                            const sel = (attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)) as number[];
                            if (sel.length === 0) return;
                            const selected = new Set(sel);
                            const picked = (attPvData || []).filter((r:any)=> selected.has(Number(r?.__idx))).map((r:any)=> ({ ...r }));
                            const rest = (attPvData || []).filter((r:any)=> !selected.has(Number(r?.__idx)));
                            const clamp = (x:number, lo:number, hi:number) => Math.max(lo, Math.min(hi, x));
                            const tgt = Number(attPvTargetIdx) || 1;
                            const ins = clamp(tgt - 1, 0, rest.length);
                            const next = [...rest.slice(0, ins), ...picked, ...rest.slice(ins)];
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            const start = ins + 1;
                            const keys = Array.from({ length: picked.length }, (_, i) => String(start + i));
                            setAttPvSelectedRowKeys(keys);
                          } catch { message.error('操作失败'); }
                        }}
                      >
                        剪切到上方
                      </Button>
                      <Button
                        size="small"
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length === 0}
                        onClick={() => {
                          try {
                            const sel = (attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)) as number[];
                            if (sel.length === 0) return;
                            const selected = new Set(sel);
                            const picked = (attPvData || []).filter((r:any)=> selected.has(Number(r?.__idx))).map((r:any)=> ({ ...r }));
                            const rest = (attPvData || []).filter((r:any)=> !selected.has(Number(r?.__idx)));
                            const clamp = (x:number, lo:number, hi:number) => Math.max(lo, Math.min(hi, x));
                            const tgt = Number(attPvTargetIdx) || 1;
                            const ins = clamp(tgt, 0, rest.length);
                            const next = [...rest.slice(0, ins), ...picked, ...rest.slice(ins)];
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            const start = ins + 1;
                            const keys = Array.from({ length: picked.length }, (_, i) => String(start + i));
                            setAttPvSelectedRowKeys(keys);
                          } catch { message.error('操作失败'); }
                        }}
                      >
                        剪切到下方
                      </Button>
                      <Button
                        size="small"
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length === 0}
                        onClick={() => {
                          try {
                            const sel = (attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)) as number[];
                            if (sel.length === 0) return;
                            const selected = new Set(sel);
                            const picked = (attPvData || []).filter((r:any)=> selected.has(Number(r?.__idx)));
                            const fieldKeys: string[] = (attPvCols || []).map((c:any)=> c?.dataIndex).filter((k:string)=> k && k !== '__idx');
                            const clones = picked.map((r:any) => {
                              const o:any = { __idx: 0 };
                              fieldKeys.forEach(k => { o[k] = r?.[k] ?? ''; });
                              return o;
                            });
                            const base = (attPvData || []);
                            const clamp = (x:number, lo:number, hi:number) => Math.max(lo, Math.min(hi, x));
                            const tgt = Number(attPvTargetIdx) || 1;
                            const ins = clamp(tgt - 1, 0, base.length);
                            const next = [...base.slice(0, ins), ...clones, ...base.slice(ins)];
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            const start = ins + 1;
                            const keys = Array.from({ length: clones.length }, (_, i) => String(start + i));
                            setAttPvSelectedRowKeys(keys);
                          } catch { message.error('操作失败'); }
                        }}
                      >
                        复制到上方
                      </Button>
                      <Button
                        size="small"
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length === 0}
                        onClick={() => {
                          try {
                            const sel = (attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)) as number[];
                            if (sel.length === 0) return;
                            const selected = new Set(sel);
                            const picked = (attPvData || []).filter((r:any)=> selected.has(Number(r?.__idx)));
                            const fieldKeys: string[] = (attPvCols || []).map((c:any)=> c?.dataIndex).filter((k:string)=> k && k !== '__idx');
                            const clones = picked.map((r:any) => {
                              const o:any = { __idx: 0 };
                              fieldKeys.forEach(k => { o[k] = r?.[k] ?? ''; });
                              return o;
                            });
                            const base = (attPvData || []);
                            const clamp = (x:number, lo:number, hi:number) => Math.max(lo, Math.min(hi, x));
                            const tgt = Number(attPvTargetIdx) || 1;
                            const ins = clamp(tgt, 0, base.length);
                            const next = [...base.slice(0, ins), ...clones, ...base.slice(ins)];
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            const start = ins + 1;
                            const keys = Array.from({ length: clones.length }, (_, i) => String(start + i));
                            setAttPvSelectedRowKeys(keys);
                          } catch { message.error('操作失败'); }
                        }}
                      >
                        复制到下方
                      </Button>
                      {/* 分割线 */}
                      <span style={{ display:'inline-block', width: 2, height: 18, background:'#595959', opacity: 0.6, margin: '0 4px' }} />
                      {/* 组2：插入（需单选） */}
                      <Button
                        size="small"
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length !== 1}
                        onClick={() => {
                          try {
                            const keys = (attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)) as number[];
                            const minIdx = keys.length > 0 ? Math.min(...keys) : (attPvData.length + 1);
                            const fieldKeys: string[] = (attPvCols || []).map((c:any)=> c?.dataIndex).filter((k:string)=> k && k !== '__idx');
                            const empty: any = { __idx: 0 };
                            fieldKeys.forEach(k => { empty[k] = ''; });
                            const insertAt = Math.max(0, Math.min(attPvData.length, minIdx - 1));
                            const next = [...attPvData.slice(0, insertAt), empty, ...attPvData.slice(insertAt)];
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            setAttPvSelectedRowKeys([String(insertAt + 1)]);
                          } catch { message.error('插入失败'); }
                        }}
                      >
                        从上方插入
                      </Button>
                      <Button
                        size="small"
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length !== 1}
                        onClick={() => {
                          try {
                            const keys = (attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)) as number[];
                            const maxIdx = keys.length > 0 ? Math.max(...keys) : attPvData.length;
                            const fieldKeys: string[] = (attPvCols || []).map((c:any)=> c?.dataIndex).filter((k:string)=> k && k !== '__idx');
                            const empty: any = { __idx: 0 };
                            fieldKeys.forEach(k => { empty[k] = ''; });
                            const insertAt = Math.max(0, Math.min(attPvData.length, maxIdx));
                            const next = [...attPvData.slice(0, insertAt), empty, ...attPvData.slice(insertAt)];
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            setAttPvSelectedRowKeys([String(insertAt + 1)]);
                          } catch { message.error('插入失败'); }
                        }}
                      >
                        从下方插入
                      </Button>
                      {/* 分割线 */}
                      <span style={{ display:'inline-block', width: 2, height: 18, background:'#595959', opacity: 0.6, margin: '0 4px' }} />
                      {/* 组3：删除 */}
                      <Button
                        size="small"
                        danger
                        disabled={!attPvSelectedRowKeys || attPvSelectedRowKeys.length === 0}
                        onClick={() => {
                          try {
                            const keys = new Set((attPvSelectedRowKeys || []).map(k => Number(String(k))).filter(n => Number.isFinite(n)));
                            if (keys.size === 0) return;
                            const next = attPvData.filter((r:any) => !keys.has(Number(r?.__idx)));
                            next.forEach((r:any, i:number) => { r.__idx = i + 1; });
                            setAttPvData(next);
                            setAttPvDirty(true);
                            setAttPvSelectedRowKeys([]);
                          } catch { message.error('删除失败'); }
                        }}
                      >
                        删除选中
                      </Button>
                      {/* 分割线 */}
                      <span style={{ display:'inline-block', width: 2, height: 18, background:'#595959', opacity: 0.6, margin: '0 4px' }} />
                      {/* 组4：保存 */}
                      <Button size="small" type="primary" disabled={!attPvDirty} onClick={saveEditedPreviewAsOverlay}>保存编辑</Button>
                    </Space>
                  </div>
                  <Table
                    size="small"
                    rowKey={(r:any, i?:number)=> String(r?.__idx ?? i ?? 0)}
                    dataSource={attPvData}
                    onRow={(row:any) => {
                      try {
                        const idx = Number(row?.__idx);
                        if (!Number.isFinite(idx)) return {};
                        // 多种错误使用不同背景色；若同时命中，按“更严重”优先级着色
                        if (attPvBmMarkBad && attPvBmMarkBad.has(idx)) return { style: { background: bmBadBg } } as any;
                        if (attPvBmMarkDup && attPvBmMarkDup.has(idx)) return { style: { background: bmDupBg } } as any;
                        if (attPvBmMarkEq && attPvBmMarkEq.has(idx)) return { style: { background: bmEqBg } } as any;
                        if (attPvBmMarkNewEmpty && attPvBmMarkNewEmpty.has(idx)) return { style: { background: bmNewEmptyBg } } as any;
                        if (attPvBmMarkNoTarget && attPvBmMarkNoTarget.has(idx)) return { style: { background: bmNoTargetBg } } as any;
                        if (attPvSltmMarkIncomplete && attPvSltmMarkIncomplete.has(idx)) return { style: { background: sltmIncompleteBg } } as any;
                        if (attPvSltmMarkBNotOne && attPvSltmMarkBNotOne.has(idx)) return { style: { background: sltmBNotOneBg } } as any;
                        if (attPvSltmMarkDupC && attPvSltmMarkDupC.has(idx)) return { style: { background: sltmDupCBg } } as any;
                        if (attPvSltmMarkDupD && attPvSltmMarkDupD.has(idx)) return { style: { background: sltmDupDBg } } as any;
                      } catch {}
                      return {};
                    }}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: attPvSelectedRowKeys,
                      onChange: (keys) => setAttPvSelectedRowKeys(keys),
                    } as any}
                    pagination={{
                      pageSize: attPvPageSize,
                      showSizeChanger: true,
                      pageSizeOptions: [10, 20, 50, 100] as any,
                      onShowSizeChange: (_:number, size:number) => setAttPvPageSize(size),
                      onChange: (_page:number, size:number) => { if (size !== attPvPageSize) setAttPvPageSize(size); },
                    }}
                    columns={(() => {
                      const baseCols = (attPvCols || []).filter(Boolean).map((col:any)=> ({
                        ...col,
                        onCell: () => ({ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }})
                      }));
                      const isShippingMap = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING';
                      const isBm = String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'BARCODE_MAPPING';
                      if (isShippingMap) return baseCols as any;
                      if (!isBm) return baseCols as any;
                      if (!(attPvKind === 'csv' || attPvKind === 'xlsx')) return baseCols as any;

                      // 备注列：hover 始终显示完整备注（不依赖是否溢出）
                      const baseCols2 = baseCols.map((c:any) => {
                        try {
                          const t = String((c as any)?.titleText || (c as any)?.title || '');
                          const di = String((c as any)?.dataIndex || '');
                          const isRemark = di === 'c4' || t.includes('备注');
                          if (!isRemark) return c;
                          return {
                            ...c,
                            ellipsis: true,
                            onCell: () => ({ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
                            render: (v:any) => {
                              const full = String(v ?? '').trim() || '-';
                              return (
                                <Tooltip title={full}>
                                  <span style={{ display:'inline-block', maxWidth:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    {full}
                                  </span>
                                </Tooltip>
                              );
                            }
                          };
                        } catch { return c; }
                      });
                      const isMatchTitle = (t:any) => {
                        try {
                          const s = String(t || '');
                          return s.includes('匹配操作对象') || s.includes('操作对象匹配信息');
                        } catch { return false; }
                      };
                      const hasMatchCol = baseCols2.some((c:any) => isMatchTitle((c as any)?.titleText) || isMatchTitle((c as any)?.title));
                      const matchRender = (_:any, row:any) => {
                        const idx = Number(row?.__idx);
                        const raw = (Number.isFinite(idx) && idx > 0)
                          ? String(attPvBmTargetMatchByIdx?.[idx] ?? (row as any)?.c4 ?? '-')
                          : '-';
                        const txt = String(raw || '').trim();
                        const isMiss = (txt === '-' || txt === '' || txt === '[]');
                        const openPick = async (e?: any) => {
                          try {
                            if (e?.stopPropagation) e.stopPropagation();
                            if (!Number.isFinite(idx) || idx <= 0) return;
                            // 从当前 JSON 解析已选 IU，回填到弹窗
                            let preIds: number[] = [];
                            try {
                              const s = String(attPvBmTargetMatchByIdx?.[idx] ?? '').trim();
                              const arr = s ? JSON.parse(s) : [];
                              const list = Array.isArray(arr) ? arr : [];
                              preIds = list.map((x:any) => Math.floor(Number(x?.iu_id || 0))).filter((n:number) => Number.isFinite(n) && n > 0);
                            } catch { preIds = []; }
                            setBmBatchPickRows([{
                              rowNo: idx + 1,
                              __idx: idx,
                              old: String((row as any)?.c0 ?? '').trim() || '-',
                              newer: String((row as any)?.c1 ?? '').trim() || '-',
                              qty: String((row as any)?.c3 ?? '').trim(),
                              remark: String((row as any)?.c4 ?? '').trim(),
                            }]);
                            setBmBatchPickSel(preIds.length ? { [idx]: preIds } : {});
                            await loadBmTargetIuOptions();
                            setBmBatchPickOpen(true);
                          } catch {}
                        };
                        if (isMiss) {
                          return (
                            <Space size={6}>
                              <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>
                              <Tooltip title="指定/编辑操作对象匹配信息">
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<EditOutlined style={{ color: '#1677ff' }} />}
                                  onClick={openPick}
                                />
                              </Tooltip>
                            </Space>
                          );
                        }
                        // JSON 存储，人类可读渲染：SYS - 是否操作全部库存 - 预计数量
                        let arr: any[] = [];
                        try { arr = JSON.parse(txt); } catch { arr = []; }
                        const list = Array.isArray(arr) ? arr : [];
                        const nodes: any[] = [];
                        list.forEach((x:any, i:number) => {
                          const sys = String(x?.system_barcode || '').trim() || (x?.iu_id ? `IU#${x.iu_id}` : '-');
                          const useAll = !!x?.use_all_available;
                          const eq = (x?.expected_qty == null || x?.expected_qty === '') ? null : Number(x.expected_qty);
                          const eqTxt = (eq == null || !Number.isFinite(eq)) ? '-' : String(eq);
                          const text = `${sys}-${useAll ? '是' : '否'}-${eqTxt}`;
                          if (i > 0) nodes.push(<span key={`sep_${i}`} style={{ color:'#8c8c8c' }}>{' | '}</span>);
                          nodes.push(
                            <span
                              key={`it_${i}`}
                              style={useAll ? { color:'#1677ff', fontWeight: 700 } : { color:'#fa541c', fontWeight: 700 }}
                            >
                              {text}
                            </span>
                          );
                        });
                        return (
                          <Space size={6}>
                            <span>{nodes}</span>
                            <Tooltip title="编辑操作对象匹配信息">
                              <Button
                                size="small"
                                type="text"
                                icon={<EditOutlined style={{ color: '#1677ff' }} />}
                                onClick={openPick}
                              />
                            </Tooltip>
                          </Space>
                        );
                      };
                      // UI 注入：如果文件里已存在该列，则覆写其渲染；否则在“数量分配(c3)”与“备注(c4)”之间插入新列
                      if (hasMatchCol) {
                        return baseCols2.map((c:any) => {
                          const t = (c as any)?.titleText || (c as any)?.title;
                          if (isMatchTitle(t)) {
                            const tip = '数据格式：操作对象-是否操作所有库存-预计数量，用于判断是否执行换标及操作记录的落库操作对象。';
                            return {
                              ...c,
                              title: (
                                <Tooltip title={tip}>
                                  <span style={{ cursor: 'help' }}>操作对象匹配信息</span>
                                </Tooltip>
                              ),
                              titleText: '操作对象匹配信息',
                              render: matchRender
                            };
                          }
                          return c;
                        }) as any;
                      }
                      const out: any[] = [];
                      for (const c of baseCols2) {
                        out.push(c);
                        if (String(c?.dataIndex || '') === 'c3') {
                          const tip = '数据格式：操作对象-是否操作所有库存-预计数量，用于判断是否执行换标及操作记录的落库操作对象。';
                          out.push({
                            title: (
                              <Tooltip title={tip}>
                                <span style={{ cursor: 'help' }}>操作对象匹配信息</span>
                              </Tooltip>
                            ),
                            titleText: '操作对象匹配信息',
                            key: '__match_target',
                            width: 180,
                            render: matchRender,
                          });
                        }
                      }
                      return out as any;
                    })()}
                    scroll={String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING'
                      ? ({ x: 'max-content', y: 600 } as any)
                      : ({ y: 600 } as any)}
                    tableLayout={String(attachPickerBaseline?.code || '').trim().toUpperCase() === 'SHIPPING_LABEL_TARGET_MAPPING' ? 'auto' : 'fixed'}
                  />
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 8, color:'#666' }}>无法在线解析该格式，点击下方打开原文件：</div>
                  <Button type="link" onClick={()=> { if (attPvUrl) window.open(attPvUrl, '_blank'); }}>在新窗口打开</Button>
                </div>
              )
            )
          )}
        </Modal>

        {/* BARCODE_MAPPING：批量指定“匹配操作对象”（仅处理无法匹配的行；多选） */}
        <Modal
          title="批量指定操作对象（匹配操作对象）"
          open={bmBatchPickOpen}
          onCancel={() => setBmBatchPickOpen(false)}
          zIndex={1200}
          getContainer={() => document.body}
          okText="应用并关闭"
          cancelText="取消"
          confirmLoading={bmTargetIuLoading}
          onOk={() => {
            try {
              const rows = Array.isArray(bmBatchPickRows) ? bmBatchPickRows : [];
              const selMap = (bmBatchPickSel || {}) as Record<number, number[]>;
              const idToMeta = new Map<number, { iu_id: number; system_barcode?: string; use_all_available: boolean; expected_qty: number | null }>();
              (bmTargetIuOptions || []).forEach((x:any) => {
                const id = Math.floor(Number(x?.id || 0));
                if (!id) return;
                const sys = String(x?.system_barcode || '').trim();
                idToMeta.set(id, { iu_id: id, system_barcode: sys || undefined, use_all_available: !!x?.use_all_available, expected_qty: (x?.expected_qty == null ? null : Number(x.expected_qty)) });
              });

              // 先更新匹配结果，再基于“更新后的匹配结果”重算 noTargetRows
              let nextMatch: Record<number, string> = {};
              const touchedIdx: number[] = [];
              setAttPvBmTargetMatchByIdx((prev) => {
                const next = { ...(prev || {}) };
                rows.forEach((r:any) => {
                  const __idx = Number(r?.__idx);
                  if (!Number.isFinite(__idx) || __idx <= 0) return;
                  const picks = Array.isArray(selMap[__idx]) ? selMap[__idx] : [];
                  if (!picks.length) return;
                  touchedIdx.push(__idx);
                  const metas = picks
                    .map((id:number) => idToMeta.get(Math.floor(Number(id) || 0)) || null)
                    .filter(Boolean) as any[];
                  if (!metas.length) return;
                  // 排序：use_all=否在前；每组 expected_qty 升序
                  metas.sort((a:any, b:any) => {
                    const ua = a.use_all_available ? 1 : 0;
                    const ub = b.use_all_available ? 1 : 0;
                    if (ua !== ub) return ua - ub;
                    const ea = (a.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
                    const eb = (b.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
                    if (ea !== eb) return ea - eb;
                    const sa = String(a.system_barcode || `IU#${a.iu_id}`);
                    const sb = String(b.system_barcode || `IU#${b.iu_id}`);
                    return sa.localeCompare(sb);
                  });
                  try { next[__idx] = JSON.stringify(metas); } catch { next[__idx] = '[]'; }
                });
                nextMatch = next;
                return next;
              });
              // 标记这些行是“手动指定”，避免后续再次审核覆盖
              try {
                if (touchedIdx.length > 0) {
                  setAttPvBmManualMatchIdx((prev) => {
                    const s = new Set<number>(Array.from(prev || []));
                    touchedIdx.forEach((n) => { if (Number.isFinite(n) && n > 0) s.add(n); });
                    return s;
                  });
                }
              } catch {}

              setAttPvBmAudit((prev) => {
                const base = prev;
                if (!base) return base;
                const rowsAll: any[] = Array.isArray(attPvData) ? attPvData : [];
                const noTarget: Array<{ rowNo: number; old: string }> = [];
                rowsAll.forEach((row:any) => {
                  const __idx = Number(row?.__idx);
                  if (!Number.isFinite(__idx) || __idx <= 0) return;
                  const txt = String((nextMatch as any)?.[__idx] ?? (attPvBmTargetMatchByIdx as any)?.[__idx] ?? '-').trim();
                  if (txt === '-' || txt === '') {
                    noTarget.push({ rowNo: __idx + 1, old: String(row?.c0 ?? '').trim() || '-' });
                  }
                });
                const next = { ...base, noTargetRows: noTarget };
                const has = (next.noTargetRows?.length || 0)
                  || (next.newEmptyRows?.length || 0)
                  || (next.sameRows?.length || 0)
                  || (next.dupPairs?.length || 0)
                  || (next.badRows?.length || 0);
                return has ? next : null;
              });

              setAttPvDirty(true);
              setBmBatchPickOpen(false);
              message.success('已应用操作对象匹配结果');
            } catch {}
          }}
          width={1280}
          bodyStyle={{ overflowX: 'hidden' }}
          destroyOnClose
        >
          <div style={{ marginBottom: 8, color:'#595959' }}>
            支持多选库存单元；最终会写入基线文件的“操作对象匹配信息”列（JSON 存储，界面人类可读展示）。
          </div>
          <Table
            size="small"
            rowKey={(r:any)=> String(r?.__idx || '')}
            dataSource={bmBatchPickRows}
            pagination={false}
            scroll={{ y: 420 }}
            tableLayout="fixed"
            columns={[
              { title: '序号', dataIndex: '__idx', width: 70, align:'center', render: (v:any)=> <span style={{ color: '#999' }}>{v || '-'}</span> },
              { title: '旧条码', dataIndex: 'old', width: 180, ellipsis: true, render: (v:any)=> <Typography.Text style={{ fontFamily:'monospace' }} ellipsis={{ tooltip: String(v||'-') }}>{String(v||'-')}</Typography.Text> },
              { title: '新条码', dataIndex: 'newer', width: 180, ellipsis: true, render: (v:any)=> <Typography.Text style={{ fontFamily:'monospace' }} ellipsis={{ tooltip: String(v||'-') }}>{String(v||'-')}</Typography.Text> },
              { title: '数量分配', dataIndex: 'qty', width: 90, render: (v:any)=> (String(v ?? '').trim() || '-') },
              { title: '备注', dataIndex: 'remark', width: 140, ellipsis: true, render: (v:any)=> <Typography.Text ellipsis={{ tooltip: String(v ?? '').trim() || '-' }}>{String(v ?? '').trim() || '-'}</Typography.Text> },
              {
                title: '选择操作对象（多选）',
                key: 'pick',
                render: (_:any, r:any) => {
                  const __idx = Number(r?.__idx);
                  const value = (Number.isFinite(__idx) && __idx > 0) ? (bmBatchPickSel[__idx] || []) : [];
                  return (
                    <Select
                      mode="multiple"
                      showSearch
                      placeholder={bmTargetIuLoading ? '加载中...' : '请选择库存单元'}
                      loading={bmTargetIuLoading}
                      style={{ width: '100%' }}
                      value={value as any}
                      popupMatchSelectWidth={false as any}
                      dropdownStyle={{ minWidth: 820 }}
                      optionFilterProp="label"
                      filterOption={(input, opt:any) => {
                        const s = String(opt?.label || '').toLowerCase();
                        return s.includes(String(input || '').toLowerCase());
                      }}
                      optionRender={(opt:any) => {
                        return (
                          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                            {String(opt?.label || '')}
                          </div>
                        );
                      }}
                      options={(bmTargetIuOptions || []).map((x:any) => {
                        const buildBrief = (arr: Array<{ barcode: string; qty: number }>) => {
                          try {
                            const list = Array.isArray(arr) ? arr : [];
                            if (!list.length) return '';
                            const parts = list.slice(0, 3).map((it:any) => {
                              const bc = String(it?.barcode || '').trim();
                              const qty = Number(it?.qty ?? 0) || 0;
                              return bc ? `${bc}×${qty}` : '';
                            }).filter(Boolean);
                            const head = parts.join(' / ');
                            if (list.length > 3) return `${head} 等${list.length}项`;
                            return head;
                          } catch { return ''; }
                        };
                        const sys = String(x?.system_barcode || '');
                        // 说明：bmTargetIuOptions.content_brief 可能为空；但 overviewUnitContentCache 已在 loadBmTargetIuOptions 中增量补拉。
                        // 为保证“下拉菜单一定能看到内容项”，这里实时用 cache 兜底生成 brief。
                        const cached = (() => {
                          try {
                            const id = Math.floor(Number(x?.id || 0));
                            if (!id) return [];
                            const arr = overviewUnitContentCache?.[String(id)];
                            return Array.isArray(arr) ? arr : [];
                          } catch { return []; }
                        })();
                        const brief = String(x?.content_brief || '').trim() || (cached.length > 0 ? buildBrief(cached as any) : '');
                        const label = sys ? `${sys} 内容项：${brief || '无内容项'}` : '-';
                        return { value: x.id, label };
                      })}
                      onChange={(vals:any) => {
                        const arr = Array.isArray(vals) ? vals : [];
                        const ids = arr.map((v:any) => Math.floor(Number(v) || 0)).filter((n:number) => Number.isFinite(n) && n > 0);
                        if (!Number.isFinite(__idx) || __idx <= 0) return;
                        setBmBatchPickSel(prev => ({ ...(prev || {}), [__idx]: ids }));
                      }}
                    />
                  );
                }
              }
            ] as any}
          />
        </Modal>

        {/* 修改状态弹窗 */}
        <Modal
          title={(() => {
            try {
              const sc = statusOf?.system_code || statusOf?.id;
              return `修改状态-${sc ?? ''}`;
            } catch { return '修改状态'; }
          })()}
          open={!!statusOf}
          onCancel={() => { setStatusOf(null); setStatusNext(undefined); }}
          confirmLoading={statusSubmitting}
          onOk={async () => {
            if (!statusOf) return;
            const next = statusNext;
            if (!next) { message.warning('请选择新状态'); return; }
            try {
              setStatusSubmitting(true);
              const payload: any = { status: next };
              // 某些后端校验会触发对 customer_answers 的校验；确保传入一个字典
              if (statusOf && (statusOf.customer_answers === undefined || statusOf.customer_answers === null)) {
                payload.customer_answers = {};
              } else if (statusOf && typeof statusOf.customer_answers === 'object') {
                payload.customer_answers = statusOf.customer_answers;
              }
              await request.patch(`/api/v1/instruction/instructions/${statusOf.id}/`, payload);
              message.success('状态已更新');
              setRowOverrides(prev => ({ ...prev, [String(statusOf.id)]: { status: next } }));
              setStatusOf(null);
              setStatusNext(undefined);
              try { await (actions as any).refresh?.(); } catch { try { actions.refresh(); } catch {} }
            } catch (e:any) {
              message.error(e?.response?.data?.detail || '更新失败');
            } finally {
              setStatusSubmitting(false);
            }
          }}
        >
          {statusOf ? (
            <div>
              <div style={{ marginBottom: 8 }}>当前状态：{(() => {
                const s = getEffectiveStatusGlobal(statusOf);
                return <Tag color={statusColorGlobal(s)}>{statusTextGlobal(s)}</Tag>;
              })()}</div>
              <div>
                <span style={{ marginRight: 8 }}>选择新状态：</span>
                <Select
                  style={{ minWidth: 200 }}
                  value={statusNext}
                  onChange={(v) => setStatusNext(v)}
                >
                  <Option value="pending">待受理</Option>
                  <Option value="in_progress">执行中</Option>
                  <Option value="operation_done">执行阶段完成(待终审)</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="cancelled">已取消</Option>
                </Select>
              </div>
            </div>
          ) : null}
        </Modal>
        {/* 列表内包装结果弹窗 */}
        <Modal
          title={(
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
              <span>{packModalMode === 'edit' ? '填写包装结果' : '包装结果'}</span>
              <span style={{ color: '#1677ff', fontWeight: 600 }}>
                {String(packModalRow?.system_code || '-')}
              </span>
              <span />
            </div>
          )}
          open={packModalOpen}
          onCancel={() => { setPackModalOpen(false); setPackModalRow(null); }}
          onOk={packModalMode === 'edit' ? savePackModal : undefined}
          confirmLoading={packModalSaving}
          footer={packModalMode === 'edit' ? undefined : [<Button key="close" onClick={() => { setPackModalOpen(false); setPackModalRow(null); }}>关闭</Button>]}
          width={640}
        >
          {packModalMode === 'edit' ? (
            <Form form={packModalForm} layout="vertical">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Form.Item name="actual_pack_length_cm" label="实际包装长度(cm)">
                  <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入长度" />
                </Form.Item>
                <Form.Item name="actual_pack_width_cm" label="实际包装宽度(cm)">
                  <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入宽度" />
                </Form.Item>
                <Form.Item name="actual_pack_height_cm" label="实际包装高度(cm)">
                  <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入高度" />
                </Form.Item>
                <Form.Item name="actual_gross_weight_g" label="实际包装重量(g)">
                  <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="请输入重量" />
                </Form.Item>
              </div>
              <div style={{ color: '#8c8c8c' }}>填写完成后，客户可在列表中直接查看包装结果。</div>
            </Form>
          ) : (
            (() => {
              const r = packModalRow || {};
              const fmt1 = (v: number) => (Math.round(v * 10) / 10).toFixed(1);
              const toInch = (cm: number) => cm / 2.54;
              const toLb = (g: number) => g / 453.59237;
              const l = Number(r?.actual_pack_length_cm);
              const w = Number(r?.actual_pack_width_cm);
              const h = Number(r?.actual_pack_height_cm);
              const g = Number(r?.actual_gross_weight_g);
              const sizeText = (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0)
                ? `${fmt1(l)} × ${fmt1(w)} × ${fmt1(h)} cm | ${fmt1(toInch(l))} × ${fmt1(toInch(w))} × ${fmt1(toInch(h))} in`
                : '-';
              const weightText = (Number.isFinite(g) && g > 0)
                ? `${fmt1(g / 1000)} kg | ${fmt1(toLb(g))} lb`
                : '-';
              return (
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="实际包装尺寸">{sizeText}</Descriptions.Item>
                  <Descriptions.Item label="实际包装重量">{weightText}</Descriptions.Item>
                </Descriptions>
              );
            })()
          )}
        </Modal>
        {/* 操作对象明细弹窗 */}
        {targetsOf && (
          <TargetsPanel record={targetsOf} projects={projects} onClose={() => setTargetsOf(null)} />
        )}
        {/* 历史指令输入IOC明细弹窗（仅用于“预估操作数量”列点击） */}
        <Modal
          title={(
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', paddingRight: 140 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <span>操作对象明细(历史指令执行结果)</span>
                <span style={{ color: '#d9480f' }}>{String(historyTargetsOf?.system_code || '')}</span>
              </span>
              <span style={{ marginRight: 12 }}>
                {(() => {
                  const rows = Array.isArray(historyTargetsRows) ? historyTargetsRows : [];
                  const total = rows.length;
                  const prjCount = (() => {
                    try {
                      const set = new Set<string>();
                      rows.forEach((r:any) => {
                        const pid = Number(r?.project_id);
                        if (Number.isFinite(pid)) set.add(String(pid));
                        else if (r?.project_code) set.add(`code:${String(r.project_code)}`);
                      });
                      return set.size;
                    } catch { return 0; }
                  })();
                  const totalQty = rows.reduce((acc:number, r:any) => {
                    // 避免父容器“含子容器汇总”与子容器重复：优先统计直接装载数量
                    const n = Number(
                      r?.output_contents_direct_total_qty != null
                        ? r?.output_contents_direct_total_qty
                        : (r?.output_contents_total_qty || 0),
                    );
                    return acc + (Number.isFinite(n) ? n : 0);
                  }, 0);
                  return (
                    <>
                      <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:600, marginRight:8 }}>
                        涉及项目数量 {prjCount}
                      </span>
                      <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #ffe58f', background:'#fffbe6', color:'#ad8b00', borderRadius:4, fontWeight:600, marginRight:8 }}>
                        指令产出容器(IOC)总数量 {total}
                      </span>
                      <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #b7eb8f', background:'#f6ffed', color:'#389e0d', borderRadius:4, fontWeight:600 }}>
                        容器内库存单元总数量 {totalQty}
                      </span>
                    </>
                  );
                })()}
              </span>
            </div>
          )}
          open={!!historyTargetsOf}
          onCancel={() => { setHistoryTargetsOf(null); setHistoryTargetsRows([]); }}
          footer={null}
          width={1280}
        >
          <style>{`
            .history-target-ioc-table .ant-table-tbody > tr.history-target-ioc-group-row > td {
              border-left: 2px solid #91caff !important;
              border-right: 2px solid #91caff !important;
            }
            .history-target-ioc-table .ant-table-tbody > tr.history-target-ioc-group-start > td {
              border-top: 2px solid #91caff !important;
            }
            .history-target-ioc-table .ant-table-tbody > tr.history-target-ioc-group-end > td {
              border-bottom: 2px solid #91caff !important;
            }
          `}</style>
          <Table
            className="history-target-ioc-table"
            size="small"
            rowKey={(r:any, i?:number)=> String(r?.__key || r?.output_container_id || r?.output_code || i)}
            dataSource={(() => {
              const arr = Array.isArray(historyTargetsVisualRows) ? historyTargetsVisualRows : [];
              const start = (historyTargetsPage - 1) * historyTargetsPageSize;
              const end = start + historyTargetsPageSize;
              return arr.slice(start, end);
            })()}
            loading={historyTargetsLoading}
            onRow={(record: any, index?: number) => {
              const rowKey = String(record?.__key || record?.output_container_id || record?.output_code || index);
              const borderMeta = historyTargetsVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
              return {
                style: {
                  borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                  borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                  borderTop: borderMeta.top ? '2px solid #91caff' : undefined,
                  borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
                },
              };
            }}
            rowClassName={(record: any, index?: number) => {
              const rowKey = String(record?.__key || record?.output_container_id || record?.output_code || index);
              const borderMeta = historyTargetsVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
              if (!borderMeta.grouped) return '';
              if (borderMeta.top) return 'history-target-ioc-group-row history-target-ioc-group-start';
              if (borderMeta.bottom) return 'history-target-ioc-group-row history-target-ioc-group-end';
              return 'history-target-ioc-group-row';
            }}
            pagination={{
              current: historyTargetsPage,
              pageSize: historyTargetsPageSize,
              total: Array.isArray(historyTargetsVisualRows) ? historyTargetsVisualRows.length : 0,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10','20','50','100'] as any,
              onChange: (p:number, ps:number) => { setHistoryTargetsPage(p); setHistoryTargetsPageSize(ps); },
            }}
            columns={[
              { title: '项目', key: 'project', render: (_:any, row:any) => {
                const code = row?.project_code || row?.project_id;
                const name = row?.project_name || '';
                return code ? `${code}${name ? ` - ${name}` : ''}` : '-';
              }},
              { title: '容器系统编码', key: 'output_code', render: (_:any, row:any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.output_code || row?.display_code || '-')}</span> },
              {
                title: '层级标识',
                key: '__hierarchy_role__',
                width: 120,
                render: (_:any, row:any) => {
                  const rowKey = String(row?.__key || row?.output_container_id || row?.output_code || '');
                  const role = historyTargetsVisualMeta.rowRoleMap.get(rowKey) || 'standalone';
                  const roleText = role === 'parent' ? '父容器' : (role === 'child' ? '子容器' : '独立容器');
                  const roleStyle = role === 'parent'
                    ? { color: '#1677ff', border: '#91caff', bg: '#e6f4ff' }
                    : (role === 'child'
                      ? { color: '#d4380d', border: '#ffbb96', bg: '#fff2e8' }
                      : { color: '#389e0d', border: '#b7eb8f', bg: '#f6ffed' });
                  return (
                    <span
                      style={{
                        display: 'inline-block',
                        minWidth: 76,
                        textAlign: 'center',
                        padding: '2px 10px',
                        borderRadius: 6,
                        border: `1px solid ${roleStyle.border}`,
                        color: roleStyle.color,
                        background: roleStyle.bg,
                        lineHeight: 1.4,
                      }}
                    >
                      {roleText}
                    </span>
                  );
                },
              },
              { title: '容器类型', dataIndex: 'container_type_display', width: 120, render: (v:any)=> {
                const key = String(v || '').trim().toLowerCase();
                return overviewContainerTypeTextMap[key] || String(v || '-');
              }},
              { title: '内容类型', dataIndex: 'output_content_kind_display', width: 140, render: (v:any, row:any)=> {
                const kind = String(row?.output_content_kind || '').toLowerCase();
                const isException = kind === 'exception';
                return (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
                    background: isException ? '#fff7e6' : '#f6ffed',
                    color: isException ? '#fa8c16' : '#389e0d',
                    lineHeight: 1.5,
                  }}>
                    {String(v || '-')}
                  </span>
                );
              }},
              { title: '容器内容统计', dataIndex: 'output_contents_summary', width: 160, render: (_:any, row:any) => {
                const kind = String(row?.output_content_kind || '').toLowerCase();
                const qty = Number(row?.output_contents_total_qty || 0);
                if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
                const raw = String(row?.output_contents_summary || '').trim();
                if (raw && raw.includes('款') && raw.includes('件')) return raw;
                const m = String(row?.output_contents_detail || '').split(/[；;]/).map((x:any)=> String(x || '').trim()).filter(Boolean).length;
                return `${Math.max(0, m)}款${Math.max(0, qty)}件`;
              }},
              { title: '容器内容', dataIndex: 'output_contents_detail', width: 260, render: (v:any, row:any) => {
                const kind = String(row?.output_content_kind || '').toLowerCase();
                if (kind === 'exception') {
                  return (
                    <Tooltip title="无法操作等异常产品">
                      <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        无法操作等异常产品
                      </div>
                    </Tooltip>
                  );
                }
                const full = String(v || '').trim();
                if (!full) return '-';
                const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                if (lines.length === 0) return '-';
                const preview = lines.slice(0, 3);
                const hasMore = lines.length > 3;
                return (
                  <div style={{ maxWidth: 300 }}>
                    {preview.map((line, idx) => (
                      <div
                        key={`${line}-${idx}`}
                        style={{
                          position: 'relative',
                          minHeight: 20,
                          marginBottom: idx === preview.length - 1 ? 0 : 2,
                        }}
                      >
                        <Tooltip title={line}>
                          <div
                            style={{
                              color: '#595959',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                            }}
                          >
                            {line}
                          </div>
                        </Tooltip>
                        {hasMore && idx === preview.length - 1 ? (
                          <Button
                            type="link"
                            size="small"
                            style={{
                              position: 'absolute',
                              right: 0,
                              bottom: -2,
                              padding: 0,
                              height: 'auto',
                              lineHeight: 1.2,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const iocCode = String(row?.output_code || row?.display_code || '-');
                              Modal.info({
                                title: (
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                                    <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                                    <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                                    <span />
                                  </div>
                                ),
                                width: 760,
                                centered: true,
                                content: (
                                  <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                    {lines.map((x, i) => (
                                      <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                        {i + 1}. {x}
                                      </div>
                                    ))}
                                  </div>
                                ),
                              });
                            }}
                          >
                            [...]
                          </Button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                );
              }},
              { title: '容器尺寸', key: 'dims', width: 180, render: (_:any, row:any) => (row?.length_cm != null && row?.width_cm != null && row?.height_cm != null) ? `${row.length_cm}×${row.width_cm}×${row.height_cm} cm` : '' },
              { title: '容器重量', dataIndex: 'weight_g', width: 120, render: (v:any) => (v != null ? `${v}g` : '') },
            ] as any}
          />
        </Modal>
        {/* 涉及项目弹窗 */}
        {projectsOf && (
          <ProjectsPanel record={projectsOf} onClose={() => setProjectsOf(null)} />
        )}
      </Card>
    </div>
  );
};

const VerifyPanel: React.FC<{ instructionId: number }> = ({ instructionId }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>({});
  const [instruction, setInstruction] = useState<any | null>(null);
  const refresh = async () => {
    setLoading(true);
    try {
      // 指令详情：用于显示问题集与答案
      const ins = await request.get(`/api/v1/instruction/instructions/${instructionId}/`);
      setInstruction(ins?.data || null);
      const att = await request.get('/api/v1/instruction/attachments/', { params: { instruction: instructionId, page_size: 200 } });
      setResult({ attachments: (att.data?.results || att.data || []) });
    } catch {}
    setLoading(false);
  };
  const load = async () => {
    await refresh();
  };
  useEffect(()=>{ load(); }, [instructionId]);
  return (
    <div>
      <Descriptions title="问题集与客户回答" size="small" column={1} bordered>
        {(() => {
          try {
            const qn = (instruction?.questionnaire_snapshot || {}) as any;
            const qs = Array.isArray(qn?.questions) ? qn.questions : [];
            const ans = (instruction?.customer_answers || {}) as any;
            if (!qs || qs.length === 0) {
              return (
                <Descriptions.Item label="问题">-</Descriptions.Item>
              );
            }
            const renderVal = (q:any) => {
              const key = String(q.key||'').trim();
              const val = ans[key];
              const type = String(q.type||'');
              if (val === undefined || val === null || val === '') {
                if (q.required) return <Tag color="error">未提交</Tag>;
                return <span style={{ color: '#999' }}>未提交</span>;
              }
              if (type === 'boolean') return val ? '是' : '否';
              if (type === 'select') {
                const opt = (q.options||[]).find((o:any)=> String(o.value)===String(val));
                return opt ? (opt.label || opt.value) : String(val);
              }
              if (type === 'multiselect') {
                const arr = Array.isArray(val) ? val : [];
                const labels = arr.map((v:any)=>{
                  const opt = (q.options||[]).find((o:any)=> String(o.value)===String(v));
                  return opt ? (opt.label || opt.value) : String(v);
                });
                return labels.join('、');
              }
              if (type === 'number') return String(val) + (q.unit ? ` ${q.unit}` : '');
              return String(val);
            };
            return qs.map((q:any, idx:number) => (
              <Descriptions.Item key={idx} label={(q.required? '＊' : '') + (q.label || q.key || `问题${idx+1}`)}>
                <Space>
                  <span>{renderVal(q)}</span>
                  {q.tips ? <Tag>{q.tips}</Tag> : null}
                </Space>
              </Descriptions.Item>
            ));
          } catch {
            return (
              <Descriptions.Item label="问题">-</Descriptions.Item>
            );
          }
        })()}
      </Descriptions>
      <div style={{ height: 12 }} />
      <Descriptions title="附件" size="small" column={1} bordered>
        {(result.attachments || []).map((a:any)=> (
          <Descriptions.Item key={a.id} label={`${a.label || a.mime_type || '附件'} (${a.scope || 'order'})`}>
            <Space>
              <span>{a.file_name || a.file_path || a.file}</span>
              <Button size="small" onClick={() => {
                try {
                  const url = getAttachmentUrl(a);
                  if (url) window.open(url, '_blank');
                } catch {}
              }}>预览/下载</Button>
              <Button size="small" danger onClick={async () => {
                try {
                  await request.delete(`/api/v1/instruction/attachments/${a.id}/`);
                  message.success('已删除');
                  refresh();
                } catch {
                  message.error('删除失败');
                }
              }}>删除</Button>
            </Space>
          </Descriptions.Item>
        ))}
        {(result.attachments || []).length === 0 && (
          <Descriptions.Item label="附件">无</Descriptions.Item>
        )}
      </Descriptions>
    </div>
  );
}

// 目标明细弹窗
const TargetsPanel: React.FC<{ record: any | null; onClose: () => void; projects?: any[] }> = ({ record, onClose, projects = [] }) => {
  const rows = Array.isArray(record?.targets_detail) ? record.targets_detail : [];
  const sum = rows.reduce((acc: number, t: any) => {
    const v = Number(t?.expected_qty ?? 0);
    return acc + (isNaN(v) ? 0 : v);
  }, 0);
  const totalKinds = rows.length;
  const allKinds = rows.filter((r:any)=> !!r?.use_all_available).length;
  const partKinds = Math.max(0, totalKinds - allKinds);
  const prjCount = (() => {
    try {
      const set = new Set<string>();
      (rows || []).forEach((r:any) => {
        const pid = r?.project_id ?? r?.target?.project_id;
        if (pid != null) { set.add(String(pid)); return; }
        const pcode = r?.project_code || r?.target?.snapshot?.project_code;
        if (pcode) { set.add(`code:${String(pcode)}`); }
      });
      return set.size;
    } catch { return 0; }
  })();
  const [targetsPage, setTargetsPage] = useState(1);
  const [targetsPageSize, setTargetsPageSize] = useState(10);
  useEffect(() => { setTargetsPage(1); }, [record]);
  const [unitContentCache, setUnitContentCache] = useState<Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>>>({});
  const [unitTypeCache, setUnitTypeCache] = useState<Record<string, string>>({});
  const [unitSortingGroupCache, setUnitSortingGroupCache] = useState<Record<string, { code: string; name: string }>>({});

  const projectMap = useMemo(() => new Map<string, any>(projects.map((p:any) => [String(p.id), p])), [projects]);
  const renderType = (v: string) => {
    const map: Record<string, string> = {
      inventory_unit: '库存单元',
      sorting_group: '分拣组',
      project_all: '项目内全部',
      instruction_output: '上游指令产出',
    };
    return map[v] || v || '-';
  };
  const renderProjectReadable = (row: any) => {
    const pid = row?.project_id != null ? String(row.project_id) : '';
    const p = pid ? projectMap.get(pid) : null;
    if (p) return `${p.project_code || p.code || p.id} - ${p.name || '-'}`;
    const snap = row?.snapshot || {};
    const code = snap.project_code || snap.code || pid;
    const name = snap.project_name || snap.name || '';
    if (code || name) return `${code}${name ? ' - ' + name : ''}`;
    return '-';
  };
  const renderInventoryUnitReadable = (row: any) => {
    const snap = row?.snapshot || {};
    const code = snap.system_barcode
      || row?.inventory_unit_system_barcode
      || row?.inventory_unit?.system_barcode
      || snap.system_code
      || snap.barcode
      || snap.code
      || '';
    if (code) return code;
    const id = row?.inventory_unit_id;
    return id != null ? String(id) : '-';
  };
  const renderSortingGroupReadable = (row: any) => {
    try {
      const targetType = String(row?.target_type || row?.target?.target_type || '').trim();
      if (targetType && targetType !== 'inventory_unit') return '-';
      const snap = row?.snapshot || row?.target?.snapshot || {};
      const info = row?.sorting_group_info || row?.target?.sorting_group_info || snap?.sorting_group_info || {};
      const uid = row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? snap?.inventory_unit_id;
      const cached = uid != null ? unitSortingGroupCache[String(uid)] : undefined;
      const code = String(
        info?.system_code
        || info?.code
        || row?.sorting_group_code
        || row?.target?.sorting_group_code
        || cached?.code
        || '',
      ).trim();
      const name = String(
        info?.name
        || row?.sorting_group_name
        || row?.target?.sorting_group_name
        || cached?.name
        || '',
      ).trim();
      if (code && name) return `${code} (${name})`;
      if (code) return code;
      if (name) return name;
      return '-';
    } catch {
      return '-';
    }
  };
  const extractUnitContentItems = (row: any): Array<{ barcode: string; qty: number; productName?: string; customCode?: string }> => {
    try {
      const snap = row?.snapshot || {};
      const candidates: any[] = [
        snap?.content?.items,
        row?.content?.items,
        row?.inventory_unit?.content?.items,
        (snap?.content && Array.isArray(snap.content) ? snap.content : null),
        row?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          return arr.map((it: any) => {
            const p = it?.product || {};
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            const productName = String(
              it?.product_name
              || p?.name
              || ''
            ).trim();
            const customCode = String(
              it?.product_custom_code
              || p?.custom_code
              || it?.product_code
              || ''
            ).trim();
            // 优先来自 InventoryDetail 风格：it.sorting_codes = [{code, description?}]
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(
                p?.sorting_barcode
                || p?.barcode_text
                || p?.system_barcode
                || p?.barcode
                || p?.code
                || p?.sku
                || it?.product_code
                || it?.system_code
                || it?.product_id
                || ''
              );
            }
            return { barcode, qty, productName, customCode };
          }).filter(x => x.barcode || x.qty);
        }
      }
    } catch {}
    return [];
  };
  const renderUnitContentItems = (row: any) => {
    const items = extractUnitContentItems(row);
    if ((!items || items.length === 0) && row?.inventory_unit_id != null) {
      const key = String(row.inventory_unit_id);
      const cached = unitContentCache[key];
      if (cached && cached.length > 0) {
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {cached.map((it, idx) => (
              <span key={idx} style={{ marginRight: 6 }}>
                {(() => {
                  const productName = String((it as any)?.productName || '').trim();
                  const customCode = String((it as any)?.customCode || '').trim();
                  const barcode = String((it as any)?.barcode || '').trim();
                  const meta = [customCode, barcode].filter(Boolean).join(' ');
                  if (productName && meta) return `${productName}(${meta})`;
                  if (productName) return productName;
                  if (meta) return meta;
                  return '-';
                })()} × {it.qty || 0}{idx < cached.length - 1 ? '、' : ''}
              </span>
            ))}
          </div>
        );
      }
    }
    if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {items.map((it, idx) => (
          <span key={idx} style={{ marginRight: 6 }}>
            {(() => {
              const productName = String((it as any)?.productName || '').trim();
              const customCode = String((it as any)?.customCode || '').trim();
              const barcode = String((it as any)?.barcode || '').trim();
              const meta = [customCode, barcode].filter(Boolean).join(' ');
              if (productName && meta) return `${productName}(${meta})`;
              if (productName) return productName;
              if (meta) return meta;
              return '-';
            })()} × {it.qty || 0}{idx < items.length - 1 ? '、' : ''}
          </span>
        ))}
      </div>
    );
  };
  const unitTypeToReadable = (v?: string) => {
    const key = String(v || '').toUpperCase();
    const map: Record<string, string> = {
      LIU: '散货库存单元',
      BIU: '箱库存单元',
      PIU: '托盘库存单元',
      AIU: '异常库存单元',
      NIU: '非产品库存单元',
    };
    return map[key] || '';
  };
  const getUnitTypeFromRow = (row: any): string | '' => {
    try {
      const snap = row?.snapshot || {};
      const direct = snap?.unit_type || snap?.inventory_unit?.unit_type || row?.unit_type || row?.inventory_unit?.unit_type;
      if (direct) return String(direct);
    } catch {}
    try {
      if (row?.inventory_unit_id != null) {
        const key = String(row.inventory_unit_id);
        const cached = unitTypeCache[key];
        if (cached) return cached;
      }
    } catch {}
    return '';
  };
  const renderTargetTypeReadable = (row: any) => {
    const v = String(row?.target_type || '').trim();
    if (v === 'inventory_unit') {
      const ut = unitTypeToReadable(getUnitTypeFromRow(row));
      return ut ? ut : '库存单元';
    }
    const map: Record<string, string> = {
      inventory_unit: '库存单元',
      sorting_group: '分拣组',
      project_all: '项目内全部',
      instruction_output: '上游指令产出',
    };
    return map[v] || v || '-';
  };
  // 若 snapshot 不含内容项，则按需补拉库存单元详情，填充缓存
  useEffect(() => {
    (async () => {
      try {
        const pendingIds = new Set<string>();
        rows.forEach((r: any) => {
          const has = extractUnitContentItems(r);
          const uid = r?.inventory_unit_id;
          const hasSortingGroup = (() => {
            try {
              const snap = r?.snapshot || r?.target?.snapshot || {};
              const info = r?.sorting_group_info || r?.target?.sorting_group_info || snap?.sorting_group_info || {};
              return !!String(
                info?.system_code
                || info?.code
                || info?.name
                || r?.sorting_group_code
                || r?.target?.sorting_group_code
                || r?.sorting_group_name
                || r?.target?.sorting_group_name
                || '',
              ).trim();
            } catch {
              return false;
            }
          })();
          if (uid != null && ((!has || has.length === 0) || !hasSortingGroup)) {
            const key = String(uid);
            if (!unitContentCache[key] || !unitSortingGroupCache[key]) pendingIds.add(key);
          }
        });
        if (pendingIds.size === 0) return;
        const updates: Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>> = {};
        const typeUpdates: Record<string, string> = {};
        const sortingGroupUpdates: Record<string, { code: string; name: string }> = {};
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            if (d?.unit_type) {
              typeUpdates[id] = String(d.unit_type);
            }
            try {
              const sg = d?.sorting_group_info || {};
              const code = String(sg?.system_code || sg?.code || '').trim();
              const name = String(sg?.name || '').trim();
              if (code || name) sortingGroupUpdates[id] = { code, name };
            } catch {}
            const mapped = (arr || []).map((it: any) => {
              const p = it?.product || {};
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              const productName = String(
                it?.product_name
                || p?.name
                || ''
              ).trim();
              const customCode = String(
                it?.product_custom_code
                || p?.custom_code
                || it?.product_code
                || ''
              ).trim();
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || ''
                );
              }
              return { barcode, qty, productName, customCode };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped && mapped.length > 0) updates[id] = mapped;
          } catch {}
        }));
        if (Object.keys(updates).length > 0) {
          setUnitContentCache(prev => ({ ...prev, ...updates }));
        }
        if (Object.keys(typeUpdates).length > 0) {
          setUnitTypeCache(prev => ({ ...prev, ...typeUpdates }));
        }
        if (Object.keys(sortingGroupUpdates).length > 0) {
          setUnitSortingGroupCache(prev => ({ ...prev, ...sortingGroupUpdates }));
        }
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <Modal
      title={(
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', paddingRight: 140 }}>
          <span>操作对象明细(当前可用库存)</span>
          {record?.system_code && (
            <span style={{ marginLeft: 12, marginRight: 'auto', color: '#d9480f', fontSize: 'inherit' }}>
              {String(record.system_code)}
            </span>
          )}
          <span>
            <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:600, fontSize:12, marginRight:6 }}>
              涉及项目数量 {prjCount}
            </span>
            <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #ffe58f', background:'#fffbe6', color:'#ad8b00', borderRadius:4, fontWeight:600, fontSize:12, marginRight:6 }}>
              操作对象种类 {totalKinds} | 操作全部库存种类 {allKinds} | 指定操作数量种类 {partKinds}
            </span>
            <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #b7eb8f', background:'#f6ffed', color:'#389e0d', borderRadius:4, fontWeight:600, fontSize:12 }}>
              预计操作总数量 {sum}
            </span>
          </span>
        </div>
      )}
      open={!!record}
      onCancel={onClose}
      footer={null}
      width={1200}
    >
      <Table
        size="small"
        rowKey={(r:any, index?: number) => String(r?.id ?? index)}
        dataSource={useMemo(() => {
          const arr = Array.isArray(rows) ? rows.slice() : [];
          const noAll = arr.filter((r:any) => !r?.use_all_available);
          const yesAll = arr.filter((r:any) => !!r?.use_all_available);
          const ordered = [...noAll, ...yesAll];
          const start = (targetsPage - 1) * targetsPageSize;
          const end = start + targetsPageSize;
          return ordered.slice(start, end);
        }, [rows, targetsPage, targetsPageSize])}
        pagination={{
          current: targetsPage,
          pageSize: targetsPageSize,
          total: (() => {
            try { return Array.isArray(rows) ? rows.length : 0; } catch { return 0; }
          })(),
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10','20','50','100'] as any,
          onChange: (p:number, ps:number) => { setTargetsPage(p); setTargetsPageSize(ps); },
        }}
        onRow={(r:any) => {
          const isAll = !!r?.use_all_available;
          return {
            style: {
              backgroundColor: isAll ? '#f6ffed' : '#fff0f6' // 是=浅绿色；否=粉红色
            }
          };
        }}
        columns={[
          { title: '项目', key: 'project', render: (_:any, row:any) => renderProjectReadable(row) },
          { title: '类型', dataIndex: 'target_type', key: 'type', render: (_v:any, row:any) => renderTargetTypeReadable(row) },
          { title: '库存单元系统编码', key: 'inventory_unit', render: (_:any, row:any) => renderInventoryUnitReadable(row) },
          { title: '分拣组', key: 'sorting_group', render: (_:any, row:any) => renderSortingGroupReadable(row) },
          { title: '库存单元内容项', key: 'inventory_unit_items', render: (_:any, row:any) => renderUnitContentItems(row) },
          { title: '上游指令ID', dataIndex: 'prev_instruction_id', key: 'prev_instruction_id', render: (v:any) => v ?? '-' },
          { title: '是否操作全部库存', dataIndex: 'use_all_available', key: 'use_all_available', render: (v:any) => {
            const isAll = !!v;
            const text = isAll ? '是' : '否';
            const style: React.CSSProperties = isAll
              ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' }
              : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' };
            return <span style={style}>{text}</span>;
          } },
          { title: '预计数量', dataIndex: 'expected_qty', key: 'expected_qty', render: (v:any) => (v ?? 0) },
        ]}
      />
    </Modal>
  );
};

// 涉及项目弹窗
const ProjectsPanel: React.FC<{ record: any | null; onClose: () => void }> = ({ record, onClose }) => {
  const rows = Array.isArray(record?.projects_detail) ? record.projects_detail : [];
  return (
    <Modal title={`涉及项目（共 ${rows.length} 个）`} open={!!record} onCancel={onClose} footer={null} width={720}>
      <Table
        size="small"
        rowKey={(r:any, index?: number) => String(r?.id ?? index)}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: '项目编码', dataIndex: 'project_code', key: 'project_code', render: (v:any) => v || '-' },
          { title: '项目名称', dataIndex: 'name', key: 'name', render: (v:any) => v || '-' },
        ]}
      />
    </Modal>
  );
};

export default InstructionListPage;


